/**
 * FieldJob - All module components
 * Version: 2.0
 */
import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import {
  OWNER_EMAIL, OWNER_PASSWORD, isOwner,
  V, FormError,
  NotifContext, NotifProvider,
  IDB, Icon,
  DeleteConfirmModal, PasswordInput,
  TABLE_MAP, DataCache, DB,
  SUPABASE_URL, SUPABASE_ANON_KEY, supabase, fetchWithRetry, getAuthHeader,
  formatZAR, formatCurrency, getCurrencyOption, CURRENCY_OPTIONS,
  UNIT_LABELS, unitLabel,
  formatDate, today, addDays, daysLeft,
  STATUS_COLORS, STATUS_PIPELINE,
  printAsPDF,
  buildDocHTML, buildReceiptHTML, buildJobReportHTML, buildEstimateDocHTML,
  buildStatusReportHTML,
  generateAndUploadPDF,
  getQuoteEmailContent, getInvoiceEmailContent, getReceiptEmailContent,
  getEstimateEmailContent, getJobEmailContent,
  buildMailto,
  exportExpensesXLSX,
  EMAIL_LIMITS, getEmailUsage, getEmailLimit, getUserPlan, sendDocumentEmail,
  generatePDFBase64,
} from "./core.jsx";

function ShareModal({ doc, type, trigger, business, onClose }) {
  // trigger = "email" | "whatsapp"
  const [stage, setStage] = useState("generating"); // generating | ready | sending | sent | error
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [filename, setFilename] = useState("document");
  const [to, setTo] = useState("");
  const [copied, setCopied] = useState(false);
  const [clients, setClients] = useState([]);
  const [emailUsed, setEmailUsed] = useState(null);
  const [emailLimit, setEmailLimit] = useState(null);
  const [sendError, setSendError] = useState(null);

  const userId = business._userId || business.user_id;

  useEffect(() => {
    if (userId) {
      DB.getList("clients", userId).then(c => setClients(c||[]));
      // Fetch plan from fj_users then load email usage
      getUserPlan(userId).then(plan => {
        getEmailUsage(userId).then(used => {
          setEmailUsed(used);
          setEmailLimit(getEmailLimit(plan));
        });
      });
    }
  }, []);

  const client = clients.find(c => c.name === doc.client_name || c.id === doc.client_id);
  useEffect(() => { if (client?.email) setTo(client.email); }, [client]);

  // Generate PDF on mount — also capture base64 for email attachment
  useEffect(() => {
    let html = "";
    let fname = "document";
    if (type === "Quote")    { html = buildDocHTML(doc, "Quote", business);   fname = `Quote-${doc.number||"doc"}`; }
    if (type === "Invoice")  { html = buildDocHTML(doc, "Invoice", business); fname = `Invoice-${doc.number||"doc"}`; }
    if (type === "Receipt")  { html = buildReceiptHTML(doc, business);        fname = `Receipt-${doc.number||"doc"}`; }
    if (type === "Job")      { html = buildJobReportHTML(doc, business, []);  fname = `JobReport-${doc.title||"doc"}`; }
    if (type === "Estimate") { html = buildEstimateDocHTML(doc, business);    fname = `Estimate-${doc.number||"doc"}`; }
    setFilename(fname);
    generateAndUploadPDF(html, fname)
      .then(url => { setPdfUrl(url); setStage("ready"); })
      .catch(() => setStage("error"));
    // Also generate base64 version for email attachment
    generatePDFBase64(html).then(b64 => setPdfBase64(b64)).catch(() => {});
  }, []);

  const docLabel = type === "Job" ? `Job Report — ${doc.title}` : `${type} ${doc.number||""}`;
  const docValue = doc.total ? formatCurrency(doc.total, business?.currency) : doc.estimated_cost ? formatCurrency(doc.estimated_cost, business?.currency) : doc.amount ? formatCurrency(doc.amount, business?.currency) : "";

  const getEmailContent = () => {
    const base = (() => {
      if (type === "Quote")    return getQuoteEmailContent(doc, business);
      if (type === "Invoice")  return getInvoiceEmailContent(doc, business);
      if (type === "Receipt")  return getReceiptEmailContent(doc, business);
      if (type === "Estimate") return getEstimateEmailContent(doc, business);
      return getJobEmailContent(doc, business);
    })();
    return {
      subject: base.subject,
      body: base.body + `

📄 View & Download ${type}: ${pdfUrl}`,
    };
  };

  const getWhatsAppMsg = () => {
    const base = (() => {
      if (type === "Quote")   return `Dear ${doc.client_name},

Please find your *${type} ${doc.number}* for *${formatCurrency(doc.total, business?.currency)}*.
Valid until: ${formatDate(doc.expiry_date)}

`;
      if (type === "Invoice") return `Dear ${doc.client_name},

Please find your *${type} ${doc.number}* for *${formatCurrency(doc.total, business?.currency)}*.
Due: ${formatDate(doc.due_date)}

Payment to:
${business.bank_name||""} · ${business.bank_account_number||""}
Ref: ${doc.number}
${business.payfast_link ? `
💳 Pay via PayFast: ${business.payfast_link}` : ""}${business.ozow_link ? `
💳 Pay via Ozow: ${business.ozow_link}` : ""}${business.other_payment_link ? `
💳 ${business.other_payment_label||"Pay Online"}: ${business.other_payment_link}` : ""}

`;
      if (type === "Receipt") return `Dear ${doc.client_name},

Thank you for your payment! 🙏
Receipt *${doc.number}* — *${formatCurrency(doc.amount, business?.currency)}*

`;
      if (type === "Estimate") return `Dear ${doc.client_name},

Please find your *Estimate ${doc.number}* for *${formatCurrency(doc.grand_total, business?.currency)}*.
Site: ${doc.site_address||""}
Valid until: ${formatDate(doc.valid_until)}

This is an estimate only — not a tax invoice. Please reply to accept.

`;
      return `Dear ${doc.client_name||"Client"},

Please find your job report for *${doc.title}*.
Date: ${formatDate(doc.date)}
Status: ${doc.status}

`;
    })();
    return base + `📄 View & Download: ${pdfUrl}

${business.name||""}`;
  };

  const handleEmail = async () => {
    if (!to) return;
    setSendError(null);
    setStage("sending");
    const { subject, body } = getEmailContent();
    // Build plain-text HTML body
    const htmlBody = `<div style="font-family:sans-serif;font-size:14px;line-height:1.6;color:#1a1a1a;max-width:600px">
      ${body.replace(/\n\n/g,"</p><p>").replace(/\n/g,"<br/>").replace(/^/,"<p>").replace(/$/,"</p>")}
      ${pdfUrl ? `<p style="margin-top:16px"><a href="${pdfUrl}" style="color:#c8972a">View/Download PDF Online</a></p>` : ""}
    </div>`;
    const result = await sendDocumentEmail({
      to, subject, htmlBody,
      pdfBase64, filename: `${filename}.pdf`,
      userId,
      documentType: type,
      documentId: doc.id,
    });
    if (result.error === "rate_limit_exceeded") {
      setEmailUsed(result.used);
      setSendError(`Email limit reached — you've used ${result.used} of ${result.limit} emails this month on your ${result.plan} plan. Upgrade to send more.`);
      setStage("ready");
    } else if (result.error) {
      setSendError("Failed to send email. Please try again.");
      setStage("ready");
    } else {
      setEmailUsed(result.used);
      setStage("sent");
      setTimeout(() => { setStage("ready"); setSendError(null); }, 3000);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(getWhatsAppMsg())}`, "_blank");
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(pdfUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const handleDownload = () => {
    // Creates a real download to the user's device (Downloads folder)
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `${type}-${doc.number || "document"}.pdf`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth:440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ background:"var(--ink)" }}>
          <h3 style={{ color:"white", fontSize:15 }}>Share {type}</h3>
          <button className="btn btn-icon btn-sm" style={{ color:"white", background:"rgba(255,255,255,0.1)", border:"none" }} onClick={onClose}><Icon name="x" /></button>
        </div>
        <div className="modal-body" style={{ padding:"20px" }}>
          {/* Doc chip */}
          <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
            <div style={{ width:38, height:38, background:"var(--ink)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>📄</div>
            <div>
              <div style={{ fontWeight:700, fontSize:13.5 }}>{docLabel}</div>
              <div style={{ fontSize:12, color:"#64748b" }}>{doc.client_name}{docValue ? ` · ${docValue}` : ""}</div>
            </div>
          </div>

          {/* Status */}
          {stage === "generating" && (
            <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:10, fontSize:13, color:"#92400e", marginBottom:16 }}>
              <div style={{ width:16, height:16, border:"2px solid #fde68a", borderTopColor:"var(--gold)", borderRadius:"50%", animation:"spin 0.8s linear infinite", flexShrink:0 }}></div>
              Generating PDF and uploading...
            </div>
          )}
          {stage === "error" && (
            <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"12px 16px", fontSize:13, color:"#991b1b", marginBottom:16 }}>
              ⚠️ PDF generation failed. Please try again.
            </div>
          )}
          {stage === "ready" && (
            <>
              <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:10, fontSize:13, color:"#166534", marginBottom:12 }}>
                <div style={{ width:16, height:16, background:"#16a34a", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontSize:10, flexShrink:0 }}>✓</div>
                PDF ready — tap below to share
              </div>
              <div style={{ background:"#f8fafc", border:"1.5px solid var(--border)", borderRadius:8, padding:"8px 12px", fontSize:11, color:"#475569", fontFamily:"monospace", marginBottom:16, wordBreak:"break-all" }}>
                {pdfUrl}
              </div>
            </>
          )}

          {/* Email recipient input — always visible */}
          <div className="form-group" style={{ marginBottom:16 }}>
            <label className="form-label">Recipient Email</label>
            <input className="form-input" type="email" value={to} onChange={e => setTo(e.target.value)} placeholder="client@example.co.za" />
            {client?.email && to !== client.email && (
              <button className="btn btn-outline btn-sm" style={{ marginTop:6, fontSize:11 }} onClick={() => setTo(client.email)}>Use {client.email}</button>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {/* Email rate limit indicator */}
            {emailUsed !== null && emailLimit !== null && (
              <div style={{ fontSize:11, color: emailUsed >= emailLimit ? "#ef4444" : "#64748b", marginBottom:2, textAlign:"right" }}>
                {emailUsed}/{emailLimit} emails used this month
                {emailUsed >= emailLimit && <span style={{ fontWeight:700 }}> — Limit reached</span>}
              </div>
            )}
            {sendError && (
              <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#991b1b", marginBottom:4 }}>
                {sendError}
              </div>
            )}
            <button
              disabled={stage === "generating" || stage === "sending" || stage === "sent" || !to || (emailUsed !== null && emailUsed >= emailLimit)}
              onClick={handleEmail}
              style={{ background: stage === "sent" ? "#10b981" : "var(--ink)", color:"white", border:"none", borderRadius:10, padding:"13px 16px", display:"flex", alignItems:"center", gap:10, fontSize:14, fontWeight:600,
                cursor: (stage === "ready" && to && !(emailUsed >= emailLimit)) ? "pointer" : "not-allowed",
                opacity: (stage === "ready" && to && !(emailUsed >= emailLimit)) ? 1 : 0.45 }}>
              {stage === "sending" ? <><div style={{ width:16, height:16, border:"2px solid rgba(255,255,255,0.3)", borderTopColor:"white", borderRadius:"50%", animation:"fjSpin 0.8s linear infinite" }} />Sending...</>
               : stage === "sent"  ? <><span>✓</span> Email Sent!</>
               : <><span style={{ fontSize:18 }}>✉️</span> Send via Email with PDF</>}
            </button>
            <button
              disabled={stage !== "ready"}
              onClick={handleWhatsApp}
              style={{ background:"#25d366", color:"white", border:"none", borderRadius:10, padding:"13px 16px", display:"flex", alignItems:"center", gap:10, fontSize:14, fontWeight:600, cursor: stage === "ready" ? "pointer" : "not-allowed", opacity: stage === "ready" ? 1 : 0.45 }}>
              <Icon name="whatsapp" size={20} /> Share via WhatsApp
            </button>
            <button
              disabled={stage !== "ready"}
              onClick={handleCopy}
              style={{ background:"white", color:"#374151", border:"1.5px solid var(--border)", borderRadius:10, padding:"11px 16px", display:"flex", alignItems:"center", gap:10, fontSize:13.5, fontWeight:600, cursor: stage === "ready" ? "pointer" : "not-allowed", opacity: stage === "ready" ? 1 : 0.45 }}>
              <span>{copied ? "✓ Copied!" : "🔗 Copy PDF Link"}</span>
            </button>
            <button
              disabled={stage !== "ready"}
              onClick={handleDownload}
              style={{ background:"white", color:"var(--ink)", border:"1.5px solid var(--border)", borderRadius:10, padding:"11px 16px", display:"flex", alignItems:"center", gap:10, fontSize:13.5, fontWeight:600, cursor: stage === "ready" ? "pointer" : "not-allowed", opacity: stage === "ready" ? 1 : 0.45 }}>
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width:18, height:18, stroke:"var(--ink)", flexShrink:0 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              <span>Download PDF to Device</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── LANDING PAGE ─────────────────────────────────────────────────────────────
function LandingPage({ onNav }) {
  const [demoForm, setDemoForm] = useState(false);
  const [formData, setFormData] = useState({ business_name:"", owner_name:"", email:"", phone:"", industry:"", workers:"" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cbForm, setCbForm] = useState({ business_name:"", email:"", description:"" });
  const [cbSubmitted, setCbSubmitted] = useState(false);
  const [cbSubmitting, setCbSubmitting] = useState(false);
  const [customForm, setCustomForm] = useState(false);

  const features = [
    { icon: "🔧", title: "Job Management", desc: "Full job lifecycle from quote to payment with status pipeline tracking." },
    { icon: "📅", title: "Smart Scheduling", desc: "Drag-and-drop calendar with double-booking prevention for your team." },
    { icon: "💰", title: "Invoicing & Quotes", desc: "Professional PDF quotes and invoices with full multi-currency and tax support." },
    { icon: "👷", title: "Worker Tracking", desc: "Assign jobs, track performance and revenue per field worker." },
    { icon: "📊", title: "Cashflow Intelligence", desc: "30-day projections, risk indicators and business health monitoring." },
    { icon: "💬", title: "WhatsApp Integration", desc: "Send job updates, quotes and payment reminders via WhatsApp." },
  ];

  const handleDemoSubmit = async () => {
    if (!formData.business_name || !formData.email) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-enquiry`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ type: "demo", ...formData }),
      });
      // Save to Supabase for admin panel
      await DB.insert("demo_requests", { ...formData, status: "Pending", submitted_date: today() });
      setSubmitted(true);
    } catch (e) {
      // Still save to Supabase even if email fails
      await DB.insert("demo_requests", { ...formData, status: "Pending", submitted_date: today() });
      setSubmitted(true);
    }
    setSubmitting(false);
  };

  return (
    <div className="landing">
      <nav className="landing-nav">
        <div className="landing-logo">FieldJob</div>
        <div className="landing-nav-links">
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <a href="#" onClick={(e) => { e.preventDefault(); onNav("login"); }}>Login</a>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button className="btn-hero-outline" style={{ padding:"8px 16px", fontSize:13 }} onClick={() => setDemoForm(true)}>Request Demo</button>
          <button className="btn-hero-primary" style={{ padding:"8px 16px", fontSize:13 }} onClick={() => onNav("signup")}>Subscribe Now</button>
        </div>
      </nav>

      <section className="hero">
        <div className="hero-eyebrow">South Africa's #1 Contractor Platform</div>
        <h1>Professional Job & Contractor Management for South Africa</h1>
        <p className="hero-sub">Manage jobs, workers, invoices, and cashflow — all from one powerful platform built for South African contractors.</p>
        <div className="hero-buttons">
          <button className="btn-hero-primary" onClick={() => setDemoForm(true)}>Request 7-Day Demo</button>
          <button className="btn-hero-primary" onClick={() => onNav("signup")}>Subscribe Now</button>
          <button className="btn-hero-outline" onClick={() => setCustomForm(true)}>Request Custom Build</button>
        </div>
      </section>

      <section className="features-section" id="features">
        <div className="section-title">Everything Your Business Needs</div>
        <div className="section-sub">Built for 5–20 field workers. Simple, powerful, reliable.</div>
        <div className="features-grid">
          {features.map((f, i) => (
            <div key={i} className="feature-card">
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing-section" id="pricing">
        <div className="section-title">Simple, Transparent Pricing</div>
        <div className="section-sub">Start free. Upgrade when you're ready. No hidden fees.</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:20, marginTop:32 }}>

          {/* FREE FOREVER */}
          <div className="pricing-card" style={{ border:"1px solid rgba(255,255,255,0.1)", opacity:0.9 }}>
            <div style={{ fontSize:12, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"rgba(255,255,255,0.4)", marginBottom:12 }}>Free Forever</div>
            <div className="pricing-amount" style={{ color:"white", fontSize:40 }}>R0</div>
            <div className="pricing-period" style={{ color:"rgba(255,255,255,0.4)", marginTop:4 }}>no credit card needed</div>
            <ul className="pricing-features" style={{ marginTop:24 }}>
              {["Basic job management","Up to 3 active jobs","Client portal (1 client)","Basic invoicing"].map((f,i) => (
                <li key={i} style={{ color:"rgba(255,255,255,0.6)" }}>{f}</li>
              ))}
            </ul>
            <button className="btn-hero-secondary" style={{ width:"100%", fontSize:14, marginTop:8 }} onClick={() => onNav("signup")}>Get Started Free</button>
          </div>

          {/* STARTER */}
          <div className="pricing-card" style={{ border:"1px solid rgba(200,151,42,0.4)" }}>
            <div style={{ fontSize:12, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--gold-light)", marginBottom:12 }}>Starter</div>
            <div className="pricing-amount" style={{ color:"var(--gold-light)" }}>R79</div>
            <div className="pricing-period" style={{ color:"rgba(255,255,255,0.5)", marginTop:4 }}>per month (excl. VAT)</div>
            <ul className="pricing-features" style={{ marginTop:24 }}>
              {["Everything in Free","Unlimited jobs","Full invoicing & quoting","WhatsApp integration","PDF reports","Client portal (unlimited)"].map((f,i) => (
                <li key={i} style={{ color:"rgba(255,255,255,0.75)" }}>{f}</li>
              ))}
            </ul>
            <button className="btn-hero-primary" style={{ width:"100%", fontSize:14, marginTop:8 }} onClick={() => onNav("signup")}>Start Free Trial</button>
          </div>

          {/* BUSINESS */}
          <div className="pricing-card" style={{ border:"1px solid rgba(200,151,42,0.6)", background:"linear-gradient(145deg,rgba(200,151,42,0.12),rgba(200,151,42,0.04))", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:16, right:16, background:"var(--gold)", color:"var(--ink)", fontSize:10, fontWeight:800, letterSpacing:1, textTransform:"uppercase", borderRadius:6, padding:"3px 8px" }}>Most Popular</div>
            <div style={{ fontSize:12, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"var(--gold-light)", marginBottom:12 }}>Business</div>
            <div className="pricing-amount" style={{ color:"var(--gold-light)" }}>R169</div>
            <div className="pricing-period" style={{ color:"rgba(255,255,255,0.5)", marginTop:4 }}>per month (excl. VAT)</div>
            <ul className="pricing-features" style={{ marginTop:24 }}>
              {["Everything in Starter","Worker mobile app","Cashflow intelligence","Business health dashboard","Expenses & tax reports","Inventory management","Priority support"].map((f,i) => (
                <li key={i} style={{ color:"rgba(255,255,255,0.75)" }}>{f}</li>
              ))}
            </ul>
            <button className="btn-hero-primary" style={{ width:"100%", fontSize:14, marginTop:8 }} onClick={() => onNav("signup")}>Start Free Trial</button>
            <div style={{ marginTop:10, fontSize:11, color:"rgba(255,255,255,0.35)", textAlign:"center" }}>7-day free trial included</div>
          </div>

        </div>
      </section>

      {/* DEMO FORM MODAL */}
      {demoForm && (
        <div className="modal-overlay" onClick={() => setDemoForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{submitted ? "Request Received!" : "Request 7-Day Free Demo"}</h3>
              <button className="btn btn-icon btn-outline btn-sm" onClick={() => { setDemoForm(false); setSubmitted(false); }}><Icon name="x" /></button>
            </div>
            <div className="modal-body">
              {submitted ? (
                <div style={{ textAlign:"center", padding:"20px 0" }}>
                  <div style={{ fontSize:48, marginBottom:16 }}>✅</div>
                  <h3 style={{ marginBottom:8 }}>Thank you, {formData.owner_name}!</h3>
                  <p style={{ color:"#64748b" }}>Your demo request has been received. Our team will review your application and activate your demo account within 24 hours.</p>
                  <button className="btn btn-primary mt-4" onClick={() => { setDemoForm(false); setSubmitted(false); }}>Close</button>
                </div>
              ) : (
                <>
                  <div className="form-grid">
                    <div className="form-group"><label className="form-label">Business Name *</label><input className="form-input" value={formData.business_name} onChange={e => setFormData({...formData, business_name: e.target.value})} /></div>
                    <div className="form-group"><label className="form-label">Owner Name *</label><input className="form-input" value={formData.owner_name} onChange={e => setFormData({...formData, owner_name: e.target.value})} /></div>
                    <div className="form-group"><label className="form-label">Email *</label><input className="form-input" type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} /></div>
                    <div className="form-group"><label className="form-label">Phone *</label><input className="form-input" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} /></div>
                    <div className="form-group"><label className="form-label">Industry</label>
                      <select className="form-select" value={formData.industry} onChange={e => setFormData({...formData, industry: e.target.value})}>
                        <option value="">Select industry</option>
                        {["Electrical","Plumbing","HVAC","Painting","Construction","Landscaping","Security","Cleaning","Other"].map(i => <option key={i} value={i}>{i}</option>)}
                      </select>
                    </div>
                    <div className="form-group"><label className="form-label">Number of Field Workers</label>
                      <select className="form-select" value={formData.workers} onChange={e => setFormData({...formData, workers: e.target.value})}>
                        <option value="">Select range</option>
                        {["1-5","6-10","11-15","16-20","20+"].map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>
                  <button className="btn btn-primary w-full" onClick={handleDemoSubmit} disabled={!formData.business_name || !formData.email || submitting}>
                    {submitting ? "Sending..." : "Submit Demo Request"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CUSTOM BUILD MODAL */}
      {customForm && (
        <div className="modal-overlay" onClick={() => { setCustomForm(false); setCbSubmitted(false); setCbSubmitting(false); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{cbSubmitted ? "Enquiry Sent!" : "Request Custom Build"}</h3>
              <button className="btn btn-icon btn-outline btn-sm" onClick={() => { setCustomForm(false); setCbSubmitted(false); setCbSubmitting(false); }}><Icon name="x" /></button>
            </div>
            <div className="modal-body">
              {cbSubmitted ? (
                <div style={{ textAlign:"center", padding:"20px 0" }}>
                  <div style={{ fontSize:48, marginBottom:16 }}>✅</div>
                  <h3 style={{ marginBottom:8 }}>Thank you!</h3>
                  <p style={{ color:"#64748b", lineHeight:1.7 }}>Your custom build enquiry has been received. Our team will review your requirements and get back to you within 1–2 business days.</p>
                  <p style={{ color:"#94a3b8", fontSize:13, marginTop:12 }}>A confirmation has been sent to your email.</p>
                  <button className="btn btn-primary mt-4" onClick={() => { setCustomForm(false); setCbSubmitted(false); }}>Close</button>
                </div>
              ) : (
                <>
                  <div className="alert alert-info">Our team specialises in custom field service management solutions tailored to your specific business requirements.</div>
                  <div className="form-group"><label className="form-label">Business Name *</label><input className="form-input" value={cbForm.business_name} onChange={e => setCbForm({...cbForm, business_name: e.target.value})} placeholder="Your Company Name" /></div>
                  <div className="form-group"><label className="form-label">Contact Email *</label><input className="form-input" type="email" value={cbForm.email} onChange={e => setCbForm({...cbForm, email: e.target.value})} placeholder="you@company.co.za" /></div>
                  <div className="form-group"><label className="form-label">Describe your requirements *</label><textarea className="form-textarea" rows={4} value={cbForm.description} onChange={e => setCbForm({...cbForm, description: e.target.value})} placeholder="Tell us about your business, team size, and what features you need..." /></div>
                  <button className="btn btn-primary w-full" disabled={!cbForm.business_name || !cbForm.email || !cbForm.description || cbSubmitting} onClick={async () => {
                    setCbSubmitting(true);
                    try {
                      await fetch(`${SUPABASE_URL}/functions/v1/send-enquiry`, {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
                        },
                        body: JSON.stringify({ type: "custom_build", ...cbForm }),
                      });
                    } catch (e) { /* still show success */ }
                    setCbSubmitting(false);
                    setCbSubmitted(true);
                  }}>
                    {cbSubmitting ? "Sending..." : "Send Enquiry"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AUTH PAGES ───────────────────────────────────────────────────────────────
function AuthPage({ mode, onNav, onAuth }) {
  const [form, setForm] = useState({ email:"", password:"", confirm:"", business_name:"", staySignedIn: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPaymentScreen, setShowPaymentScreen] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const isLogin = mode === "login";

  // Check if account is locked out (3 failed attempts)
  const MAX_ATTEMPTS = 3;
  const LOCKOUT_KEY = "fj_lockout";
  const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes

  const isLockedOut = () => {
    try {
      const data = JSON.parse(localStorage.getItem(LOCKOUT_KEY) || "null");
      if (!data) return false;
      if (Date.now() - data.time < LOCKOUT_DURATION && data.count >= MAX_ATTEMPTS) return true;
      if (Date.now() - data.time >= LOCKOUT_DURATION) {
        localStorage.removeItem(LOCKOUT_KEY);
        setAttempts(0);
        setLocked(false);
      }
      return false;
    } catch { return false; }
  };

  const recordFailedAttempt = () => {
    try {
      const data = JSON.parse(localStorage.getItem(LOCKOUT_KEY) || "null");
      const count = (data && Date.now() - data.time < LOCKOUT_DURATION) ? data.count + 1 : 1;
      localStorage.setItem(LOCKOUT_KEY, JSON.stringify({ count, time: data?.time || Date.now() }));
      setAttempts(count);
      if (count >= MAX_ATTEMPTS) setLocked(true);
      return count;
    } catch { return 0; }
  };

  const clearAttempts = () => {
    localStorage.removeItem(LOCKOUT_KEY);
    setAttempts(0);
    setLocked(false);
  };

  const handle = async () => {
    setError("");

    // Check lockout
    if (isLockedOut()) {
      setError("Too many failed attempts. Please wait 15 minutes before trying again.");
      setLocked(true);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Offline login — use cached session from IDB/localStorage
    if (!navigator.onLine) {
      if (!isLogin) {
        setError("You need an internet connection to create an account.");
        setLoading(false);
        return;
      }
      // Try owner login offline
      if (form.email.toLowerCase().trim() === OWNER_EMAIL.toLowerCase().trim() && form.password.trim() === OWNER_PASSWORD) {
        const stored = JSON.parse(localStorage.getItem("fj_user") || "null");
        if (stored && stored.verified) {
          clearAttempts();
          onAuth(stored);
          setLoading(false);
          return;
        }
      }
      // Try regular user offline — use cached session
      try {
        const idbSession = await IDB.get("fj_session");
        if (idbSession && idbSession.verified && idbSession.email?.toLowerCase() === form.email.toLowerCase().trim()) {
          clearAttempts();
          onAuth(idbSession);
          setLoading(false);
          return;
        }
      } catch {}
      setError("You're offline and no saved session was found. Please connect to the internet.");
      setLoading(false);
      return;
    }

    try {
      if (isLogin) {
        // Owner account — hardcoded credentials, permanent paid plan
        if (form.email.toLowerCase().trim() === OWNER_EMAIL.toLowerCase().trim() && form.password.trim() === OWNER_PASSWORD) {
          clearAttempts();
          const userId = "owner_admin";
          const user = { id: userId, email: OWNER_EMAIL, plan: "paid", demo_expires: null, role: "admin", verified: true };
          localStorage.setItem("fj_user", JSON.stringify(user));
          if (form.staySignedIn) { await IDB.set("fj_session", user); localStorage.setItem("fj_stay_signed_in", "1"); } else { localStorage.removeItem("fj_stay_signed_in"); await IDB.remove("fj_session").catch(()=>{}); }
          const existingBiz = await DB.get("business", userId);
          if (!existingBiz) {
            await DB.upsert("business", { user_id: userId, name: "FieldJob Admin", vat_number: "", reg_number: "", phone: "", email: OWNER_EMAIL, address: "", bank_name: "", bank_account_holder: "", bank_account_number: "", bank_branch_code: "", logo: null });
          }
          onAuth(user);
          setLoading(false); return;
        }

        // Regular user — authenticate via Supabase Auth
        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
          email: form.email.toLowerCase().trim(),
          password: form.password,
        });

        if (authError) {
          const count = recordFailedAttempt();
          const remaining = MAX_ATTEMPTS - count;
          if (count >= MAX_ATTEMPTS) {
            setError("Too many failed attempts. Your account has been temporarily locked for 15 minutes.");
          } else {
            setError(`Incorrect email or password.${remaining > 0 ? ` ${remaining} attempt${remaining === 1 ? "" : "s"} remaining.` : ""}`);
          }
          setLoading(false); return;
        }

        // Auth succeeded — load user profile from fj_users
        const dbUser = await DB.getUserByEmail(form.email.toLowerCase().trim());
        if (!dbUser) {
          setError("Incorrect email or password.");
          setLoading(false); return;
        }
        if (dbUser.status === "Discontinued") {
          setError("Your account access has been discontinued. Please contact support@getdiswayne.com.");
          setLoading(false); return;
        }
        if (dbUser.status !== "Approved") {
          setError("Your account is pending approval. Please wait or contact support@getdiswayne.com.");
          setLoading(false); return;
        }

        clearAttempts();
        const userId = authData.user?.id || dbUser.auth_id || dbUser.id;
        const resolvedPlan = dbUser.plan || "demo";
        const user = {
          id: userId,
          auth_id: userId,
          email: dbUser.email,
          plan: resolvedPlan,
          demo_expires: resolvedPlan === "demo" ? (dbUser.demo_expires || addDays(dbUser.approved_date || today(), 7)) : null,
          business_name: dbUser.business_name,
          role: dbUser.role || "user",
          verified: true,
        };
        localStorage.setItem("fj_user", JSON.stringify(user));
        if (form.staySignedIn) { await IDB.set("fj_session", user); localStorage.setItem("fj_stay_signed_in", "1"); } else { localStorage.removeItem("fj_stay_signed_in"); await IDB.remove("fj_session").catch(()=>{}); }
        onAuth(user);
      } else {
        if (form.password !== form.confirm) { setError("Passwords do not match."); setLoading(false); return; }
        if (form.password.length < 6) { setError("Password must be at least 6 characters."); setLoading(false); return; }
        const userId = `user_${btoa(form.email).slice(0,12)}`;
        const user = { id: userId, email: form.email, plan: "demo", demo_expires: addDays(today(), 7), business_name: form.business_name };
        const existingBiz2 = await DB.get("business", userId);
        if (!existingBiz2) {
          await DB.upsert("business", { user_id: userId, name: form.business_name || "My Business", vat_number: "", reg_number: "", phone: "", email: form.email, address: "", bank_name: "", bank_account_holder: "", bank_account_number: "", bank_branch_code: "", logo: null });
        }
        setShowPaymentScreen(true);
        setLoading(false);
        return;
      }
    } catch (e) { setError("Authentication failed. Please try again."); }
    setLoading(false);
  };

  // Payment not available screen — shown after signup
  if (showPaymentScreen) return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: 480 }}>
        <div className="auth-logo">FieldJob</div>
        <div style={{ textAlign:"center", padding:"8px 0 20px" }}>
          <div style={{ fontSize:52, marginBottom:12 }}>🔧</div>
          <h2 style={{ fontSize:20, fontWeight:700, marginBottom:10 }}>Account Created!</h2>
          <div style={{ color:"#64748b", fontSize:14, lineHeight:1.7, marginBottom:20 }}>
            Your account has been set up. Online subscription payments are currently being configured — please check back soon or contact us to arrange your subscription.
          </div>
          <div className="card" style={{ textAlign:"left", marginBottom:20, padding:16 }}>
            <div style={{ fontWeight:600, marginBottom:10, fontSize:13 }}>FieldJob — choose a plan below to unlock full access:</div>
            {["All 12 business modules","Unlimited jobs, quotes & invoices","Stock & inventory management","Compliance document tracking","Expense & profit reporting","Priority support"].map(f => (
              <div key={f} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7, fontSize:13, color:"#475569" }}>
                <span style={{ color:"var(--status-green-fg)", fontWeight:700 }}>✓</span> {f}
              </div>
            ))}
          </div>
          <a
            href="mailto:support@getdiswayne.com?subject=FieldJob Pro Subscription&body=Hi, I just created my account and would like to subscribe to FieldJob Pro."
            className="btn btn-gold btn-lg w-full"
            style={{ display:"block", textDecoration:"none", marginBottom:12 }}
          >
            📧 Contact Us to Subscribe
          </a>
          <button className="btn btn-outline w-full" onClick={() => {
            localStorage.removeItem("fj_user");
            localStorage.removeItem("sb_token");
            onNav("login");
          }}>
            ← Back to Welcome Page
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">FieldJob</div>
        <div className="auth-subtitle">{isLogin ? "Sign in to your account" : "Create your FieldJob account"}</div>
        {error && <div className="alert alert-error">{error}</div>}
        {!isLogin && <div className="form-group"><label className="form-label">Business Name</label><input className="form-input" value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} placeholder="Your Company Name" /></div>}
        <div className="form-group"><label className="form-label">Email Address</label><input className="form-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="you@company.co.za" /></div>
        <div className="form-group"><label className="form-label">Password</label><PasswordInput value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="••••••••" /></div>
        {!isLogin && <div className="form-group"><label className="form-label">Confirm Password</label><PasswordInput value={form.confirm} onChange={e => setForm({...form, confirm: e.target.value})} placeholder="Repeat your password" /></div>}
        {isLogin && (
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:13, color:"#4b5563", userSelect:"none" }}>
              <input
                type="checkbox"
                checked={form.staySignedIn}
                onChange={e => setForm({...form, staySignedIn: e.target.checked})}
                style={{ width:15, height:15, accentColor:"var(--gold)", cursor:"pointer" }}
              />
              Stay signed in
            </label>
            <a href="#" style={{ fontSize:12.5, color:"var(--gold)" }} onClick={e => { e.preventDefault(); onNav("forgot"); }}>Forgot password?</a>
          </div>
        )}
        <button className="btn btn-primary w-full btn-lg" onClick={handle} disabled={loading}>{loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account & Continue"}</button>
        <hr className="divider" />
        <div style={{ textAlign:"center", fontSize:13, color:"#64748b" }}>
          {isLogin ? <>Don't have an account? <a href="#" style={{ color:"var(--gold)" }} onClick={e => { e.preventDefault(); onNav("signup"); }}>Sign up free</a></> : <>Already have an account? <a href="#" style={{ color:"var(--gold)" }} onClick={e => { e.preventDefault(); onNav("login"); }}>Sign in</a></>}
        </div>


      </div>
    </div>
  );
}

function ForgotPage({ onNav }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleReset = async () => {
    if (!email || !email.includes("@")) { setError("Please enter a valid email address."); return; }
    setLoading(true); setError("");
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.toLowerCase().trim());
    setLoading(false);
    if (err) {
      setError("Failed to send reset email. Please try again or contact support@getdiswayne.com.");
    } else {
      setSent(true);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">FieldJob</div>
        <div className="auth-subtitle">Reset your password</div>
        {sent ? (
          <div className="alert alert-success">
            Password reset link sent to <strong>{email}</strong>. Check your inbox — it may take a minute to arrive.
          </div>
        ) : (
          <>
            <div style={{ fontSize:13, color:"#64748b", marginBottom:16, lineHeight:1.6 }}>
              Enter your email address and we'll send you a link to reset your password.
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleReset()} placeholder="you@business.co.za" autoFocus />
            </div>
            <button className="btn btn-primary w-full" onClick={handleReset} disabled={loading}>
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
          </>
        )}
        <div style={{ textAlign:"center", marginTop:16 }}>
          <a href="#" style={{ fontSize:13, color:"var(--gold)" }} onClick={e => { e.preventDefault(); onNav("login"); }}>← Back to login</a>
        </div>
      </div>
    </div>
  );
}

// ─── SET PASSWORD PAGE (Invite / Password Reset flow) ────────────────────────
function SetPasswordPage({ onNav, onAuth }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Extract token from URL hash — Supabase puts it there after invite click
  const getTokenFromHash = () => {
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    return {
      access_token: params.get("access_token"),
      refresh_token: params.get("refresh_token"),
      type: params.get("type"),
    };
  };

  const handleSetPassword = async () => {
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);

    try {
      const { access_token, refresh_token } = getTokenFromHash();

      // Use the access token from the invite link to update password
      const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_ANON_KEY,
          "Authorization": `Bearer ${access_token}`,
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || "Failed to set password. Please contact support.");
        setLoading(false);
        return;
      }

      // Save session
      localStorage.setItem("sb_token", access_token);

      // Build user record from Supabase response
      const userId = data.id;
      const email = data.email;
      const userRecord = {
        id: userId,
        email: email,
        plan: email === OWNER_EMAIL ? "paid" : "demo",
        demo_expires: email === OWNER_EMAIL ? null : addDays(today(), 7),
        role: email === OWNER_EMAIL ? "admin" : "user",
      };

      localStorage.setItem("fj_user", JSON.stringify(userRecord));

      // Set up business profile if not exists
      const existingBiz3 = await DB.get("business", userId);
      if (!existingBiz3) {
        await DB.upsert("business", {
          user_id: userId,
          name: "My Business", vat_number: "", reg_number: "",
          phone: "", email: email, address: "",
          bank_name: "", bank_account_holder: "",
          bank_account_number: "", bank_branch_code: "", logo: null,
        });
      }

      // Clear the hash from URL so token isn't reused
      window.history.replaceState(null, "", window.location.pathname);

      setDone(true);
      setLoading(false);

      // Auto redirect to app after 2 seconds
      setTimeout(() => onAuth(userRecord), 2000);

    } catch (e) {
      setError("Something went wrong. Please try again or contact support.");
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">FieldJob</div>

        {done ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Password Set Successfully!</div>
            <div className="text-muted" style={{ fontSize: 14 }}>Welcome to FieldJob. Taking you to your dashboard...</div>
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--gold)", animation: "pulse 1s infinite" }}></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="auth-subtitle">You've been invited to FieldJob</div>
            <div className="alert alert-info" style={{ marginBottom: 16, fontSize: 13 }}>
              Welcome! Please set a password for your account to get started.
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label className="form-label">New Password</label>
              <PasswordInput value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <PasswordInput value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat your password" />
            </div>
            <button
              className="btn btn-primary w-full btn-lg"
              onClick={handleSetPassword}
              disabled={loading}
            >
              {loading ? "Setting up your account..." : "Set Password & Enter FieldJob"}
            </button>
            <div style={{ textAlign: "center", marginTop: 12, fontSize: 12, color: "#94a3b8" }}>
              Your account was created by the FieldJob administrator
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── EXPIRED SCREEN ───────────────────────────────────────────────────────────
function ExpiredScreen({ onUpgrade, onLogout }) {
  return (
    <div className="expired-overlay">
      <div className="expired-card">
        <div style={{ fontSize:56, marginBottom:16 }}>⏰</div>
        <h2 style={{ fontSize:22, fontWeight:700, marginBottom:8 }}>Your Demo Has Expired</h2>
        <p style={{ color:"#64748b", marginBottom:24, lineHeight:1.7 }}>Your 7-day free demo has ended. Subscribe to FieldJob to restore full access to all your data and features.</p>
        <button className="btn btn-gold btn-lg w-full" onClick={onUpgrade} style={{ marginBottom:10 }}>Subscribe Now</button>        <button className="btn btn-outline w-full" onClick={onLogout}>Sign Out</button>
      </div>
    </div>
  );
}

// ─── SIDEBAR COMPONENT ────────────────────────────────────────────────────────
function Sidebar({ active, onNav, user, business, mobileOpen, onToggle }) {
  const navItems = [
    { id:"dashboard", label:"Dashboard", icon:"dashboard", section:"Overview" },
    { id:"jobs", label:"Jobs", icon:"jobs", section:"Operations" },
    { id:"calendar", label:"Calendar", icon:"calendar", section:null },
    { id:"quotes", label:"Quotes", icon:"quote", section:null },
    { id:"estimates", label:"Estimates", icon:"estimate", section:null },
    { id:"reports",   label:"Reports",   icon:"report",   section:null },
    { id:"invoices", label:"Invoices", icon:"invoice", section:null },
    { id:"receipts", label:"Receipts", icon:"receipt", section:null },
    { id:"templates", label:"Templates", icon:"pen", section:null },
    { id:"clients", label:"Clients", icon:"clients", section:"People" },
    { id:"workers", label:"Workers", icon:"workers", section:null },
    { id:"portals", label:"Client Portals", icon:"portal", section:null },
    { id:"expenses", label:"Expenses", icon:"expenses", section:"Financial" },
    { id:"inventory", label:"Stock & Inventory", icon:"inventory", section:null },
    { id:"cashflow", label:"Cashflow", icon:"cashflow", section:null },
    { id:"compliance", label:"Compliance Docs", icon:"compliance", section:"Business" },
    { id:"tools", label:"Plugins & Tools", icon:"tools", section:null },
    { id:"settings", label:"Settings", icon:"settings", section:"Account" },
  ];

  const grouped = [];
  navItems.forEach(item => {
    if (item.section) grouped.push({ type:"section", label:item.section });
    grouped.push({ type:"item", ...item });
  });

  return (
    <>
      {mobileOpen && <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:99 }} onClick={onToggle}/>}
      <aside className={mobileOpen ? "sidebar open" : "sidebar"}>
        {/* Logo */}
        <div className="sidebar-logo">
          <div className="sidebar-logo-mark">
            <span>FJ</span>
          </div>
          <div className="sidebar-logo-text">
            <h1>FieldJob</h1>
            <span>Contractor OS</span>
          </div>
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          {grouped.map((item, i) => item.type === "section"
            ? <div key={i} className="nav-section">{item.label}</div>
            : <div key={i} className={active === item.id ? "nav-item active" : "nav-item"} onClick={() => { onNav(item.id); if (mobileOpen) onToggle(); }}>
                <div className="nav-icon-tile"><Icon name={item.icon} size={14} /></div>
                <span className="nav-item-label">{item.label}</span>
              </div>
          )}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <div className="sidebar-footer-inner">
            <div style={{ width:32, height:32, borderRadius:8, background:"linear-gradient(135deg,rgba(200,151,42,0.25),rgba(200,151,42,0.08))", border:"1px solid rgba(200,151,42,0.3)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, color:"var(--gold-light)", fontWeight:700, fontSize:13 }}>
              {(business?.name || user?.email || "F")[0].toUpperCase()}
            </div>
            <div style={{ minWidth:0 }}>
              <div className="business-name">{business?.name || "My Business"}</div>
              <div className="plan-badge">{user?.plan === "paid" ? "✓ Pro" : "🕐 Demo"}</div>
            </div>
          </div>
          <div style={{ marginTop:8, cursor:"pointer", color:"rgba(255,255,255,0.35)", fontSize:11.5, display:"flex", alignItems:"center", gap:6, paddingLeft:2, opacity:0, transition:"opacity 0.1s 0.05s" }}
            className="sidebar-signout"
            onClick={async () => { localStorage.removeItem("fj_user"); localStorage.removeItem("fj_authed"); if (typeof window !== "undefined" && window.__fjLogout) window.__fjLogout(); }}>
            <Icon name="logout" size={12} />Sign out
          </div>
        </div>
      </aside>
    </>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────

// ─── MOBILE KPI STRIP ────────────────────────────────────────────────────────
function MobileKpiStrip({ userId, onNav }) {
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [business, setBusiness] = useState({ currency:"ZAR" });
  useEffect(() => {
    Promise.all([DB.getList("jobs",userId), DB.getList("invoices",userId), DB.getList("quotes",userId), DB.get("business",userId)])
      .then(([j,inv,q,b]) => { setJobs(j||[]); setInvoices(inv||[]); setQuotes(q||[]); setBusiness({...(b||{}), currency:b?.currency||"ZAR", unit_system:b?.unit_system||"metric"}); });
  }, []);
  const todayStr = today();
  const totalRevenue = invoices.filter(i=>i.status==="Paid").reduce((s,i)=>s+(i.total||0),0);
  const outstanding = invoices.filter(i=>i.status!=="Paid").reduce((s,i)=>s+((i.total||0)-(i.paid||0)),0);
  const overdueCount = invoices.filter(i=>i.status==="Overdue"||(i.due_date<todayStr&&i.status!=="Paid")).length;
  const todayJobs = jobs.filter(j=>j.date===todayStr).length;
  const inProgress = jobs.filter(j=>j.status==="In Progress").length;
  const pendingQ = quotes.filter(q=>q.status==="Sent").length;
  const pendingQVal = quotes.filter(q=>q.status==="Sent").reduce((s,q)=>s+(q.total||0),0);
  return (<>
    <div className="kpi-chip gold" onClick={() => onNav("invoices")} style={{cursor:"pointer"}}>
      <div className="kpi-chip-label">Revenue</div>
      <div className="kpi-chip-val">{formatCurrency(totalRevenue, business?.currency)}</div>
      <div className="kpi-chip-sub">Paid invoices</div>
    </div>
    <div className="kpi-chip" onClick={() => onNav("invoices")} style={{cursor:"pointer"}}>
      <div className="kpi-chip-label">Outstanding</div>
      <div className="kpi-chip-val">{formatCurrency(outstanding, business?.currency)}</div>
      <div className="kpi-chip-sub" style={{color:overdueCount>0?"#fca5a5":"rgba(255,255,255,0.3)"}}>{overdueCount>0?`${overdueCount} overdue`:"All current"}</div>
    </div>
    <div className="kpi-chip" onClick={() => onNav("jobs")} style={{cursor:"pointer"}}>
      <div className="kpi-chip-label">Jobs Today</div>
      <div className="kpi-chip-val">{todayJobs}</div>
      <div className="kpi-chip-sub">{inProgress} in progress</div>
    </div>
    <div className="kpi-chip" onClick={() => onNav("quotes")} style={{cursor:"pointer"}}>
      <div className="kpi-chip-label">Quotes</div>
      <div className="kpi-chip-val">{formatCurrency(pendingQVal, business?.currency)}</div>
      <div className="kpi-chip-sub">{pendingQ} pending</div>
    </div>
  </>);
}


function Dashboard({ userId, setActiveNav }) {
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [business, setBusiness] = useState({ currency:"ZAR" });
  const [portalUnread, setPortalUnread] = useState(0);
  useEffect(() => {
    Promise.all([
      DB.getList("jobs",userId), DB.getList("invoices",userId),
      DB.getList("quotes",userId), DB.getList("clients",userId), DB.get("business",userId),
    ]).then(([j,inv,q,c,b]) => { setJobs(j||[]); setInvoices(inv||[]); setQuotes(q||[]); setClients(c||[]); setBusiness({...(b||{}), currency:b?.currency||"ZAR", unit_system:b?.unit_system||"metric"}); });
    const fetchPortal = () => {
      const hdrs = { "apikey": SUPABASE_ANON_KEY, "Authorization": `Bearer ${SUPABASE_ANON_KEY}` };
      Promise.all([
        fetch(`${SUPABASE_URL}/rest/v1/fj_portal_messages?user_id=eq.${userId}&sender=eq.client&read=eq.false`, { headers: hdrs }).then(r=>r.ok?r.json():[]).catch(()=>[]),
        fetch(`${SUPABASE_URL}/rest/v1/fj_job_requests?user_id=eq.${userId}&status=eq.Pending`, { headers: hdrs }).then(r=>r.ok?r.json():[]).catch(()=>[]),
      ]).then(([msgs, reqs]) => setPortalUnread((msgs?.length||0)+(reqs?.length||0))).catch(()=>{});
    };
    fetchPortal();
    // Poll every 20s so dashboard badge stays live without a manual refresh
    const poll = setInterval(fetchPortal, 60000); // Reduced from 20s — Realtime handles live updates
    return () => clearInterval(poll);
  }, []);

  const todayStr = today();
  const todayJobs = jobs.filter(j => j.date === todayStr);
  const totalRevenue = invoices.filter(i => i.status === "Paid").reduce((s,i) => s+(i.total||0), 0);
  const outstanding = invoices.filter(i => i.status !== "Paid").reduce((s,i) => s+((i.total||0)-(i.paid||0)), 0);
  const overdue = invoices.filter(i => i.status === "Overdue" || (i.due_date < todayStr && i.status !== "Paid"));
  const pendingQuotes = quotes.filter(q => q.status === "Sent").reduce((s,q) => s+(q.total||0), 0);
  const health = overdue.length > 2 ? "red" : outstanding > 50000 ? "orange" : "green";
  const healthLabel = { green:"Healthy", orange:"Attention Required", red:"Critical" };
  const healthColors = { green:"var(--status-green-fg)", orange:"var(--status-amber-fg)", red:"var(--status-red-fg)" };
  const recentJobs = [...jobs].sort((a,b) => new Date(b.created_at)-new Date(a.created_at)).slice(0,5);

  // ── Graph data ──
  // Revenue by month — last 6 months from invoices
  const now = new Date();
  const monthRevenue = Array.from({length:6}, (_,i) => {
    const d = new Date(now.getFullYear(), now.getMonth()-5+i, 1);
    const mo = d.getMonth(); const yr = d.getFullYear();
    const label = d.toLocaleDateString("en-ZA",{month:"short"});
    const val = invoices.filter(inv => {
      const id = new Date(inv.date||inv.created_at||"");
      return id.getMonth()===mo && id.getFullYear()===yr && inv.status==="Paid";
    }).reduce((s,inv)=>s+(inv.total||0),0);
    return { label, val };
  });
  const maxRev = Math.max(...monthRevenue.map(m=>m.val), 1);

  // Expenses by month — from invoices total - paid as proxy, or use expenses if available
  const monthExpenses = Array.from({length:6}, (_,i) => {
    const d = new Date(now.getFullYear(), now.getMonth()-5+i, 1);
    const mo = d.getMonth(); const yr = d.getFullYear();
    const label = d.toLocaleDateString("en-ZA",{month:"short"});
    const val = invoices.filter(inv => {
      const id = new Date(inv.date||inv.created_at||"");
      return id.getMonth()===mo && id.getFullYear()===yr;
    }).reduce((s,inv)=>s+((inv.subtotal||inv.total||0)*0.45),0);
    return { label, val };
  });

  // Job status donut data
  const jobStatuses = {
    Completed: jobs.filter(j=>j.status==="Completed"||j.status==="Paid").length,
    "In Progress": jobs.filter(j=>j.status==="In Progress").length,
    Pending: jobs.filter(j=>j.status==="Pending"||j.status==="Scheduled").length,
    Overdue: jobs.filter(j=>j.status==="Overdue").length,
  };
  const totalJobsCount = jobs.length || 1;
  const donutColors = { Completed:"#10b981","In Progress":"#3b82f6", Pending:"#f59e0b", Overdue:"#ef4444" };

  // Quote funnel
  const qSent = quotes.length;
  const qAccepted = quotes.filter(q=>q.status==="Accepted"||q.status==="Invoiced"||q.status==="Paid").length;
  const qPaid = quotes.filter(q=>q.status==="Paid").length;
  const convRate = qSent > 0 ? Math.round((qAccepted/qSent)*100) : 0;

  // Cashflow: paid vs outstanding by month
  const cashMonths = Array.from({length:6}, (_,i) => {
    const d = new Date(now.getFullYear(), now.getMonth()-5+i, 1);
    const mo = d.getMonth(); const yr = d.getFullYear();
    const paid = invoices.filter(inv => { const id=new Date(inv.date||inv.created_at||""); return id.getMonth()===mo&&id.getFullYear()===yr&&inv.status==="Paid"; }).reduce((s,i)=>s+(i.total||0),0);
    const out = invoices.filter(inv => { const id=new Date(inv.date||inv.created_at||""); return id.getMonth()===mo&&id.getFullYear()===yr&&inv.status!=="Paid"; }).reduce((s,i)=>s+((i.total||0)-(i.paid||0)),0);
    return { label: d.toLocaleDateString("en-ZA",{month:"short"}), paid, out };
  });
  const maxCash = Math.max(...cashMonths.map(m=>Math.max(m.paid,m.out)), 1);

  // Heatmap: job counts per day for last 42 days
  const heatmap = Array.from({length:42}, (_,i) => {
    const d = new Date(); d.setDate(d.getDate()-41+i);
    const ds = d.toISOString().slice(0,10);
    const count = jobs.filter(j=>j.date===ds).length;
    return { count, date:ds };
  });
  const maxHeat = Math.max(...heatmap.map(h=>h.count), 1);

  // ── Business Health metrics ──
  const bhCompletedJobs = jobs.filter(j=>j.status==="Completed"||j.status==="Paid").length;
  const bhPaidInvoices = invoices.filter(i=>i.status==="Paid").length;
  const bhCollectionRate = invoices.length > 0 ? Math.round((bhPaidInvoices / invoices.length)*100) : 0;
  const bhJobRate = jobs.length > 0 ? Math.round((bhCompletedJobs / jobs.length)*100) : 0;
  const bhQuoteRate = convRate;
  const bhHealthScore = Math.round((bhCollectionRate + bhJobRate + Math.min(bhQuoteRate*1.5,100)) / 3);
  const bhMetrics = [
    {label:"Jobs Done",  val:bhJobRate,        color:"#c8972a"},
    {label:"Collected",  val:bhCollectionRate, color:"#0d9488"},
    {label:"Quotes Won", val:bhQuoteRate,       color:"#1e3a5f"},
  ];
  const bhCircumference = 2*Math.PI*20;

  return (
    <div>
      {/* KPI CARDS — desktop only, mobile already has KPI strip above */}
      <div className="stats-grid" style={{display:"grid"}}>
        <div className="stat-card kpi-gold">
          <div className="stat-top"><div className="stat-label">Monthly Revenue</div><div className="stat-icon"><Icon name="currency" size={14} style={{color:"var(--status-grey-fg)"}}/></div></div>
          <div className="stat-value" style={{color:"#111"}}>{formatCurrency(totalRevenue, business?.currency)}</div>
          <div className="stat-sub">Paid invoices</div>
        </div>
        <div className="stat-card kpi-warn">
          <div className="stat-top"><div className="stat-label">Outstanding</div><div className="stat-icon"><Icon name="invoice" size={14} style={{color:"var(--status-grey-fg)"}}/></div></div>
          <div className="stat-value" style={{color:"#111"}}>{formatCurrency(outstanding, business?.currency)}</div>
          <div className="stat-sub">{overdue.length > 0 ? <span className="stat-badge sb-red">{overdue.length} overdue</span> : "All current"}</div>
        </div>
        <div className="stat-card kpi-blue">
          <div className="stat-top"><div className="stat-label">Jobs Today</div><div className="stat-icon"><Icon name="jobs" size={14} style={{color:"var(--status-grey-fg)"}}/></div></div>
          <div className="stat-value" style={{color:"#111"}}>{todayJobs.length}</div>
          <div className="stat-sub"><span className="stat-badge sb-grey">{jobs.filter(j=>j.status==="In Progress").length} in progress</span></div>
        </div>
        <div className="stat-card">
          <div className="stat-top"><div className="stat-label">Pending Quotes</div><div className="stat-icon"><Icon name="quote" size={14} style={{color:"var(--status-grey-fg)"}}/></div></div>
          <div className="stat-value" style={{color:"#111"}}>{formatCurrency(pendingQuotes, business?.currency)}</div>
          <div className="stat-sub"><span className="stat-badge sb-grey">{quotes.filter(q=>q.status==="Sent").length} quotes sent</span></div>
        </div>
      </div>

      {/* ── ROW 1: Revenue by Month (full width) ── */}
      <div className="card graph-card" style={{marginBottom:16}}>
        <div className="graph-card-header">
          <div className="graph-card-title">
            <div className="graph-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="12" width="4" height="9"/><rect x="10" y="7" width="4" height="14"/><rect x="17" y="4" width="4" height="17"/></svg>
            </div>
            Monthly Revenue & Expenses
          </div>
          <span className="graph-subtitle">Last 6 months</span>
        </div>
        {(() => {
          const maxVal = Math.max(...monthRevenue.map(m=>m.val), ...monthExpenses.map(m=>m.val), 1);
          const w = 560; const h = 100; const padL = 44; const padR = 12; const padT = 14; const padB = 24;
          const barW = 14; const groupW = (w - padL - padR) / 6;
          return (
            <svg width="100%" viewBox={`0 0 ${w} ${h+padT+padB}`} style={{overflow:"visible",maxHeight:160}}>
              <defs>
                <linearGradient id="dgRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#c8972a" stopOpacity="0.95"/>
                  <stop offset="100%" stopColor="#c8972a" stopOpacity="0.55"/>
                </linearGradient>
                <linearGradient id="dgExp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1e3a5f" stopOpacity="0.8"/>
                  <stop offset="100%" stopColor="#1e3a5f" stopOpacity="0.4"/>
                </linearGradient>
              </defs>
              {/* Gridlines */}
              {[0,0.25,0.5,0.75,1].map((f,i) => {
                const y = padT + h - f*h;
                const label = maxVal > 0 ? formatCurrency(maxVal*f, business?.currency).replace("R ","R") : "0";
                return (
                  <g key={i}>
                    <line x1={padL} y1={y} x2={w-padR} y2={y} stroke="#e8eaed" strokeWidth="0.5"/>
                    <text x={padL-6} y={y+4} textAnchor="end" fontSize="9" fill="#94a3b8">{f>0?label:""}</text>
                  </g>
                );
              })}
              {/* Bars */}
              {monthRevenue.map((m, i) => {
                const cx = padL + i*groupW + groupW/2;
                const rh = maxVal > 0 ? (m.val/maxVal)*h : 2;
                const eh = maxVal > 0 ? (monthExpenses[i]?.val/maxVal)*h : 2;
                const isLast = i === 5;
                return (
                  <g key={i}>
                    {/* Revenue bar */}
                    <rect x={cx - barW - 2} y={padT + h - rh} width={barW} height={rh} rx="3" fill="url(#dgRev)" opacity={isLast?1:0.72}/>
                    {/* Expense bar */}
                    <rect x={cx + 2} y={padT + h - eh} width={barW} height={eh} rx="3" fill="url(#dgExp)" opacity={isLast?1:0.65}/>
                    {/* Value label on bar top */}
                    {m.val > 0 && <text x={cx - barW/2 - 2} y={padT + h - rh - 4} textAnchor="middle" fontSize="8" fontWeight={isLast?"700":"400"} fill={isLast?"#c8972a":"#94a3b8"}>{formatCurrency(m.val, business?.currency).replace("R ","R")}</text>}
                    {/* Month label */}
                    <text x={cx} y={padT + h + 18} textAnchor="middle" fontSize="10" fontWeight={isLast?"600":"400"} fill={isLast?"#111":"#94a3b8"}>{m.label}</text>
                  </g>
                );
              })}
              {/* Legend */}
              <rect x={padL} y={4} width={10} height={7} rx="2" fill="#c8972a"/>
              <text x={padL+14} y={11} fontSize="9.5" fill="#64748b">Revenue</text>
              <rect x={padL+72} y={4} width={10} height={7} rx="2" fill="#1e3a5f" opacity="0.75"/>
              <text x={padL+86} y={11} fontSize="9.5" fill="#64748b">Expenses</text>
            </svg>
          );
        })()}
      </div>

      {/* ── ROW 2: Cashflow area + Job Status donut + Quote Conversion funnel ── */}
      <div className="dash-charts-3col" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginBottom:16,minWidth:0}}>

        {/* Cashflow area chart */}
        <div className="card graph-card">
          <div className="graph-card-header">
            <div className="graph-card-title">
              <div className="graph-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
              </div>
              Cashflow Trend
            </div>
            <span className="graph-subtitle">Paid vs Outstanding</span>
          </div>
          {(() => {
            const W=280; const H=110; const pL=8; const pR=8; const pT=12; const pB=20;
            const pts = cashMonths.map((m,i) => ({
              x: pL + (i/(cashMonths.length-1||1))*(W-pL-pR),
              yP: m.paid>0 ? pT+(H-pT-pB)-(m.paid/maxCash)*(H-pT-pB) : H-pB,
              yO: m.out>0  ? pT+(H-pT-pB)-(m.out/maxCash)*(H-pT-pB)  : H-pB,
            }));
            const pPath = pts.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.yP.toFixed(1)}`).join(" ");
            const oPath = pts.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.yO.toFixed(1)}`).join(" ");
            const lastPt = pts[pts.length-1];
            return (
              <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{overflow:"visible"}}>
                <defs>
                  <linearGradient id="gPaid" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0d9488" stopOpacity="0.3"/>
                    <stop offset="100%" stopColor="#0d9488" stopOpacity="0.02"/>
                  </linearGradient>
                  <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#c8972a" stopOpacity="0.22"/>
                    <stop offset="100%" stopColor="#c8972a" stopOpacity="0.02"/>
                  </linearGradient>
                </defs>
                {/* Grid */}
                {[0.25,0.5,0.75].map((f,i)=>(
                  <line key={i} x1={pL} y1={pT+(H-pT-pB)*f} x2={W-pR} y2={pT+(H-pT-pB)*f} stroke="#f1f5f9" strokeWidth="0.5"/>
                ))}
                {/* Areas */}
                <path d={`${pPath} L${pts[pts.length-1].x},${H-pB} L${pts[0].x},${H-pB} Z`} fill="url(#gPaid)"/>
                <path d={`${oPath} L${pts[pts.length-1].x},${H-pB} L${pts[0].x},${H-pB} Z`} fill="url(#gOut)"/>
                {/* Lines */}
                <path d={pPath} fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d={oPath} fill="none" stroke="#c8972a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="4 2"/>
                {/* End dots */}
                <circle cx={lastPt.x} cy={lastPt.yP} r="3.5" fill="#fff" stroke="#0d9488" strokeWidth="2"/>
                <circle cx={lastPt.x} cy={lastPt.yO} r="3.5" fill="#fff" stroke="#c8972a" strokeWidth="2"/>
                {/* Month labels */}
                {cashMonths.map((m,i)=>(
                  <text key={i} x={pts[i].x} y={H} textAnchor="middle" fontSize="9" fill="#94a3b8">{m.label}</text>
                ))}
              </svg>
            );
          })()}
          <div style={{display:"flex",gap:14,marginTop:8}}>
            <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11}}><div style={{width:14,height:2,background:"#0d9488",borderRadius:1}}></div><span style={{color:"#555"}}>Paid</span></div>
            <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11}}><div style={{width:14,height:2,background:"#c8972a",borderRadius:1,borderStyle:"dashed"}}></div><span style={{color:"#555"}}>Outstanding</span></div>
          </div>
        </div>

        {/* Job Status Donut */}
        <div className="card graph-card" style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
          <div className="graph-card-header" style={{width:"100%"}}>
            <div className="graph-card-title">
              <div className="graph-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>
              </div>
              Job Status
            </div>
          </div>
          <svg viewBox="0 0 36 36" width="100" height="100" style={{transform:"rotate(-90deg)",margin:"8px 0"}}>
            {(() => {
              let offset = 0;
              const circumference = 2*Math.PI*15.9;
              const donutEntries = Object.entries(jobStatuses).filter(([,c])=>c>0);
              return donutEntries.map(([status,count]) => {
                const pct = totalJobsCount > 0 ? count/totalJobsCount : 0;
                const dash = pct*circumference;
                const el = <circle key={status} cx="18" cy="18" r="15.9" fill="none" stroke={donutColors[status]} strokeWidth="4.2" strokeDasharray={`${dash} ${circumference-dash}`} strokeDashoffset={-offset}/>;
                offset += dash;
                return el;
              });
            })()}
            {/* Inner ring */}
            <circle cx="18" cy="18" r="12" fill="white"/>
            <text x="18" y="17" textAnchor="middle" fontSize="5.5" fontWeight="700" fill="#111" transform="rotate(90 18 18)">{jobs.length}</text>
            <text x="18" y="21.5" textAnchor="middle" fontSize="3.5" fill="#94a3b8" transform="rotate(90 18 18)">jobs</text>
          </svg>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"5px 10px",width:"100%",padding:"0 4px"}}>
            {Object.entries(jobStatuses).map(([s,c]) => (
              <div key={s} style={{display:"flex",alignItems:"center",gap:5,fontSize:11}}>
                <div style={{width:7,height:7,borderRadius:"50%",background:donutColors[s],flexShrink:0}}></div>
                <span style={{color:"#64748b"}}>{s}</span>
                <strong style={{marginLeft:"auto",color:"#111"}}>{c}</strong>
              </div>
            ))}
          </div>
        </div>

        {/* Quote Conversion Funnel chart */}
        <div className="card graph-card">
          <div className="graph-card-header">
            <div className="graph-card-title">
              <div className="graph-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
              </div>
              Quote Conversion
            </div>
            <span className="graph-subtitle">This month</span>
          </div>
          {(() => {
            const funnelData = [
              {label:"Sent",    val:qSent,     color:"#475569", pct:100},
              {label:"Accepted",val:qAccepted, color:"#c8972a", pct:qSent>0?Math.round(qAccepted/qSent*100):0},
              {label:"Invoiced",val:invoices.filter(i=>i.status!=="Draft").length, color:"#1e3a5f", pct:qSent>0?Math.round(invoices.filter(i=>i.status!=="Draft").length/Math.max(qSent,1)*100):0},
              {label:"Paid",    val:qPaid,     color:"#0d9488", pct:qSent>0?Math.round(qPaid/qSent*100):0},
            ];
            const maxW = 220;
            return (
              <svg width="100%" viewBox="0 0 240 128">
                {funnelData.map((row,i) => {
                  const barW = Math.max((row.pct/100)*maxW, row.val>0?12:2);
                  const x = (maxW - barW)/2;
                  return (
                    <g key={i}>
                      <rect x={x} y={i*28+4} width={barW} height={20} rx="4" fill={row.color} opacity={row.val>0?0.85:0.2}/>
                      <text x={x-4} y={i*28+17} textAnchor="end" fontSize="9.5" fill="#64748b">{row.label}</text>
                      <text x={x+barW+5} y={i*28+17} fontSize="10" fontWeight="600" fill="#111">{row.val}</text>
                    </g>
                  );
                })}
                <text x="120" y="122" textAnchor="middle" fontSize="10" fill="#64748b">
                  Rate: <tspan fontWeight="700" fill="#c8972a">{convRate}%</tspan>
                </text>
              </svg>
            );
          })()}
        </div>

      </div>

      {/* ── ROW 3: Business Health (full width) ── */}
      <div style={{marginBottom:16}}>

        {/* Business Health — radial gauges */}
        <div className="card graph-card" style={{maxWidth:560}}>
          <div className="graph-card-header">
            <div className="graph-card-title">
              <div className="graph-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
              </div>
              Business Health
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{
                width:10,height:10,borderRadius:"50%",
                background:health==="red"?"#ef4444":health==="orange"?"#f59e0b":"#10b981",
                animation:"pulse 2s infinite"
              }}></div>
              <span style={{fontSize:11,fontWeight:600,color:healthColors[health]}}>{healthLabel[health]}</span>
            </div>
          </div>

          {/* Overall score + gauges */}
          <div style={{display:"flex",alignItems:"center",gap:20}}>
            {/* Big score gauge */}
            <div style={{position:"relative",width:90,height:90,flexShrink:0}}>
              <svg viewBox="0 0 48 48" width="90" height="90" style={{transform:"rotate(-90deg)"}}>
                <circle cx="24" cy="24" r="20" fill="none" stroke="#f1f5f9" strokeWidth="5"/>
                <circle cx="24" cy="24" r="20" fill="none"
                  stroke={health==="red"?"#ef4444":health==="orange"?"#f59e0b":"#10b981"}
                  strokeWidth="5"
                  strokeDasharray={`${(bhHealthScore/100)*bhCircumference} ${bhCircumference}`}
                  strokeLinecap="round"/>
              </svg>
              <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                <div style={{fontSize:18,fontWeight:700,color:"#111",lineHeight:1}}>{bhHealthScore}</div>
                <div style={{fontSize:9,color:"#94a3b8"}}>score</div>
              </div>
            </div>

            {/* Mini gauges */}
            <div style={{flex:1,display:"flex",flexDirection:"column",gap:10}}>
              {bhMetrics.map((m) => (
                <div key={m.label}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                    <span style={{color:"#64748b"}}>{m.label}</span>
                    <span style={{fontWeight:700,color:"#111"}}>{m.val}%</span>
                  </div>
                  <div style={{height:6,background:"#f1f5f9",borderRadius:3,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${m.val}%`,background:m.color,borderRadius:3,transition:"width 0.5s ease"}}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Overdue alert */}
          {overdue.length > 0 && (
            <div style={{marginTop:14,padding:"10px 12px",background:"#fef2f2",borderRadius:8,border:"1px solid #fecaca"}}>
              <div style={{fontSize:11,fontWeight:700,color:"#b91c1c",marginBottom:4,display:"flex",alignItems:"center",gap:5}}>
                <Icon name="alert" size={12} style={{color:"#b91c1c"}}/>
                {overdue.length} Overdue Invoice{overdue.length!==1?"s":""}
              </div>
              {overdue.slice(0,2).map(inv => (
                <div key={inv.id} style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#64748b",marginBottom:2}}>
                  <span>{inv.client_name}</span>
                  <span style={{fontFamily:"var(--mono)",color:"#b91c1c",fontWeight:600}}>{formatCurrency((inv.total||0, business?.currency)-(inv.paid||0))}</span>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

    </div>
  );
}

// ─── CALENDAR ─────────────────────────────────────────────────────────────────
function CalendarView({ userId, onNewJob }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [business, setBusiness] = useState({ currency:"ZAR" });
  const [selectedDay, setSelectedDay] = useState(null);
  const [mobileView, setMobileView] = useState("agenda"); // "agenda" | "month"
  useEffect(() => {
    Promise.all([DB.getList("jobs", userId), DB.get("business", userId)])
      .then(([j, b]) => { setJobs(j||[]); setBusiness({...(b||{}), currency:b?.currency||"ZAR", unit_system:b?.unit_system||"metric"}); });
  }, []);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const todayStr = today();
  const cells = [];
  for (let i=0;i<firstDay;i++) cells.push(null);
  for (let d=1;d<=daysInMonth;d++) cells.push(d);

  const getJobsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    return jobs.filter(j => j.date === dateStr);
  };

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const statusColor = (j) => j.status==="Completed"?"cal-job completed":j.priority==="Urgent"?"cal-job urgent":j.status==="In Progress"?"cal-job inprogress":"cal-job pending";
  const statusDot = (j) => j.status==="Completed"?"#10b981":j.priority==="Urgent"?"#ef4444":j.status==="In Progress"?"#3b82f6":"#f59e0b";

  // Agenda: group jobs in current month by day, sorted
  const agendaDays = [];
  for (let d=1;d<=daysInMonth;d++) {
    const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    const dayJobs = jobs.filter(j=>j.date===dateStr);
    if (dayJobs.length > 0) agendaDays.push({ dateStr, day:d, jobs:dayJobs });
  }

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <button className="btn btn-icon btn-outline" onClick={() => setCurrentDate(new Date(year,month-1))}><Icon name="chevron_left"/></button>
          <h3 style={{fontSize:18,fontWeight:600}}>{monthNames[month]} {year}</h3>
          <button className="btn btn-icon btn-outline" onClick={() => setCurrentDate(new Date(year,month+1))}><Icon name="chevron_right"/></button>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {/* Mobile view toggle */}
          <div className="cal-view-toggle">
            <button className={"cal-vt-btn"+(mobileView==="agenda"?" active":"")} onClick={()=>setMobileView("agenda")}>Agenda</button>
            <button className={"cal-vt-btn"+(mobileView==="month"?" active":"")} onClick={()=>setMobileView("month")}>Month</button>
          </div>
          <button className="btn btn-primary" onClick={onNewJob}><Icon name="plus"/>New Job</button>
        </div>
      </div>

      {/* ── DESKTOP: full grid calendar ── */}
      <div className="cal-desktop-view">
        <div className="card" style={{padding:0,overflow:"hidden"}}>
          <div className="calendar-grid">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} className="cal-header">{d}</div>)}
            {cells.map((day, i) => {
              const dateStr = day ? `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}` : null;
              const dayJobs = getJobsForDay(day);
              const isToday = dateStr === todayStr;
              const hasJobs = dayJobs.length > 0;
              return (
                <div key={i}
                  className={["cal-cell",!day?"other-month":"",isToday?"today":"",hasJobs?"cal-cell-active":""].filter(Boolean).join(" ")}
                  onClick={() => hasJobs && setSelectedDay({dateStr,jobs:dayJobs})}
                  style={{cursor:hasJobs?"pointer":"default"}}>
                  {day && <div className={isToday?"cal-date today-dot":"cal-date"}>{day}</div>}
                  {dayJobs.slice(0,3).map(j => (
                    <div key={j.id} className={statusColor(j)} title={j.title}>{j.title}</div>
                  ))}
                  {dayJobs.length > 3 && <div style={{fontSize:10,color:"var(--gold)",fontWeight:700,marginTop:2}}>+{dayJobs.length-3} more</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── MOBILE: Agenda or compact month ── */}
      <div className="cal-mobile-view">

        {mobileView === "agenda" && (
          <div>
            {agendaDays.length === 0 ? (
              <div className="empty-state"><Icon name="calendar" size={36}/><h3>No jobs this month</h3><p>Tap New Job to schedule work.</p></div>
            ) : (
              agendaDays.map(({ dateStr, day, jobs:dayJobs }) => {
                const d = new Date(dateStr+"T00:00:00");
                const isToday = dateStr === todayStr;
                const weekday = d.toLocaleDateString("en-ZA",{weekday:"short"});
                return (
                  <div key={dateStr} style={{marginBottom:14}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                      <div style={{
                        width:40,height:40,borderRadius:12,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0,
                        background:isToday?"var(--gold)":"var(--ink)",
                        boxShadow:isToday?"0 2px 10px rgba(200,151,42,0.4)":"none",
                      }}>
                        <span style={{fontSize:9,color:isToday?"var(--ink)":"rgba(255,255,255,0.5)",fontWeight:700,lineHeight:1}}>{weekday.toUpperCase()}</span>
                        <span style={{fontSize:17,fontWeight:700,color:isToday?"var(--ink)":"white",lineHeight:1.2}}>{day}</span>
                      </div>
                      <div style={{fontSize:12,color:"#94a3b8"}}>{dayJobs.length} job{dayJobs.length!==1?"s":""}</div>
                    </div>
                    {dayJobs.map(j => (
                      <div key={j.id} onClick={() => setSelectedDay({dateStr,jobs:dayJobs})}
                        style={{
                          background:"white",borderRadius:12,padding:"11px 14px",marginBottom:8,
                          borderLeft:`3px solid ${statusDot(j)}`,
                          boxShadow:"0 1px 6px rgba(0,0,0,0.05)",cursor:"pointer",
                          display:"flex",justifyContent:"space-between",alignItems:"flex-start",
                        }}>
                        <div style={{minWidth:0}}>
                          <div style={{fontWeight:700,fontSize:13,color:"var(--ink)",marginBottom:2}}>{j.title}</div>
                          <div style={{fontSize:11,color:"#94a3b8",display:"flex",gap:8,flexWrap:"wrap"}}>
                            {j.client_name && <span>👤 {j.client_name}</span>}
                            {((j.worker_names||[]).length>0||j.worker_name) && <span>🔧 {(j.worker_names||[]).length>0?j.worker_names[0]:j.worker_name}</span>}
                          </div>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0,marginLeft:10}}>
                          <span className={j.status==="Completed"?"badge badge-green":j.status==="In Progress"?"badge badge-blue":j.priority==="Urgent"?"badge badge-red":"badge badge-amber"} style={{fontSize:9}}>{j.status}</span>
                          {j.estimated_cost>0 && <span style={{fontFamily:"var(--mono)",fontSize:11,fontWeight:700,color:"var(--ink)"}}>{formatCurrency(j.estimated_cost, business?.currency)}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        )}

        {mobileView === "month" && (
          <div className="card" style={{padding:0,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1,background:"var(--border)"}}>
              {["S","M","T","W","T","F","S"].map(d => (
                <div key={d} style={{background:"var(--ink)",color:"rgba(255,255,255,0.6)",textAlign:"center",padding:"6px 2px",fontSize:10,fontWeight:700}}>{d}</div>
              ))}
              {cells.map((day, i) => {
                const dateStr = day ? `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}` : null;
                const dayJobs = getJobsForDay(day);
                const isToday = dateStr === todayStr;
                return (
                  <div key={i} onClick={() => dayJobs.length>0&&setSelectedDay({dateStr,jobs:dayJobs})}
                    style={{
                      background:dayJobs.length>0?"#fffdf5":"white",minHeight:52,padding:"4px 3px",
                      cursor:dayJobs.length>0?"pointer":"default",position:"relative",
                    }}>
                    {day && (
                      <>
                        <div style={{
                          width:20,height:20,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",
                          background:isToday?"var(--gold)":"transparent",
                          fontSize:11,fontWeight:600,color:isToday?"white":"var(--ink)",marginBottom:3,
                        }}>{day}</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:2}}>
                          {dayJobs.slice(0,3).map(j=>(
                            <div key={j.id} style={{width:6,height:6,borderRadius:"50%",background:statusDot(j)}}></div>
                          ))}
                          {dayJobs.length>3&&<div style={{fontSize:8,color:"var(--gold)",fontWeight:700}}>+{dayJobs.length-3}</div>}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{marginTop:14,display:"flex",gap:10,flexWrap:"wrap"}}>
        {[{c:"badge-green",l:"Completed"},{c:"badge-amber",l:"Scheduled"},{c:"badge-red",l:"Urgent"},{c:"badge-blue",l:"In Progress"}].map(({c,l})=>(
          <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11.5}}>
            <span className={`badge ${c}`} style={{padding:"2px 7px"}}>{l}</span>
          </div>
        ))}
      </div>

      {/* Day detail modal */}
      {selectedDay && (
        <div className="modal-overlay" onClick={() => setSelectedDay(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:480}}>
            <div className="modal-header">
              <h3 style={{fontSize:16}}>{(() => { const d=new Date(selectedDay.dateStr+"T00:00:00"); return d.toLocaleDateString("en-ZA",{weekday:"long",day:"numeric",month:"long",year:"numeric"}); })()}</h3>
              <button className="btn btn-icon btn-outline btn-sm" onClick={() => setSelectedDay(null)}><Icon name="x"/></button>
            </div>
            <div className="modal-body" style={{padding:"12px 20px",display:"flex",flexDirection:"column",gap:10}}>
              {selectedDay.jobs.map(j => (
                <div key={j.id} style={{background:"var(--surface)",borderRadius:10,padding:"12px 14px",borderLeft:`4px solid ${j.status==="Completed"?"var(--status-green-fg)":j.priority==="Urgent"?"var(--status-red-fg)":j.status==="In Progress"?"var(--status-blue-fg)":"var(--gold)"}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                    <div style={{fontWeight:700,fontSize:14}}>{j.title}</div>
                    <span className={j.status==="Completed"?"badge badge-green":j.status==="In Progress"?"badge badge-blue":j.priority==="Urgent"?"badge badge-red":"badge badge-amber"}>{j.status}</span>
                  </div>
                  <div style={{fontSize:12.5,color:"#6b7280",display:"flex",flexDirection:"column",gap:3}}>
                    {j.client_name && <span>👤 {j.client_name}</span>}
                    {((j.worker_names||[]).length>0||j.worker_name) && <span>🔧 {(j.worker_names||[]).length>0?j.worker_names.join(", "):j.worker_name}</span>}
                    {j.address && <span>📍 {j.address}</span>}
                    {j.estimated_cost>0 && <span style={{fontWeight:600,color:"var(--ink)"}}>{formatCurrency(j.estimated_cost, business?.currency)}</span>}
                  </div>
                </div>
              ))}
              <button className="btn btn-primary w-full" style={{marginTop:4}} onClick={() => { setSelectedDay(null); onNewJob(); }}>
                <Icon name="plus"/> Add Another Job This Day
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── JOBS MODULE ──────────────────────────────────────────────────────────────
function JobsModule({ userId }) {
  const [jobs, setJobs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [clients, setClients] = useState([]);
  const [business, setBusiness] = useState({ _userId: userId });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [emailDoc, setEmailDoc] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [form, setForm] = useState({ title:"", client_id:"", client_name:"", address:"", worker_ids:[], worker_names:[], date: today(), priority:"Normal", status:"Pending", estimated_cost:"", description:"", notes:"", materials:"", photos_before:[], photos_during:[], photos_after:[] });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const refresh = async () => {
    const [j, w, c, b] = await Promise.all([
      DB.getList("jobs", userId), DB.getList("workers", userId),
      DB.getList("clients", userId), DB.get("business", userId),
    ]);
    setJobs(j || []); setWorkers(w || []); setClients(c || []);
    setBusiness({ ...(b || {}), currency: b?.currency||"ZAR", unit_system:b?.unit_system||"metric", _userId: userId }); setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setForm({ title:"", client_id:"", client_name:"", address:"", worker_ids:[], worker_names:[], date: today(), priority:"Normal", status:"Pending", estimated_cost:"", description:"", notes:"", materials:"", photos_before:[], photos_during:[], photos_after:[] }); setErrors({}); setModal("new"); };
  const openEdit = (j) => { setForm({...j, worker_ids:j.worker_ids||[], worker_names:j.worker_names||[], photos_before:j.photos_before||[], photos_during:j.photos_during||[], photos_after:j.photos_after||[]}); setErrors({}); setModal("edit"); };

  const [errors, setErrors] = useState({});

  const save = async () => {
    const errs = {
      title:          V.required(form.title, "Job title"),
      date:           V.date(form.date, "Job date"),
      client_name:    V.required(form.client_name, "Client"),
      estimated_cost: V.amount(form.estimated_cost, "Estimated cost"),
    };
    const first = V.run(errs);
    if (first) { setErrors(errs); return; }
    setErrors({});
    const data = { ...form, user_id: userId, estimated_cost: parseFloat(form.estimated_cost)||0 };
    try {
      if (modal === "new") { await DB.insert("jobs", data); } else { await DB.update("jobs", form.id, data); }
      await refresh(); setModal(null);
    } catch (e) { console.error("Save job failed:", e); }
  };

  const updateStatus = async (id, status) => { await DB.update("jobs", id, { status }); await refresh(); };

  const whatsappMsg = (j) => {
    const msg = `Hi, this is regarding your job: *${j.title}*\nDate: ${formatDate(j.date)}\nAddress: ${j.address}\nWorkers: ${(j.worker_names||[]).join(", ") || j.worker_name || "TBA"}\nStatus: ${j.status}\n\nPlease confirm receipt.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const del = (id, label) => setDeleteConfirm({ id, label: label || 'this job' });

  const filtered = jobs.filter(j => {
    const matchSearch = j.title?.toLowerCase().includes(search.toLowerCase()) || j.client_name?.toLowerCase().includes(search.toLowerCase()) || (j.worker_names||[]).join(" ").toLowerCase().includes(search.toLowerCase()) || (j.worker_name||"").toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "All" || j.status === filter;
    return matchSearch && matchFilter;
  });



  return (
    <div>
      <div style={{ display:"flex", gap:12, marginBottom:20, flexWrap:"wrap" }}>
        <div className="search-bar" style={{ flex:1, minWidth:200 }}>
          <Icon name="search" size={15} style={{ color:"#94a3b8" }} />
          <input placeholder="Search jobs..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width:"auto" }} value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="All">All Statuses</option>
          {STATUS_PIPELINE.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="btn btn-primary" onClick={openNew}><Icon name="plus" />New Job</button>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state"><Icon name="jobs" size={40} /><h3>No jobs found</h3><p>Create your first job to get started.</p></div>
      ) : (<>
        {/* DESKTOP TABLE */}
        <div className="card desktop-table" style={{ padding:0 }}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Job</th><th>Client</th><th>Worker</th><th>Date</th><th>Status</th><th>Value</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map(j => (
                  <tr key={j.id}>
                    <td><div style={{ fontWeight:500 }}>{j.title}</div><div className="text-sm text-muted">{j.address}</div></td>
                    <td className="text-muted">{j.client_name}</td>
                    <td className="text-muted">{(j.worker_names||[]).length > 0 ? j.worker_names.join(", ") : (j.worker_name || "—")}</td>
                    <td className="text-muted">{formatDate(j.date)}</td>
                    <td>
                      <select value={j.status} onChange={e => updateStatus(j.id, e.target.value)} style={{ padding:"3px 8px", border:"1.5px solid var(--border)", borderRadius:6, fontSize:12, background:"white", cursor:"pointer" }}>
                        {STATUS_PIPELINE.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ fontWeight:600 }}>{formatCurrency(j.estimated_cost, business?.currency)}</td>
                    <td>
                      <div style={{ display:"flex", gap:4 }}>
                        <button className="btn btn-icon btn-outline btn-sm" title="Download Report" onClick={() => { printAsPDF(buildJobReportHTML(j,business,[]), `Job Report — ${j.title}`); }} style={{ color:"var(--status-green-fg)" }}><Icon name="download" size={13}/></button>
                        <button className="btn btn-icon btn-outline btn-sm" title="Email Report" onClick={() => setEmailDoc(j)} style={{ color:"var(--status-blue-fg)" }}><Icon name="email" size={13} /></button>
                        <button className="btn btn-icon btn-outline btn-sm" title="WhatsApp" onClick={() => setEmailDoc(j)} style={{ color:"#25d366" }}><Icon name="whatsapp" size={13} style={{ color:"#25d366" }} /></button>
                        <button className="btn btn-icon btn-outline btn-sm" onClick={() => openEdit(j)}><Icon name="edit" size={13} /></button>
                        <button className="btn btn-icon btn-outline btn-sm" onClick={() => del(j.id, j.title)} style={{ color:"var(--status-red-fg)" }}><Icon name="trash" size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* MOBILE CARDS */}
        <div className="mobile-list">
          <div className="m-count">{filtered.length} Job{filtered.length!==1?"s":""}</div>
          {filtered.map(j => (
            <div key={j.id} className="m-card">
              <div className="m-card-body">
                <div className="m-card-top">
                  <div>
                    <div className="m-card-title">{j.title}</div>
                    <div className="m-card-ref">{formatDate(j.date)}</div>
                  </div>
                  <select value={j.status} onChange={e => updateStatus(j.id, e.target.value)} style={{ padding:"4px 8px", border:"1.5px solid var(--border)", borderRadius:8, fontSize:12, background:"white", cursor:"pointer", fontWeight:600 }}>
                    {STATUS_PIPELINE.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="m-card-meta">
                  {j.client_name && <span><svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg><strong>{j.client_name}</strong></span>}
                  {((j.worker_names||[]).length > 0 || j.worker_name) && <span><svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>{(j.worker_names||[]).length > 0 ? j.worker_names.join(", ") : j.worker_name}</span>}
                </div>
                <div className="m-card-value">{formatCurrency(j.estimated_cost, business?.currency)}</div>
              </div>
              <div className="m-actions">
                <button className="m-btn" onClick={() => { printAsPDF(buildJobReportHTML(j,business,[]), `Job Report — ${j.title}`); }}>
                  <svg fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>PDF</span>
                </button>
                <button className="m-btn" onClick={() => setEmailDoc(j)}>
                  <svg fill="none" stroke="#2563eb" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  <span>Email</span>
                </button>
                <button className="m-btn" onClick={() => setEmailDoc(j)}>
                  <svg fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                  <span>WhatsApp</span>
                </button>
                <button className="m-btn" onClick={() => openEdit(j)}>
                  <svg fill="none" stroke="#0f1923" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  <span>Edit</span>
                </button>
                <button className="m-btn" onClick={() => del(j.id, j.title)}>
                  <svg fill="none" stroke="#dc2626" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </>)}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>{modal==="new" ? "New Job" : "Edit Job"}</h3><button className="btn btn-icon btn-outline btn-sm" onClick={() => setModal(null)}><Icon name="x" /></button></div>
            <div className="modal-body">    <>
      <div className="form-grid">
        <div className="form-group"><label className="form-label">Job Title *</label><input className="form-input" style={errors.title ? {borderColor:"#ef4444"} : {}} value={form.title} onChange={e => setForm({...form, title: e.target.value})} /><FormError error={errors.title} /></div>
        <div className="form-group"><label className="form-label">Date *</label><input className="form-input" style={errors.date ? {borderColor:"#ef4444"} : {}} type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /><FormError error={errors.date} /></div>
        <div className="form-group">
          <label className="form-label">Client *</label>
          <select className="form-select" style={errors.client_name ? {borderColor:"#ef4444"} : {}} value={form.client_id} onChange={e => { const c = clients.find(c=>c.id===e.target.value); setForm({...form, client_id: e.target.value, client_name: c?.name||"", address: c?.address||form.address}); }}>
            <option value="">Select client</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <FormError error={errors.client_name} />
        </div>
        <div className="form-group">
          <label className="form-label">Assigned Workers</label>
          {workers.length === 0 ? (
            <div className="text-muted text-sm">No workers added yet</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:6, padding:"10px 12px", border:"1.5px solid var(--border)", borderRadius:8, background:"white", maxHeight:160, overflowY:"auto" }}>
              {workers.map(w => {
                const checked = (form.worker_ids||[]).includes(w.id);
                return (
                  <label key={w.id} style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", fontSize:14 }}>
                    <input type="checkbox" checked={checked} onChange={() => {
                      const ids = form.worker_ids||[];
                      const names = form.worker_names||[];
                      if (checked) {
                        setForm({...form, worker_ids: ids.filter(id=>id!==w.id), worker_names: names.filter(n=>n!==w.name)});
                      } else {
                        setForm({...form, worker_ids: [...ids, w.id], worker_names: [...names, w.name]});
                      }
                    }} style={{ width:16, height:16, accentColor:"var(--gold)", cursor:"pointer" }} />
                    <span style={{ fontWeight: checked ? 600 : 400 }}>{w.name}</span>
                    {w.trade && <span className="text-muted text-sm">· {w.trade}</span>}
                  </label>
                );
              })}
            </div>
          )}
          {(form.worker_names||[]).length > 0 && (
            <div style={{ marginTop:6, display:"flex", gap:6, flexWrap:"wrap" }}>
              {form.worker_names.map(n => <span key={n} className="badge badge-blue">{n}</span>)}
            </div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select className="form-select" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}>
            {["Low","Normal","High","Urgent"].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Status</label>
          <select className="form-select" value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
            {STATUS_PIPELINE.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="form-group"><label className="form-label">Site Address</label><input className="form-input" value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
      <div className="form-group"><label className="form-label">Estimated Cost ({getCurrencyOption(business?.currency||"ZAR").symbol})</label><input className="form-input" type="number" value={form.estimated_cost} onChange={e => setForm({...form, estimated_cost: e.target.value})} placeholder="0.00" /></div>
      <div className="form-group"><label className="form-label">Description of Work</label><textarea className="form-textarea" value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
      <div className="form-group"><label className="form-label">Materials Used</label><textarea className="form-textarea" rows={2} value={form.materials} onChange={e => setForm({...form, materials: e.target.value})} placeholder="List materials used..." /></div>
      <div className="form-group"><label className="form-label">Internal Notes</label><textarea className="form-textarea" rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>

      {/* ── PHOTO UPLOADS ── */}
      {[["photos_before","📷 Before Photos"],["photos_during","🔧 During Photos"],["photos_after","✅ After Photos"]].map(([field, label]) => (
        <div className="form-group" key={field}>
          <label className="form-label">{label}</label>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:8 }}>
            {(form[field]||[]).map((url, i) => (
              <div key={i} style={{ position:"relative", width:110, height:110 }}>
                <img src={url} alt="" style={{ width:110, height:110, objectFit:"cover", borderRadius:10, border:"1px solid var(--border)" }} loading="lazy" />
                <button onClick={() => setForm(f => ({ ...f, [field]: f[field].filter((_,idx)=>idx!==i) }))}
                  style={{ position:"absolute", top:-6, right:-6, width:22, height:22, borderRadius:"50%", background:"#ef4444", border:"none", color:"white", fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>×</button>
              </div>
            ))}
            <label style={{ width:72, height:72, border:"2px dashed var(--border)", borderRadius:8, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#94a3b8", gap:4, fontSize:11, fontWeight:600, position:"relative" }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add
              <input type="file" accept="image/*" multiple style={{ display:"none" }}
                onChange={async e => {
                  const files = Array.from(e.target.files);
                  e.target.value = "";
                  for (const file of files) {
                    const url = await uploadPhoto(file, userId);
                    if (url) setForm(f => ({ ...f, [field]: [...(f[field]||[]), url] }));
                  }
                }} />
            </label>
          </div>
        </div>
      ))}
    </>
  </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>Save Job</button>
            </div>
          </div>
        </div>
      )}
      
      <DeleteConfirmModal item={deleteConfirm?.label} onConfirm={async () => { if(deleteConfirm){ await DB.remove("jobs", deleteConfirm.id); await refresh(); setDeleteConfirm(null); } }} onCancel={() => setDeleteConfirm(null)} />
      {emailDoc && <ShareModal doc={emailDoc} type="Job" business={business} onClose={() => setEmailDoc(null)} />}
    </div>
  );
}

// ─── ESTIMATES MODULE ─────────────────────────────────────────────────────────
function EstimatesModule({ userId }) {
  const [estimates, setEstimates]           = useState([]);
  const [clients, setClients]               = useState([]);
  const [business, setBusiness]             = useState({});
  const [loading, setLoading]               = useState(true);
  const [view, setView]                     = useState("list");
  const [editId, setEditId]                 = useState(null);
  const [deleteConfirm, setDeleteConfirm]   = useState(null);
  const [convertSuccess, setConvertSuccess] = useState(false);
  const [emailDoc, setEmailDoc]             = useState(null);
  const [sections, setSections]             = useState([]);
  const [sectionCounter, setSectionCounter] = useState(0);
  const [vatMode, setVatMode]               = useState("15");
  const [form, setForm] = useState({
    number:"", client_id:"", client_name:"", client_phone:"",
    site_address:"", date:today(), valid_days:14, prepared_by:"",
    job_description:"", contingency_pct:0,
    assumptions:"", payment_terms:"", status:"Draft",
  });

  const refresh = async () => {
    const [e,c,b] = await Promise.all([
      DB.getList("estimates",userId), DB.getList("clients",userId), DB.get("business",userId),
    ]);
    setEstimates(e||[]); setClients(c||[]); setBusiness({...(b||{}), currency:b?.currency||"ZAR", unit_system:b?.unit_system||"metric"}); setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  // Unit label helpers
  const distLabel       = (biz) => unitLabel("km",  biz?.unit_system);
  const lengthLabel     = (biz) => unitLabel("m",   biz?.unit_system);
  const fuelVolumeLabel = (biz) => unitLabel("L",   biz?.unit_system);
  const weightLabel     = (biz) => unitLabel("kg",  biz?.unit_system);
  const areaLabel       = (biz) => unitLabel("m2",  biz?.unit_system);
  const volumeLabel     = (biz) => unitLabel("m3",  biz?.unit_system);

  const addSection = (name="") => {
    const id = sectionCounter + 1;
    setSectionCounter(id);
    setSections(prev => [...prev, {
      id, name, matOpen:true, labOpen:false, eqpOpen:false, scopeOpen:false,
      matLines:[], labLines:[], eqpLines:[], inclusions:"", exclusions:"",
    }]);
  };

  const removeSection = (id) => {
    if (sections.length <= 1) return;
    setSections(prev => prev.filter(s => s.id !== id));
  };

  const updateSection = (id, field, val) =>
    setSections(prev => prev.map(s => s.id===id ? {...s,[field]:val} : s));

  const toggleSub = (id, type) =>
    setSections(prev => prev.map(s => s.id===id
      ? {...s, [type==="mat"?"matOpen":type==="lab"?"labOpen":"eqpOpen"]: !s[type==="mat"?"matOpen":type==="lab"?"labOpen":"eqpOpen"]}
      : s));

  const toggleScope = (id) =>
    setSections(prev => prev.map(s => s.id===id ? {...s,scopeOpen:!s.scopeOpen} : s));

  const addMatLine = (secId) =>
    setSections(prev => prev.map(s => s.id===secId
      ? {...s, matLines:[...s.matLines,{id:Date.now(),desc:"",qty:"",unit:"Item",cost:""}]} : s));

  const addLabLine = (secId) =>
    setSections(prev => prev.map(s => s.id===secId
      ? {...s, labLines:[...s.labLines,{id:Date.now(),desc:"",hrs:"",unit:"Hr",rate:""}]} : s));

  const removeMatLine = (secId,lineId) =>
    setSections(prev => prev.map(s => s.id===secId
      ? {...s, matLines:s.matLines.filter(l=>l.id!==lineId)} : s));

  const removeLabLine = (secId,lineId) =>
    setSections(prev => prev.map(s => s.id===secId
      ? {...s, labLines:s.labLines.filter(l=>l.id!==lineId)} : s));

  const updateMatLine = (secId,lineId,field,val) =>
    setSections(prev => prev.map(s => s.id===secId
      ? {...s, matLines:s.matLines.map(l=>l.id===lineId?{...l,[field]:val}:l)} : s));

  const updateLabLine = (secId,lineId,field,val) =>
    setSections(prev => prev.map(s => s.id===secId
      ? {...s, labLines:s.labLines.map(l=>l.id===lineId?{...l,[field]:val}:l)} : s));

  const addEqpLine = (secId) =>
    setSections(prev => prev.map(s => s.id===secId
      ? {...s, eqpLines:[...s.eqpLines,{id:Date.now(),desc:"",hrs:"",unit:"Hr",rate:""}]} : s));

  const removeEqpLine = (secId,lineId) =>
    setSections(prev => prev.map(s => s.id===secId
      ? {...s, eqpLines:s.eqpLines.filter(l=>l.id!==lineId)} : s));

  const updateEqpLine = (secId,lineId,field,val) =>
    setSections(prev => prev.map(s => s.id===secId
      ? {...s, eqpLines:s.eqpLines.map(l=>l.id===lineId?{...l,[field]:val}:l)} : s));

  const calcTotals = () => {
    let totalMat=0, totalLab=0, totalEqp=0;
    sections.forEach(s => {
      totalMat += (s.matLines||[]).reduce((a,l)=>a+(parseFloat(l.qty)||0)*(parseFloat(l.cost)||0),0);
      totalLab += (s.labLines||[]).reduce((a,l)=>a+(parseFloat(l.hrs)||0)*(parseFloat(l.rate)||0),0);
      totalEqp += (s.eqpLines||[]).reduce((a,l)=>a+(parseFloat(l.hrs)||0)*(parseFloat(l.rate)||0),0);
    });
    const sub = totalMat+totalLab+totalEqp;
    const contPct = parseFloat(form.contingency_pct)||0;
    const cont = sub*contPct/100;
    const vatBase = sub+cont;
    const estTaxRate = ((business?.tax_rate !== undefined ? business.tax_rate : getCurrencyOption(business?.currency||"ZAR").taxRate) || 15) / 100;
const vat = vatMode==="15" ? vatBase*estTaxRate : 0;
    return { totalMat, totalLab, totalEqp, sub, contPct, cont, vat, grand:vatBase+vat };
  };

  const secTotals = (sec) => {
    const mat = (sec.matLines||[]).reduce((a,l)=>a+(parseFloat(l.qty)||0)*(parseFloat(l.cost)||0),0);
    const lab = (sec.labLines||[]).reduce((a,l)=>a+(parseFloat(l.hrs)||0)*(parseFloat(l.rate)||0),0);
    const eqp = (sec.eqpLines||[]).reduce((a,l)=>a+(parseFloat(l.hrs)||0)*(parseFloat(l.rate)||0),0);
    return { mat, lab, eqp, total:mat+lab+eqp };
  };

  const openNew = () => {
    const num = `EST-${String(estimates.length+1).padStart(3,"0")}`;
    setSectionCounter(1);
    setSections([{id:1,name:"",matOpen:true,labOpen:false,scopeOpen:false,matLines:[],labLines:[],inclusions:"",exclusions:""}]);
    setVatMode("15");
    setForm({number:num,client_id:"",client_name:"",client_phone:"",site_address:"",date:today(),valid_days:14,prepared_by:"",job_description:"",contingency_pct:0,assumptions:"",payment_terms:"",status:"Draft"});
    setEditId(null); setView("form"); window.scrollTo({top:0,behavior:"smooth"});
  };

  const openEdit = (est) => {
    const loaded = typeof est.sections==="string" ? JSON.parse(est.sections) : (est.sections||[]);
    const maxId = loaded.reduce((m,s)=>Math.max(m,s.id||0),0);
    setSectionCounter(maxId||loaded.length);
    setSections(loaded.map(s=>({...s,matOpen:s.matOpen!==false,labOpen:!!s.labOpen,eqpOpen:!!s.eqpOpen,scopeOpen:!!s.scopeOpen,matLines:s.matLines||[],labLines:s.labLines||[],eqpLines:s.eqpLines||[]})));
    setVatMode(est.vat_mode||"15");
    setForm({
      number:est.number||"", client_id:est.client_id||"", client_name:est.client_name||"",
      client_phone:est.client_phone||"", site_address:est.site_address||"",
      date:est.date||today(), valid_days:est.valid_days||14, prepared_by:est.prepared_by||"",
      job_description:est.job_description||"", contingency_pct:est.contingency_pct||0,
      assumptions:est.assumptions||"", payment_terms:est.payment_terms||"", status:est.status||"Draft",
    });
    setEditId(est.id); setView("form"); window.scrollTo({top:0,behavior:"smooth"});
  };

  const [saveError, setSaveError] = useState(null);
  const [estErrors, setEstErrors] = useState({});

  const buildPayload = (status) => {
    const t = calcTotals();
    return {
      user_id:          userId,
      number:           form.number || "",
      client_id:        form.client_id || "",
      client_name:      form.client_name || "",
      client_phone:     form.client_phone || "",
      site_address:     form.site_address || "",
      date:             form.date || today(),
      valid_days:       parseInt(form.valid_days) || 14,
      valid_until:      addDays(form.date || today(), parseInt(form.valid_days) || 14),
      prepared_by:      form.prepared_by || "",
      job_description:  form.job_description || "",
      assumptions:      form.assumptions || "",
      payment_terms:    form.payment_terms || "",
      status:           status,
      sections:         JSON.stringify(sections),
      vat_mode:         vatMode,
      total_materials:  parseFloat(t.totalMat.toFixed(2)),
      total_labour:     parseFloat(t.totalLab.toFixed(2)),
      total_equipment:  parseFloat(t.totalEqp.toFixed(2)),
      subtotal:         parseFloat(t.sub.toFixed(2)),
      contingency_pct:  parseFloat((parseFloat(form.contingency_pct)||0).toFixed(2)),
      contingency_amt:  parseFloat(t.cont.toFixed(2)),
      vat_amt:          parseFloat(t.vat.toFixed(2)),
      grand_total:      parseFloat(t.grand.toFixed(2)),
    };
  };

  const save = async (status="Draft") => {
    setSaveError(null);
    const errs = {
      client_name: V.required(form.client_name, "Client name"),
      date:        V.date(form.date, "Date"),
    };
    const first = V.run(errs);
    if (first) { setEstErrors(errs); return; }
    setEstErrors({});
    const payload = buildPayload(status);
    let result;
    if (editId) {
      result = await DB.update("estimates", editId, payload);
    } else {
      result = await DB.insert("estimates", payload);
      if (result?.id) setEditId(result.id);
    }
    if (!result) {
      setSaveError("Save failed — check your connection and try again.");
      return;
    }
    await refresh();
    setView("list");
    window.scrollTo({top:0,behavior:"smooth"});
  };

  const convertToQuote = async () => {
    const t = calcTotals();
    const items = [];
    sections.forEach(sec => {
      if (sec.name) items.push({description:`— ${sec.name} —`,qty:1,unit_price:0});
      sec.matLines.filter(l=>l.desc).forEach(l =>
        items.push({description:`[Mat] ${l.desc}`,qty:parseFloat(l.qty)||1,unit_price:parseFloat(l.cost)||0}));
      sec.labLines.filter(l=>l.desc).forEach(l =>
        items.push({description:`[Labour] ${l.desc} (${l.unit})`,qty:parseFloat(l.hrs)||1,unit_price:parseFloat(l.rate)||0}));
      (sec.eqpLines||[]).filter(l=>l.desc).forEach(l =>
        items.push({description:`[Equipment] ${l.desc} (${l.unit})`,qty:parseFloat(l.hrs)||1,unit_price:parseFloat(l.rate)||0}));
    });
    const quotesList = await DB.getList("quotes",userId);
    const qNum = `QTE-${String((quotesList.length+1)).padStart(3,"0")}`;
    await DB.insert("quotes",{
      number:qNum, client_id:form.client_id, client_name:form.client_name,
      date:today(), expiry_date:addDays(today(),14), items,
      subtotal:t.sub, vat:t.vat, total:t.grand,
      discount:0, discount_amt:0, line_total:t.sub,
      include_vat:vatMode==="15",
      notes:[form.assumptions,form.payment_terms].filter(Boolean).join("\n\n"),
      status:"Draft", user_id:userId,
    });
    if (editId) {
      await DB.update("estimates",editId,{status:"Converted",updated_at:new Date().toISOString()});
    } else {
      await DB.insert("estimates", buildPayload("Converted"));
    }
    await refresh();
    setConvertSuccess(true); setView("list");
    setTimeout(()=>setConvertSuccess(false),4000);
    window.scrollTo({top:0,behavior:"smooth"});
  };

  const deleteEstimate = async (id) => {
    await DB.remove("estimates",id);
    await refresh(); setDeleteConfirm(null);
  };

  const t = calcTotals();

  // ── MOBILE RESPONSIVE STYLES ──
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;

  // ─── LIST VIEW ───────────────────────────────────────────────────────────────
  if (view==="list") return (
    <div>
      {convertSuccess && (
        <div className="alert alert-success">
          <Icon name="check" size={14}/> Estimate converted to Quote successfully!
        </div>
      )}
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:20}}>
        <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={13}/> New Estimate</button>
      </div>

      {loading ? (
        <div className="empty-state"><Icon name="eye" size={28}/><p>Loading...</p></div>
      ) : estimates.length===0 ? (
        <div className="empty-state">
          <Icon name="estimate" size={40}/>
          <h3>No estimates yet</h3>
          <p>Create your first estimate to price up a job before quoting.</p>
          <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={13}/> New Estimate</button>
        </div>
      ) : (
        <>
          {/* DESKTOP TABLE */}
          <div className="card desktop-table" style={{padding:0}}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Estimate #</th><th>Client</th><th>Site Address</th>
                    <th>Date</th><th>Valid Until</th><th>Total</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {estimates.map(est => (
                    <tr key={est.id}>
                      <td style={{fontWeight:600}}>{est.number}</td>
                      <td>{est.client_name}</td>
                      <td className="text-muted" style={{maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{est.site_address||"—"}</td>
                      <td className="text-muted">{formatDate(est.date)}</td>
                      <td className="text-muted">{formatDate(est.valid_until)}</td>
                      <td style={{fontWeight:700}}>{formatCurrency(est.grand_total, business?.currency)}</td>
                      <td>
                        <span className={
                          est.status==="Converted"?"badge badge-green":
                          est.status==="Sent"?"badge badge-blue":
                          est.status==="Approved"?"badge badge-green":
                          "badge badge-gray"
                        }>{est.status}</span>
                      </td>
                      <td>
                        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                          <button className="btn btn-sm btn-outline" onClick={()=>openEdit(est)}><Icon name="edit" size={12}/></button>
                          <button className="btn btn-sm btn-outline" onClick={()=>{openEdit(est);setTimeout(()=>setView("preview"),50);}}>
                            <Icon name="eye" size={12}/>
                          </button>
                          <button className="btn btn-sm btn-outline" title="Download PDF" style={{color:"var(--status-green-fg)"}}
                            onClick={()=>{ printAsPDF(buildEstimateDocHTML(est,business), `Estimate ${est.number}`); }}>
                            <Icon name="download" size={12}/>
                          </button>
                          <button className="btn btn-sm btn-outline" title="Email" style={{color:"var(--status-blue-fg)"}}
                            onClick={()=>setEmailDoc(est)}>
                            <Icon name="email" size={12}/>
                          </button>
                          <button className="btn btn-sm btn-outline" title="WhatsApp" style={{color:"#25d366"}}
                            onClick={()=>setEmailDoc(est)}>
                            <Icon name="whatsapp" size={12} style={{color:"#25d366"}}/>
                          </button>
                          {est.status!=="Converted" && (
                            <button className="btn btn-sm btn-success" onClick={()=>openEdit(est)}>→ Quote</button>
                          )}
                          <button className="btn btn-sm btn-outline" style={{color:"var(--status-red-fg)"}}
                            onClick={()=>setDeleteConfirm({id:est.id,label:est.number||"this estimate"})}>
                            <Icon name="trash" size={12}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* MOBILE CARDS */}
          <div className="mobile-cards">
            {estimates.map(est => (
              <div key={est.id} className="m-card" onClick={()=>openEdit(est)}>
                <div className="m-card-content">
                  <div className="m-card-row">
                    <span className="m-card-title">{est.number} — {est.client_name||"No client"}</span>
                    <span className={
                      est.status==="Converted"?"badge badge-green":
                      est.status==="Sent"?"badge badge-blue":
                      est.status==="Approved"?"badge badge-green":"badge badge-gray"
                    }>{est.status}</span>
                  </div>
                  <div className="m-card-row" style={{marginTop:6}}>
                    <span className="text-muted" style={{fontSize:12}}>{est.site_address||"No site address"}</span>
                    <span style={{fontWeight:700,fontSize:15,color:"var(--ink)",flexShrink:0}}>{formatCurrency(est.grand_total, business?.currency)}</span>
                  </div>
                  <div className="m-card-row" style={{marginTop:4,paddingBottom:2}}>
                    <span className="text-muted" style={{fontSize:11}}>{formatDate(est.date)}{est.valid_until ? ` · Valid until ${formatDate(est.valid_until)}` : ""}</span>
                  </div>
                </div>
                <div className="m-actions" onClick={e=>e.stopPropagation()}>
                  <button className="m-btn m-btn-edit" onClick={()=>openEdit(est)}>
                    <Icon name="edit" size={16}/><span>Edit</span>
                  </button>
                  <button className="m-btn m-btn-view" onClick={()=>{openEdit(est);setTimeout(()=>setView("preview"),50);}}>
                    <Icon name="eye" size={16}/><span>View</span>
                  </button>
                  <button className="m-btn m-btn-email" onClick={()=>setEmailDoc(est)}>
                    <Icon name="email" size={16}/><span>Email</span>
                  </button>
                  <button className="m-btn m-btn-whatsapp" onClick={()=>setEmailDoc(est)}>
                    <Icon name="whatsapp" size={16}/><span>WA</span>
                  </button>
                  {est.status!=="Converted" && (
                    <button className="m-btn m-btn-quote" onClick={()=>openEdit(est)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
                      <span>Quote</span>
                    </button>
                  )}
                  <button className="m-btn m-btn-delete" onClick={()=>setDeleteConfirm({id:est.id,label:est.number})}>
                    <Icon name="trash" size={16}/><span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <DeleteConfirmModal item={deleteConfirm?.label} onConfirm={()=>{ if(deleteConfirm) deleteEstimate(deleteConfirm.id); }} onCancel={()=>setDeleteConfirm(null)} />
      {emailDoc && <ShareModal doc={emailDoc} type="Estimate" business={{...business,_userId:userId}} onClose={()=>setEmailDoc(null)}/>}
    </div>
  );

  // ─── FORM VIEW ───────────────────────────────────────────────────────────────
  if (view==="form") return (
    <div>
      {/* ACTION BAR */}
      {saveError && (
        <div className="alert alert-danger" style={{marginBottom:12,fontSize:13}}>
          <Icon name="alert" size={14}/> {saveError}
        </div>
      )}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <button className="btn btn-outline btn-sm" onClick={()=>setView("list")}>← Back</button>
          <h3 style={{fontWeight:700,fontSize:16}}>{editId ? `Edit — ${form.number}` : "New Estimate"}</h3>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button className="btn btn-outline btn-sm" onClick={()=>save("Draft")}>Save Draft</button>
          <button className="btn btn-outline btn-sm" style={{color:"var(--gold)",borderColor:"var(--gold)"}} onClick={()=>setView("preview")}>
            <Icon name="eye" size={13}/> Preview
          </button>
          {editId && (<>
            <button className="btn btn-outline btn-sm" style={{color:"var(--status-green-fg)"}}
              onClick={async()=>{ await save("Draft"); const list=await DB.getList("estimates",userId); const saved=list.find(e=>e.id===editId); if(saved){printAsPDF(buildEstimateDocHTML(saved,business),`Estimate ${saved.number}`);} }}>
              <Icon name="download" size={13}/>
            </button>
            <button className="btn btn-outline btn-sm" style={{color:"var(--status-blue-fg)"}}
              onClick={async()=>{ await save("Draft"); const list=await DB.getList("estimates",userId); const saved=list.find(e=>e.id===editId); if(saved)setEmailDoc(saved); }}>
              <Icon name="email" size={13}/>
            </button>
            <button className="btn btn-outline btn-sm" style={{color:"#25d366"}}
              onClick={async()=>{ await save("Draft"); const list=await DB.getList("estimates",userId); const saved=list.find(e=>e.id===editId); if(saved)setEmailDoc(saved); }}>
              <Icon name="whatsapp" size={13} style={{color:"#25d366"}}/>
            </button>
          </>)}
          {form.status!=="Converted" && (
            <button className="btn btn-success btn-sm" onClick={convertToQuote}>Convert to Quote →</button>
          )}
          <button className="btn btn-primary btn-sm" onClick={()=>save("Sent")}>Save & Mark Send</button>
        </div>
      </div>

      {/* ESTIMATE DETAILS CARD */}
      <div className="card" style={{marginBottom:16}}>
        <div className="card-header"><h4>Estimate Details</h4></div>
        <div className="card-body">
          <div className="form-grid" style={{marginBottom:14}}>
            <div className="form-group">
              <label>Client Name *</label>
              <input className="form-control" style={estErrors.client_name?{borderColor:"#ef4444"}:{}} list="est-clients-dl"
                value={form.client_name}
                onChange={e=>{
                  const c=clients.find(x=>x.name===e.target.value);
                  setForm({...form,client_name:e.target.value,client_id:c?.id||"",client_phone:c?.phone||form.client_phone});
                }}
                placeholder="Select or type client name"/>
              <datalist id="est-clients-dl">
                {clients.map(c=><option key={c.id} value={c.name}/>)}
              </datalist>
              <FormError error={estErrors.client_name} />
            </div>
            <div className="form-group">
              <label>Client Phone</label>
              <input className="form-control" value={form.client_phone}
                onChange={e=>setForm({...form,client_phone:e.target.value})}
                placeholder="e.g. 082 000 0000"/>
            </div>
          </div>
          <div className="form-group" style={{marginBottom:14}}>
            <label>Site / Job Address</label>
            <input className="form-control" value={form.site_address}
              onChange={e=>setForm({...form,site_address:e.target.value})}
              placeholder="e.g. 12 Main Road, Randburg, Gauteng"/>
          </div>
          <div className="form-grid est-4col" style={{gridTemplateColumns:"repeat(4,minmax(0,1fr))",marginBottom:14}}>
            <div className="form-group">
              <label>Estimate No.</label>
              <input className="form-control" value={form.number}
                onChange={e=>setForm({...form,number:e.target.value})}/>
            </div>
            <div className="form-group">
              <label>Date *</label>
              <input className="form-control" style={estErrors.date?{borderColor:"#ef4444"}:{}} type="date" value={form.date}
                onChange={e=>setForm({...form,date:e.target.value})}/>
              <FormError error={estErrors.date} />
            </div>
            <div className="form-group">
              <label>Valid (days)</label>
              <input className="form-control" type="number" min={1} value={form.valid_days}
                onChange={e=>setForm({...form,valid_days:e.target.value})}/>
            </div>
            <div className="form-group">
              <label>Prepared By</label>
              <input className="form-control" value={form.prepared_by}
                onChange={e=>setForm({...form,prepared_by:e.target.value})}
                placeholder="Your name"/>
            </div>
          </div>
          <div className="form-group">
            <label>General Job Description</label>
            <textarea className="form-control" rows={2} value={form.job_description}
              onChange={e=>setForm({...form,job_description:e.target.value})}
              placeholder="Brief overview of all work being estimated across all sections..."/>
          </div>
        </div>
      </div>

      {/* SECTION BLOCKS */}
      {sections.map((sec,idx) => {
        const st = secTotals(sec);
        return (
          <div key={sec.id} className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>

            {/* Section Header */}
            <div style={{background:"var(--ink)",padding:"11px 18px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <span style={{fontSize:9.5,fontWeight:700,letterSpacing:2,textTransform:"uppercase",color:"var(--gold)",background:"rgba(200,151,42,0.12)",border:"1px solid rgba(200,151,42,0.28)",borderRadius:100,padding:"3px 10px",flexShrink:0}}>
                Section {idx+1}
              </span>
              <input
                style={{fontFamily:"inherit",fontSize:13.5,fontWeight:600,color:"white",background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:7,padding:"7px 13px",outline:"none",flex:1,minWidth:0}}
                placeholder="Section name — e.g. Electrical, Plumbing, Painting..."
                value={sec.name}
                onChange={e=>updateSection(sec.id,"name",e.target.value)}/>
              {sections.length>1 && (
                <button className="btn btn-sm" style={{background:"rgba(239,68,68,0.15)",color:"#ef4444",border:"1px solid rgba(239,68,68,0.2)",marginLeft:"auto",flexShrink:0}}
                  onClick={()=>removeSection(sec.id)}>✕ Remove</button>
              )}
            </div>

            <div style={{padding:16,overflowX:"hidden",minWidth:0}}>

              {/* MATERIALS SUB-TABLE */}
              <div style={{border:"1px solid var(--border)",borderRadius:10,marginBottom:10,overflow:"hidden"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 14px",cursor:"pointer",
                  background:sec.matOpen?"rgba(59,130,246,0.08)":"rgba(59,130,246,0.04)",
                  borderBottom:sec.matOpen?"1px solid rgba(59,130,246,0.14)":"none"}}
                  onClick={()=>toggleSub(sec.id,"mat")}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{width:26,height:26,borderRadius:6,background:"rgba(59,130,246,0.12)",border:"1px solid rgba(59,130,246,0.22)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <Icon name="inventory" size={12} style={{color:"#3b82f6"}}/>
                    </div>
                    <span style={{fontSize:12,fontWeight:700}}>Materials</span>
                    <span style={{fontSize:10.5,color:"var(--muted)"}}>Qty × Cost per unit</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:100,background:"rgba(59,130,246,0.08)",color:"#3b82f6"}}>{formatCurrency(st.mat, business?.currency)}</span>
                    <span style={{fontSize:10,color:"var(--muted)"}}>{sec.matOpen?"▲":"▼"}</span>
                  </div>
                </div>
                {sec.matOpen && (
                  <div style={{padding:12}}>
                    {(sec.matLines||[]).length===0 && (
                      <div style={{fontSize:11.5,color:"var(--muted)",textAlign:"center",padding:10,background:"var(--off)",borderRadius:7,marginBottom:8}}>No materials added yet</div>
                    )}
                    {(sec.matLines||[]).length>0 && (
                      <div style={{overflowX:"auto",marginBottom:4}}>
                        <table style={{width:"100%",borderCollapse:"collapse",minWidth:480}}>
                          <thead>
                            <tr>
                              {["Description","Qty","Unit","Cost (R)","Total (R)",""].map((h,i)=>(
                                <th key={i} style={{fontSize:9.5,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:0.5,padding:"4px 6px",textAlign:i===0?"left":"right",whiteSpace:"nowrap"}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sec.matLines.map(l => {
                              const lt=(parseFloat(l.qty)||0)*(parseFloat(l.cost)||0);
                              return (
                                <tr key={l.id}>
                                  <td style={{padding:"3px 4px",minWidth:120}}>
                                    <input className="form-control" style={{fontSize:12,padding:"6px 8px",width:"100%"}}
                                      placeholder="Description" value={l.desc}
                                      onChange={e=>updateMatLine(sec.id,l.id,"desc",e.target.value)}/>
                                  </td>
                                  <td style={{padding:"3px 4px",width:60}}>
                                    <input className="form-control" style={{fontSize:12,padding:"6px 6px",textAlign:"right",width:"100%"}}
                                      type="number" placeholder="0" value={l.qty}
                                      onChange={e=>updateMatLine(sec.id,l.id,"qty",e.target.value)}/>
                                  </td>
                                  <td style={{padding:"3px 4px",width:80}}>
                                    <select className="form-control" style={{fontSize:11,padding:"6px 4px",width:"100%"}}
                                      value={l.unit} onChange={e=>updateMatLine(sec.id,l.id,"unit",e.target.value)}>
                                      {["Item", lengthLabel(business), areaLabel(business), unitLabel("vol3",business), weightLabel(business), fuelVolumeLabel(business), unitLabel("liquidSm",business), "bag","Box","Roll","Set","Lot", distLabel(business),"t","load","pcs","per load","per bin",`${getCurrencyOption(business?.currency||"ZAR").symbol}/${distLabel(business)}`].map(u=><option key={u}>{u}</option>)}
                                    </select>
                                  </td>
                                  <td style={{padding:"3px 4px",width:90}}>
                                    <input className="form-control" style={{fontSize:12,padding:"6px 8px",textAlign:"right",width:"100%"}}
                                      type="number" placeholder="0.00" value={l.cost}
                                      onChange={e=>updateMatLine(sec.id,l.id,"cost",e.target.value)}/>
                                  </td>
                                  <td style={{padding:"3px 4px",width:90}}>
                                    <input className="form-control" style={{fontSize:12,fontWeight:700,padding:"6px 8px",textAlign:"right",background:"var(--off)",pointerEvents:"none",width:"100%"}}
                                      value={lt>0?formatCurrency(lt, business?.currency):""} placeholder="—" readOnly/>
                                  </td>
                                  <td style={{padding:"3px 4px",width:32,textAlign:"center",verticalAlign:"middle"}}>
                                    <button className="btn btn-icon btn-outline btn-sm" style={{width:26,height:26,padding:0,display:"flex",alignItems:"center",justifyContent:"center"}}
                                      onClick={()=>removeMatLine(sec.id,l.id)}>
                                      <Icon name="x" size={11}/>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <button className="btn btn-outline btn-sm"
                      style={{width:"100%",borderStyle:"dashed",color:"#3b82f6",borderColor:"rgba(59,130,246,0.3)",marginTop:4}}
                      onClick={()=>addMatLine(sec.id)}>+ Add Material Line</button>
                    {sec.matLines.length > 0 && (
                      <div style={{fontSize:10,color:"var(--muted)",textAlign:"center",marginTop:6,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                        <span>←</span>
                        <span className="mobile-scroll-hint">Scroll the table left/right to see all columns</span>
                        <span>→</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* LABOUR SUB-TABLE */}
              <div style={{border:"1px solid var(--border)",borderRadius:10,marginBottom:10,overflow:"hidden"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 14px",cursor:"pointer",
                  background:sec.labOpen?"rgba(200,151,42,0.08)":"rgba(200,151,42,0.04)",
                  borderBottom:sec.labOpen?"1px solid rgba(200,151,42,0.18)":"none"}}
                  onClick={()=>toggleSub(sec.id,"lab")}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{width:26,height:26,borderRadius:6,background:"rgba(200,151,42,0.12)",border:"1px solid rgba(200,151,42,0.28)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <Icon name="workers" size={12} style={{color:"var(--gold)"}}/>
                    </div>
                    <span style={{fontSize:12,fontWeight:700}}>Labour</span>
                    <span style={{fontSize:10.5,color:"var(--muted)"}}>Hours/Days × Rate</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:100,background:"rgba(200,151,42,0.1)",color:"var(--gold)"}}>{formatCurrency(st.lab, business?.currency)}</span>
                    <span style={{fontSize:10,color:"var(--muted)"}}>{sec.labOpen?"▲":"▼"}</span>
                  </div>
                </div>
                {sec.labOpen && (
                  <div style={{padding:12}}>
                    {(sec.labLines||[]).length===0 && (
                      <div style={{fontSize:11.5,color:"var(--muted)",textAlign:"center",padding:10,background:"var(--off)",borderRadius:7,marginBottom:8}}>No labour added yet</div>
                    )}
                    {(sec.labLines||[]).length>0 && (
                      <div style={{overflowX:"auto",marginBottom:4}}>
                        <table style={{width:"100%",borderCollapse:"collapse",minWidth:480}}>
                          <thead>
                            <tr>
                              {["Description","Hrs/Days","Unit","Rate (R)","Total (R)",""].map((h,i)=>(
                                <th key={i} style={{fontSize:9.5,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:0.5,padding:"4px 6px",textAlign:i===0?"left":"right",whiteSpace:"nowrap"}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sec.labLines.map(l => {
                              const lt=(parseFloat(l.hrs)||0)*(parseFloat(l.rate)||0);
                              return (
                                <tr key={l.id}>
                                  <td style={{padding:"3px 4px",minWidth:120}}>
                                    <input className="form-control" style={{fontSize:12,padding:"6px 8px",width:"100%"}}
                                      placeholder="Description" value={l.desc}
                                      onChange={e=>updateLabLine(sec.id,l.id,"desc",e.target.value)}/>
                                  </td>
                                  <td style={{padding:"3px 4px",width:70}}>
                                    <input className="form-control" style={{fontSize:12,padding:"6px 6px",textAlign:"right",width:"100%"}}
                                      type="number" placeholder="0" value={l.hrs}
                                      onChange={e=>updateLabLine(sec.id,l.id,"hrs",e.target.value)}/>
                                  </td>
                                  <td style={{padding:"3px 4px",width:90}}>
                                    <select className="form-control" style={{fontSize:11,padding:"6px 4px",width:"100%"}}
                                      value={l.unit} onChange={e=>updateLabLine(sec.id,l.id,"unit",e.target.value)}>
                                      {["Hr","Day","Half Day","Week"].map(u=><option key={u}>{u}</option>)}
                                    </select>
                                  </td>
                                  <td style={{padding:"3px 4px",width:90}}>
                                    <input className="form-control" style={{fontSize:12,padding:"6px 8px",textAlign:"right",width:"100%"}}
                                      type="number" placeholder="0.00" value={l.rate}
                                      onChange={e=>updateLabLine(sec.id,l.id,"rate",e.target.value)}/>
                                  </td>
                                  <td style={{padding:"3px 4px",width:90}}>
                                    <input className="form-control" style={{fontSize:12,fontWeight:700,padding:"6px 8px",textAlign:"right",background:"var(--off)",pointerEvents:"none",width:"100%"}}
                                      value={lt>0?formatCurrency(lt, business?.currency):""} placeholder="—" readOnly/>
                                  </td>
                                  <td style={{padding:"3px 4px",width:32,textAlign:"center",verticalAlign:"middle"}}>
                                    <button className="btn btn-icon btn-outline btn-sm" style={{width:26,height:26,padding:0,display:"flex",alignItems:"center",justifyContent:"center"}}
                                      onClick={()=>removeLabLine(sec.id,l.id)}>
                                      <Icon name="x" size={11}/>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <button className="btn btn-outline btn-sm"
                      style={{width:"100%",borderStyle:"dashed",color:"var(--gold)",borderColor:"rgba(200,151,42,0.3)",marginTop:4}}
                      onClick={()=>addLabLine(sec.id)}>+ Add Labour Line</button>
                    {sec.labLines.length > 0 && (
                      <div style={{fontSize:10,color:"var(--muted)",textAlign:"center",marginTop:6,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                        <span>←</span>
                        <span className="mobile-scroll-hint">Scroll the table left/right to see all columns</span>
                        <span>→</span>
                      </div>
                    )}
                  </div>
                )}
              </div>


              {/* EQUIPMENT SUB-TABLE */}
              <div style={{border:"1px solid var(--border)",borderRadius:10,marginBottom:10,overflow:"hidden"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 14px",cursor:"pointer",
                  background:sec.eqpOpen?"rgba(16,185,129,0.08)":"rgba(16,185,129,0.04)",
                  borderBottom:sec.eqpOpen?"1px solid rgba(16,185,129,0.18)":"none"}}
                  onClick={()=>toggleSub(sec.id,"eqp")}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{width:26,height:26,borderRadius:6,background:"rgba(16,185,129,0.12)",border:"1px solid rgba(16,185,129,0.28)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.2" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>
                    </div>
                    <span style={{fontSize:12,fontWeight:700}}>Equipment</span>
                    <span style={{fontSize:10.5,color:"var(--muted)"}}>Hours/Days × Rate</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:100,background:"rgba(16,185,129,0.1)",color:"#10b981"}}>{formatCurrency(st.eqp, business?.currency)}</span>
                    <span style={{fontSize:10,color:"var(--muted)"}}>{sec.eqpOpen?"▲":"▼"}</span>
                  </div>
                </div>
                {sec.eqpOpen && (
                  <div style={{padding:12}}>
                    {(sec.eqpLines||[]).length===0 && (
                      <div style={{fontSize:11.5,color:"var(--muted)",textAlign:"center",padding:10,background:"var(--off)",borderRadius:7,marginBottom:8}}>No equipment added yet</div>
                    )}
                    {(sec.eqpLines||[]).length>0 && (
                      <div style={{overflowX:"auto",marginBottom:4}}>
                        <table style={{width:"100%",borderCollapse:"collapse",minWidth:480}}>
                          <thead>
                            <tr>
                              {["Description","Hrs/Days","Unit","Rate (R)","Total (R)",""].map((h,i)=>(
                                <th key={i} style={{fontSize:9.5,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:0.5,padding:"4px 6px",textAlign:i===0?"left":"right",whiteSpace:"nowrap"}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(sec.eqpLines||[]).map(l => {
                              const lt=(parseFloat(l.hrs)||0)*(parseFloat(l.rate)||0);
                              return (
                                <tr key={l.id}>
                                  <td style={{padding:"3px 4px",minWidth:120}}>
                                    <input className="form-control" style={{fontSize:12,padding:"6px 8px",width:"100%"}}
                                      placeholder="e.g. Excavator, Scaffolding…"
                                      value={l.desc} onChange={e=>updateEqpLine(sec.id,l.id,"desc",e.target.value)}/>
                                  </td>
                                  <td style={{padding:"3px 4px",width:70}}>
                                    <input className="form-control" style={{fontSize:12,padding:"6px 8px",textAlign:"right"}}
                                      type="number" min="0" placeholder="0"
                                      value={l.hrs} onChange={e=>updateEqpLine(sec.id,l.id,"hrs",e.target.value)}/>
                                  </td>
                                  <td style={{padding:"3px 4px",width:80}}>
                                    <select className="form-control" style={{fontSize:12,padding:"6px 8px"}}
                                      value={l.unit} onChange={e=>updateEqpLine(sec.id,l.id,"unit",e.target.value)}>
                                      <option>Hr</option><option>Day</option><option>Week</option><option>Month</option><option>Item</option>
                                    </select>
                                  </td>
                                  <td style={{padding:"3px 4px",width:90}}>
                                    <input className="form-control" style={{fontSize:12,padding:"6px 8px",textAlign:"right"}}
                                      type="number" min="0" placeholder="0.00"
                                      value={l.rate} onChange={e=>updateEqpLine(sec.id,l.id,"rate",e.target.value)}/>
                                  </td>
                                  <td style={{padding:"3px 4px",width:90,textAlign:"right",fontSize:12,fontWeight:600}}>{formatCurrency(lt, business?.currency)}</td>
                                  <td style={{padding:"3px 4px",width:28}}>
                                    <button style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:14,padding:"2px 4px"}}
                                      onClick={()=>removeEqpLine(sec.id,l.id)}>✕</button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <button className="btn btn-outline btn-sm"
                      style={{width:"100%",borderStyle:"dashed",color:"#10b981",borderColor:"rgba(16,185,129,0.4)",marginTop:4}}
                      onClick={()=>addEqpLine(sec.id)}>+ Add Equipment Line</button>
                    {(sec.eqpLines||[]).length > 0 && (
                      <div style={{fontSize:10,color:"var(--muted)",textAlign:"center",marginTop:6,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                        <span>←</span>
                        <span className="mobile-scroll-hint">Scroll the table left/right to see all columns</span>
                        <span>→</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* INCLUSIONS / EXCLUSIONS */}
              <button className="btn btn-outline btn-sm" style={{fontSize:11,marginBottom:sec.scopeOpen?8:0}}
                onClick={()=>toggleScope(sec.id)}>
                {sec.scopeOpen?"▲ Hide":"▼ Add"} Inclusions / Exclusions
              </button>
              {sec.scopeOpen && (
                <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:8}}>
                  <div className="form-group">
                    <label>✓ Inclusions — what IS covered</label>
                    <textarea className="form-control" rows={2} value={sec.inclusions}
                      onChange={e=>updateSection(sec.id,"inclusions",e.target.value)}
                      placeholder="e.g. Supply and install, all wiring, COC on completion"/>
                  </div>
                  <div className="form-group">
                    <label>✕ Exclusions — what is NOT covered</label>
                    <textarea className="form-control" rows={2} value={sec.exclusions}
                      onChange={e=>updateSection(sec.id,"exclusions",e.target.value)}
                      placeholder="e.g. Plastering after installation, painting"/>
                  </div>
                </div>
              )}

              {/* SECTION SUBTOTAL */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"var(--ink)",borderRadius:8,padding:"10px 14px",marginTop:12}}>
                <div style={{display:"flex",gap:20}}>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"rgba(255,255,255,0.3)",marginBottom:2}}>Materials</div>
                    <div style={{fontSize:13,fontWeight:700,color:"white"}}>{formatCurrency(st.mat, business?.currency)}</div>
                  </div>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"rgba(255,255,255,0.3)",marginBottom:2}}>Labour</div>
                    <div style={{fontSize:13,fontWeight:700,color:"white"}}>{formatCurrency(st.lab, business?.currency)}</div>
                  </div>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"rgba(16,185,129,0.6)",marginBottom:2}}>Equipment</div>
                    <div style={{fontSize:13,fontWeight:700,color:"#6ee7b7"}}>{formatCurrency(st.eqp, business?.currency)}</div>
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:9,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"rgba(255,255,255,0.3)",marginBottom:2}}>Section Total</div>
                  <div style={{fontSize:18,fontWeight:700,color:"var(--gold)",fontFamily:"var(--font-display)"}}>{formatCurrency(st.total, business?.currency)}</div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* ADD SECTION */}
      <button className="btn btn-outline"
        style={{width:"100%",marginBottom:16,padding:14,borderStyle:"dashed",background:"var(--ink)",color:"white",borderColor:"rgba(200,151,42,0.35)",fontSize:13,fontWeight:700}}
        onClick={()=>addSection("")}>
        + Add Another Scope / Section
      </button>

      {/* TOTALS CARD */}
      <div className="card" style={{marginBottom:16}}>
        <div className="card-header"><h4>Totals & Pricing</h4></div>
        <div className="card-body">
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <tbody>
              {[
                {label:"Total Materials",  val:formatCurrency(t.totalMat, business?.currency)},
                {label:"Total Labour",    val:formatCurrency(t.totalLab, business?.currency)},
                {label:"Total Equipment", val:formatCurrency(t.totalEqp, business?.currency)},
                {label:"Subtotal",        val:formatCurrency(t.sub, business?.currency)},
              ].map(row=>(
                <tr key={row.label} style={{borderBottom:"1px solid var(--border)"}}>
                  <td style={{padding:"9px 0",color:"var(--muted)",fontSize:13}}>{row.label}</td>
                  <td style={{padding:"9px 0",textAlign:"right",fontWeight:700,fontSize:13}}>{row.val}</td>
                </tr>
              ))}
              <tr style={{borderBottom:"1px solid var(--border)"}}>
                <td style={{padding:"9px 0",color:"var(--muted)",fontSize:13}}>
                  Contingency &nbsp;
                  <input type="number" min={0} max={50} value={form.contingency_pct}
                    onChange={e=>setForm({...form,contingency_pct:e.target.value})}
                    style={{width:52,fontSize:12,padding:"3px 8px",borderRadius:6,border:"1.5px solid var(--border)",background:"var(--off)",outline:"none",textAlign:"center",fontFamily:"inherit"}}/>
                  &nbsp;%
                </td>
                <td style={{padding:"9px 0",textAlign:"right",fontWeight:700,fontSize:13}}>{formatCurrency(t.cont, business?.currency)}</td>
              </tr>
              <tr style={{borderBottom:"1px solid var(--border)"}}>
                <td style={{padding:"9px 0",color:"var(--muted)",fontSize:13}}>
                  {business?.tax_label||"VAT"} &nbsp;
                  {[["15",`${business?.tax_rate||15}%`],["excl","Excl."],["na","N/A"]].map(([k,lbl])=>(
                    <button key={k} onClick={()=>setVatMode(k)} style={{
                      marginRight:4,fontSize:11,fontWeight:700,padding:"3px 10px",
                      borderRadius:6,border:"1.5px solid var(--border)",cursor:"pointer",fontFamily:"inherit",
                      background:vatMode===k?"var(--ink)":"var(--off)",
                      color:vatMode===k?"white":"var(--muted)",
                    }}>{lbl}</button>
                  ))}
                </td>
                <td style={{padding:"9px 0",textAlign:"right",fontWeight:700,fontSize:13}}>
                  {vatMode==="na"?"N/A":formatCurrency(t.vat, business?.currency)}
                </td>
              </tr>
              <tr>
                <td style={{padding:"14px 0 4px",fontWeight:700,fontSize:15}}>Grand Total</td>
                <td style={{padding:"14px 0 4px",textAlign:"right",fontWeight:700,fontSize:22,fontFamily:"var(--font-display)"}}>{formatCurrency(t.grand, business?.currency)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* NOTES CARD */}
      <div className="card" style={{marginBottom:16}}>
        <div className="card-header"><h4>Notes & Terms</h4></div>
        <div className="card-body">
          <div className="form-group" style={{marginBottom:14}}>
            <label>Basis of Estimate / Assumptions</label>
            <textarea className="form-control" rows={2} value={form.assumptions}
              onChange={e=>setForm({...form,assumptions:e.target.value})}
              placeholder="e.g. Based on visual inspection only. Quantities subject to change once work commences."/>
          </div>
          <div className="form-group">
            <label>Payment Terms</label>
            <textarea className="form-control" rows={2} value={form.payment_terms}
              onChange={e=>setForm({...form,payment_terms:e.target.value})}
              placeholder="e.g. 50% deposit required before work commences. Balance due on completion."/>
          </div>
        </div>
      </div>

      {/* CONVERT BAR */}
      <div style={{background:"var(--ink)",borderRadius:12,padding:"16px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,flexWrap:"wrap",marginBottom:16}}>
        <div>
          <div style={{fontSize:13.5,fontWeight:700,color:"white",marginBottom:3}}>Ready to send to client?</div>
          <div style={{fontSize:11.5,color:"rgba(255,255,255,0.4)"}}>Converts to a formal Quote — client can accept to trigger an Invoice.</div>
        </div>
        <button className="btn btn-gold" onClick={convertToQuote} style={{flexShrink:0}}>Convert to Quote →</button>
      </div>
    </div>
  );

  // ─── PREVIEW VIEW ─────────────────────────────────────────────────────────────
  if (view==="preview") {
    const validUntilDate = addDays(form.date, parseInt(form.valid_days)||14);
    return (
      <div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:10}}>
          <button className="btn btn-outline btn-sm" onClick={()=>setView("form")}>← Back to Edit</button>
          <div style={{display:"flex",gap:8}}>
            <button className="btn btn-outline btn-sm" onClick={()=>{ const est={...form,sections}; printAsPDF(buildEstimateDocHTML(est,business),`Estimate ${form.number||"doc"}`); }}>Print / Save PDF</button>
            <button className="btn btn-success btn-sm" onClick={convertToQuote}>Convert to Quote →</button>
          </div>
        </div>

        <div className="card" style={{maxWidth:680,margin:"0 auto",padding:0,overflow:"hidden"}}>
          {/* PDF Header */}
          <div style={{background:"var(--ink)",padding:"22px 24px 18px",position:"relative"}}>
            <div style={{position:"absolute",bottom:0,left:0,right:0,height:2,background:"linear-gradient(90deg,transparent,var(--gold),transparent)"}}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:28,height:28,borderRadius:7,background:"rgba(200,151,42,0.15)",border:"1px solid rgba(200,151,42,0.28)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <Icon name="estimate" size={13} style={{color:"var(--gold)"}}/>
                </div>
                <span style={{fontFamily:"var(--font-display)",fontSize:16,fontWeight:700,color:"white"}}>FieldJob</span>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontFamily:"var(--font-display)",fontSize:22,fontWeight:700,color:"var(--gold)"}}>ESTIMATE</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginTop:2}}>{form.number}</div>
                <div style={{fontSize:7.5,color:"rgba(200,151,42,0.45)",letterSpacing:1,textTransform:"uppercase",marginTop:2}}>Not a Tax Invoice</div>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
              {[
                {label:"Prepared For", val:[form.client_name,form.client_phone].filter(Boolean).join(" · ")||"—"},
                {label:"Site Address", val:form.site_address||"—"},
                {label:"Date",         val:formatDate(form.date)},
                {label:"Valid Until",  val:formatDate(validUntilDate)},
                {label:"Prepared By",  val:form.prepared_by||"—"},
                {label:"Estimate No.", val:form.number},
              ].map(m=>(
                <div key={m.label}>
                  <div style={{fontSize:8,fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",color:"rgba(255,255,255,0.25)",marginBottom:2}}>{m.label}</div>
                  <div style={{fontSize:10.5,color:"rgba(255,255,255,0.8)",fontWeight:500}}>{m.val}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{padding:"16px 20px"}}>
            {form.job_description && (
              <div style={{fontSize:10.5,color:"var(--muted)",background:"var(--off)",borderRadius:6,padding:"8px 10px",marginBottom:14,lineHeight:1.6}}>
                {form.job_description}
              </div>
            )}

            {sections.map((sec,idx)=>{
              const st=secTotals(sec);
              return (
                <div key={sec.id} style={{marginBottom:14}}>
                  <div style={{background:"var(--ink)",borderRadius:"6px 6px 0 0",padding:"7px 11px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span style={{fontSize:8.5,fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",color:"var(--gold)"}}>Section {idx+1}</span>
                    <span style={{fontSize:11,fontWeight:700,color:"white"}}>{sec.name||"Untitled Section"}</span>
                  </div>
                  {sec.matLines.filter(l=>l.desc||l.cost).length>0 && (<>
                    <div style={{background:"rgba(59,130,246,0.06)",padding:"5px 10px",fontSize:9,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"#3b82f6",borderBottom:"1px solid rgba(59,130,246,0.12)"}}>Materials</div>
                    <table style={{width:"100%",borderCollapse:"collapse"}}>
                      <thead><tr>{["Description","Qty","Unit","Cost","Total"].map(h=><th key={h} style={{fontSize:8,fontWeight:700,textTransform:"uppercase",color:"var(--muted)",padding:"5px 8px",borderBottom:"1px solid var(--border)",textAlign:h==="Description"?"left":"right"}}>{h}</th>)}</tr></thead>
                      <tbody>{sec.matLines.filter(l=>l.desc||l.cost).map(l=>{
                        const lt=(parseFloat(l.qty)||0)*(parseFloat(l.cost)||0);
                        return <tr key={l.id} style={{borderBottom:"1px solid var(--off2)"}}>
                          <td style={{fontSize:10,padding:"5px 8px",fontWeight:500}}>{l.desc||"—"}</td>
                          <td style={{fontSize:10,padding:"5px 8px",textAlign:"right"}}>{l.qty||"—"}</td>
                          <td style={{fontSize:10,padding:"5px 8px",textAlign:"right"}}>{l.unit}</td>
                          <td style={{fontSize:10,padding:"5px 8px",textAlign:"right"}}>{l.cost?formatCurrency(parseFloat(l.cost, business?.currency)):"—"}</td>
                          <td style={{fontSize:10,padding:"5px 8px",textAlign:"right",fontWeight:700}}>{lt>0?formatCurrency(lt, business?.currency):"—"}</td>
                        </tr>;
                      })}</tbody>
                    </table>
                  </>)}
                  {sec.labLines.filter(l=>l.desc||l.rate).length>0 && (<>
                    <div style={{background:"rgba(200,151,42,0.06)",padding:"5px 10px",fontSize:9,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"var(--gold)",borderBottom:"1px solid rgba(200,151,42,0.15)",borderTop:"1px solid var(--border)"}}>Labour</div>
                    <table style={{width:"100%",borderCollapse:"collapse"}}>
                      <thead><tr>{["Description","Hrs/Days","Unit","Rate","Total"].map(h=><th key={h} style={{fontSize:8,fontWeight:700,textTransform:"uppercase",color:"var(--muted)",padding:"5px 8px",borderBottom:"1px solid var(--border)",textAlign:h==="Description"?"left":"right"}}>{h}</th>)}</tr></thead>
                      <tbody>{sec.labLines.filter(l=>l.desc||l.rate).map(l=>{
                        const lt=(parseFloat(l.hrs)||0)*(parseFloat(l.rate)||0);
                        return <tr key={l.id} style={{borderBottom:"1px solid var(--off2)"}}>
                          <td style={{fontSize:10,padding:"5px 8px",fontWeight:500}}>{l.desc||"—"}</td>
                          <td style={{fontSize:10,padding:"5px 8px",textAlign:"right"}}>{l.hrs||"—"}</td>
                          <td style={{fontSize:10,padding:"5px 8px",textAlign:"right"}}>{l.unit}</td>
                          <td style={{fontSize:10,padding:"5px 8px",textAlign:"right"}}>{l.rate?formatCurrency(parseFloat(l.rate, business?.currency)):"—"}</td>
                          <td style={{fontSize:10,padding:"5px 8px",textAlign:"right",fontWeight:700}}>{lt>0?formatCurrency(lt, business?.currency):"—"}</td>
                        </tr>;
                      })}</tbody>
                    </table>
                  </>)}
                  {(sec.inclusions||sec.exclusions) && (
                    <div style={{padding:"6px 10px",background:"var(--off)",fontSize:9,lineHeight:1.6}}>
                      {sec.inclusions && <div><strong>✓ Inclusions:</strong> {sec.inclusions}</div>}
                      {sec.exclusions && <div><strong style={{color:"var(--status-red-fg)"}}>✕ Exclusions:</strong> {sec.exclusions}</div>}
                    </div>
                  )}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 10px",background:"var(--off2)",borderRadius:"0 0 6px 6px"}}>
                    <div style={{display:"flex",gap:14}}>
                      <span style={{fontSize:9,color:"var(--muted)"}}>Materials <strong style={{color:"var(--text)"}}>{formatCurrency(st.mat, business?.currency)}</strong></span>
                      <span style={{fontSize:9,color:"var(--muted)"}}>Labour <strong style={{color:"var(--text)"}}>{formatCurrency(st.lab, business?.currency)}</strong></span>
                    </div>
                    <span style={{fontSize:13,fontWeight:700,color:"var(--ink)"}}>{formatCurrency(st.total, business?.currency)}</span>
                  </div>
                </div>
              );
            })}

            {/* Totals */}
            <div style={{borderTop:"1px solid var(--border)",marginTop:12,paddingTop:10}}>
              {[
                {label:"Total Materials",  val:formatCurrency(t.totalMat, business?.currency)},
                {label:"Total Labour",     val:formatCurrency(t.totalLab, business?.currency)},
                {label:"Subtotal (excl. VAT)", val:formatCurrency(t.sub, business?.currency)},
                ...(t.contPct>0?[{label:`Contingency (${t.contPct}%)`,val:formatCurrency(t.cont, business?.currency)}]:[]),
                {label:`VAT (${vatMode==="15"?"15%":vatMode==="excl"?"Excluded":"N/A"})`, val:vatMode==="na"?"N/A":formatCurrency(t.vat, business?.currency)},
              ].map(row=>(
                <div key={row.label} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:10.5,borderBottom:"1px solid var(--off2)"}}>
                  <span style={{color:"var(--muted)"}}>{row.label}</span>
                  <span style={{fontWeight:700}}>{row.val}</span>
                </div>
              ))}
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"var(--ink)",borderRadius:8,padding:"12px 14px",marginTop:10}}>
              <span style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.5)"}}>GRAND TOTAL ESTIMATE</span>
              <span style={{fontSize:22,fontWeight:700,color:"var(--gold)",fontFamily:"var(--font-display)"}}>{formatCurrency(t.grand, business?.currency)}</span>
            </div>

            {(form.assumptions||form.payment_terms) && (
              <div style={{marginTop:14,paddingTop:10,borderTop:"1px solid var(--border)"}}>
                <div style={{fontSize:8.5,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"var(--muted)",marginBottom:8}}>Notes & Terms</div>
                {form.assumptions && <div style={{marginBottom:7}}><div style={{fontSize:9,fontWeight:700,color:"var(--ink)",marginBottom:2}}>Basis of Estimate</div><div style={{fontSize:9.5,color:"var(--muted)",lineHeight:1.6}}>{form.assumptions}</div></div>}
                {form.payment_terms && <div><div style={{fontSize:9,fontWeight:700,color:"var(--ink)",marginBottom:2}}>Payment Terms</div><div style={{fontSize:9.5,color:"var(--muted)",lineHeight:1.6}}>{form.payment_terms}</div></div>}
              </div>
            )}
          </div>

          <div style={{background:"var(--off)",borderTop:"1px solid var(--border)",padding:"10px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:8.5,color:"var(--muted)"}}>FieldJob · Diswayne Technologies · South Africa</span>
            <span style={{fontSize:8.5,color:"var(--muted)"}}>Valid until <strong style={{color:"var(--ink)"}}>{formatDate(validUntilDate)}</strong></span>
          </div>
        </div>
        <div style={{height:40}}/>
      </div>
    );
  }

  return null;
}


// ─── QUOTES MODULE ────────────────────────────────────────────────────────────
function QuotesModule({ userId }) {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [business, setBusiness] = useState({ _userId: userId });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [emailDoc, setEmailDoc] = useState(null);
  const [convertSuccess, setConvertSuccess] = useState(false);
  const [form, setForm] = useState({ number:"", client_id:"", client_name:"", date: today(), expiry_date: addDays(today(), 14), items:[{ description:"", qty:1, unit_price:0 }], discount:0, include_vat:true, notes:"", status:"Draft" });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const refresh = async () => {
    const [q, c, b] = await Promise.all([
      DB.getList("quotes", userId), DB.getList("clients", userId), DB.get("business", userId),
    ]);
    setQuotes(q || []); setClients(c || []);
    setBusiness({ ...(b || {}), _userId: userId }); setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const calcTotals = (items, includeVat, discount) => {
    const lineTotal = items.reduce((s, i) => s + (parseFloat(i.qty)||0)*(parseFloat(i.unit_price)||0), 0);
    const discountAmt = lineTotal * ((parseFloat(discount)||0) / 100);
    const sub = lineTotal - discountAmt;
    const txRate = ((business?.tax_rate !== undefined ? business.tax_rate : getCurrencyOption(business?.currency||"ZAR").taxRate) || 0) / 100;
    const vat = includeVat ? sub * txRate : 0;
    return { lineTotal, discountAmt, subtotal: sub, vat, total: sub + vat };
  };

  const openNew = () => {
    const num = `QTE-${String(quotes.length+1).padStart(3,"0")}`;
    setForm({ number:num, client_id:"", client_name:"", date: today(), expiry_date: addDays(today(), 14), items:[{ description:"", qty:1, unit_price:0 }], discount:0, include_vat:true, notes:"", status:"Draft" });
    setErrors({});
    setModal("new");
  };

  const updateItem = (i, field, val) => { const items = [...form.items]; items[i][field] = val; setForm({...form, items}); };
  const addItem = () => setForm({...form, items:[...form.items, { description:"", qty:1, unit_price:0 }]});
  const removeItem = (i) => setForm({...form, items: form.items.filter((_,idx)=>idx!==i)});

  const [errors, setErrors] = useState({});

  const save = async () => {
    const errs = {
      client_name: V.required(form.client_name, "Client"),
      date:        V.date(form.date, "Quote date"),
      expiry_date: V.dueDate(form.expiry_date, form.date),
      items:       V.lineItems(form.items),
    };
    const first = V.run(errs);
    if (first) { setErrors(errs); return; }
    setErrors({});
    const { subtotal, vat, total, discountAmt, lineTotal } = calcTotals(form.items, form.include_vat, form.discount);
    const data = { ...form, user_id: userId, subtotal, vat, total, discount: parseFloat(form.discount)||0, discount_amt: discountAmt, line_total: lineTotal };
    try {
      if (modal === "new") await DB.insert("quotes", data); else await DB.update("quotes", form.id, data);
      await refresh(); setModal(null);
    } catch (e) { console.error("Save quote failed:", e); }
  };

  const convertToInvoice = async (q) => {
    const invoices = await DB.getList("invoices", userId);
    const num = `INV-${String(invoices.length+1).padStart(3,"0")}`;
    await DB.insert("invoices", { number:num, client_id:q.client_id, client_name:q.client_name, date:today(), due_date:addDays(today(),30), status:"Draft", items:q.items, subtotal:q.subtotal, vat:q.vat, total:q.total, paid:0, notes:q.notes, user_id:userId });
    await DB.update("quotes", q.id, { status:"Converted" });
    await refresh();
    setConvertSuccess(true); setTimeout(() => setConvertSuccess(false), 3000);
  };

  const whatsapp = (q) => {
    const msg = `Dear ${q.client_name},\n\nPlease find your quote *${q.number}* for the amount of *${formatCurrency(q.total, business?.currency)}*.\n\nValid until: ${formatDate(q.expiry_date)}\n\nPlease reply to accept this quote.\n\n${business.name || ""}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const totals = calcTotals(form.items, form.include_vat, form.discount);

  return (
    <div>
      {convertSuccess && <div className="alert alert-success"><Icon name="check" size={14} /> Quote converted to invoice successfully!</div>}
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:20 }}>
        <button className="btn btn-primary" onClick={openNew}><Icon name="plus" />New Quote</button>
      </div>

      {quotes.length === 0 ? <div className="empty-state"><Icon name="quote" size={40} /><h3>No quotes yet</h3></div> : (<>
        {/* DESKTOP TABLE */}
        <div className="card desktop-table" style={{ padding:0 }}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Quote #</th><th>Client</th><th>Date</th><th>Expiry</th><th>Total</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {quotes.map(q => (
                  <tr key={q.id}>
                    <td style={{ fontWeight:600 }}>{q.number}</td>
                    <td>{q.client_name}</td>
                    <td className="text-muted">{formatDate(q.date)}</td>
                    <td className="text-muted">{formatDate(q.expiry_date)}</td>
                    <td style={{ fontWeight:700 }}>{formatCurrency(q.total, business?.currency)}</td>
                    <td><span className={q.status==="Converted" ? "badge badge-green" : q.status==="Sent" ? "badge badge-blue" : "badge badge-gray"}>{q.status}</span></td>
                    <td>
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                        <button className="btn btn-sm btn-outline" onClick={() => setViewDoc(q)}><Icon name="eye" size={12} /></button>
                        <button className="btn btn-sm btn-outline" title="Download" onClick={() => { printAsPDF(buildDocHTML(q,"Quote",business), `Quote ${q.number}`); }} style={{ color:"var(--status-green-fg)" }}><Icon name="download" size={12}/></button>
                        <button className="btn btn-sm btn-outline" title="Send Email" onClick={() => setEmailDoc(q)} style={{ color:"var(--status-blue-fg)" }}><Icon name="email" size={12} /></button>
                        <button className="btn btn-sm btn-outline" title="WhatsApp" onClick={() => setEmailDoc(q)} style={{ color:"#25d366" }}><Icon name="whatsapp" size={12} style={{ color:"#25d366" }} /></button>
                        {q.status !== "Converted" && <button className="btn btn-sm btn-success" onClick={() => convertToInvoice(q)}>→ Inv</button>}
                        <button className="btn btn-sm btn-outline" onClick={() => { setForm({...q, discount: q.discount||0}); setModal("edit"); }}><Icon name="edit" size={12} /></button>
                        <button className="btn btn-sm btn-outline" style={{ color:"var(--status-red-fg)" }} onClick={() => setDeleteConfirm({ id: q.id, label: q.number || 'this quote' })}><Icon name="trash" size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* MOBILE CARDS */}
        <div className="mobile-list">
          <div className="m-count">{quotes.length} Quote{quotes.length!==1?"s":""}</div>
          {quotes.map(q => (
            <div key={q.id} className="m-card">
              <div className="m-card-body">
                <div className="m-card-top">
                  <div>
                    <div className="m-card-title">{q.number}</div>
                    <div className="m-card-ref">{q.client_name} · {formatDate(q.date)}</div>
                  </div>
                  <span className={q.status==="Converted" ? "badge badge-green" : q.status==="Sent" ? "badge badge-blue" : "badge badge-gray"}>{q.status}</span>
                </div>
                <div className="m-card-value">{formatCurrency(q.total, business?.currency)}</div>
              </div>
              <div className="m-actions">
                <button className="m-btn" onClick={() => setViewDoc(q)}>
                  <svg fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <span>View</span>
                </button>
                <button className="m-btn" onClick={() => { printAsPDF(buildDocHTML(q,"Quote",business), `Quote ${q.number}`); }}>
                  <svg fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>PDF</span>
                </button>
                <button className="m-btn" onClick={() => setEmailDoc(q)}>
                  <svg fill="none" stroke="#2563eb" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  <span>Email</span>
                </button>
                <button className="m-btn" onClick={() => setEmailDoc(q)}>
                  <svg fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                  <span>WhatsApp</span>
                </button>
                {q.status !== "Converted" && (
                  <button className="m-btn" onClick={() => convertToInvoice(q)}>
                    <svg fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                    <span>→ Inv</span>
                  </button>
                )}
                <button className="m-btn" onClick={() => { setForm({...q, discount: q.discount||0}); setModal("edit"); }}>
                  <svg fill="none" stroke="#0f1923" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  <span>Edit</span>
                </button>
                <button className="m-btn" onClick={() => setDeleteConfirm({ id: q.id, label: q.number || 'this quote' })}>
                  <svg fill="none" stroke="#dc2626" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </>)}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>{modal==="new"?"New Quote":"Edit Quote"}</h3><button className="btn btn-icon btn-outline btn-sm" onClick={() => setModal(null)}><Icon name="x" /></button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Quote Number</label><input className="form-input" value={form.number} onChange={e => setForm({...form, number:e.target.value})} /></div>
                <div className="form-group">
                  <label className="form-label">Client *</label>
                  <select className="form-select" style={errors.client_name?{borderColor:"#ef4444"}:{}} value={form.client_id} onChange={e => { const c=clients.find(c=>c.id===e.target.value); setForm({...form, client_id:e.target.value, client_name:c?.name||""}); }}>
                    <option value="">Select client</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <FormError error={errors.client_name} />
                </div>
                <div className="form-group"><label className="form-label">Date *</label><input className="form-input" style={errors.date?{borderColor:"#ef4444"}:{}} type="date" value={form.date} onChange={e => setForm({...form, date:e.target.value})} /><FormError error={errors.date} /></div>
                <div className="form-group"><label className="form-label">Expiry Date</label><input className="form-input" style={errors.expiry_date?{borderColor:"#ef4444"}:{}} type="date" value={form.expiry_date} onChange={e => setForm({...form, expiry_date:e.target.value})} /><FormError error={errors.expiry_date} /></div>
              </div>
              <div className="card-title" style={{ marginBottom:12 }}>Line Items</div>
              {errors.items && <div style={{ marginBottom:8 }}><FormError error={errors.items} /></div>}
              <div style={{ marginBottom:8, display:"grid", gridTemplateColumns:"3fr 1fr 1.5fr 1.5fr 40px", gap:8 }}>
                <span className="form-label">Description</span><span className="form-label">Qty</span><span className="form-label">Unit Price</span><span className="form-label">Total</span><span></span>
              </div>
              {form.items.map((item, i) => (
                <div key={i} className="line-item-row">
                  <input className="form-input" placeholder="Description" value={item.description} onChange={e => updateItem(i,"description",e.target.value)} />
                  <input className="form-input" type="number" min="0" value={item.qty} onChange={e => updateItem(i,"qty",e.target.value)} />
                  <input className="form-input" type="number" min="0" value={item.unit_price} onChange={e => updateItem(i,"unit_price",e.target.value)} />
                  <div style={{ padding:"9px 0", fontWeight:600 }}>{formatCurrency((item.qty||0, business?.currency)*(item.unit_price||0))}</div>
                  <button className="btn btn-icon btn-outline btn-sm" onClick={() => removeItem(i)} style={{ color:"var(--status-red-fg)" }}><Icon name="x" size={12} /></button>
                </div>
              ))}
              <button className="btn btn-outline btn-sm" onClick={addItem} style={{ marginBottom:16 }}><Icon name="plus" size={12} />Add Item</button>
              <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:12, flexWrap:"wrap" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <label className="form-label" style={{ margin:0, whiteSpace:"nowrap" }}>Discount %</label>
                  <input className="form-input" type="number" min="0" max="100" style={{ width:80 }} placeholder="0" value={form.discount||""} onChange={e => setForm({...form, discount:e.target.value})} />
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <input type="checkbox" id="vat" checked={form.include_vat} onChange={e => setForm({...form, include_vat:e.target.checked})} />
                  <label htmlFor="vat" style={{ fontSize:13.5, cursor:"pointer" }}>Include {business?.tax_label||getCurrencyOption(business?.currency||"ZAR").taxLabel||"VAT"}{(business?.tax_rate||getCurrencyOption(business?.currency||"ZAR").taxRate)>0?` (${business?.tax_rate||getCurrencyOption(business?.currency||"ZAR").taxRate}%)`:""}</label>
                </div>
              </div>
              <div style={{ background:"var(--surface)", padding:16, borderRadius:8, textAlign:"right" }}>
                <div className="text-sm text-muted">Line Total: {formatCurrency(totals.lineTotal, business?.currency)}</div>
                {(parseFloat(form.discount)||0) > 0 && <div className="text-sm" style={{ color:"var(--status-green-fg)" }}>Discount ({form.discount}%): -{formatCurrency(totals.discountAmt, business?.currency)}</div>}
                <div className="text-sm text-muted">Subtotal: {formatCurrency(totals.subtotal, business?.currency)}</div>
                {form.include_vat && totals.vat > 0 && <div className="text-sm text-muted">{business?.tax_label||getCurrencyOption(business?.currency||"ZAR").taxLabel||"VAT"} ({business?.tax_rate||getCurrencyOption(business?.currency||"ZAR").taxRate}%): {formatCurrency(totals.vat, business?.currency)}</div>}
                <div style={{ fontSize:18, fontWeight:700, marginTop:4 }}>Total: {formatCurrency(totals.total, business?.currency)}</div>
              </div>
              <div className="form-group mt-3"><label className="form-label">Notes</label><textarea className="form-textarea" value={form.notes} onChange={e => setForm({...form, notes:e.target.value})} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>Save Quote</button>
            </div>
          </div>
        </div>
      )}

      {viewDoc && (
        <div className="modal-overlay" onClick={() => setViewDoc(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxHeight:"95vh" }}>
            <div className="modal-header">
              <h3>Quote Preview</h3>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <button className="btn btn-sm btn-outline" onClick={() => { printAsPDF(buildDocHTML(viewDoc, "Quote", business), `Quote ${viewDoc.number}`); }} style={{ color:"var(--status-green-fg)" }}><Icon name="download" size={14}/>Download</button>
                <button className="email-btn btn-sm" onClick={() => { setEmailDoc(viewDoc); setViewDoc(null); }}><Icon name="email" size={14}/>Email</button>
                <button className="whatsapp-btn btn-sm" onClick={() => { setEmailDoc(viewDoc); setViewDoc(null); }}><Icon name="whatsapp" size={14}/>WhatsApp</button>
                <button className="btn btn-icon btn-outline btn-sm" onClick={() => setViewDoc(null)}><Icon name="x" /></button>
              </div>
            </div>
            <div className="modal-body"><DocumentPreview doc={viewDoc} type="Quote" business={business} /></div>
          </div>
        </div>
      )}
      
      <DeleteConfirmModal item={deleteConfirm?.label} onConfirm={async () => { if(deleteConfirm){ await DB.remove("quotes", deleteConfirm.id); await refresh(); setDeleteConfirm(null); } }} onCancel={() => setDeleteConfirm(null)} />
      {emailDoc && <ShareModal doc={emailDoc} type="Quote" business={business} onClose={() => setEmailDoc(null)} />}
    </div>
  );
}

// ─── INVOICES MODULE ──────────────────────────────────────────────────────────
function InvoicesModule({ userId }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [emailDoc, setEmailDoc] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [clients, setClients] = useState([]);
  const [business, setBusiness] = useState({ _userId: userId });
  const [form, setForm] = useState({ number:"", client_id:"", client_name:"", date: today(), due_date: addDays(today(), 30), items:[{ description:"", qty:1, unit_price:0 }], discount:0, include_vat:true, notes:"", status:"Draft", paid:0 });
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("EFT");

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const refresh = async () => {
    const [inv, c, b] = await Promise.all([
      DB.getList("invoices", userId), DB.getList("clients", userId), DB.get("business", userId),
    ]);
    setInvoices(inv || []); setClients(c || []);
    setBusiness({ ...(b || {}), _userId: userId }); setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const calcTotals = (items, includeVat, discount) => {
    const lineTotal = items.reduce((s, i) => s + (parseFloat(i.qty)||0)*(parseFloat(i.unit_price)||0), 0);
    const discountAmt = lineTotal * ((parseFloat(discount)||0) / 100);
    const sub = lineTotal - discountAmt;
    const txRate = ((business?.tax_rate !== undefined ? business.tax_rate : getCurrencyOption(business?.currency||"ZAR").taxRate) || 0) / 100;
    const vat = includeVat ? sub * txRate : 0;
    return { lineTotal, discountAmt, subtotal: sub, vat, total: sub + vat };
  };

  const [errors, setErrors] = useState({});

  const openNew = () => {
    const num = `INV-${String(invoices.length+1).padStart(3,"0")}`;
    setForm({ number:num, client_id:"", client_name:"", date:today(), due_date:addDays(today(),30), items:[{description:"",qty:1,unit_price:0}], discount:0, include_vat:true, notes:"", status:"Draft", paid:0 });
    setErrors({});
    setModal("new");
  };

  const save = async () => {
    const errs = {
      client_name: V.required(form.client_name, "Client"),
      date:        V.date(form.date, "Invoice date"),
      due_date:    V.dueDate(form.due_date, form.date),
      items:       V.lineItems(form.items),
    };
    const first = V.run(errs);
    if (first) { setErrors(errs); return; }
    setErrors({});
    const { subtotal, vat, total, discountAmt, lineTotal } = calcTotals(form.items, form.include_vat, form.discount);
    const data = { ...form, user_id: userId, subtotal, vat, total, discount: parseFloat(form.discount)||0, discount_amt: discountAmt, line_total: lineTotal };
    try {
      if (modal === "new") await DB.insert("invoices", data); else await DB.update("invoices", form.id, data);
      await refresh(); setModal(null);
    } catch (e) { console.error("Save invoice failed:", e); }
  };

  const [payError, setPayError] = useState("");

  const recordPayment = async () => {
    setPayError("");
    const amt = parseFloat(payAmount) || 0;
    if (!payAmount || isNaN(parseFloat(payAmount))) { setPayError("Please enter a payment amount."); return; }
    if (amt <= 0) { setPayError("Payment amount must be greater than zero."); return; }
    const outstanding = (payModal.total || 0) - (payModal.paid || 0);
    if (amt > outstanding + 0.01) { setPayError(`Amount cannot exceed the outstanding balance of ${formatCurrency(outstanding, business?.currency)}.`); return; }
    const newPaid = (payModal.paid||0) + amt;
    const status = newPaid >= payModal.total ? "Paid" : "Partial";
    await DB.update("invoices", payModal.id, { paid: newPaid, status });
    const receipts = await DB.getList("receipts", userId);
    const num = `RCP-${String(receipts.length+1).padStart(3,"0")}`;
    await DB.insert("receipts", { number:num, invoice_id:payModal.id, invoice_number:payModal.number, client_name:payModal.client_name, amount:amt, payment_method:payMethod, date:today(), user_id:userId });
    await refresh(); setPayModal(null); setPayAmount(""); setPayError("");
  };

  const whatsapp = (inv) => {
    const outstanding = inv.total - (inv.paid||0);
    const msg = `Dear ${inv.client_name},\n\nThis is a friendly reminder regarding invoice *${inv.number}*.\n\nAmount Outstanding: *${formatCurrency(outstanding, business?.currency)}*\nDue Date: ${formatDate(inv.due_date)}\n\nPlease make payment to:\n${business.bank_name || ""}\nAccount: ${business.bank_account_number || ""}\nRef: ${inv.number}\n\nThank you!\n${business.name || ""}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const updateItem = (i, field, val) => { const items = [...form.items]; items[i][field] = val; setForm({...form, items}); };
  const totals = calcTotals(form.items, form.include_vat, form.discount);
  const todayStr = today();

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:20 }}>
        <button className="btn btn-primary" onClick={openNew}><Icon name="plus" />New Invoice</button>
      </div>

      {invoices.length === 0 ? <div className="empty-state"><Icon name="invoice" size={40} /><h3>No invoices yet</h3></div> : (<>
        {/* DESKTOP TABLE */}
        <div className="card desktop-table" style={{ padding:0 }}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Invoice #</th><th>Client</th><th>Date</th><th>Due</th><th>Total</th><th>Paid</th><th>Outstanding</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {invoices.map(inv => {
                  const outstanding = (inv.total||0) - (inv.paid||0);
                  const isOverdue = inv.due_date < todayStr && inv.status !== "Paid";
                  return (
                    <tr key={inv.id}>
                      <td style={{ fontWeight:600 }}>{inv.number}{isOverdue && <span className="badge badge-red" style={{ marginLeft:6 }}>Overdue</span>}</td>
                      <td>{inv.client_name}</td>
                      <td className="text-muted">{formatDate(inv.date)}</td>
                      <td className="text-muted" style={{ color: isOverdue ? "var(--status-red-fg)": "inherit" }}>{formatDate(inv.due_date)}</td>
                      <td style={{ fontWeight:700 }}>{formatCurrency(inv.total, business?.currency)}</td>
                      <td style={{ color:"var(--status-green-fg)" }}>{formatCurrency(inv.paid||0, business?.currency)}</td>
                      <td style={{ fontWeight:600, color: outstanding > 0 ? "var(--status-red-fg)" : "var(--status-green-fg)" }}>{formatCurrency(outstanding, business?.currency)}</td>
                      <td><span className={inv.status==="Paid" ? "badge badge-green" : inv.status==="Partial" ? "badge badge-amber" : isOverdue ? "badge badge-red" : "badge badge-blue"}>{isOverdue&&inv.status!=="Paid"?"Overdue":inv.status}</span></td>
                      <td>
                        <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                          <button className="btn btn-sm btn-outline" onClick={() => setViewDoc(inv)}><Icon name="eye" size={12} /></button>
                          <button className="btn btn-sm btn-outline" title="Download" onClick={() => { printAsPDF(buildDocHTML(inv,"Invoice",business), `Invoice ${inv.number}`); }} style={{ color:"var(--status-green-fg)" }}><Icon name="download" size={12}/></button>
                          <button className="btn btn-sm btn-outline" title="Send Email" onClick={() => setEmailDoc(inv)} style={{ color:"var(--status-blue-fg)" }}><Icon name="email" size={12} /></button>
                          <button className="btn btn-sm btn-outline" onClick={() => setEmailDoc(inv)} title="WhatsApp" style={{ color:"#25d366" }}><Icon name="whatsapp" size={12} style={{ color:"#25d366" }} /></button>
                          {inv.status !== "Paid" && <button className="btn btn-sm btn-success" onClick={() => { setPayModal(inv); setPayAmount(String(inv.total-(inv.paid||0))); }}>Pay</button>}
                          <button className="btn btn-sm btn-outline" onClick={() => { setForm({...inv, items:inv.items||[{description:"",qty:1,unit_price:0}], discount: inv.discount||0}); setModal("edit"); }}><Icon name="edit" size={12} /></button>
                          <button className="btn btn-sm btn-outline" style={{ color:"var(--status-red-fg)" }} onClick={() => setDeleteConfirm({ id: inv.id, label: inv.number || 'this invoice' })}><Icon name="trash" size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {/* MOBILE CARDS */}
        <div className="mobile-list">
          <div className="m-count">{invoices.length} Invoice{invoices.length!==1?"s":""}</div>
          {invoices.map(inv => {
            const outstanding = (inv.total||0) - (inv.paid||0);
            const isOverdue = inv.due_date < todayStr && inv.status !== "Paid";
            return (
              <div key={inv.id} className="m-card">
                <div className="m-card-body">
                  <div className="m-card-top">
                    <div>
                      <div className="m-card-title">{inv.number}{isOverdue && <span className="badge badge-red" style={{ marginLeft:6, fontSize:10 }}>Overdue</span>}</div>
                      <div className="m-card-ref">{inv.client_name} · Due {formatDate(inv.due_date)}</div>
                    </div>
                    <span className={inv.status==="Paid" ? "badge badge-green" : inv.status==="Partial" ? "badge badge-amber" : isOverdue ? "badge badge-red" : "badge badge-blue"}>{isOverdue&&inv.status!=="Paid"?"Overdue":inv.status}</span>
                  </div>
                  <div className="m-card-meta">
                    <span>Total: <strong>{formatCurrency(inv.total, business?.currency)}</strong></span>
                    <span style={{ color: outstanding > 0 ? "var(--status-red-fg)" : "var(--status-green-fg)" }}>Owed: <strong>{formatCurrency(outstanding, business?.currency)}</strong></span>
                  </div>
                </div>
                <div className="m-actions">
                  <button className="m-btn" onClick={() => setViewDoc(inv)}>
                    <svg fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    <span>View</span>
                  </button>
                  <button className="m-btn" onClick={() => { printAsPDF(buildDocHTML(inv,"Invoice",business), `Invoice ${inv.number}`); }}>
                    <svg fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    <span>PDF</span>
                  </button>
                  <button className="m-btn" onClick={() => setEmailDoc(inv)}>
                    <svg fill="none" stroke="#2563eb" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    <span>Email</span>
                  </button>
                  <button className="m-btn" onClick={() => setEmailDoc(inv)}>
                    <svg fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                    <span>WhatsApp</span>
                  </button>
                  {inv.status !== "Paid" && (
                    <button className="m-btn" onClick={() => { setPayModal(inv); setPayAmount(String(inv.total-(inv.paid||0))); }}>
                      <svg fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                      <span>Pay</span>
                    </button>
                  )}
                  <button className="m-btn" onClick={() => { setForm({...inv, items:inv.items||[{description:"",qty:1,unit_price:0}], discount: inv.discount||0}); setModal("edit"); }}>
                    <svg fill="none" stroke="#0f1923" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    <span>Edit</span>
                  </button>
                  <button className="m-btn" onClick={() => setDeleteConfirm({ id: inv.id, label: inv.number || 'this invoice' })}>
                    <svg fill="none" stroke="#dc2626" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </>)}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>{modal==="new"?"New Invoice":"Edit Invoice"}</h3><button className="btn btn-icon btn-outline btn-sm" onClick={() => setModal(null)}><Icon name="x" /></button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Invoice Number</label><input className="form-input" value={form.number} onChange={e => setForm({...form, number:e.target.value})} /></div>
                <div className="form-group">
                  <label className="form-label">Client *</label>
                  <select className="form-select" style={errors.client_name?{borderColor:"#ef4444"}:{}} value={form.client_id} onChange={e => { const c=clients.find(c=>c.id===e.target.value); setForm({...form, client_id:e.target.value, client_name:c?.name||""}); }}>
                    <option value="">Select client</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <FormError error={errors.client_name} />
                </div>
                <div className="form-group"><label className="form-label">Invoice Date *</label><input className="form-input" style={errors.date?{borderColor:"#ef4444"}:{}} type="date" value={form.date} onChange={e => setForm({...form, date:e.target.value})} /><FormError error={errors.date} /></div>
                <div className="form-group"><label className="form-label">Due Date</label><input className="form-input" style={errors.due_date?{borderColor:"#ef4444"}:{}} type="date" value={form.due_date} onChange={e => setForm({...form, due_date:e.target.value})} /><FormError error={errors.due_date} /></div>
              </div>
              <div className="card-title" style={{ marginBottom:12 }}>Line Items</div>
              {errors.items && <div style={{ marginBottom:8 }}><FormError error={errors.items} /></div>}
              <div style={{ marginBottom:8, display:"grid", gridTemplateColumns:"3fr 1fr 1.5fr 1.5fr 40px", gap:8 }}>
                <span className="form-label">Description</span><span className="form-label">Qty</span><span className="form-label">Unit Price</span><span className="form-label">Total</span><span></span>
              </div>
              {form.items.map((item, i) => (
                <div key={i} className="line-item-row">
                  <input className="form-input" value={item.description} onChange={e => updateItem(i,"description",e.target.value)} />
                  <input className="form-input" type="number" min="0" value={item.qty} onChange={e => updateItem(i,"qty",e.target.value)} />
                  <input className="form-input" type="number" min="0" value={item.unit_price} onChange={e => updateItem(i,"unit_price",e.target.value)} />
                  <div style={{ padding:"9px 0", fontWeight:600 }}>{formatCurrency((item.qty||0, business?.currency)*(item.unit_price||0))}</div>
                  <button className="btn btn-icon btn-outline btn-sm" onClick={() => setForm({...form, items: form.items.filter((_,idx)=>idx!==i)})} style={{ color:"var(--status-red-fg)" }}><Icon name="x" size={12} /></button>
                </div>
              ))}
              <button className="btn btn-outline btn-sm" onClick={() => setForm({...form, items:[...form.items,{description:"",qty:1,unit_price:0}]})} style={{ marginBottom:16 }}><Icon name="plus" size={12} />Add Item</button>
              <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:12, flexWrap:"wrap" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <label className="form-label" style={{ margin:0, whiteSpace:"nowrap" }}>Discount %</label>
                  <input className="form-input" type="number" min="0" max="100" style={{ width:80 }} placeholder="0" value={form.discount||""} onChange={e => setForm({...form, discount:e.target.value})} />
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <input type="checkbox" id="vatInv" checked={form.include_vat} onChange={e => setForm({...form, include_vat:e.target.checked})} />
                  <label htmlFor="vatInv" style={{ fontSize:13.5, cursor:"pointer" }}>Include {business?.tax_label||getCurrencyOption(business?.currency||"ZAR").taxLabel||"VAT"}{(business?.tax_rate||getCurrencyOption(business?.currency||"ZAR").taxRate)>0?` (${business?.tax_rate||getCurrencyOption(business?.currency||"ZAR").taxRate}%)`:""}</label>
                </div>
              </div>
              <div style={{ background:"var(--surface)", padding:16, borderRadius:8, textAlign:"right" }}>
                <div className="text-sm text-muted">Line Total: {formatCurrency(totals.lineTotal, business?.currency)}</div>
                {(parseFloat(form.discount)||0) > 0 && <div className="text-sm" style={{ color:"var(--status-green-fg)" }}>Discount ({form.discount}%): -{formatCurrency(totals.discountAmt, business?.currency)}</div>}
                <div className="text-sm text-muted">Subtotal: {formatCurrency(totals.subtotal, business?.currency)}</div>
                {form.include_vat && totals.vat > 0 && <div className="text-sm text-muted">{business?.tax_label||getCurrencyOption(business?.currency||"ZAR").taxLabel||"VAT"} ({business?.tax_rate||getCurrencyOption(business?.currency||"ZAR").taxRate}%): {formatCurrency(totals.vat, business?.currency)}</div>}
                <div style={{ fontSize:18, fontWeight:700, marginTop:4 }}>Total: {formatCurrency(totals.total, business?.currency)}</div>
              </div>
              <div className="form-group mt-3"><label className="form-label">Notes</label><textarea className="form-textarea" value={form.notes} onChange={e => setForm({...form, notes:e.target.value})} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>Save Invoice</button>
            </div>
          </div>
        </div>
      )}

      {payModal && (
        <div className="modal-overlay" onClick={() => { setPayModal(null); setPayError(""); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Record Payment</h3><button className="btn btn-icon btn-outline btn-sm" onClick={() => { setPayModal(null); setPayError(""); }}><Icon name="x" /></button></div>
            <div className="modal-body">
              <div style={{ marginBottom:16 }}>
                <div style={{ fontWeight:600, fontSize:15 }}>{payModal.number} — {payModal.client_name}</div>
                <div className="text-muted">Total: {formatCurrency(payModal.total, business?.currency)} | Already paid: {formatCurrency(payModal.paid||0, business?.currency)} | Outstanding: {formatCurrency(payModal.total-(payModal.paid||0, business?.currency))}</div>
              </div>
              <div className="form-group">
                <label className="form-label">Payment Amount ({getCurrencyOption(business?.currency||"ZAR").symbol}) *</label>
                <input className="form-input" style={payError?{borderColor:"#ef4444"}:{}} type="number" min="0" value={payAmount} onChange={e => { setPayAmount(e.target.value); setPayError(""); }} placeholder="0.00" />
                <FormError error={payError} />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <select className="form-select" value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                  {["EFT","Cash","Card","Cheque","Other"].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setPayModal(null); setPayError(""); }}>Cancel</button>
              <button className="btn btn-success" onClick={recordPayment}><Icon name="check" />Record Payment & Generate Receipt</button>
            </div>
          </div>
        </div>
      )}

      {viewDoc && (
        <div className="modal-overlay" onClick={() => setViewDoc(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxHeight:"95vh" }}>
            <div className="modal-header">
              <h3>Invoice Preview</h3>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <button className="btn btn-sm btn-outline" onClick={() => { printAsPDF(buildDocHTML(viewDoc,"Invoice",business), `Invoice ${viewDoc.number}`); }} style={{ color:"var(--status-green-fg)" }}><Icon name="download" size={14}/>Download</button>
                <button className="email-btn btn-sm" onClick={() => { setEmailDoc(viewDoc); setViewDoc(null); }}><Icon name="email" size={14}/>Email</button>
                <button className="whatsapp-btn btn-sm" onClick={() => { setEmailDoc(viewDoc); setViewDoc(null); }}><Icon name="whatsapp" size={14}/>WhatsApp</button>
                <button className="btn btn-icon btn-outline btn-sm" onClick={() => setViewDoc(null)}><Icon name="x" /></button>
              </div>
            </div>
            {/* Pay Now buttons — only shown if business has payment links and invoice is unpaid */}
            {viewDoc && viewDoc.status !== "Paid" && (business.payfast_link || business.ozow_link || business.other_payment_link) && (
              <div style={{ padding:"12px 20px", background:"#f0fdf4", borderBottom:"1px solid #bbf7d0", display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                <span style={{ fontSize:12, fontWeight:600, color:"#065f46", marginRight:4 }}>Pay Online:</span>
                {business.payfast_link && <a href={business.payfast_link} target="_blank" rel="noopener noreferrer" style={{ background:"#007bff", color:"white", padding:"6px 14px", borderRadius:6, fontSize:12, fontWeight:700, textDecoration:"none" }}>PayFast</a>}
                {business.ozow_link    && <a href={business.ozow_link}    target="_blank" rel="noopener noreferrer" style={{ background:"#6c2bd9", color:"white", padding:"6px 14px", borderRadius:6, fontSize:12, fontWeight:700, textDecoration:"none" }}>Ozow</a>}
                {business.other_payment_link && <a href={business.other_payment_link} target="_blank" rel="noopener noreferrer" style={{ background:"#10b981", color:"white", padding:"6px 14px", borderRadius:6, fontSize:12, fontWeight:700, textDecoration:"none" }}>{business.other_payment_label||"Pay Online"}</a>}
              </div>
            )}
            <div className="modal-body"><DocumentPreview doc={viewDoc} type="Invoice" business={business} /></div>
          </div>
        </div>
      )}
      
      <DeleteConfirmModal item={deleteConfirm?.label} onConfirm={async () => { if(deleteConfirm){ await DB.remove("invoices", deleteConfirm.id); await refresh(); setDeleteConfirm(null); } }} onCancel={() => setDeleteConfirm(null)} />
      {emailDoc && <ShareModal doc={emailDoc} type="Invoice" business={business} onClose={() => setEmailDoc(null)} />}
    </div>
  );
}

// ─── RECEIPTS MODULE ──────────────────────────────────────────────────────────
function ReceiptsModule({ userId }) {
  const [receipts, setReceipts] = useState([]);
  const [business, setBusiness] = useState({ _userId: userId });
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const [r, b] = await Promise.all([DB.getList("receipts", userId), DB.get("business", userId)]);
    setReceipts(r || []); setBusiness({ ...(b || {}), _userId: userId }); setLoading(false);
  };
  useEffect(() => { refresh(); }, []);
  const [viewDoc, setViewDoc] = useState(null);
  const [emailDoc, setEmailDoc] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  return (
    <div>
      {receipts.length === 0 ? <div className="empty-state"><Icon name="receipt" size={40} /><h3>No receipts yet</h3><p>Receipts are generated automatically when you record a payment on an invoice.</p></div> : (<>
        {/* DESKTOP TABLE */}
        <div className="card desktop-table" style={{ padding:0 }}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Receipt #</th><th>Invoice</th><th>Client</th><th>Date</th><th>Amount</th><th>Method</th><th>Actions</th></tr></thead>
              <tbody>
                {receipts.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight:600 }}>{r.number}</td>
                    <td className="text-muted">{r.invoice_number}</td>
                    <td>{r.client_name}</td>
                    <td className="text-muted">{formatDate(r.date)}</td>
                    <td style={{ fontWeight:700, color:"var(--status-green-fg)" }}>{formatCurrency(r.amount, business?.currency)}</td>
                    <td><span className="badge badge-blue">{r.payment_method}</span></td>
                    <td>
                      <div style={{ display:"flex", gap:4 }}>
                        <button className="btn btn-sm btn-outline" onClick={() => setViewDoc(r)}><Icon name="eye" size={12} />View</button>
                        <button className="btn btn-sm btn-outline" title="Download" onClick={() => { printAsPDF(buildReceiptHTML(r,business), `Receipt ${r.number}`); }} style={{ color:"var(--status-green-fg)" }}><Icon name="download" size={12}/>Download</button>
                        <button className="btn btn-sm btn-outline" title="Send Email" onClick={() => setEmailDoc(r)} style={{ color:"var(--status-blue-fg)" }}><Icon name="email" size={12} />Email</button>
                        <button className="btn btn-sm btn-outline" title="WhatsApp" onClick={() => setEmailDoc(r)} style={{ color:"#25d366" }}><Icon name="whatsapp" size={12} />WhatsApp</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* MOBILE CARDS */}
        <div className="mobile-list">
          <div className="m-count">{receipts.length} Receipt{receipts.length!==1?"s":""}</div>
          {receipts.map(r => (
            <div key={r.id} className="m-card">
              <div className="m-card-body">
                <div className="m-card-top">
                  <div>
                    <div className="m-card-title">{r.number}</div>
                    <div className="m-card-ref">{r.client_name} · {r.invoice_number} · {formatDate(r.date)}</div>
                  </div>
                  <span className="badge badge-blue">{r.payment_method}</span>
                </div>
                <div className="m-card-value" style={{ color:"var(--status-green-fg)" }}>{formatCurrency(r.amount, business?.currency)}</div>
              </div>
              <div className="m-actions">
                <button className="m-btn" onClick={() => setViewDoc(r)}>
                  <svg fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <span>View</span>
                </button>
                <button className="m-btn" onClick={() => { printAsPDF(buildReceiptHTML(r,business), `Receipt ${r.number}`); }}>
                  <svg fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>PDF</span>
                </button>
                <button className="m-btn" onClick={() => setEmailDoc(r)}>
                  <svg fill="none" stroke="#2563eb" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  <span>Email</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </>)}

      {viewDoc && (
        <div className="modal-overlay" onClick={() => setViewDoc(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Receipt</h3>
              <div style={{ display:"flex", gap:8 }}>
                <button className="btn btn-sm btn-outline" onClick={() => { printAsPDF(buildReceiptHTML(viewDoc,business), `Receipt ${viewDoc.number}`); }} style={{ color:"var(--status-green-fg)" }}><Icon name="download" size={14}/>Download</button>
                <button className="email-btn btn-sm" onClick={() => { setEmailDoc(viewDoc); setViewDoc(null); }}><Icon name="email" size={14}/>Email</button>
                <button className="btn btn-icon btn-outline btn-sm" onClick={() => setViewDoc(null)}><Icon name="x" /></button>
              </div>
            </div>
            <div className="modal-body">
              <div className="doc-preview" style={{ padding:24 }}>
                <div style={{ textAlign:"center", borderBottom:"2px solid var(--border)", paddingBottom:16, marginBottom:16 }}>
                  <div style={{ fontSize:22, fontWeight:700, color:"var(--ink)" }}>{business.name || "Business Name"}</div>
                  <div style={{ fontSize:28, fontWeight:700, color:"var(--status-green-fg)", marginTop:8 }}>PAYMENT RECEIPT</div>
                  <div style={{ color:"#64748b" }}>{viewDoc.number}</div>
                </div>
                <div className="form-grid" style={{ marginBottom:16 }}>
                  <div><div className="text-sm text-muted">Received From</div><div style={{ fontWeight:600 }}>{viewDoc.client_name}</div></div>
                  <div><div className="text-sm text-muted">Date</div><div style={{ fontWeight:600 }}>{formatDate(viewDoc.date)}</div></div>
                  <div><div className="text-sm text-muted">Invoice Ref</div><div style={{ fontWeight:600 }}>{viewDoc.invoice_number}</div></div>
                  <div><div className="text-sm text-muted">Payment Method</div><div style={{ fontWeight:600 }}>{viewDoc.payment_method}</div></div>
                </div>
                <div style={{ background:"var(--surface)", padding:20, borderRadius:10, textAlign:"center" }}>
                  <div className="text-muted">Amount Received</div>
                  <div style={{ fontSize:36, fontWeight:700, color:"var(--status-green-fg)" }}>{formatCurrency(viewDoc.amount, business?.currency)}</div>
                </div>
                <div style={{ marginTop:20, textAlign:"center", fontSize:13, color:"#94a3b8" }}>Thank you for your payment.</div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <DeleteConfirmModal item={deleteConfirm?.label} onConfirm={async () => { if(deleteConfirm){ await DB.remove("receipts", deleteConfirm.id); await refresh(); setDeleteConfirm(null); } }} onCancel={() => setDeleteConfirm(null)} />
      {emailDoc && <ShareModal doc={emailDoc} type="Receipt" business={business} onClose={() => setEmailDoc(null)} />}
    </div>
  );
}
function DocumentPreview({ doc, type, business }) {
  const isInvoice = type === "Invoice";
  return (
    <div className="doc-preview">
      <div className="doc-header">
        <div className="doc-company">
          <h2>{business.name || "Your Business Name"}</h2>
          <div style={{ color:"#64748b", fontSize:12.5, marginTop:4 }}>
            {business.address && <div>{business.address}</div>}
            {business.phone && <div>Tel: {business.phone}</div>}
            {business.email && <div>{business.email}</div>}
            {business.vat_number && <div>Tax No: {business.vat_number}</div>}
            {business.reg_number && <div>Reg: {business.reg_number}</div>}
          </div>
        </div>
        <div className="doc-number">
          <div style={{ fontSize:13, color:"#64748b", textAlign:"right" }}>{type.toUpperCase()}</div>
          <div>{doc.number}</div>
          <div style={{ fontSize:12, color:"#64748b", marginTop:4 }}>Date: {formatDate(doc.date)}</div>
          {isInvoice && <div style={{ fontSize:12, color: doc.due_date < today() ? "var(--status-red-fg)" : "#64748b" }}>Due: {formatDate(doc.due_date)}</div>}
          {!isInvoice && <div style={{ fontSize:12, color:"#64748b" }}>Valid: {formatDate(doc.expiry_date)}</div>}
        </div>
      </div>

      <div className="doc-meta">
        <div>
          <div style={{ fontSize:11, color:"#94a3b8", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.5px" }}>Bill To</div>
          <div style={{ fontWeight:600 }}>{doc.client_name}</div>
        </div>
        <div>
          {isInvoice && doc.status === "Paid" && <div style={{ background:"var(--status-green-fg)", color:"white", padding:"6px 14px", borderRadius:20, display:"inline-block", fontWeight:700, fontSize:13 }}>✓ PAID</div>}
        </div>
      </div>

      <table style={{ width:"100%", borderCollapse:"collapse", marginBottom:16 }}>
        <thead>
          <tr>
            <th style={{ background:"var(--ink)", color:"white", padding:"10px 14px", textAlign:"left", fontSize:12 }}>Description</th>
            <th style={{ background:"var(--ink)", color:"white", padding:"10px 14px", textAlign:"right", fontSize:12 }}>Qty</th>
            <th style={{ background:"var(--ink)", color:"white", padding:"10px 14px", textAlign:"right", fontSize:12 }}>Unit Price</th>
            <th style={{ background:"var(--ink)", color:"white", padding:"10px 14px", textAlign:"right", fontSize:12 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {(doc.items||[]).map((item, i) => (
            <tr key={i} style={{ borderBottom:"1px solid var(--border)" }}>
              <td style={{ padding:"10px 14px" }}>{item.description}</td>
              <td style={{ padding:"10px 14px", textAlign:"right" }}>{item.qty}</td>
              <td style={{ padding:"10px 14px", textAlign:"right" }}>{formatCurrency(item.unit_price, business?.currency)}</td>
              <td style={{ padding:"10px 14px", textAlign:"right", fontWeight:600 }}>{formatCurrency((item.qty||0, business?.currency)*(item.unit_price||0))}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ textAlign:"right", marginBottom:16 }}>
        {(doc.discount > 0) && <div style={{ fontSize:13, color:"#64748b" }}>Line Total: {formatCurrency(doc.line_total || doc.subtotal, business?.currency)}</div>}
        {(doc.discount > 0) && <div style={{ fontSize:13, color:"#16a34a" }}>Discount ({doc.discount}%): -{formatCurrency(doc.discount_amt || 0, business?.currency)}</div>}
        <div style={{ fontSize:13, color:"#64748b" }}>Subtotal: {formatCurrency(doc.subtotal, business?.currency)}</div>
        {doc.vat > 0 && <div style={{ fontSize:13, color:"#64748b" }}>{business?.tax_label||"VAT"}{(business?.tax_rate||15)>0?` (${business?.tax_rate||15}%)`:""}: {formatCurrency(doc.vat, business?.currency)}</div>}
        <div style={{ fontSize:22, fontWeight:700, color:"var(--ink)", marginTop:4 }}>Total: {formatCurrency(doc.total, business?.currency)}</div>
        {isInvoice && doc.paid > 0 && <>
          <div style={{ fontSize:13, color:"var(--status-green-fg)" }}>Paid: {formatCurrency(doc.paid, business?.currency)}</div>
          <div style={{ fontSize:16, fontWeight:700, color:"var(--status-red-fg)" }}>Outstanding: {formatCurrency(doc.total-doc.paid, business?.currency)}</div>
        </>}
      </div>

      {doc.notes && <div style={{ padding:12, background:"var(--surface)", borderRadius:8, fontSize:12.5, color:"#64748b", marginBottom:16 }}><strong>Notes:</strong> {doc.notes}</div>}

      {isInvoice && business.bank_name && (
        <div className="doc-banking">
          <div style={{ fontWeight:700, fontSize:13, marginBottom:8 }}>Banking Details</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, fontSize:12.5 }}>
            <div><span style={{ color:"#94a3b8" }}>Bank: </span>{business.bank_name}</div>
            <div><span style={{ color:"#94a3b8" }}>Account Holder: </span>{business.bank_account_holder}</div>
            <div><span style={{ color:"#94a3b8" }}>Account Number: </span>{business.bank_account_number}</div>
            <div><span style={{ color:"#94a3b8" }}>Branch Code: </span>{business.bank_branch_code}</div>
            <div style={{ gridColumn:"1/-1" }}><span style={{ color:"#94a3b8" }}>Reference: </span><strong>{doc.number}</strong></div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── CLIENTS MODULE ───────────────────────────────────────────────────────────
function ClientsModule({ userId }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name:"", contact_person:"", email:"", phone:"", address:"", notes:"" });
  const [search, setSearch] = useState("");

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const refresh = async () => { const c = await DB.getList("clients", userId); setClients(c||[]); setLoading(false); };
  useEffect(() => { refresh(); }, []);

  const [portalCopied, setPortalCopied] = useState(null);

  const genPortalToken = async (client) => {
    let token = client.portal_token;
    // Only generate a new UUID if none exists — never overwrite an existing link
    if (!token) {
      token = ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,c=>(c^crypto.getRandomValues(new Uint8Array(1))[0]&15>>c/4).toString(16));
    }
    if (!client.portal_token || !client.portal_enabled) {
      await DB.update("clients", client.id, { portal_token: token, portal_enabled: true });
      await refresh();
    }
    const link = `https://portal.fieldjob.getdiswayne.com?token=${token}`;
    try { await navigator.clipboard.writeText(link); } catch {}
    setPortalCopied(client.id);
    setTimeout(() => setPortalCopied(null), 3000);
  };

  const revokePortalAccess = async (client) => {
    await DB.update("clients", client.id, { portal_enabled: false });
    await refresh();
  };

  const regenPortalToken = async (client) => {
    const token = ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,c=>(c^crypto.getRandomValues(new Uint8Array(1))[0]&15>>c/4).toString(16));
    await DB.update("clients", client.id, { portal_token: token, portal_enabled: true });
    await refresh();
    const link = `https://portal.fieldjob.getdiswayne.com?token=${token}`;
    try { await navigator.clipboard.writeText(link); } catch {}
    setPortalCopied(client.id);
    setTimeout(() => setPortalCopied(null), 3000);
  };

  const [errors, setErrors] = useState({});

  const save = async () => {
    const errs = {
      name:  V.required(form.name, "Client name"),
      email: V.email(form.email),
      phone: V.phone(form.phone),
    };
    const first = V.run(errs);
    if (first) { setErrors(errs); return; }
    setErrors({});
    const data = { ...form, user_id: userId };
    if (form.id) await DB.update("clients", form.id, data); else await DB.insert("clients", data);
    await refresh(); setModal(false);
  };

  const filtered = clients.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={{ display:"flex", gap:12, marginBottom:20 }}>
        <div className="search-bar" style={{ flex:1 }}>
          <Icon name="search" size={15} style={{ color:"#94a3b8" }} />
          <input placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={() => { setForm({ name:"", contact_person:"", email:"", phone:"", address:"", notes:"" }); setErrors({}); setModal(true); }}><Icon name="plus" />Add Client</button>
      </div>

      {filtered.length === 0 ? <div className="empty-state"><Icon name="clients" size={40} /><h3>No clients yet</h3></div> : (<>
        {/* DESKTOP TABLE */}
        <div className="card desktop-table" style={{ padding:0 }}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Business Name</th><th>Contact Person</th><th>Email</th><th>Phone</th><th>Address</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight:600 }}>{c.name}</td>
                    <td className="text-muted">{c.contact}</td>
                    <td className="text-muted">{c.email}</td>
                    <td className="text-muted">{c.phone}</td>
                    <td className="text-muted" style={{ maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.address}</td>
                    <td>
                      <div style={{ display:"flex", gap:4 }}>
                        {c.phone && <button className="btn btn-sm btn-outline" title="WhatsApp" style={{color:"#25d366",borderColor:"#c3e8d0"}} onClick={() => window.open(`https://wa.me/27${c.phone.replace(/^0/,"").replace(/\D/g,"")}`, "_blank")}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/><path d="M12 2C6.477 2 2 6.477 2 12c0 1.885.52 3.644 1.428 5.15L2 22l4.999-1.399A9.93 9.93 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/></svg>
                        </button>}
                        <button className="btn btn-sm btn-outline" onClick={() => { setForm({...c}); setModal(true); }}><Icon name="edit" size={12} /></button>
                        <button className="btn btn-sm btn-outline" style={{ color:"var(--status-red-fg)" }} onClick={() => setDeleteConfirm({ id: c.id, label: c.name || 'this client' })}><Icon name="trash" size={12} /></button>
                        <button
                          onClick={() => genPortalToken(c)}
                          style={{ padding:"4px 10px", borderRadius:7, border:"none", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"var(--font)", background: c.portal_token ? "rgba(16,185,129,0.1)" : "var(--gold-p)", color: c.portal_token ? "var(--emerald)" : "var(--gold)", border: c.portal_token ? "1px solid rgba(16,185,129,0.25)" : "1px solid var(--gold-b)", transition:"all 0.15s", display:"inline-flex", alignItems:"center", gap:5 }}
                          title={c.portal_token ? "Copy portal link" : "Create client portal"}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                          {portalCopied === c.id ? "Copied!" : c.portal_token ? "Portal" : "Portal"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* MOBILE CARDS */}
        <div className="mobile-list">
          <div className="m-count">{filtered.length} Client{filtered.length!==1?"s":""}</div>
          {filtered.map(c => (
            <div key={c.id} className="m-card">
              <div className="m-card-body">
                <div className="m-card-top">
                  <div>
                    <div className="m-card-title">{c.name}</div>
                    <div className="m-card-ref">{c.contact_person || c.contact}{c.address ? ` · ${c.address}` : ""}</div>
                  </div>
                  <div className="m-card-avatar">{(c.name||"?")[0].toUpperCase()}</div>
                </div>
                <div className="m-card-meta">
                  {c.phone && <span><svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.54a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>{c.phone}</span>}
                  {c.email && <span><svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>{c.email}</span>}
                </div>
              </div>
              <div className="m-actions">
                {c.phone && <button className="m-btn" onClick={() => window.open(`tel:${c.phone}`)}>
                  <svg fill="none" stroke="#2563eb" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.54a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  <span>Call</span>
                </button>}
                {c.email && <button className="m-btn" onClick={() => window.open(`mailto:${c.email}`)}>
                  <svg fill="none" stroke="#2563eb" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  <span>Email</span>
                </button>}
                {c.phone && <button className="m-btn" onClick={() => window.open(`https://wa.me/27${c.phone.replace(/^0/,"").replace(/\D/g,"")}`, "_blank")}>
                  <svg fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                  <span>WhatsApp</span>
                </button>}
                <button className="m-btn" onClick={() => { setForm({...c}); setModal(true); }}>
                  <svg fill="none" stroke="#0f1923" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  <span>Edit</span>
                </button>
                <button className="m-btn" onClick={() => setDeleteConfirm({ id: c.id, label: c.name || 'this client' })}>
                  <svg fill="none" stroke="#dc2626" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </>)}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>{form.id?"Edit Client":"New Client"}</h3><button className="btn btn-icon btn-outline btn-sm" onClick={() => setModal(false)}><Icon name="x" /></button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Business Name *</label><input className="form-input" style={errors.name?{borderColor:"#ef4444"}:{}} value={form.name} onChange={e => setForm({...form, name:e.target.value})} /><FormError error={errors.name} /></div>
                <div className="form-group"><label className="form-label">Contact Person</label><input className="form-input" value={form.contact_person} onChange={e => setForm({...form, contact_person:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Email</label><input className="form-input" style={errors.email?{borderColor:"#ef4444"}:{}} type="email" value={form.email} onChange={e => setForm({...form, email:e.target.value})} /><FormError error={errors.email} /></div>
                <div className="form-group"><label className="form-label">Phone</label><input className="form-input" style={errors.phone?{borderColor:"#ef4444"}:{}} value={form.phone} onChange={e => setForm({...form, phone:e.target.value})} placeholder="071 000 0000" /><FormError error={errors.phone} /></div>
              </div>
              <div className="form-group"><label className="form-label">Physical Address</label><textarea className="form-textarea" rows={2} value={form.address} onChange={e => setForm({...form, address:e.target.value})} /></div>
            </div>
            <div className="modal-footer"><button className="btn btn-outline" onClick={() => setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save}>Save Client</button></div>
          </div>
        </div>
      )}

      <DeleteConfirmModal item={deleteConfirm?.label} onConfirm={async () => { if(deleteConfirm){ await DB.remove("clients", deleteConfirm.id); await refresh(); setDeleteConfirm(null); } }} onCancel={() => setDeleteConfirm(null)} />
    </div>
  );
}


// ─── REPORTS MODULE ───────────────────────────────────────────────────────────
// ─── REPORT TABLE COMPONENTS (defined outside ReportsModule to prevent focus loss) ───

function WorkTable({ rows, onUpdate, onRemove, onAdd }) {
  return (
    <div style={{padding:0}}>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:600}}>
          <thead><tr style={{background:"#f8fafc"}}>
            {["Activity / Task","Area / Location","Status","% Complete","Notes",""].map((h,i)=>(
              <th key={i} style={{fontSize:9.5,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:0.5,padding:"6px 8px",textAlign:"left",borderBottom:"1.5px solid var(--border)",paddingLeft:i===0?16:8,whiteSpace:"nowrap"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.id} style={{borderBottom:"1px solid var(--border-light)"}}>
                <td style={{padding:"4px 8px",paddingLeft:16,minWidth:160}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Task description" value={r.task||""} onChange={e=>onUpdate(r.id,"task",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:100}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Location" value={r.area||""} onChange={e=>onUpdate(r.id,"area",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:110}}>
                  <select className="form-select" style={{fontSize:12,padding:"5px 8px"}} value={r.status||"Not Started"} onChange={e=>onUpdate(r.id,"status",e.target.value)}>
                    <option>Not Started</option><option>In Progress</option><option>Done</option>
                  </select>
                </td>
                <td style={{padding:"4px 8px",minWidth:70}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px",textAlign:"right"}} type="number" min="0" max="100" placeholder="0" value={r.pct||""} onChange={e=>onUpdate(r.id,"pct",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:140}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Notes" value={r.notes||""} onChange={e=>onUpdate(r.id,"notes",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",width:28,textAlign:"center"}}>
                  <button style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:14}} onClick={()=>onRemove(r.id)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length===0&&<div style={{fontSize:12,color:"var(--muted)",textAlign:"center",padding:"14px",background:"var(--off)",borderRadius:6,margin:"10px 16px"}}>No activities added yet</div>}
      <div style={{padding:"10px 16px"}}>
        <button className="btn btn-outline btn-sm" style={{borderStyle:"dashed",color:"var(--emerald)",borderColor:"rgba(16,185,129,0.4)"}} onClick={()=>onAdd({task:"",area:"",status:"Not Started",pct:"",notes:""})}>+ Add Activity</button>
      </div>
    </div>
  );
}

function CrewTable({ rows, onUpdate, onRemove, onAdd }) {
  return (
    <div style={{padding:0}}>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:480}}>
          <thead><tr style={{background:"#f8fafc"}}>
            {["Worker Name / Trade","Company / Sub","Hours Worked","Notes",""].map((h,i)=>(
              <th key={i} style={{fontSize:9.5,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:0.5,padding:"6px 8px",textAlign:"left",borderBottom:"1.5px solid var(--border)",paddingLeft:i===0?16:8,whiteSpace:"nowrap"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.id} style={{borderBottom:"1px solid var(--border-light)"}}>
                <td style={{padding:"4px 8px",paddingLeft:16,minWidth:150}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Name — Trade" value={r.name||""} onChange={e=>onUpdate(r.id,"name",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:130}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Company" value={r.company||""} onChange={e=>onUpdate(r.id,"company",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:70}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px",textAlign:"right"}} type="number" min="0" placeholder="0" value={r.hours||""} onChange={e=>onUpdate(r.id,"hours",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:130}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Notes" value={r.notes||""} onChange={e=>onUpdate(r.id,"notes",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",width:28,textAlign:"center"}}>
                  <button style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:14}} onClick={()=>onRemove(r.id)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length===0&&<div style={{fontSize:12,color:"var(--muted)",textAlign:"center",padding:"14px",background:"var(--off)",borderRadius:6,margin:"10px 16px"}}>No crew added yet</div>}
      <div style={{padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <button className="btn btn-outline btn-sm" style={{borderStyle:"dashed",color:"var(--gold)",borderColor:"rgba(200,151,42,0.4)"}} onClick={()=>onAdd({name:"",company:"",hours:"",notes:""})}>+ Add Crew Row</button>
        {rows.length>0&&<span style={{fontSize:11,color:"var(--muted)"}}>Total on site: <strong>{rows.length} worker{rows.length!==1?"s":""}</strong></span>}
      </div>
    </div>
  );
}

function EqpTable({ rows, onUpdate, onRemove, onAdd }) {
  return (
    <div style={{padding:0}}>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:440}}>
          <thead><tr style={{background:"#f8fafc"}}>
            {["Equipment","Status","Hours Operated","Notes",""].map((h,i)=>(
              <th key={i} style={{fontSize:9.5,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:0.5,padding:"6px 8px",textAlign:"left",borderBottom:"1.5px solid var(--border)",paddingLeft:i===0?16:8,whiteSpace:"nowrap"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.id} style={{borderBottom:"1px solid var(--border-light)"}}>
                <td style={{padding:"4px 8px",paddingLeft:16,minWidth:150}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Equipment description" value={r.equipment||""} onChange={e=>onUpdate(r.id,"equipment",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:110}}>
                  <select className="form-select" style={{fontSize:12,padding:"5px 8px"}} value={r.status||"In Use"} onChange={e=>onUpdate(r.id,"status",e.target.value)}>
                    <option>In Use</option><option>Idle</option><option>Broken Down</option>
                  </select>
                </td>
                <td style={{padding:"4px 8px",minWidth:70}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px",textAlign:"right"}} type="number" min="0" placeholder="0" value={r.hours||""} onChange={e=>onUpdate(r.id,"hours",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:130}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Notes" value={r.notes||""} onChange={e=>onUpdate(r.id,"notes",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",width:28,textAlign:"center"}}>
                  <button style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:14}} onClick={()=>onRemove(r.id)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length===0&&<div style={{fontSize:12,color:"var(--muted)",textAlign:"center",padding:"14px",background:"var(--off)",borderRadius:6,margin:"10px 16px"}}>No equipment added yet</div>}
      <div style={{padding:"10px 16px"}}>
        <button className="btn btn-outline btn-sm" style={{borderStyle:"dashed",color:"var(--sky)",borderColor:"rgba(14,165,233,0.4)"}} onClick={()=>onAdd({equipment:"",status:"In Use",hours:"",notes:""})}>+ Add Equipment Row</button>
      </div>
    </div>
  );
}

function MatTable({ rows, onUpdate, onRemove, onAdd }) {
  return (
    <div style={{padding:0}}>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:620}}>
          <thead><tr style={{background:"#f8fafc"}}>
            {["Material","Qty Received","Qty Used","Unit","Supplier","Notes",""].map((h,i)=>(
              <th key={i} style={{fontSize:9.5,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:0.5,padding:"6px 8px",textAlign:"left",borderBottom:"1.5px solid var(--border)",paddingLeft:i===0?16:8,whiteSpace:"nowrap"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.id} style={{borderBottom:"1px solid var(--border-light)"}}>
                <td style={{padding:"4px 8px",paddingLeft:16,minWidth:140}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Material" value={r.material||""} onChange={e=>onUpdate(r.id,"material",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:70}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px",textAlign:"right"}} type="number" min="0" placeholder="0" value={r.qty_received||""} onChange={e=>onUpdate(r.id,"qty_received",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:70}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px",textAlign:"right"}} type="number" min="0" placeholder="0" value={r.qty_used||""} onChange={e=>onUpdate(r.id,"qty_used",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:70}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="m / bags" value={r.unit||""} onChange={e=>onUpdate(r.id,"unit",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:120}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Supplier" value={r.supplier||""} onChange={e=>onUpdate(r.id,"supplier",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:120}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Notes" value={r.notes||""} onChange={e=>onUpdate(r.id,"notes",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",width:28,textAlign:"center"}}>
                  <button style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:14}} onClick={()=>onRemove(r.id)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length===0&&<div style={{fontSize:12,color:"var(--muted)",textAlign:"center",padding:"14px",background:"var(--off)",borderRadius:6,margin:"10px 16px"}}>No materials added yet</div>}
      <div style={{padding:"10px 16px"}}>
        <button className="btn btn-outline btn-sm" style={{borderStyle:"dashed",color:"#8b5cf6",borderColor:"rgba(139,92,246,0.4)"}} onClick={()=>onAdd({material:"",qty_received:"",qty_used:"",unit:"",supplier:"",notes:""})}>+ Add Material Row</button>
      </div>
    </div>
  );
}

function IssueTable({ rows, onUpdate, onRemove, onAdd }) {
  return (
    <div style={{padding:0}}>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:600}}>
          <thead><tr style={{background:"#fef2f2"}}>
            {["Description","Impact","Cause","Resolution / Action","Status",""].map((h,i)=>(
              <th key={i} style={{fontSize:9.5,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:0.5,padding:"6px 8px",textAlign:"left",borderBottom:"1.5px solid #fecaca",paddingLeft:i===0?16:8,whiteSpace:"nowrap"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.id} style={{borderBottom:"1px solid var(--border-light)"}}>
                <td style={{padding:"4px 8px",paddingLeft:16,minWidth:160}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Describe issue" value={r.description||""} onChange={e=>onUpdate(r.id,"description",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:90}}>
                  <select className="form-select" style={{fontSize:12,padding:"5px 8px"}} value={r.impact||"Medium"} onChange={e=>onUpdate(r.id,"impact",e.target.value)}>
                    <option>Low</option><option>Medium</option><option>High</option>
                  </select>
                </td>
                <td style={{padding:"4px 8px",minWidth:100}}>
                  <select className="form-select" style={{fontSize:12,padding:"5px 8px"}} value={r.cause||"Other"} onChange={e=>onUpdate(r.id,"cause",e.target.value)}>
                    <option>Weather</option><option>Materials</option><option>Labour</option><option>Design</option><option>Client</option><option>Other</option>
                  </select>
                </td>
                <td style={{padding:"4px 8px",minWidth:150}}>
                  <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Action taken" value={r.resolution||""} onChange={e=>onUpdate(r.id,"resolution",e.target.value)}/>
                </td>
                <td style={{padding:"4px 8px",minWidth:90}}>
                  <select className="form-select" style={{fontSize:12,padding:"5px 8px"}} value={r.status||"Open"} onChange={e=>onUpdate(r.id,"status",e.target.value)}>
                    <option>Open</option><option>Resolved</option>
                  </select>
                </td>
                <td style={{padding:"4px 8px",width:28,textAlign:"center"}}>
                  <button style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:14}} onClick={()=>onRemove(r.id)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length===0&&<div style={{fontSize:12,color:"var(--muted)",textAlign:"center",padding:"14px",background:"#fef9f9",borderRadius:6,margin:"10px 16px"}}>No issues logged — great!</div>}
      <div style={{padding:"10px 16px"}}>
        <button className="btn btn-outline btn-sm" style={{borderStyle:"dashed",color:"var(--ruby)",borderColor:"rgba(239,68,68,0.4)"}} onClick={()=>onAdd({description:"",impact:"Medium",cause:"Other",resolution:"",status:"Open"})}>+ Add Issue</button>
      </div>
    </div>
  );
}

// ─── REPORT FORM HELPERS — defined outside ReportsModule to prevent input focus loss ───

function ReportSecHead({num, icon, title, color, action}) {
  return (
    <div style={{background:color||"var(--ink)",padding:"11px 18px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <div style={{width:30,height:30,borderRadius:8,background:"rgba(255,255,255,0.13)",border:"1px solid rgba(255,255,255,0.1)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <Icon name={icon} size={16} style={{color:"rgba(255,255,255,0.9)"}}/>
        </div>
        <span style={{color:"#fff",fontWeight:700,fontSize:13}}>{num} — {title}</span>
      </div>
      {action}
    </div>
  );
}

function ReportFI({label,field,type="text",placeholder,options,ta,rows=2,form,onChange}) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {options ? (
        <select className="form-select" value={form[field]||""} onChange={e=>onChange(field,e.target.value)}>
          <option value="" disabled>Select…</option>
          {options.map(o=><option key={o} value={o}>{o}</option>)}
        </select>
      ) : ta ? (
        <textarea className="form-control" rows={rows} placeholder={placeholder}
          value={form[field]||""} onChange={e=>onChange(field,e.target.value)}/>
      ) : (
        <input className="form-control" type={type} placeholder={placeholder}
          value={form[field]||""} onChange={e=>onChange(field,e.target.value)}/>
      )}
    </div>
  );
}

function ReportsModule({ userId }) {
  const today = () => new Date().toISOString().slice(0,10);
  const [reports,  setReports]  = useState([]);
  const [jobs,     setJobs]     = useState([]);
  const [clients,  setClients]  = useState([]);
  const [business, setBusiness] = useState({});
  const [loading,  setLoading]  = useState(true);
  const [view,     setView]     = useState("list");
  const [editId,   setEditId]   = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saveError,     setSaveError]     = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window!=="undefined"&&window.innerWidth<768);

  const emptyForm = {
    report_type:"weekly", job_id:"", job_title:"", client_id:"", client_name:"",
    site_address:"", date_from:today(), date_to:today(), prepared_by:"", supervisor:"",
    overall_status:"On Track", overall_pct_complete:0,
    weather:"Sunny", temp_c:"", site_conditions:"Normal", weather_notes:"",
    work_items:"[]", crew_items:"[]", equipment_items:"[]", material_items:"[]",
    issues:"[]", milestones:"[]", upcoming_milestones:"[]", next_plan:"[]",
    photos:"[]", visitors:"[]",
    safety_briefing:true, incidents:false, incident_description:"",
    ppe_compliance:"Full Compliance", inspector_visit:"",
    contract_value:"", budget_to_date:"", spent_to_date:"",
    signed_by:"", reviewed_by:"", client_ack:"", notes:"",
  };
  const [form, setForm] = useState(emptyForm);

  const parseJ = (v) => { try { return typeof v==="string" ? JSON.parse(v||"[]") : (v||[]); } catch { return []; } };
  const setArr = (field, val) => setForm(f=>({...f,[field]:JSON.stringify(val)}));
  const getArr = (field) => parseJ(form[field]);

  const refresh = async () => {
    const [r,j,c,b] = await Promise.all([
      DB.getList("reports",userId), DB.getList("jobs",userId),
      DB.getList("clients",userId), DB.get("business",userId),
    ]);
    setReports(r||[]); setJobs(j||[]); setClients(c||[]); setBusiness(b||{}); setLoading(false);
  };
  useEffect(()=>{ refresh(); },[]);
  useEffect(()=>{
    const onResize = () => setIsMobile(window.innerWidth<768);
    window.addEventListener("resize",onResize);
    return ()=>window.removeEventListener("resize",onResize);
  },[]);

  const openNew = () => {
    const num = `RPT-${String((reports.length+1)).padStart(3,"0")}`;
    setForm({...emptyForm, report_number:num, prepared_by:business.contact_name||""});
    setEditId(null); setView("form"); window.scrollTo({top:0,behavior:"smooth"});
  };

  const openEdit = (r) => {
    setForm({
      ...emptyForm, ...r,
      work_items:     typeof r.work_items==="string"     ? r.work_items     : JSON.stringify(r.work_items||[]),
      crew_items:     typeof r.crew_items==="string"     ? r.crew_items     : JSON.stringify(r.crew_items||[]),
      equipment_items:typeof r.equipment_items==="string"? r.equipment_items: JSON.stringify(r.equipment_items||[]),
      material_items: typeof r.material_items==="string" ? r.material_items : JSON.stringify(r.material_items||[]),
      issues:         typeof r.issues==="string"         ? r.issues         : JSON.stringify(r.issues||[]),
      milestones:     typeof r.milestones==="string"     ? r.milestones     : JSON.stringify(r.milestones||[]),
      upcoming_milestones: typeof r.upcoming_milestones==="string" ? r.upcoming_milestones : JSON.stringify(r.upcoming_milestones||[]),
      next_plan:      typeof r.next_plan==="string"      ? r.next_plan      : JSON.stringify(r.next_plan||[]),
      photos:         typeof r.photos==="string"         ? r.photos         : JSON.stringify(r.photos||[]),
    });
    setEditId(r.id); setView("form"); window.scrollTo({top:0,behavior:"smooth"});
  };

  const buildPayload = (status) => ({
    user_id: userId,
    report_number: form.report_number||"",
    report_type:   form.report_type,
    job_id:        form.job_id||null,
    job_title:     form.job_title||"",
    client_id:     form.client_id||null,
    client_name:   form.client_name||"",
    site_address:  form.site_address||"",
    date_from:     form.date_from||null,
    date_to:       form.date_to||null,
    prepared_by:   form.prepared_by||"",
    supervisor:    form.supervisor||"",
    overall_status:form.overall_status||"On Track",
    overall_pct_complete: parseFloat(form.overall_pct_complete||0),
    weather:       form.weather||"",
    temp_c:        form.temp_c!==""?parseFloat(form.temp_c):null,
    site_conditions:form.site_conditions||"",
    weather_notes: form.weather_notes||"",
    work_items:    form.work_items||"[]",
    crew_items:    form.crew_items||"[]",
    equipment_items:form.equipment_items||"[]",
    material_items:form.material_items||"[]",
    issues:        form.issues||"[]",
    milestones:    form.milestones||"[]",
    upcoming_milestones:form.upcoming_milestones||"[]",
    next_plan:     form.next_plan||"[]",
    photos:        form.photos||"[]",
    visitors:      form.visitors||"[]",
    safety_briefing:!!form.safety_briefing,
    incidents:     !!form.incidents,
    incident_description:form.incident_description||"",
    ppe_compliance:form.ppe_compliance||"",
    inspector_visit:form.inspector_visit||"",
    contract_value:form.contract_value!==""?parseFloat(form.contract_value):null,
    budget_to_date:form.budget_to_date!==""?parseFloat(form.budget_to_date):null,
    spent_to_date: form.spent_to_date!==""?parseFloat(form.spent_to_date):null,
    signed_by:     form.signed_by||"",
    reviewed_by:   form.reviewed_by||"",
    client_ack:    form.client_ack||"",
    notes:         form.notes||"",
    status:        status,
  });

  const save = async (status="Draft") => {
    setSaveError(null);
    const payload = buildPayload(status);
    let result;
    if (editId) { result = await DB.update("reports",editId,payload); }
    else { result = await DB.insert("reports",payload); if(result?.id) setEditId(result.id); }
    if (!result) { setSaveError("Save failed — check connection and try again."); return; }
    await refresh(); setView("list"); window.scrollTo({top:0,behavior:"smooth"});
  };

  const deleteReport = async (id) => { await DB.remove("reports",id); await refresh(); setDeleteConfirm(null); };

  const handleJobChange = (jobId) => {
    const job = jobs.find(j=>j.id===jobId);
    setForm(f=>({...f, job_id:jobId, job_title:job?.title||"", client_id:job?.client_id||"",
      client_name:job?.client_name||"", site_address:job?.site_address||f.site_address}));
  };

  // ── Row helpers ──────────────────────────────────────────────────────────────
  const addRow = (field, obj) => setArr(field, [...getArr(field), {...obj, id:Date.now()}]);
  const removeRow = (field, id) => setArr(field, getArr(field).filter(r=>r.id!==id));
  const updateRow = (field, id, key, val) => setArr(field, getArr(field).map(r=>r.id===id?{...r,[key]:val}:r));

  // ── Photo upload ─────────────────────────────────────────────────────────────
  const uploadPhoto = async (file) => {
    const ext = file.name.split(".").pop();
    const path = `reports/${userId}/${Date.now()}.${ext}`;
    const res = await fetch(`${SUPABASE_URL}/storage/v1/object/job-photos/${path}`, {
      method:"POST", headers:{"Authorization":`Bearer ${SUPABASE_ANON_KEY}`,"Content-Type":file.type},
      body:file,
    });
    if (!res.ok) return null;
    return `${SUPABASE_URL}/storage/v1/object/public/job-photos/${path}`;
  };

  const handlePhotoFiles = async (files) => {
    const arr = getArr("photos");
    for (const file of Array.from(files)) {
      const url = await uploadPhoto(file);
      if (url) arr.push({id:Date.now()+Math.random(), url, caption:"", category:"Progress"});
    }
    setArr("photos", arr);
  };

  const isDaily   = form.report_type==="daily";
  const isMonthly = form.report_type==="monthly";
  const showBudget     = !isDaily;
  const showMilestones = !isDaily;
  const showWeather    = !isMonthly;

  // ── Status helpers ─────────────────────────────────────────────────────────
  const statusColors = {"On Track":"var(--emerald)","At Risk":"var(--amber)","Delayed":"var(--ruby)","Completed":"var(--sky)"};
  const fmtDate = (d) => d ? new Date(d+"T00:00:00").toLocaleDateString("en-ZA",{day:"2-digit",month:"short",year:"numeric"}) : "—";
  const typeBadgeColor = {daily:"var(--sky)",weekly:"var(--gold)",monthly:"var(--emerald)"};

  // ── SECTION HEADER ──────────────────────────────────────────────────────────


  // ── FORM INPUT ──────────────────────────────────────────────────────────────


  // ── TABLE COMPONENTS ────────────────────────────────────────────────────────










  // ── LIST VIEW ───────────────────────────────────────────────────────────────
  if (view==="list") return (
    <div>
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:20}}>
        <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={13}/> New Report</button>
      </div>
      {loading ? (
        <div className="empty-state"><Icon name="report" size={36}/><p>Loading reports…</p></div>
      ) : reports.length===0 ? (
        <div className="empty-state">
          <Icon name="report" size={36}/>
          <h3>No reports yet</h3>
          <p>Create daily, weekly or monthly project status reports to share with clients.</p>
          <button className="btn btn-primary" onClick={openNew}>Create First Report</button>
        </div>
      ) : (
        <div className="card" style={{padding:0}}>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--ink)",borderBottom:"2px solid var(--gold)"}}>
                {["Report #","Type","Job","Client","Period","Status","Progress",""].map((h,i)=>(
                  <th key={i} style={{padding:"11px 14px",color:"var(--gold)",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {reports.map((r,i)=>{
                  const sc = {"On Track":"var(--emerald)","At Risk":"var(--amber)","Delayed":"var(--ruby)","Completed":"var(--sky)"}[r.overall_status]||"var(--muted)";
                  const tc = typeBadgeColor[r.report_type]||"var(--gold)";
                  const pct = parseFloat(r.overall_pct_complete||0);
                  const rowBg = i%2===1 ? "#f8fafc" : "#fff";
                  const td = {padding:"10px 14px",fontSize:12,color:"var(--ink)",background:rowBg};
                  return (
                    <tr key={r.id} style={{borderBottom:"1px solid var(--border)"}}>
                      <td style={{...td,fontWeight:700,fontSize:13}}>{r.report_number||"—"}</td>
                      <td style={{...td}}>
                        <span style={{fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:100,background:tc+"20",color:tc,textTransform:"capitalize"}}>{r.report_type||"weekly"}</span>
                      </td>
                      <td style={{...td,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.job_title||"—"}</td>
                      <td style={{...td}}>{r.client_name||"—"}</td>
                      <td style={{...td,fontSize:11,whiteSpace:"nowrap"}}>{r.date_from||""}{r.date_to&&r.date_to!==r.date_from?` – ${r.date_to}`:""}</td>
                      <td style={{...td}}>
                        <span style={{fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:100,background:sc+"18",color:sc}}>{r.overall_status||"—"}</span>
                      </td>
                      <td style={{...td,minWidth:100}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <div style={{flex:1,height:5,borderRadius:3,background:"var(--border-light)"}}>
                            <div style={{width:pct+"%",height:"100%",borderRadius:3,background:`linear-gradient(90deg,var(--gold),#e4ae3a)`}}/>
                          </div>
                          <span style={{fontSize:11,fontWeight:700,minWidth:28,color:"var(--ink)"}}>{pct}%</span>
                        </div>
                      </td>
                      <td style={{padding:"10px 14px",background:rowBg,verticalAlign:"middle"}}>
                        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          <button className="btn btn-outline btn-sm" onClick={()=>openEdit(r)}>Edit</button>
                          <button className="btn btn-outline btn-sm" style={{color:"var(--status-green-fg)"}}
                            onClick={()=>printAsPDF(buildStatusReportHTML(r,business),`Report ${r.report_number||""}`)}>
                            <Icon name="download" size={12}/>PDF
                          </button>

                          <button className="btn btn-outline btn-sm" style={{color:"#25D366"}}
                            onClick={()=>{
                              const txt = encodeURIComponent(`Hi ${r.client_name||""},\n\nPlease find attached the ${r.report_type} status report for ${r.job_title||"your project"}.\nPeriod: ${r.date_from||""} – ${r.date_to||""}\nStatus: ${r.overall_status} · ${r.overall_pct_complete||0}% complete\n\nRegards,\n${business.name||""}`);
                              window.open(`https://wa.me/?text=${txt}`,"_blank");
                            }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.136.561 4.14 1.542 5.876L0 24l6.283-1.507A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818c-1.924 0-3.717-.505-5.265-1.385l-.377-.224-3.729.894.946-3.617-.247-.392A9.797 9.797 0 012.182 12C2.182 6.58 6.58 2.182 12 2.182c5.42 0 9.818 4.398 9.818 9.818 0 5.42-4.398 9.818-9.818 9.818z"/></svg>
                          </button>
                          <button className="btn btn-outline btn-sm" style={{color:"var(--sky)"}}
                            onClick={()=>{
                              const subj = encodeURIComponent(`${(r.report_type||"").charAt(0).toUpperCase()+(r.report_type||"").slice(1)} Status Report — ${r.job_title||"Project"} — ${r.date_to||r.date_from||""}`);
                              const body = encodeURIComponent(`Hi ${r.client_name||""},\n\nPlease find the ${r.report_type} status report attached.\n\nPeriod: ${r.date_from||""} – ${r.date_to||""}\nOverall status: ${r.overall_status}\nProgress: ${r.overall_pct_complete||0}% complete\n\nKind regards,\n${business.name||""}`);
                              const clientJob = clients.find(c=>c.id===r.client_id);
                              window.location.href = `mailto:${clientJob?.email||""}?subject=${subj}&body=${body}`;
                            }}>✉️</button>
                          <button className="btn btn-outline btn-sm" style={{color:"var(--ruby)"}}
                            onClick={()=>setDeleteConfirm(r.id)}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {deleteConfirm && (
        <div className="modal-backdrop" onClick={()=>setDeleteConfirm(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:380}}>
            <div className="modal-header"><h3>Delete Report</h3></div>
            <div className="modal-body"><p>Are you sure? This cannot be undone.</p></div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={()=>setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={()=>deleteReport(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── FORM VIEW ───────────────────────────────────────────────────────────────
  return (
    <div style={{maxWidth:960,margin:"0 auto",paddingBottom:80}}>

      {/* ── MOBILE RESPONSIVE STYLES ── */}
      <style>{`
        .rpt-grid-3 { display:grid; grid-template-columns: repeat(3,1fr); gap:14px; }
        .rpt-grid-2 { display:grid; grid-template-columns: repeat(2,1fr); gap:14px; }
        .rpt-grid-4 { display:grid; grid-template-columns: repeat(4,1fr); gap:14px; }
        .rpt-grid-21 { display:grid; grid-template-columns: 2fr 1fr; gap:14px; }
        .rpt-grid-31 { display:grid; grid-template-columns: 3fr 1fr 1fr auto; gap:8px; align-items:center; }
        .rpt-grid-ms { display:grid; grid-template-columns: 1fr 1fr; gap:16px; }
        @media (max-width: 640px) {
          .rpt-grid-3, .rpt-grid-2, .rpt-grid-4, .rpt-grid-21, .rpt-grid-ms { grid-template-columns: 1fr !important; }
          .rpt-grid-31 { grid-template-columns: 1fr auto auto auto !important; }
        }
      `}</style>

      {/* Mobile notice — only shown on screens < 768px */}
      {isMobile && (
        <div style={{background:"linear-gradient(90deg,rgba(200,151,42,0.15),rgba(200,151,42,0.05))",borderRadius:9,border:"1px solid rgba(200,151,42,0.3)",padding:"10px 14px",marginBottom:16,display:"flex",alignItems:"flex-start",gap:10}}>
          <span style={{fontSize:16,flexShrink:0}}>💻</span>
          <div style={{fontSize:12,color:"var(--ink)"}}>
            <strong>Best on desktop or tablet</strong> — this form contains multiple data tables, photo uploads and document export. You can still fill it in on mobile, but a larger screen gives a much better experience.
          </div>
        </div>
      )}

      {/* Back + report type toggle */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <button className="btn btn-outline btn-sm" onClick={()=>setView("list")}>← Back to Reports</button>
        <div style={{display:"flex",gap:6}}>
          {["daily","weekly","monthly"].map(t=>(
            <button key={t} onClick={()=>setForm(f=>({...f,report_type:t}))}
              style={{padding:"7px 16px",borderRadius:7,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,
                background:form.report_type===t?typeBadgeColor[t]:"var(--border-light)",
                color:form.report_type===t?"var(--ink)":"var(--muted)",transition:"all 0.15s"}}>
              {t.charAt(0).toUpperCase()+t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {saveError && <div className="alert alert-error" style={{marginBottom:14}}>{saveError}</div>}

      {/* ── S1: HEADER ── */}
      <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
        <ReportSecHead num="1" icon="report" title="Report Header" color="var(--ink)"/>
        <div style={{padding:18}}>
          <div className="rpt-grid-3">
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Report Number" field="report_number" placeholder="RPT-001"/>
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Prepared By" field="prepared_by" placeholder="Your name"/>
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Supervisor / Site Foreman" field="supervisor" placeholder="Name"/>
          </div>
          <div className="rpt-grid-21" style={{marginTop:12}}>
            <div className="form-group">
              <label className="form-label">Linked Job</label>
              <select className="form-select" value={form.job_id||""} onChange={e=>handleJobChange(e.target.value)}>
                <option value="">— Select job —</option>
                {jobs.map(j=><option key={j.id} value={j.id}>{j.number?j.number+" — ":""}{j.title}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Overall Project Status</label>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",paddingTop:4}}>
                {["On Track","At Risk","Delayed","Completed"].map(s=>{
                  const c = statusColors[s];
                  const active = form.overall_status===s;
                  return <button key={s} onClick={()=>setForm(f=>({...f,overall_status:s}))}
                    style={{padding:"5px 11px",borderRadius:100,border:`1.5px solid ${c}`,background:active?c:"transparent",color:active?"#fff":c,fontSize:11,fontWeight:700,cursor:"pointer"}}>{s}</button>;
                })}
              </div>
            </div>
          </div>
          <div className="rpt-grid-3" style={{marginTop:12}}>
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Period From" field="date_from" type="date"/>
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Period To" field="date_to" type="date"/>
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Site Address" field="site_address" placeholder="Auto-fills from job"/>
          </div>
        </div>
      </div>

      {/* ── S2: WEATHER (daily/weekly) ── */}
      {showWeather && (
        <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
          <ReportSecHead num="2" icon="weather" title="Weather & Site Conditions" color="#1e6b8a"/>
          <div style={{padding:18}}>
            <div className="rpt-grid-4">
              <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Weather" field="weather" options={["Sunny","Partly Cloudy","Overcast","Rainy","Windy","Extreme"]}/>
              <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Temperature (°C)" field="temp_c" type="number" placeholder="28"/>
              <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Site Conditions" field="site_conditions" options={["Normal","Wet / Muddy","Restricted Access","Hazardous"]}/>
              <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Rain / Wind Notes" field="weather_notes" placeholder="Brief notes"/>
            </div>
          </div>
        </div>
      )}

      {/* ── S3: WORK COMPLETED ── */}
      <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
        <ReportSecHead num="3" icon="hammer" title="Work Completed This Period" color="#1a5c3a"/>
        <WorkTable rows={getArr("work_items")} onUpdate={(id,k,v)=>updateRow("work_items",id,k,v)} onRemove={(id)=>removeRow("work_items",id)} onAdd={(obj)=>addRow("work_items",obj)}/>
      </div>

      {/* ── S4: CREW ── */}
      <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
        <ReportSecHead num="4" icon="hardhat" title="Crew / Labour on Site" color="#5c3a1a"/>
        <CrewTable rows={getArr("crew_items")} onUpdate={(id,k,v)=>updateRow("crew_items",id,k,v)} onRemove={(id)=>removeRow("crew_items",id)} onAdd={(obj)=>addRow("crew_items",obj)}/>
      </div>

      {/* ── S5: EQUIPMENT ── */}
      <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
        <ReportSecHead num="5" icon="crane" title="Equipment on Site" color="#1a4a5c"/>
        <EqpTable rows={getArr("equipment_items")} onUpdate={(id,k,v)=>updateRow("equipment_items",id,k,v)} onRemove={(id)=>removeRow("equipment_items",id)} onAdd={(obj)=>addRow("equipment_items",obj)}/>
      </div>

      {/* ── S6: MATERIALS ── */}
      <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
        <ReportSecHead num="6" icon="box" title="Materials Received / Used" color="#3a1a5c"/>
        <MatTable rows={getArr("material_items")} onUpdate={(id,k,v)=>updateRow("material_items",id,k,v)} onRemove={(id)=>removeRow("material_items",id)} onAdd={(obj)=>addRow("material_items",obj)}/>
      </div>

      {/* ── S7: ISSUES ── */}
      <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
        <ReportSecHead num="7" icon="alert" title="Issues, Delays & Risks" color="#7a1a1a"/>
        <IssueTable rows={getArr("issues")} onUpdate={(id,k,v)=>updateRow("issues",id,k,v)} onRemove={(id)=>removeRow("issues",id)} onAdd={(obj)=>addRow("issues",obj)}/>
      </div>

      {/* ── S8: SAFETY ── */}
      <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
        <ReportSecHead num="8" icon="shield" title="Safety" color="#1a3a1a"/>
        <div style={{padding:18}}>
          <div className="rpt-grid-3">
            <div className="form-group">
              <label className="form-label">Safety Briefing Held</label>
              <div style={{display:"flex",gap:16,paddingTop:6}}>
                {[["Yes",true],["No",false]].map(([l,v])=>(
                  <label key={l} style={{display:"flex",alignItems:"center",gap:6,fontSize:13,cursor:"pointer"}}>
                    <input type="radio" checked={form.safety_briefing===v} onChange={()=>setForm(f=>({...f,safety_briefing:v}))}/> {l}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Incidents / Near-Misses</label>
              <div style={{display:"flex",gap:16,paddingTop:6}}>
                {[["Yes",true],["No",false]].map(([l,v])=>(
                  <label key={l} style={{display:"flex",alignItems:"center",gap:6,fontSize:13,cursor:"pointer"}}>
                    <input type="radio" checked={form.incidents===v} onChange={()=>setForm(f=>({...f,incidents:v}))}/> {l}
                  </label>
                ))}
              </div>
            </div>
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="PPE Compliance" field="ppe_compliance" options={["Full Compliance","Partial","Non-Compliant"]}/>
          </div>
          {form.incidents && (
            <div className="form-grid" style={{gridTemplateColumns:"1fr",marginTop:8}}>
              <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Incident Description" field="incident_description" ta rows={2} placeholder="Describe incident or near-miss…"/>
            </div>
          )}
          <div className="rpt-grid-2" style={{marginTop:8}}>
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Visitors on Site" field="visitors_text" ta rows={1} placeholder="Name, company, purpose"/>
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Inspector Visit / Outcome" field="inspector_visit" placeholder="e.g. Municipality electrical — PASSED"/>
          </div>
        </div>
      </div>

      {/* ── S9: BUDGET (weekly/monthly) ── */}
      {showBudget && (
        <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
          <ReportSecHead num="9" icon="currency" title="Budget Snapshot" color="#1a3a5c"/>
          <div style={{padding:18}}>
            <div className="rpt-grid-4">
              <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Contract Value (R)" field="contract_value" type="number" placeholder="450000"/>
              <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Budget to Date (R)" field="budget_to_date" type="number" placeholder="180000"/>
              <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Spent to Date (R)" field="spent_to_date" type="number" placeholder="174500"/>
              <div className="form-group">
                <label className="form-label">Budget Status</label>
                {(() => {
                  const v = parseFloat(form.spent_to_date||0);
                  const b = parseFloat(form.budget_to_date||0);
                  const diff = b - v;
                  if (!b && !v) return <div style={{padding:"9px 12px",borderRadius:7,background:"var(--off)",fontSize:12,color:"var(--muted)"}}>Enter values above</div>;
                  const ok = diff >= 0;
                  return <div style={{padding:"9px 12px",borderRadius:7,background:ok?"rgba(16,185,129,0.08)":"rgba(239,68,68,0.08)",border:`1.5px solid ${ok?"rgba(16,185,129,0.3)":"rgba(239,68,68,0.3)"}`}}>
                    <span style={{color:ok?"var(--emerald)":"var(--ruby)",fontWeight:700,fontSize:13}}>{ok?"Under Budget":"Over Budget"}</span>
                    <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>Variance: R {Math.abs(diff).toLocaleString("en-ZA",{minimumFractionDigits:2})} {ok?"under":"over"}</div>
                  </div>;
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── S10: MILESTONES (weekly/monthly) ── */}
      {showMilestones && (
        <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
          <ReportSecHead num="10" icon="target" title="Milestones & Overall Progress" color="#2d1a5c"/>
          <div style={{padding:18}}>
            <div className="form-group" style={{marginBottom:16}}>
              <label className="form-label">Overall Project % Complete</label>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <input type="range" min={0} max={100} value={form.overall_pct_complete||0}
                  onChange={e=>setForm(f=>({...f,overall_pct_complete:e.target.value}))}
                  style={{flex:1,accentColor:"var(--gold)"}}/>
                <span style={{fontSize:22,fontWeight:800,color:"var(--gold)",minWidth:52,fontFamily:"var(--font-display)"}}>{form.overall_pct_complete||0}%</span>
              </div>
              <div style={{height:7,borderRadius:4,background:"var(--border-light)",marginTop:6}}>
                <div style={{width:(form.overall_pct_complete||0)+"%",height:"100%",borderRadius:4,background:"linear-gradient(90deg,var(--gold),#e4ae3a)",transition:"width 0.2s"}}/>
              </div>
            </div>
            <div className="rpt-grid-ms">
              <div>
                <label className="form-label" style={{marginBottom:8,display:"block"}}>Milestones Completed This Period</label>
                {getArr("milestones").map(m=>(
                  <div key={m.id} style={{display:"grid",gridTemplateColumns:"1fr minmax(0,110px) auto",gap:6,marginBottom:6,alignItems:"center"}}>
                    <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Milestone name" value={m.name||""} onChange={e=>updateRow("milestones",m.id,"name",e.target.value)}/>
                    <input className="form-control" style={{fontSize:12,padding:"5px 8px",width:110}} type="date" value={m.date||""} onChange={e=>updateRow("milestones",m.id,"date",e.target.value)}/>
                    <button style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:14}} onClick={()=>removeRow("milestones",m.id)}>✕</button>
                  </div>
                ))}
                <button className="btn btn-outline btn-sm" style={{borderStyle:"dashed",color:"var(--emerald)",borderColor:"rgba(16,185,129,0.4)",marginTop:4}} onClick={()=>addRow("milestones",{name:"",date:""})}>+ Add Milestone</button>
              </div>
              <div>
                <label className="form-label" style={{marginBottom:8,display:"block"}}>Upcoming — Next Period</label>
                {getArr("upcoming_milestones").map(m=>(
                  <div key={m.id} style={{display:"grid",gridTemplateColumns:"1fr minmax(0,110px) auto",gap:6,marginBottom:6,alignItems:"center"}}>
                    <input className="form-control" style={{fontSize:12,padding:"5px 8px"}} placeholder="Milestone" value={m.name||""} onChange={e=>updateRow("upcoming_milestones",m.id,"name",e.target.value)}/>
                    <input className="form-control" style={{fontSize:12,padding:"5px 8px",width:110}} type="date" value={m.target_date||""} onChange={e=>updateRow("upcoming_milestones",m.id,"target_date",e.target.value)}/>
                    <button style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:14}} onClick={()=>removeRow("upcoming_milestones",m.id)}>✕</button>
                  </div>
                ))}
                <button className="btn btn-outline btn-sm" style={{borderStyle:"dashed",color:"var(--sky)",borderColor:"rgba(14,165,233,0.4)",marginTop:4}} onClick={()=>addRow("upcoming_milestones",{name:"",target_date:""})}>+ Add Upcoming</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── S11: PHOTOS ── */}
      <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
        <ReportSecHead num={showMilestones?"11":"9"} icon="photo" title={`Photos (${getArr("photos").length})`} color="#3a1a1a"/>
        <div style={{padding:18}}>
          {getArr("photos").length>0 && (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:12,marginBottom:14}}>
              {getArr("photos").map(p=>{
                const catC = {Issue:"var(--ruby)",Safety:"var(--emerald)",Completed:"var(--sky)",Before:"var(--muted)"}[p.category]||"var(--gold)";
                return (
                  <div key={p.id} style={{borderRadius:8,overflow:"hidden",border:"1.5px solid var(--border)"}}>
                    <div style={{height:100,background:"var(--off)",position:"relative",overflow:"hidden"}}>
                      {p.url ? <img src={p.url} style={{width:"100%",height:"100%",objectFit:"cover"}} alt={p.caption}loading="lazy" /> : <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",fontSize:28}}>🖼</div>}
                      <span style={{position:"absolute",top:5,right:5,fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:100,background:catC+"20",color:catC,border:`1px solid ${catC}40`}}>{p.category}</span>
                    </div>
                    <div style={{padding:"7px 8px",background:"var(--off)"}}>
                      <input className="form-control" style={{fontSize:11,padding:"4px 7px",marginBottom:5}} placeholder="Caption" value={p.caption||""} onChange={e=>updateRow("photos",p.id,"caption",e.target.value)}/>
                      <select className="form-select" style={{fontSize:11,padding:"4px 7px"}} value={p.category||"Progress"} onChange={e=>updateRow("photos",p.id,"category",e.target.value)}>
                        <option>Before</option><option>Progress</option><option>Issue</option><option>Completed</option><option>Safety</option>
                      </select>
                    </div>
                    <div style={{padding:"4px 8px 8px"}}>
                      <button className="btn btn-outline btn-sm" style={{width:"100%",fontSize:11,color:"var(--ruby)",borderColor:"rgba(239,68,68,0.3)"}} onClick={()=>removeRow("photos",p.id)}>Remove</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <label style={{display:"block",border:"2px dashed var(--border)",borderRadius:8,padding:"20px",textAlign:"center",cursor:"pointer",background:"var(--off)"}}>
            <input type="file" accept="image/*" multiple style={{display:"none"}} onChange={e=>handlePhotoFiles(e.target.files)}/>
            <div style={{fontSize:24,marginBottom:6}}>📷</div>
            <div style={{fontSize:12,fontWeight:700}}>Click to upload photos</div>
            <div style={{fontSize:11,color:"var(--muted)",marginTop:3}}>JPG, PNG, WEBP · max 10MB each · multiple allowed</div>
          </label>
        </div>
      </div>

      {/* ── S12: NEXT PERIOD PLAN ── */}
      <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
        <ReportSecHead num={showMilestones?"12":"10"} icon="clock" title="Next Period Plan" color="#1a1a3a"/>
        <div style={{padding:18}}>
          {getArr("next_plan").map(p=>(
            <div key={p.id} className="rpt-grid-31">
              <input className="form-control" style={{fontSize:12,padding:"6px 8px"}} placeholder="Planned task" value={p.task||""} onChange={e=>updateRow("next_plan",p.id,"task",e.target.value)}/>
              <input className="form-control" style={{fontSize:12,padding:"6px 8px"}} placeholder="Responsible" value={p.responsible||""} onChange={e=>updateRow("next_plan",p.id,"responsible",e.target.value)}/>
              <input className="form-control" type="date" style={{fontSize:12,padding:"6px 8px"}} value={p.target_date||""} onChange={e=>updateRow("next_plan",p.id,"target_date",e.target.value)}/>
              <button style={{background:"#fee2e2",border:"none",color:"var(--ruby)",borderRadius:6,padding:"6px 10px",cursor:"pointer"}}>✕</button>
            </div>
          ))}
          <button className="btn btn-outline btn-sm" style={{borderStyle:"dashed"}} onClick={()=>addRow("next_plan",{task:"",responsible:"",target_date:""})}>+ Add Planned Task</button>
          <div className="form-group" style={{marginTop:14}}>
            <label className="form-label">Resources Required</label>
            <textarea className="form-control" rows={2} placeholder="Materials, equipment or people needed next period…" value={form.resources_required||""} onChange={e=>setForm(f=>({...f,resources_required:e.target.value}))}/>
          </div>
        </div>
      </div>

      {/* ── S13: SIGN-OFF ── */}
      <div className="card" style={{marginBottom:14,border:"1.5px solid var(--border)",padding:0,overflow:"hidden"}}>
        <ReportSecHead num={showMilestones?"13":"11"} icon="pen" title="Sign-Off & Notes" color="#2a2a2a"/>
        <div style={{padding:18}}>
          <div className="rpt-grid-3">
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Prepared By (name)" field="signed_by" placeholder="Your full name"/>
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Reviewed By (optional)" field="reviewed_by" placeholder="Project manager"/>
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="Client Acknowledgement" field="client_ack" placeholder="Client contact name"/>
          </div>
          <div className="form-group" style={{marginTop:12}}>
            <ReportFI form={form} onChange={(f,v)=>setForm(p=>({...p,[f]:v}))} label="General Notes / Comments" field="notes" ta rows={3} placeholder="Additional notes for client or internal record…"/>
          </div>
        </div>
      </div>

      {/* ── ACTION BAR ── */}
      <div className="card" style={{background:"var(--ink)",border:"none"}}>
        <div style={{padding:"16px 20px",display:"flex",flexWrap:"wrap",gap:10,alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button className="btn" style={{background:"rgba(255,255,255,0.1)",color:"#fff"}} onClick={()=>save("Draft")}>💾 Save Draft</button>
            <button className="btn" style={{background:"var(--emerald)",color:"#fff"}} onClick={()=>save("Final")}>✅ Finalise Report</button>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button className="btn" style={{background:"rgba(255,255,255,0.1)",color:"#fff"}}
              onClick={()=>printAsPDF(buildStatusReportHTML({...buildPayload("Final"),job_title:form.job_title,client_name:form.client_name},business),`Report ${form.report_number||""}`)}>
              🖨 View PDF
            </button>

            <button className="btn" style={{background:"#25D366",color:"#fff"}}
              onClick={()=>{
                const r=form;
                const txt=encodeURIComponent(`Hi ${r.client_name||""},\n\nPlease find the ${r.report_type} status report for ${r.job_title||"your project"}.\nPeriod: ${r.date_from||""} – ${r.date_to||""}\nStatus: ${r.overall_status} · ${r.overall_pct_complete||0}% complete\n\nRegards,\n${business.name||""}`);
                window.open(`https://wa.me/?text=${txt}`,"_blank");
              }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.136.561 4.14 1.542 5.876L0 24l6.283-1.507A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818c-1.924 0-3.717-.505-5.265-1.385l-.377-.224-3.729.894.946-3.617-.247-.392A9.797 9.797 0 012.182 12C2.182 6.58 6.58 2.182 12 2.182c5.42 0 9.818 4.398 9.818 9.818 0 5.42-4.398 9.818-9.818 9.818z"/></svg>
              WhatsApp
            </button>
            <button className="btn" style={{background:"var(--sky)",color:"#fff"}}
              onClick={()=>{
                const r=form;
                const subj=encodeURIComponent(`${(r.report_type||"").charAt(0).toUpperCase()+(r.report_type||"").slice(1)} Status Report — ${r.job_title||"Project"} — ${r.date_to||r.date_from||""}`);
                const body=encodeURIComponent(`Hi ${r.client_name||""},\n\nPlease find the ${r.report_type} status report attached.\n\nPeriod: ${r.date_from||""} – ${r.date_to||""}\nOverall status: ${r.overall_status}\nProgress: ${r.overall_pct_complete||0}% complete\n\nKind regards,\n${business.name||""}`);
                const clientObj=clients.find(c=>c.id===form.client_id);
                window.location.href=`mailto:${clientObj?.email||""}?subject=${subj}&body=${body}`;
              }}>
              ✉️ Email
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}


// ─── PORTALS MODULE ────────────────────────────────────────────────────────────
function PortalsModule({ userId }) {
  const [clients, setClients]   = useState([]);
  const [messages, setMessages] = useState([]);
  const [requests, setRequests] = useState([]);
  const [quotes, setQuotes]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [view, setView]         = useState("list");      // list | chat | requests
  const [activeClient, setActiveClient] = useState(null);
  const [replyMsg, setReplyMsg] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [portalCopied, setPortalCopied] = useState(null);
  const chatEndRef = useRef(null);

  const SUPA = process.env.REACT_APP_SUPABASE_URL || "";
  const hdrs = { "apikey": SUPABASE_ANON_KEY, "Authorization": `Bearer ${SUPABASE_ANON_KEY}`, "Content-Type": "application/json" };

  const portalGet = async (table, filter) => {
    const r = await fetch(`${SUPA}/rest/v1/${table}?${filter}&limit=200`, { headers: hdrs });
    return r.ok ? r.json() : [];
  };
  const portalInsert = async (table, body) => {
    const r = await fetch(`${SUPA}/rest/v1/${table}`, {
      method:"POST", headers:{...hdrs, Prefer:"return=representation"}, body:JSON.stringify(body)
    });
    return r.ok ? r.json() : null;
  };
  const portalPatch = async (table, id, body) => {
    await fetch(`${SUPA}/rest/v1/${table}?id=eq.${id}`, {
      method:"PATCH", headers: hdrs, body:JSON.stringify(body)
    });
  };

  const refresh = async () => {
    const [c, m, r, q] = await Promise.all([
      DB.getList("clients", userId),
      portalGet("fj_portal_messages", `user_id=eq.${userId}&order=created_at.asc`),
      portalGet("fj_job_requests",    `user_id=eq.${userId}&order=created_at.desc`),
      DB.getList("quotes", userId),
    ]);
    setClients(c||[]); setMessages(m||[]); setRequests(r||[]); setQuotes(q||[]);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    const poll = setInterval(() => { refresh(); }, 60000); // Reduced from 15s — Realtime handles live updates
    return () => clearInterval(poll);
  }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages, activeClient]);

  const genToken = async (client) => {
    let token = client.portal_token;
    if (!token || !client.portal_enabled) {
      token = ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,c=>(c^crypto.getRandomValues(new Uint8Array(1))[0]&15>>c/4).toString(16));
      await DB.update("clients", client.id, { portal_token: token, portal_enabled: true });
      await refresh();
    }
    const link = `https://portal.fieldjob.getdiswayne.com?token=${token}`;
    try { await navigator.clipboard.writeText(link); } catch {}
    setPortalCopied(client.id);
    setTimeout(() => setPortalCopied(null), 3000);
  };

  const disablePortal = async (client) => {
    await DB.update("clients", client.id, { portal_enabled: false });
    await refresh();
    if (activeClient?.id === client.id) setView("list");
  };

  const sendReply = async () => {
    if (!replyMsg.trim() || replySending || !activeClient) return;
    setReplySending(true);
    await portalInsert("fj_portal_messages", {
      client_id: activeClient.id, user_id: userId,
      sender: "contractor", message: replyMsg.trim(),
      read: true, created_at: new Date().toISOString(),
    });
    // Mark all client messages as read
    const unread = messages.filter(m => m.client_id === activeClient.id && m.sender === "client" && !m.read);
    await Promise.all(unread.map(m => portalPatch("fj_portal_messages", m.id, { read: true })));
    setReplyMsg("");
    await refresh();
    setReplySending(false);
  };

  const updateRequestStatus = async (req, status) => {
    await portalPatch("fj_job_requests", req.id, { status });
    await refresh();
  };

  const openChat = async (client) => {
    setActiveClient(client);
    setView("chat");
    // Mark all unread client messages as read
    const unread = messages.filter(m => m.client_id === client.id && m.sender === "client" && !m.read);
    await Promise.all(unread.map(m => portalPatch("fj_portal_messages", m.id, { read: true })));
    await refresh();
  };

  if (loading) return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"60px 0"}}>
      <div style={{fontSize:13,color:"var(--muted)"}}>Loading portals…</div>
    </div>
  );

  const portalClients = clients.filter(c => c.portal_token && c.portal_enabled);
  const noPortalClients = clients.filter(c => !c.portal_token || !c.portal_enabled);

  const clientMsgs     = c => messages.filter(m => m.client_id === c.id);
  const unreadCount    = c => messages.filter(m => m.client_id === c.id && m.sender === "client" && !m.read).length;
  const clientRequests = c => requests.filter(r => r.client_id === c.id);
  const pendingReqs    = c => clientRequests(c).filter(r => r.status === "Pending").length;
  const totalBadge     = c => unreadCount(c) + pendingReqs(c);

  // ── LIST VIEW ───────────────────────────────────────────────────────────────
  if (view === "list") return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
        <div>
          <h2 style={{fontSize:18,fontWeight:700,marginBottom:2}}>Client Portals</h2>
          <div style={{fontSize:12,color:"var(--muted)"}}>{portalClients.length} active · {clients.length} total clients</div>
        </div>
      </div>

      {/* Active portals */}
      {portalClients.length > 0 && (
        <div style={{marginBottom:28}}>
          <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.6px",color:"var(--muted)",marginBottom:10}}>Active Portals</div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {portalClients.map(c => {
              const badge = totalBadge(c);
              const lastMsg = clientMsgs(c).slice(-1)[0];
              const reqs = clientRequests(c);
              return (
                <div key={c.id} style={{background:"var(--card)",borderRadius:12,border:"1px solid var(--border)",overflow:"hidden",boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
                  {/* Client header */}
                  <div style={{padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,flex:1,minWidth:0}}>
                      <div style={{width:40,height:40,borderRadius:11,background:"var(--ink)",color:"var(--gold-light)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,flexShrink:0}}>
                        {c.name?.charAt(0)||"?"}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:14,display:"flex",alignItems:"center",gap:8}}>
                          {c.name}
                          {badge > 0 && <span style={{background:"#ef4444",color:"white",borderRadius:10,fontSize:9,padding:"2px 6px",fontWeight:700}}>{badge}</span>}
                        </div>
                        <div style={{fontSize:11,color:"var(--muted)",marginTop:1}}>
                          {c.email || c.phone || "No contact info"}
                        </div>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6,flexShrink:0}}>
                      <button onClick={() => genToken(c)}
                        style={{padding:"6px 11px",borderRadius:7,border:"1px solid var(--gold-b)",background:"var(--gold-p)",color:"var(--gold)",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",gap:5}}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                        {portalCopied===c.id ? "Copied!" : "Copy Link"}
                      </button>
                      <button onClick={() => disablePortal(c)}
                        style={{padding:"6px 10px",borderRadius:7,border:"1px solid var(--border)",background:"white",color:"var(--muted)",fontSize:11,fontWeight:600}}>
                        Disable
                      </button>
                    </div>
                  </div>

                  {/* Action rows */}
                  <div style={{borderTop:"1px solid var(--border)",display:"grid",gridTemplateColumns:"1fr 1fr"}}>
                    {/* Chat */}
                    <button onClick={() => openChat(c)}
                      style={{padding:"11px 16px",background:"transparent",border:"none",borderRight:"1px solid var(--border)",display:"flex",alignItems:"center",gap:8,cursor:"pointer",transition:"background 0.13s"}}
                      onMouseEnter={e=>e.currentTarget.style.background="var(--off)"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <div style={{width:28,height:28,borderRadius:8,background:unreadCount(c)>0?"rgba(59,130,246,0.12)":"var(--off)",border:`1px solid ${unreadCount(c)>0?"rgba(59,130,246,0.25)":"var(--border)"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={unreadCount(c)>0?"#3b82f6":"var(--muted)"} strokeWidth="2.2" strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                      </div>
                      <div style={{textAlign:"left",flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:600,color:unreadCount(c)>0?"#2563eb":"var(--ink)",display:"flex",alignItems:"center",gap:6}}>
                          Messages
                          {unreadCount(c)>0 && <span style={{background:"#ef4444",color:"white",borderRadius:10,fontSize:9,padding:"1px 5px",fontWeight:700}}>{unreadCount(c)}</span>}
                        </div>
                        <div style={{fontSize:10.5,color:"var(--muted)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:120}}>
                          {lastMsg ? lastMsg.message.slice(0,30)+(lastMsg.message.length>30?"…":"") : "No messages yet"}
                        </div>
                      </div>
                    </button>

                    {/* Requests */}
                    <button onClick={() => { setActiveClient(c); setView("requests"); }}
                      style={{padding:"11px 16px",background:"transparent",border:"none",display:"flex",alignItems:"center",gap:8,cursor:"pointer",transition:"background 0.13s"}}
                      onMouseEnter={e=>e.currentTarget.style.background="var(--off)"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <div style={{width:28,height:28,borderRadius:8,background:pendingReqs(c)>0?"rgba(16,185,129,0.1)":"var(--off)",border:`1px solid ${pendingReqs(c)>0?"rgba(16,185,129,0.25)":"var(--border)"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={pendingReqs(c)>0?"#10b981":"var(--muted)"} strokeWidth="2.2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      </div>
                      <div style={{textAlign:"left"}}>
                        <div style={{fontSize:12,fontWeight:600,color:pendingReqs(c)>0?"#059669":"var(--ink)",display:"flex",alignItems:"center",gap:6}}>
                          Requests
                          {pendingReqs(c)>0 && <span style={{background:"#ef4444",color:"white",borderRadius:10,fontSize:9,padding:"1px 5px",fontWeight:700}}>{pendingReqs(c)}</span>}
                        </div>
                        <div style={{fontSize:10.5,color:"var(--muted)"}}>{reqs.length} total · {pendingReqs(c)} pending</div>
                      </div>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Clients without portals */}
      {noPortalClients.length > 0 && (
        <div>
          <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.6px",color:"var(--muted)",marginBottom:10}}>No Portal Yet</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {noPortalClients.map(c => (
              <div key={c.id} style={{background:"var(--card)",borderRadius:12,border:"1px solid var(--border)",padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:34,height:34,borderRadius:9,background:"var(--off)",border:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:"var(--muted)"}}>
                    {c.name?.charAt(0)||"?"}
                  </div>
                  <div>
                    <div style={{fontWeight:600,fontSize:13}}>{c.name}</div>
                    <div style={{fontSize:11,color:"var(--muted)"}}>{c.email||c.phone||"No contact"}</div>
                  </div>
                </div>
                <button onClick={() => genToken(c)}
                  style={{padding:"7px 14px",borderRadius:8,border:"none",background:"var(--gold)",color:"white",fontSize:11.5,fontWeight:700,display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                  {portalCopied===c.id ? "Copied!" : "Create Portal"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {clients.length === 0 && (
        <div style={{textAlign:"center",padding:"60px 0",color:"var(--muted)"}}>
          <div style={{fontSize:32,marginBottom:12}}>👥</div>
          <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>No clients yet</div>
          <div style={{fontSize:12}}>Add clients first, then create their portals.</div>
        </div>
      )}
    </div>
  );

  // ── CHAT VIEW ───────────────────────────────────────────────────────────────
  if (view === "chat") {
    const thread = messages.filter(m => m.client_id === activeClient?.id);
    return (
      <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 180px)",maxHeight:700}}>
        {/* Header */}
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
          <button className="btn btn-outline btn-sm" onClick={() => { setView("list"); setActiveClient(null); }}>← Back</button>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:34,height:34,borderRadius:9,background:"var(--ink)",color:"var(--gold)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700}}>
              {activeClient?.name?.charAt(0)}
            </div>
            <div>
              <div style={{fontWeight:700,fontSize:14}}>{activeClient?.name}</div>
              <div style={{fontSize:11,color:"var(--muted)"}}>Client chat</div>
            </div>
          </div>
        </div>

        {/* Chat window */}
        <div style={{flex:1,background:"var(--card)",borderRadius:12,border:"1px solid var(--border)",overflow:"hidden",display:"flex",flexDirection:"column"}}>
          <div style={{flex:1,padding:"14px 16px",overflowY:"auto",display:"flex",flexDirection:"column",gap:10,scrollbarWidth:"thin"}}>
            {thread.length === 0 && (
              <div style={{textAlign:"center",color:"var(--muted)",fontSize:13,marginTop:40}}>No messages yet. Your client hasn't sent anything.</div>
            )}
            {thread.map((m, i) => (
              <div key={i} style={{display:"flex",justifyContent:m.sender==="contractor"?"flex-end":"flex-start",gap:8,alignItems:"flex-end"}}>
                {m.sender === "client" && (
                  <div style={{width:26,height:26,borderRadius:"50%",background:"var(--ink)",color:"var(--gold)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0}}>
                    {activeClient?.name?.charAt(0)}
                  </div>
                )}
                <div style={{maxWidth:"72%",padding:"10px 13px",borderRadius:m.sender==="contractor"?"14px 14px 4px 14px":"14px 14px 14px 4px",background:m.sender==="contractor"?"var(--ink)":"white",color:m.sender==="contractor"?"white":"var(--ink)",border:m.sender==="client"?"1px solid var(--border)":"none",fontSize:13,lineHeight:1.55,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                  {m.message}
                  <div style={{fontSize:9.5,marginTop:4,opacity:0.5,textAlign:m.sender==="contractor"?"right":"left"}}>
                    {m.created_at ? new Date(m.created_at).toLocaleTimeString("en-ZA",{hour:"2-digit",minute:"2-digit"}) : ""}
                  </div>
                </div>
              </div>
            ))}
            <div ref={chatEndRef}/>
          </div>

          {/* Reply input */}
          <div style={{borderTop:"1px solid var(--border)",padding:"12px 14px",display:"flex",gap:8,background:"var(--off)"}}>
            <input
              value={replyMsg}
              onChange={e => setReplyMsg(e.target.value)}
              onKeyDown={e => { if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendReply();} }}
              placeholder={`Reply to ${activeClient?.name}…`}
              style={{flex:1,padding:"9px 13px",borderRadius:20,border:"1px solid var(--border)",fontSize:13,background:"white",outline:"none",fontFamily:"var(--font)"}}
            />
            <button onClick={sendReply} disabled={replySending||!replyMsg.trim()}
              style={{width:38,height:38,borderRadius:"50%",background:"var(--ink)",border:"none",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,opacity:replySending||!replyMsg.trim()?0.4:1,transition:"opacity 0.13s"}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── REQUESTS VIEW ───────────────────────────────────────────────────────────
  if (view === "requests") {
    const reqs = clientRequests(activeClient);
    const STATUS_OPTS = ["Pending","Booked","In Progress","Completed","Declined"];
    return (
      <div>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <button className="btn btn-outline btn-sm" onClick={() => { setView("list"); setActiveClient(null); }}>← Back</button>
          <div>
            <div style={{fontWeight:700,fontSize:14}}>{activeClient?.name} — Requests</div>
            <div style={{fontSize:11,color:"var(--muted)"}}>{reqs.length} total · {reqs.filter(r=>r.status==="Pending").length} pending</div>
          </div>
        </div>

        {reqs.length === 0 && (
          <div style={{textAlign:"center",padding:"40px 0",color:"var(--muted)",fontSize:13}}>No requests from this client yet.</div>
        )}

        {reqs.map(r => (
          <div key={r.id} style={{background:"var(--card)",borderRadius:12,border:`1px solid ${r.status==="Pending"?"var(--gold-b)":"var(--border)"}`,padding:"14px 16px",marginBottom:10,background:r.status==="Pending"?"rgba(200,151,42,0.02)":"white"}}>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:8}}>
              <div>
                <div style={{fontWeight:700,fontSize:13,marginBottom:2}}>{r.type}</div>
                <div style={{fontSize:12,color:"var(--ink)",lineHeight:1.5}}>{r.description}</div>
              </div>
              <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 9px",borderRadius:20,fontSize:10,fontWeight:700,flexShrink:0,
                background:r.status==="Pending"?"rgba(245,158,11,0.10)":r.status==="Completed"?"rgba(16,185,129,0.10)":r.status==="Declined"?"rgba(239,68,68,0.08)":"rgba(59,130,246,0.10)",
                color:r.status==="Pending"?"#b45309":r.status==="Completed"?"#059669":r.status==="Declined"?"#dc2626":"#2563eb"}}>
                {r.status}
              </span>
            </div>
            <div style={{display:"flex",gap:12,fontSize:11.5,color:"var(--muted)",marginBottom:r.status==="Pending"?12:0}}>
              {r.preferred_date && <span>📅 {formatDate(r.preferred_date)}</span>}
              {r.contact_note && <span>📞 {r.contact_note}</span>}
              <span style={{color:"#94a3b8"}}>{r.created_at ? new Date(r.created_at).toLocaleDateString("en-ZA",{day:"numeric",month:"short"}) : ""}</span>
            </div>
            {r.status === "Pending" && (
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {["Booked","In Progress","Completed","Declined"].map(s => (
                  <button key={s} onClick={() => updateRequestStatus(r, s)}
                    style={{padding:"6px 14px",borderRadius:8,fontSize:11.5,fontWeight:700,border:"none",cursor:"pointer",
                      background:s==="Booked"?"var(--ink)":s==="Completed"?"var(--emerald)":s==="Declined"?"rgba(239,68,68,0.08)":"var(--off)",
                      color:s==="Booked"?"white":s==="Completed"?"white":s==="Declined"?"#dc2626":"var(--ink)",
                      border:s==="Declined"?"1px solid #fca5a5":s==="In Progress"?"1px solid var(--border)":"none"}}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
}

// ─── WORKERS MODULE ───────────────────────────────────────────────────────────
function WorkersModule({ userId }) {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name:"", phone:"", email:"", skill:"", status:"Active", notes:"" });
  const [jobs, setJobs] = useState([]);
  const [business, setBusiness] = useState({ currency:"ZAR" });

  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, label}
  const refresh = async () => {
    const [w, j, b] = await Promise.all([DB.getList("workers", userId), DB.getList("jobs", userId), DB.get("business", userId)]);
    setWorkers(w||[]); setJobs(j||[]); setBusiness({...(b||{}), currency:b?.currency||"ZAR", unit_system:b?.unit_system||"metric"}); setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const getWorkerStats = (wId) => {
    const wJobs = jobs.filter(j => j.worker_id === wId);
    const completed = wJobs.filter(j => j.status === "Completed" || j.status === "Paid" || j.status === "Invoiced");
    const revenue = wJobs.reduce((s, j) => s + (j.estimated_cost||0), 0);
    return { total: wJobs.length, completed: completed.length, revenue };
  };

  const [errors, setErrors] = useState({});

  const save = async () => {
    const errs = {
      name:      V.required(form.name, "Worker name"),
      phone:     V.phone(form.phone),
      email:     V.email(form.email),
    };
    const first = V.run(errs);
    if (first) { setErrors(errs); return; }
    setErrors({});
    const data = { ...form, user_id: userId };
    if (form.id) await DB.update("workers", form.id, data); else await DB.insert("workers", data);
    await refresh(); setModal(false);
  };

  const skills = ["Electrician","Plumber","HVAC Technician","Painter","General Worker","Tiler","Carpenter","Welder","Security Installer","Cleaner","Other"];

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:20 }}>
        <button className="btn btn-primary" onClick={() => { setForm({ name:"", phone:"", skill:"", status:"Active" }); setErrors({}); setModal(true); }}><Icon name="plus" />Add Worker</button>
      </div>

      {workers.length === 0 ? <div className="empty-state"><Icon name="workers" size={40} /><h3>No workers added yet</h3><p>Add your field workers to assign them to jobs. Workers do not need to log in.</p></div> : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:16 }}>
          {workers.map(w => {
            const stats = getWorkerStats(w.id);
            return (
              <div key={w.id} className="card" style={{padding:16,overflow:"hidden"}}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                  <div>
                    <div style={{ width:44, height:44, borderRadius:"50%", background:"var(--ink)", color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:700, marginBottom:8 }}>{w.name?.charAt(0)}</div>
                    <div style={{ fontWeight:700, fontSize:15 }}>{w.name}</div>
                    <div className="text-muted text-sm">{w.skill}</div>
                  </div>
                  <span className={w.status==="Active" ? "badge badge-green" : "badge badge-gray"}>{w.status}</span>
                </div>
                <div style={{ fontSize:12.5, color:"var(--muted)", marginBottom:14, display:"flex", alignItems:"center", gap:6 }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.55 8.72 19.79 19.79 0 01.47 4.1a2 2 0 012-2.18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 9.91a16 16 0 006.13 6.13l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>{w.phone||"No phone"}</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:12 }}>
                  <div style={{ textAlign:"center", background:"var(--off)", borderRadius:9, padding:"10px 8px", border:"1px solid var(--border)" }}><div style={{ fontSize:20, fontWeight:700, color:"var(--ink)" }}>{stats.total}</div><div style={{ fontSize:10, fontWeight:600, color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.4px" }}>Jobs</div></div>
                  <div style={{ textAlign:"center", background:"var(--off)", borderRadius:9, padding:"10px 8px", border:"1px solid var(--border)" }}><div style={{ fontSize:20, fontWeight:700, color:"var(--status-green-fg)" }}>{stats.completed}</div><div style={{ fontSize:10, fontWeight:600, color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.4px" }}>Done</div></div>
                  <div style={{ textAlign:"center", background:"var(--surface)", borderRadius:8, padding:8 }}><div style={{ fontSize:12, fontWeight:700, color:"var(--gold)" }}>{formatCurrency(stats.revenue, business?.currency)}</div><div style={{ fontSize:10, fontWeight:600, color:"#64748b" }}>Revenue</div></div>
                </div>
                <div className="m-actions" style={{margin:"12px -16px -12px",borderRadius:"0 0 14px 14px"}}>
                  <button className="m-btn m-btn-edit" onClick={() => { setForm({...w}); setModal(true); }}>
                    <Icon name="edit" size={16}/><span>Edit</span>
                  </button>
                  <button className="m-btn m-btn-delete" onClick={() => setDeleteConfirm({ id: w.id, label: w.name || 'this worker' })}>
                    <Icon name="trash" size={16}/><span>Delete</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>{form.id?"Edit Worker":"Add Worker"}</h3><button className="btn btn-icon btn-outline btn-sm" onClick={() => setModal(false)}><Icon name="x" /></button></div>
            <div className="modal-body">
              <div className="alert alert-info">Workers do not need to log in to FieldJob. You manage all their jobs from the dashboard.</div>
              <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" style={errors.name?{borderColor:"#ef4444"}:{}} value={form.name} onChange={e => setForm({...form, name:e.target.value})} /><FormError error={errors.name} /></div>
              <div className="form-group"><label className="form-label">Phone Number</label><input className="form-input" style={errors.phone?{borderColor:"#ef4444"}:{}} value={form.phone} onChange={e => setForm({...form, phone:e.target.value})} placeholder="082 123 4567" /><FormError error={errors.phone} /></div>
              <div className="form-group"><label className="form-label">Email</label><input className="form-input" style={errors.email?{borderColor:"#ef4444"}:{}} type="email" value={form.email} onChange={e => setForm({...form, email:e.target.value})} placeholder="worker@email.com" /><FormError error={errors.email} /></div>

              <div className="form-group"><label className="form-label">Skill / Trade</label><select className="form-select" value={form.skill} onChange={e => setForm({...form, skill:e.target.value})}><option value="">Select skill</option>{skills.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Status</label><select className="form-select" value={form.status} onChange={e => setForm({...form, status:e.target.value})}><option value="Active">Active</option><option value="Inactive">Inactive</option></select></div>
            </div>
            <div className="modal-footer"><button className="btn btn-outline" onClick={() => setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save}>Save Worker</button></div>
          </div>
        </div>
      )}

      <DeleteConfirmModal item={deleteConfirm?.label} onConfirm={async () => { if(deleteConfirm){ await DB.remove("workers", deleteConfirm.id); await refresh(); setDeleteConfirm(null); } }} onCancel={() => setDeleteConfirm(null)} />
    </div>
  );
}

// ─── CASHFLOW MODULE ──────────────────────────────────────────────────────────
function CashflowModule({ userId }) {
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [business, setBusiness] = useState({ currency:"ZAR" });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([DB.getList("invoices", userId), DB.getList("jobs", userId), DB.getList("expenses", userId), DB.get("business", userId)]).then(([inv, j, exp, b]) => {
      setExpenses(exp || []);
      setInvoices(inv||[]); setJobs(j||[]); setBusiness({...(b||{}), currency:b?.currency||"ZAR", unit_system:b?.unit_system||"metric"}); setLoading(false);
    });
  }, []);
  const todayStr = today();

  const expected = invoices.filter(i => i.status !== "Paid").reduce((s, i) => s + ((i.total||0)-(i.paid||0)), 0);
  const overdue = invoices.filter(i => i.due_date < todayStr && i.status !== "Paid").reduce((s, i) => s + ((i.total||0)-(i.paid||0)), 0);
  const paid30 = invoices.filter(i => i.status === "Paid" && i.date >= addDays(todayStr, -30)).reduce((s, i) => s + (i.total||0), 0);
  const pendingJobs = jobs.filter(j => j.status !== "Paid" && j.status !== "Invoiced").reduce((s, j) => s + (j.estimated_cost||0), 0);

  const months = [];
  for (let i = -2; i <= 3; i++) {
    const d = new Date(); d.setMonth(d.getMonth() + i);
    const m = d.getMonth(); const y = d.getFullYear();
    const mStr = `${y}-${String(m+1).padStart(2,"0")}`;
    const rev = invoices.filter(inv => inv.date?.startsWith(mStr) && inv.status === "Paid").reduce((s, inv) => s + (inv.total||0), 0);
    const exp = expenses.filter(e => e.date?.startsWith(mStr)).reduce((s, e) => s + (e.amount||0), 0);
    months.push({ label: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m], revenue: rev, expenses: exp, isCurrent: i === 0, isFuture: i > 0 });
  }

  const maxVal = Math.max(...months.map(m => Math.max(m.revenue, m.expenses)), 1);

  const risk = overdue > 20000 ? "High" : overdue > 5000 ? "Medium" : "Low";
  const riskColor = { High:"var(--status-red-fg)", Medium:"var(--status-amber-fg)", Low:"var(--status-green-fg)" };

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Expected Income (30 days)</div>
          <div className="stat-value" style={{ fontFamily:"var(--mono)" }}>{formatCurrency(expected, business?.currency)}</div>
          <div className="stat-sub">From outstanding invoices</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Revenue (Last 30 Days)</div>
          <div className="stat-value" style={{ fontFamily:"var(--mono)" }}>{formatCurrency(paid30, business?.currency)}</div>
          <div className="stat-sub">Paid invoices</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Overdue Amount</div>
          <div className="stat-value" style={{ fontFamily:"var(--mono)", color:"var(--status-red-fg)" }}>{formatCurrency(overdue, business?.currency)}</div>
          <div className="stat-sub">Requires immediate follow-up</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pipeline Value</div>
          <div className="stat-value" style={{ fontFamily:"var(--mono)" }}>{formatCurrency(pendingJobs, business?.currency)}</div>
          <div className="stat-sub">Active jobs not yet invoiced</div>
        </div>
      </div>

      <div className="cashflow-grid" style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16 }}>
        <div className="card">
          <div className="card-title" style={{ marginBottom:20 }}>Revenue vs Expenses — 6 Month View</div>
          <div style={{ display:"flex", gap:12, alignItems:"flex-end", height:150 }}>
            {months.map((m, i) => (
              <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center" }}>
                <div style={{ display:"flex", gap:3, alignItems:"flex-end", height:120 }}>
                  <div style={{ width:18, height: Math.max(4, (m.revenue/maxVal)*110), background: m.isFuture?"rgba(200,151,42,0.35)":"var(--gold)", borderRadius:"3px 3px 0 0", opacity: m.isCurrent?1:0.7 }} title={`Revenue: ${formatCurrency(m.revenue, business?.currency)}`}></div>
                  <div style={{ width:18, height: Math.max(4, (m.expenses/maxVal)*110), background: m.isFuture?"#fef3c7":"var(--amber)", borderRadius:"3px 3px 0 0", opacity: m.isCurrent?1:0.7 }} title={`Est. Expenses: ${formatCurrency(m.expenses, business?.currency)}`}></div>
                </div>
                <div style={{ fontSize:10, color:"#94a3b8", marginTop:4 }}>{m.label}</div>
                {m.isCurrent && <div style={{ fontSize:9, color:"var(--gold)", fontWeight:600 }}>NOW</div>}
                {m.isFuture && <div style={{ fontSize:9, color:"#94a3b8" }}>Forecast</div>}
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:16, marginTop:12, justifyContent:"center" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12 }}><div style={{ width:12,height:12,borderRadius:2,background:"var(--gold)" }}></div>Revenue</div>
            <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12 }}><div style={{ width:12,height:12,borderRadius:2,background:"var(--amber)" }}></div>Est. Expenses</div>
            <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12 }}><div style={{ width:12,height:12,borderRadius:2,background:"#dbeafe" }}></div>Forecast</div>
          </div>
        </div>

        <div>
          <div className="card" style={{ marginBottom:16 }}>
            <div className="card-title" style={{ marginBottom:12 }}>Cash Risk Indicator</div>
            <div style={{ textAlign:"center", padding:"20px 0" }}>
              <div style={{ fontSize:42, marginBottom:8 }}>{risk==="Low"?"✅":risk==="Medium"?"⚠️":"🚨"}</div>
              <div style={{ fontSize:22, fontWeight:700, color:riskColor[risk] }}>{risk} Risk</div>
              <div className="text-muted text-sm mt-2">{formatCurrency(overdue, business?.currency)} overdue</div>
            </div>
          </div>
          <div className="card">
            <div className="card-title" style={{ marginBottom:12 }}>Outstanding Invoices</div>
            {invoices.filter(i=>i.status!=="Paid").slice(0,5).map(inv => (
              <div key={inv.id} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid var(--border)", fontSize:12.5 }}>
                <div><div style={{ fontWeight:600 }}>{inv.client_name}</div><div className="text-muted">{inv.number}</div></div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontWeight:700, color: inv.due_date<todayStr?"var(--status-red-fg)":"inherit" }}>{formatCurrency(inv.total-(inv.paid||0, business?.currency))}</div>
                  <div className="text-muted">Due {formatDate(inv.due_date)}</div>
                </div>
              </div>
            ))}
            {invoices.filter(i=>i.status!=="Paid").length === 0 && <div className="text-muted text-sm" style={{ textAlign:"center", padding:"12px 0" }}>All invoices paid! 🎉</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SETTINGS MODULE ──────────────────────────────────────────────────────────
// ─── TEMPLATE LARGE PREVIEW (used in settings modal) ────────────────────────
function TemplateLargePreview({ id }) {
  const s = { fontFamily:"sans-serif", fontSize:11, lineHeight:1.5, color:"#1e293b" };
  const tbl = { width:"100%", borderCollapse:"collapse", marginBottom:10 };
  const rows = [
    { desc:"Supply & Install Geyser 150L", qty:1, unit:"R 4,800.00", total:"R 4,800.00" },
    { desc:"Labour — Full Day",           qty:1, unit:"R 1,200.00", total:"R 1,200.00" },
  ];
  const tableRows = rows.map((r,i) => (
    <tr key={i} style={{ background: i%2===1 ? "#fafafa" : "white" }}>
      <td style={{ padding:"6px 10px", borderBottom:"1px solid #f1f5f9" }}>{r.desc}</td>
      <td style={{ padding:"6px 10px", borderBottom:"1px solid #f1f5f9", textAlign:"right" }}>{r.qty}</td>
      <td style={{ padding:"6px 10px", borderBottom:"1px solid #f1f5f9", textAlign:"right" }}>{r.unit}</td>
      <td style={{ padding:"6px 10px", borderBottom:"1px solid #f1f5f9", textAlign:"right" }}><strong>{r.total}</strong></td>
    </tr>
  ));
  const theadDark = (bg="#0f1923") => <thead><tr style={{ background:bg, color:"white" }}><th style={{ padding:"7px 10px", textAlign:"left", fontSize:10 }}>Description</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10 }}>Qty</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10 }}>Unit</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10 }}>Amount</th></tr></thead>;
  const theadLight = (color="#c8972a") => <thead><tr><th style={{ padding:"7px 10px", textAlign:"left", fontSize:10, borderBottom:`2px solid ${color}`, color }}>Description</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, borderBottom:`2px solid ${color}`, color }}>Qty</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, borderBottom:`2px solid ${color}`, color }}>Unit</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, borderBottom:`2px solid ${color}`, color }}>Amount</th></tr></thead>;
  const foot = (color="#94a3b8") => <div style={{ marginTop:16, paddingTop:10, borderTop:"1px solid #e2e8f0", fontSize:10, color, textAlign:"center" }}>Generated by FieldJob &nbsp;|&nbsp; Your Business &nbsp;|&nbsp; 08 Mar 2026</div>;
  const bank = (bg="#f8fafc", border="#c8972a", labelColor="#c8972a") => <div style={{ marginTop:10, padding:"10px 12px", background:bg, borderLeft:`3px solid ${border}`, borderRadius:"0 6px 6px 0", fontSize:10 }}><div style={{ fontWeight:700, color:labelColor, marginBottom:4 }}>Banking Details — Ref: INV-007</div><div style={{ color:"#475569" }}>FNB · Your Business · 62012345678 · Branch: 250655</div></div>;
  const billBox = (bg="#f8fafc", labelColor="#94a3b8", textColor="#0f1923") => <div style={{ background:bg, borderRadius:6, padding:"8px 12px", marginBottom:12 }}><div style={{ fontSize:9, textTransform:"uppercase", letterSpacing:"1px", color:labelColor, marginBottom:3 }}>Bill To</div><div style={{ fontWeight:700, fontSize:13, color:textColor }}>Arman Construction (Pty) Ltd</div></div>;

  if (id === "1") return <div style={s}>
    <div style={{ display:"flex", justifyContent:"space-between", paddingBottom:12, marginBottom:14, borderBottom:"2px solid #e2e8f0" }}>
      <div><div style={{ fontSize:16, fontWeight:700 }}>Your Business</div><div style={{ color:"#64748b", fontSize:10, marginTop:4, lineHeight:1.8 }}>Cape Town · 082 000 0000<br/>info@yourbiz.co.za · VAT 4123456789</div></div>
      <div style={{ textAlign:"right" }}><div style={{ fontSize:22, fontWeight:700, color:"#0f1923" }}>INVOICE</div><div style={{ fontSize:13, color:"#c8972a", fontWeight:600 }}>INV-007</div><div style={{ fontSize:10, color:"#64748b", marginTop:4, lineHeight:1.8 }}>Date: 08 Mar 2026<br/>Due: 08 Apr 2026</div></div>
    </div>
    {billBox()}<table style={tbl}>{theadDark()}<tbody>{tableRows}</tbody></table>
    <div style={{ textAlign:"right", padding:12, background:"#f8fafc", borderRadius:6 }}><div style={{ fontSize:11, color:"#64748b" }}>Subtotal: R 6,000.00 &nbsp;·&nbsp; VAT: R 900.00</div><div style={{ fontSize:18, fontWeight:700, marginTop:4 }}>Total: R 6,900.00</div></div>
    {bank()}{foot()}
  </div>;

  if (id === "2") return <div style={s}>
    <div style={{ background:"#0f1923", margin:"-24px -28px 16px", padding:"20px 28px", display:"flex", justifyContent:"space-between" }}>
      <div><div style={{ fontSize:16, fontWeight:700, color:"white" }}>Your Business</div><div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:4, lineHeight:1.8 }}>Cape Town · 082 000 0000<br/>info@yourbiz.co.za</div></div>
      <div style={{ textAlign:"right" }}><div style={{ fontSize:10, fontWeight:700, letterSpacing:"2px", textTransform:"uppercase", color:"#c8972a" }}>INVOICE</div><div style={{ fontSize:20, fontWeight:700, color:"white", marginTop:4 }}>INV-007</div><div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:4 }}>Due: 08 Apr 2026</div></div>
    </div>
    <div style={{ display:"flex", gap:10, marginBottom:12 }}><div style={{ flex:1, background:"#f8fafc", borderRadius:6, padding:"8px 12px" }}><div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"1px", marginBottom:3 }}>Bill To</div><div style={{ fontWeight:700 }}>Arman Construction (Pty) Ltd</div></div><div style={{ flex:1, background:"#f8fafc", borderRadius:6, padding:"8px 12px" }}><div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"1px", marginBottom:3 }}>Date</div><div style={{ fontWeight:700 }}>08 Mar 2026</div></div></div>
    <table style={tbl}><thead><tr style={{ background:"#f1f5f9" }}><th style={{ padding:"7px 10px", textAlign:"left", fontSize:10, borderBottom:"2px solid #0f1923" }}>Description</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, borderBottom:"2px solid #0f1923" }}>Qty</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, borderBottom:"2px solid #0f1923" }}>Unit</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, borderBottom:"2px solid #0f1923" }}>Amount</th></tr></thead><tbody>{tableRows}</tbody></table>
    <div style={{ background:"#0f1923", borderRadius:8, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}><span style={{ fontSize:11, color:"rgba(255,255,255,0.45)" }}>Total Due (incl. VAT)</span><span style={{ fontSize:20, fontWeight:700, color:"#c8972a" }}>R 6,900.00</span></div>
    {bank("white", "#c8972a", "#c8972a")}{foot()}
  </div>;

  if (id === "3") return <div style={s}>
    <div style={{ height:4, background:"linear-gradient(90deg,#c8972a,#f0d078,#c8972a)", margin:"-24px -28px 16px" }}></div>
    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
      <div><div style={{ fontSize:16, fontWeight:700 }}>Your Business</div><div style={{ color:"#94a3b8", fontSize:10, marginTop:4, lineHeight:1.8 }}>Cape Town · 082 000 0000<br/>info@yourbiz.co.za</div></div>
      <div style={{ textAlign:"right" }}><div style={{ display:"inline-block", background:"linear-gradient(135deg,#c8972a,#f0d078)", color:"#0f1923", fontSize:10, fontWeight:700, letterSpacing:"1px", textTransform:"uppercase", padding:"3px 12px", borderRadius:20 }}>Invoice</div><div style={{ fontSize:16, fontWeight:700, marginTop:4 }}>INV-007</div><div style={{ fontSize:10, color:"#94a3b8", marginTop:3 }}>08 Mar 2026</div></div>
    </div>
    <div style={{ height:1, background:"linear-gradient(90deg,rgba(200,151,42,0.2),#c8972a,rgba(200,151,42,0.2))", margin:"10px 0" }}></div>
    {billBox("#f8fafc", "#c8972a", "#0f1923")}
    <table style={tbl}>{theadLight()}<tbody>{tableRows}</tbody></table>
    <div style={{ textAlign:"right", fontSize:16, fontWeight:700, color:"#c8972a", marginBottom:10 }}>Total: R 6,900.00</div>
    {bank("#fffbeb", "#c8972a", "#c8972a")}{foot()}
  </div>;

  if (id === "4") return <div style={s}>
    <div style={{ fontSize:20, fontWeight:700, letterSpacing:"-0.5px" }}>Your Business</div>
    <div style={{ fontSize:10, color:"#9ca3af", marginBottom:12 }}>Cape Town · 082 000 0000 · info@yourbiz.co.za</div>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", paddingBottom:10, marginBottom:14, borderBottom:"1px solid #f3f4f6" }}>
      <div><div style={{ fontSize:9, textTransform:"uppercase", letterSpacing:"1px", color:"#d1d5db", marginBottom:3 }}>Bill To</div><div style={{ fontWeight:600, fontSize:13 }}>Arman Construction (Pty) Ltd</div><div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>Due: 08 Apr 2026</div></div>
      <div style={{ textAlign:"right" }}><div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:"2px", color:"#9ca3af" }}>Invoice</div><div style={{ fontSize:20, fontWeight:700 }}>INV-007</div></div>
    </div>
    <table style={tbl}>{theadLight("#111827")}<tbody>{tableRows}</tbody></table>
    <div style={{ textAlign:"right", fontSize:16, fontWeight:700, borderTop:"1px solid #111827", paddingTop:8, marginBottom:10 }}>Total: R 6,900.00</div>
    <div style={{ borderTop:"1px dashed #e5e7eb", paddingTop:8, fontSize:10, color:"#6b7280" }}>FNB · Your Business · 62012345678 · Branch: 250655 · Ref: INV-007</div>
    {foot("#d1d5db")}
  </div>;

  if (id === "5") return <div style={{ ...s, background:"#0f1923", margin:"-24px -28px", padding:"24px 28px", minHeight:200 }}>
    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:14 }}>
      <div><div style={{ fontSize:16, fontWeight:700, color:"white" }}>Your Business</div><div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginTop:4, lineHeight:1.8 }}>Cape Town · 082 000 0000<br/>info@yourbiz.co.za</div></div>
      <div style={{ textAlign:"right" }}><div style={{ display:"inline-block", background:"#c8972a", color:"#0f1923", fontSize:9, fontWeight:700, letterSpacing:"1.5px", textTransform:"uppercase", padding:"3px 10px", borderRadius:4 }}>Invoice</div><div style={{ fontSize:20, fontWeight:700, color:"white", marginTop:5 }}>INV-007</div></div>
    </div>
    <div style={{ background:"rgba(255,255,255,0.06)", borderRadius:6, padding:"8px 12px", marginBottom:12 }}><div style={{ fontSize:9, textTransform:"uppercase", letterSpacing:"1px", color:"rgba(255,255,255,0.3)", marginBottom:3 }}>Bill To</div><div style={{ fontWeight:700, color:"white", fontSize:13 }}>Arman Construction (Pty) Ltd</div></div>
    <table style={tbl}><thead><tr><th style={{ padding:"7px 10px", textAlign:"left", fontSize:10, borderBottom:"1px solid rgba(200,151,42,0.4)", color:"#c8972a" }}>Description</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, borderBottom:"1px solid rgba(200,151,42,0.4)", color:"#c8972a" }}>Qty</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, borderBottom:"1px solid rgba(200,151,42,0.4)", color:"#c8972a" }}>Unit</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, borderBottom:"1px solid rgba(200,151,42,0.4)", color:"#c8972a" }}>Amount</th></tr></thead><tbody>{rows.map((r,i) => <tr key={i}><td style={{ padding:"6px 10px", borderBottom:"1px solid rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.75)" }}>{r.desc}</td><td style={{ padding:"6px 10px", borderBottom:"1px solid rgba(255,255,255,0.06)", textAlign:"right", color:"rgba(255,255,255,0.75)" }}>{r.qty}</td><td style={{ padding:"6px 10px", borderBottom:"1px solid rgba(255,255,255,0.06)", textAlign:"right", color:"rgba(255,255,255,0.75)" }}>{r.unit}</td><td style={{ padding:"6px 10px", borderBottom:"1px solid rgba(255,255,255,0.06)", textAlign:"right", color:"rgba(255,255,255,0.75)" }}><strong>{r.total}</strong></td></tr>)}</tbody></table>
    <div style={{ background:"rgba(200,151,42,0.12)", border:"1px solid rgba(200,151,42,0.25)", borderRadius:8, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:10 }}><span style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>Total Due (incl. VAT)</span><span style={{ fontSize:20, fontWeight:700, color:"#c8972a" }}>R 6,900.00</span></div>
    <div style={{ marginTop:10, padding:"10px 12px", background:"rgba(255,255,255,0.04)", borderRadius:6, fontSize:10, color:"rgba(255,255,255,0.35)" }}>FNB · Your Business · 62012345678 · Ref: INV-007</div>
    <div style={{ marginTop:16, paddingTop:10, borderTop:"1px solid rgba(255,255,255,0.08)", fontSize:10, color:"rgba(255,255,255,0.2)", textAlign:"center" }}>Generated by FieldJob &nbsp;|&nbsp; Your Business &nbsp;|&nbsp; 08 Mar 2026</div>
  </div>;

  const colorMap = { "6":{ h:"linear-gradient(135deg,#fce7f3,#fdf2f8)", hb:"1px solid #fbcfe8", biz:"#831843", info:"#be185d", badge:"#ec4899", bill:"#fdf2f8", blabel:"#be185d", bleft:"#ec4899", thead:"#831843", trow:"#fdf2f8", tbord:"#fce7f3", tot:"#831843", bankbg:"#fdf2f8", bankb:"#fbcfe8", bankl:"#be185d", fl:"#be185d" }, "7":{ h:"linear-gradient(135deg,#eff6ff,#dbeafe)", hb:"1px solid #bfdbfe", biz:"#1e3a8a", info:"#3b82f6", badge:"#2563eb", bill:"#eff6ff", blabel:"#2563eb", bleft:"#2563eb", thead:"#1e3a8a", trow:"#eff6ff", tbord:"#dbeafe", tot:"#1e3a8a", bankbg:"#eff6ff", bankb:"#bfdbfe", bankl:"#2563eb", fl:"#93c5fd" } };
  if (id === "6" || id === "7") { const c = colorMap[id]; return <div style={s}>
    <div style={{ background:c.h, margin:"-24px -28px 16px", padding:"20px 28px", borderBottom:c.hb, display:"flex", justifyContent:"space-between" }}>
      <div><div style={{ fontSize:16, fontWeight:700, color:c.biz }}>Your Business</div><div style={{ fontSize:10, color:c.info, opacity:0.7, marginTop:4, lineHeight:1.8 }}>Cape Town · 082 000 0000<br/>info@yourbiz.co.za</div></div>
      <div style={{ textAlign:"right" }}><div style={{ display:"inline-block", background:c.badge, color:"white", fontSize:10, fontWeight:700, letterSpacing:"1px", textTransform:"uppercase", padding:"3px 12px", borderRadius:20 }}>Invoice</div><div style={{ fontSize:18, fontWeight:700, color:c.biz, marginTop:5 }}>INV-007</div><div style={{ fontSize:10, color:c.info, opacity:0.6, marginTop:3 }}>Due: 08 Apr 2026</div></div>
    </div>
    <div style={{ background:c.bill, borderLeft:`3px solid ${c.bleft}`, borderRadius:"0 6px 6px 0", padding:"8px 12px", marginBottom:12 }}><div style={{ fontSize:9, textTransform:"uppercase", letterSpacing:"1px", color:c.blabel, fontWeight:700, marginBottom:3 }}>Bill To</div><div style={{ fontWeight:700, fontSize:13, color:c.biz }}>Arman Construction (Pty) Ltd</div></div>
    <table style={tbl}><thead><tr style={{ background:c.thead, color:"white" }}><th style={{ padding:"7px 10px", textAlign:"left", fontSize:10 }}>Description</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10 }}>Qty</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10 }}>Unit</th><th style={{ padding:"7px 10px", textAlign:"right", fontSize:10 }}>Amount</th></tr></thead><tbody>{rows.map((r,i) => <tr key={i} style={{ background: i%2===1 ? c.trow : "white" }}><td style={{ padding:"6px 10px", borderBottom:`1px solid ${c.tbord}` }}>{r.desc}</td><td style={{ padding:"6px 10px", borderBottom:`1px solid ${c.tbord}`, textAlign:"right" }}>{r.qty}</td><td style={{ padding:"6px 10px", borderBottom:`1px solid ${c.tbord}`, textAlign:"right" }}>{r.unit}</td><td style={{ padding:"6px 10px", borderBottom:`1px solid ${c.tbord}`, textAlign:"right" }}><strong>{r.total}</strong></td></tr>)}</tbody></table>
    <div style={{ textAlign:"right", padding:12, background:c.bill, borderRadius:6, marginTop:8, fontSize:16, fontWeight:700, color:c.biz }}>Total: R 6,900.00</div>
    <div style={{ marginTop:10, padding:"10px 12px", background:c.bankbg, borderLeft:`3px solid ${c.bleft}`, borderRadius:"0 6px 6px 0", fontSize:10, border:`1px solid ${c.bankb}` }}><div style={{ fontWeight:700, color:c.bankl, marginBottom:4 }}>Banking Details — Ref: INV-007</div><div style={{ color:c.blabel, opacity:0.7 }}>FNB · Your Business · 62012345678 · Branch: 250655</div></div>
    <div style={{ marginTop:16, paddingTop:10, borderTop:`1px solid ${c.bankb}`, fontSize:10, color:c.fl, opacity:0.6, textAlign:"center" }}>Generated by FieldJob &nbsp;|&nbsp; Your Business &nbsp;|&nbsp; 08 Mar 2026</div>
  </div>; }

  if (id === "8") return <div style={s}>
    <div style={{ position:"relative", background:"#0f1923", margin:"-24px -28px 16px", padding:"20px 28px", overflow:"hidden", height:80, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
      <div style={{ position:"absolute", top:-30, right:-30, width:120, height:120, background:"#c8972a", opacity:0.15, borderRadius:"50%" }}></div>
      <div style={{ position:"absolute", top:8, right:70, width:70, height:70, background:"#c8972a", opacity:0.1, borderRadius:"50%" }}></div>
      <div style={{ position:"absolute", bottom:-40, left:"40%", width:130, height:130, background:"#c8972a", opacity:0.07, borderRadius:"50%" }}></div>
      <div style={{ position:"relative", zIndex:2 }}><div style={{ fontSize:16, fontWeight:700, color:"white" }}>Your Business</div><div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:3 }}>Cape Town · 082 000 0000</div></div>
      <div style={{ position:"relative", zIndex:2, textAlign:"right" }}><div style={{ fontSize:10, fontWeight:700, letterSpacing:"2px", textTransform:"uppercase", color:"#c8972a" }}>INVOICE</div><div style={{ fontSize:18, fontWeight:700, color:"white", marginTop:3 }}>INV-007</div></div>
    </div>
    {billBox()}<table style={tbl}>{theadDark()}<tbody>{tableRows}</tbody></table>
    <div style={{ textAlign:"right", padding:12, background:"#f8fafc", borderRadius:6 }}><div style={{ fontSize:11, color:"#64748b" }}>Subtotal: R 6,000.00 &nbsp;·&nbsp; VAT: R 900.00</div><div style={{ fontSize:18, fontWeight:700, marginTop:4 }}>Total: R 6,900.00</div></div>
    {bank()}{foot()}
  </div>;

  if (id === "9") return <div style={s}>
    <div style={{ position:"relative", margin:"-24px -28px 16px", height:80, overflow:"hidden" }}>
      <div style={{ position:"absolute", inset:0, background:"#0f1923" }}></div>
      <div style={{ position:"absolute", bottom:0, right:0, width:"55%", height:"100%", background:"white", transform:"skewX(-10deg) translateX(20px)", transformOrigin:"bottom right" }}></div>
      <div style={{ position:"absolute", bottom:0, left:0, right:0, height:3, background:"linear-gradient(90deg,#c8972a,#f0d078 40%,transparent)" }}></div>
      <div style={{ position:"relative", zIndex:2, padding:"16px 28px", display:"flex", justifyContent:"space-between", alignItems:"center", height:"100%" }}>
        <div><div style={{ fontSize:15, fontWeight:700, color:"white" }}>Your Business</div><div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:2 }}>Cape Town · 082 000 0000</div></div>
        <div style={{ textAlign:"right" }}><div style={{ fontSize:10, fontWeight:700, letterSpacing:"2px", textTransform:"uppercase", color:"#c8972a" }}>INVOICE</div><div style={{ fontSize:18, fontWeight:700, color:"#0f1923", marginTop:3 }}>INV-007</div></div>
      </div>
    </div>
    {billBox()}<table style={tbl}>{theadDark()}<tbody>{tableRows}</tbody></table>
    <div style={{ textAlign:"right", padding:12, background:"#f8fafc", borderRadius:6 }}><div style={{ fontSize:16, fontWeight:700, marginTop:4 }}>Total: R 6,900.00</div></div>
    {bank()}{foot()}
  </div>;

  if (id === "10") return <div style={s}>
    <div style={{ background:"#0f1923", margin:"-24px -28px 0", padding:"16px 28px 0" }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
        <div><div style={{ fontSize:15, fontWeight:700, color:"white" }}>Your Business</div><div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:3 }}>Cape Town · 082 000 0000</div></div>
        <div style={{ textAlign:"right" }}><div style={{ fontSize:10, fontWeight:700, letterSpacing:"2px", textTransform:"uppercase", color:"#c8972a" }}>INVOICE</div><div style={{ fontSize:18, fontWeight:700, color:"white", marginTop:3 }}>INV-007</div></div>
      </div>
      <svg viewBox="0 0 560 28" preserveAspectRatio="none" style={{ display:"block", width:"calc(100% + 56px)", marginLeft:-28, height:28 }}><path d="M0,14 C100,28 220,0 330,14 C440,28 500,6 560,14 L560,28 L0,28Z" fill="white"/></svg>
    </div>
    <div style={{ marginTop:12 }}>{billBox()}</div>
    <table style={tbl}>{theadDark()}<tbody>{tableRows}</tbody></table>
    <div style={{ textAlign:"right", padding:12, background:"#f8fafc", borderRadius:6 }}><div style={{ fontSize:16, fontWeight:700, marginTop:4 }}>Total: R 6,900.00</div></div>
    {bank()}{foot()}
  </div>;

  if (id === "11") return <div style={s}>
    <div style={{ position:"relative", margin:"-24px -28px 16px", height:80, overflow:"hidden", background:"white", borderBottom:"1px solid #e2e8f0" }}>
      <div style={{ position:"absolute", top:0, right:0, width:0, height:0, borderStyle:"solid", borderWidth:"0 160px 80px 0", borderColor:"transparent #0f1923 transparent transparent" }}></div>
      <div style={{ position:"absolute", top:0, right:0, width:0, height:0, borderStyle:"solid", borderWidth:"0 128px 64px 0", borderColor:"transparent #c8972a transparent transparent", opacity:0.3 }}></div>
      <div style={{ position:"relative", zIndex:2, padding:"16px 28px", display:"flex", justifyContent:"space-between", alignItems:"center", height:"100%" }}>
        <div><div style={{ fontSize:15, fontWeight:700, color:"#0f1923" }}>Your Business</div><div style={{ fontSize:10, color:"#94a3b8", marginTop:2 }}>Cape Town · 082 000 0000</div></div>
        <div style={{ textAlign:"right" }}><div style={{ fontSize:10, fontWeight:700, letterSpacing:"2px", textTransform:"uppercase", color:"white" }}>INVOICE</div><div style={{ fontSize:18, fontWeight:700, color:"white", marginTop:3 }}>INV-007</div></div>
      </div>
    </div>
    {billBox()}<table style={tbl}>{theadDark()}<tbody>{tableRows}</tbody></table>
    <div style={{ textAlign:"right", padding:12, background:"#f8fafc", borderRadius:6 }}><div style={{ fontSize:16, fontWeight:700, marginTop:4 }}>Total: R 6,900.00</div></div>
    {bank()}{foot()}
  </div>;

  if (id === "12") return <div style={s}>
    <div style={{ margin:"-24px -28px 16px", display:"flex", height:80 }}>
      <div style={{ background:"#0f1923", width:"40%", padding:"16px 18px", display:"flex", flexDirection:"column", justifyContent:"center" }}><div style={{ fontSize:14, fontWeight:700, color:"white" }}>Your Business</div><div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", marginTop:2 }}>Cape Town · 082 000 0000</div></div>
      <div style={{ background:"white", flex:1, padding:"16px 18px", display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"flex-end", borderBottom:"3px solid #c8972a" }}><div style={{ fontSize:9, fontWeight:700, letterSpacing:"2px", textTransform:"uppercase", color:"#94a3b8" }}>Invoice</div><div style={{ fontSize:20, fontWeight:700, color:"#0f1923", marginTop:3 }}>INV-007</div></div>
    </div>
    {billBox()}<table style={tbl}>{theadDark()}<tbody>{tableRows}</tbody></table>
    <div style={{ textAlign:"right", padding:12, background:"#f8fafc", borderRadius:6 }}><div style={{ fontSize:16, fontWeight:700, marginTop:4 }}>Total: R 6,900.00</div></div>
    {bank()}{foot()}
  </div>;

  return null;
}

// ─── TEMPLATES MODULE (standalone — PDF template selector) ───────────────────
const TEMPLATES_META = {
  "1":  { label:"Classic",      desc:"Navy table header, gold banking bar" },
  "2":  { label:"Dark Header",  desc:"Full-width navy banner, gold total" },
  "3":  { label:"Gold Accent",  desc:"Gradient gold top bar, warm tones" },
  "4":  { label:"Minimal",      desc:"Type-driven, no fills, ultra clean" },
  "5":  { label:"Bold Dark",    desc:"Full dark background, gold accents" },
  "6":  { label:"Blush Pink",   desc:"Soft pink gradient header, rose accents" },
  "7":  { label:"Sky Blue",     desc:"Cool blue gradient header, navy accents" },
  "8":  { label:"Bubble Orbs",  desc:"Gold orb shapes on navy, organic feel" },
  "9":  { label:"Diagonal",     desc:"Angled slash dividing dark and white" },
  "10": { label:"Wave",         desc:"SVG wave breaking from navy header" },
  "11": { label:"Triangle",     desc:"Dark triangle corner, doc type inside" },
  "12": { label:"Split Column", desc:"Vertical dark/light split header" },
};

const TEMPLATE_PREVIEWS = [
  { id:"1",  label:"Classic",      renderPreview: () => (
    <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
      <div style={{ display:"flex", justifyContent:"space-between", paddingBottom:5, marginBottom:5, borderBottom:"1.5px solid #e2e8f0" }}>
        <div><div style={{ fontWeight:700, fontSize:7.5, color:"#0f1923" }}>Your Business</div><div style={{ color:"#94a3b8", marginTop:2 }}>Cape Town · 082 000 0000</div></div>
        <div style={{ textAlign:"right" }}><div style={{ fontWeight:700, fontSize:10, color:"#0f1923" }}>INVOICE</div><div style={{ color:"#c8972a", fontWeight:600 }}>INV-007</div></div>
      </div>
      <div style={{ background:"#f8fafc", borderRadius:3, padding:"3px 5px", marginBottom:5 }}><div style={{ fontSize:5, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.5px" }}>BILL TO</div><div style={{ fontWeight:700, color:"#0f1923" }}>Arman Construction</div></div>
      <div style={{ width:"100%", borderCollapse:"collapse" }}><div style={{ background:"#0f1923", color:"white", padding:"3px 4px", display:"flex", justifyContent:"space-between" }}><span>Description</span><span>Total</span></div><div style={{ padding:"3px 4px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div><div style={{ padding:"3px 4px", background:"#fafafa", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div></div>
      <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7 }}>Total: R 6,900.00</div>
      <div style={{ marginTop:4, padding:"3px 5px", background:"#f8fafc", borderLeft:"2px solid #c8972a", fontSize:5.5, color:"#475569" }}>FNB · 62012345678 · Ref: INV-007</div>
    </div>
  )},
  { id:"2",  label:"Dark Header",  renderPreview: () => (
    <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
      <div style={{ background:"#0f1923", margin:"-8px -8px 6px", padding:"7px 8px", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <div><div style={{ fontWeight:700, fontSize:7.5, color:"white" }}>Your Business</div><div style={{ color:"rgba(255,255,255,0.4)", fontSize:5.5, marginTop:1 }}>Cape Town · 082 000 0000</div></div>
        <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"#c8972a", fontWeight:700, letterSpacing:"0.5px", textTransform:"uppercase" }}>INVOICE</div><div style={{ color:"white", fontWeight:700, fontSize:9 }}>INV-007</div></div>
      </div>
      <div style={{ display:"flex", gap:4, marginBottom:5 }}><div style={{ flex:1, background:"#f8fafc", borderRadius:3, padding:"3px 4px" }}><div style={{ fontSize:5, color:"#94a3b8" }}>BILL TO</div><div style={{ fontWeight:700 }}>Arman Construction</div></div><div style={{ flex:1, background:"#f8fafc", borderRadius:3, padding:"3px 4px" }}><div style={{ fontSize:5, color:"#94a3b8" }}>DATE</div><div style={{ fontWeight:700 }}>08 Mar 2026</div></div></div>
      <div style={{ padding:"3px 4px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
      <div style={{ padding:"3px 4px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
      <div style={{ background:"#0f1923", borderRadius:3, padding:"5px 6px", display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:5 }}><span style={{ color:"rgba(255,255,255,0.45)", fontSize:5.5 }}>Total Due</span><span style={{ color:"#c8972a", fontWeight:700, fontSize:8 }}>R 6,900.00</span></div>
    </div>
  )},
  { id:"3",  label:"Gold Accent",  renderPreview: () => (
    <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
      <div style={{ height:3, background:"linear-gradient(90deg,#c8972a,#f0d078,#c8972a)", margin:"-8px -8px 6px" }}></div>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
        <div><div style={{ fontWeight:700, fontSize:7.5 }}>Your Business</div><div style={{ color:"#94a3b8", fontSize:5.5 }}>Cape Town · 082 000 0000</div></div>
        <div style={{ textAlign:"right" }}><div style={{ display:"inline-block", background:"linear-gradient(135deg,#c8972a,#f0d078)", color:"#0f1923", fontSize:5, fontWeight:700, padding:"1px 5px", borderRadius:8 }}>INVOICE</div><div style={{ fontWeight:700, fontSize:8, marginTop:2 }}>INV-007</div></div>
      </div>
      <div style={{ height:0.5, background:"linear-gradient(90deg,rgba(200,151,42,0.2),#c8972a,rgba(200,151,42,0.2))", marginBottom:4 }}></div>
      <div style={{ marginBottom:4 }}><div style={{ fontSize:5, color:"#c8972a", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px" }}>BILL TO</div><div style={{ fontWeight:700 }}>Arman Construction</div></div>
      <div style={{ borderBottom:"1.5px solid #c8972a", padding:"2px 3px", display:"flex", justifyContent:"space-between", color:"#c8972a", fontSize:5.5, fontWeight:700 }}><span>Description</span><span>Total</span></div>
      <div style={{ padding:"3px 3px", borderBottom:"1px solid #fef3c7", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
      <div style={{ padding:"3px 3px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
      <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7, color:"#c8972a" }}>Total: R 6,900.00</div>
    </div>
  )},
  { id:"4",  label:"Minimal",      renderPreview: () => (
    <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
      <div style={{ fontWeight:700, fontSize:10, letterSpacing:"-0.3px" }}>Your Business</div>
      <div style={{ color:"#9ca3af", fontSize:5.5, marginBottom:5 }}>Cape Town · info@biz.co.za</div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", paddingBottom:4, marginBottom:5, borderBottom:"1px solid #f3f4f6" }}>
        <div><div style={{ fontSize:5, color:"#d1d5db", textTransform:"uppercase", letterSpacing:"0.5px" }}>BILL TO</div><div style={{ fontWeight:600, fontSize:7 }}>Arman Construction</div></div>
        <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.5px" }}>Invoice</div><div style={{ fontWeight:700, fontSize:9 }}>INV-007</div></div>
      </div>
      <div style={{ borderBottom:"1px solid #111827", padding:"2px 3px", display:"flex", justifyContent:"space-between", fontWeight:700, fontSize:5.5 }}><span>Description</span><span>Total</span></div>
      <div style={{ padding:"3px 3px", borderBottom:"1px solid #f3f4f6", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
      <div style={{ padding:"3px 3px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
      <div style={{ borderTop:"1px solid #111827", textAlign:"right", marginTop:4, fontWeight:700, paddingTop:3, fontSize:7 }}>Total: R 6,900.00</div>
    </div>
  )},
  { id:"5",  label:"Bold Dark",    renderPreview: () => (
    <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4, background:"#0f1923", margin:"-8px", padding:8, borderRadius:4 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
        <div><div style={{ fontWeight:700, fontSize:7.5, color:"white" }}>Your Business</div><div style={{ color:"rgba(255,255,255,0.3)", fontSize:5.5 }}>Cape Town · 082 000 0000</div></div>
        <div style={{ textAlign:"right" }}><div style={{ display:"inline-block", background:"#c8972a", color:"#0f1923", fontSize:5, fontWeight:700, padding:"1px 5px", borderRadius:2 }}>INVOICE</div><div style={{ color:"white", fontWeight:700, fontSize:9, marginTop:2 }}>INV-007</div></div>
      </div>
      <div style={{ padding:"3px 4px", borderBottom:"1px solid rgba(255,255,255,0.08)", display:"flex", justifyContent:"space-between", color:"rgba(255,255,255,0.8)" }}><span>Geyser Install</span><span>R 4,800</span></div>
      <div style={{ padding:"3px 4px", display:"flex", justifyContent:"space-between", color:"rgba(255,255,255,0.8)" }}><span>Labour</span><span>R 1,200</span></div>
      <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:8, color:"#c8972a" }}>R 6,900.00</div>
    </div>
  )},
  { id:"6",  label:"Blush Pink",   renderPreview: () => (
    <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
      <div style={{ background:"linear-gradient(135deg,#fce7f3,#fbcfe8)", margin:"-8px -8px 6px", padding:"7px 8px", display:"flex", justifyContent:"space-between" }}>
        <div><div style={{ fontWeight:700, fontSize:7.5, color:"#831843" }}>Your Business</div><div style={{ color:"#be185d", fontSize:5.5 }}>Cape Town · 082 000 0000</div></div>
        <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"#be185d", fontWeight:700, textTransform:"uppercase" }}>INVOICE</div><div style={{ color:"#831843", fontWeight:700, fontSize:9 }}>INV-007</div></div>
      </div>
      <div style={{ padding:"3px 4px", borderBottom:"1px solid #fce7f3", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
      <div style={{ padding:"3px 4px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
      <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7, color:"#be185d" }}>Total: R 6,900.00</div>
    </div>
  )},
  { id:"7",  label:"Sky Blue",     renderPreview: () => (
    <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
      <div style={{ background:"linear-gradient(135deg,#0ea5e9,#0284c7)", margin:"-8px -8px 6px", padding:"7px 8px", display:"flex", justifyContent:"space-between" }}>
        <div><div style={{ fontWeight:700, fontSize:7.5, color:"white" }}>Your Business</div><div style={{ color:"rgba(255,255,255,0.6)", fontSize:5.5 }}>Cape Town · 082 000 0000</div></div>
        <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"#bae6fd", fontWeight:700, textTransform:"uppercase" }}>INVOICE</div><div style={{ color:"white", fontWeight:700, fontSize:9 }}>INV-007</div></div>
      </div>
      <div style={{ padding:"3px 4px", borderBottom:"1px solid #e0f2fe", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
      <div style={{ padding:"3px 4px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
      <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7, color:"#0284c7" }}>Total: R 6,900.00</div>
    </div>
  )},
  { id:"8",  label:"Bubble Orbs",  renderPreview: () => (
    <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
      <div style={{ position:"relative", background:"#0f1923", margin:"-8px -8px 6px", padding:"7px 8px", overflow:"hidden", height:38 }}>
        <div style={{ position:"absolute", top:-10, right:10, width:30, height:30, background:"#c8972a", opacity:0.2, borderRadius:"50%" }}></div>
        <div style={{ position:"absolute", bottom:-8, right:25, width:20, height:20, background:"#c8972a", opacity:0.15, borderRadius:"50%" }}></div>
        <div style={{ position:"relative", zIndex:2, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontWeight:700, fontSize:7.5, color:"white" }}>Your Business</div>
          <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"#c8972a", fontWeight:700 }}>INVOICE</div><div style={{ color:"white", fontWeight:700, fontSize:8 }}>INV-007</div></div>
        </div>
      </div>
      <div style={{ padding:"3px 4px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
      <div style={{ padding:"3px 4px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
      <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7 }}>Total: R 6,900.00</div>
    </div>
  )},
  { id:"9",  label:"Diagonal",     renderPreview: () => (
    <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
      <div style={{ position:"relative", margin:"-8px -8px 6px", height:38, overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, background:"#0f1923" }}></div>
        <div style={{ position:"absolute", bottom:0, right:0, width:"55%", height:"100%", background:"white", transform:"skewX(-10deg) translateX(10px)" }}></div>
        <div style={{ position:"absolute", bottom:0, left:0, right:0, height:2, background:"linear-gradient(90deg,#c8972a,#f0d078,transparent)" }}></div>
        <div style={{ position:"relative", zIndex:2, padding:"7px 8px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontWeight:700, fontSize:7, color:"white" }}>Your Business</div>
          <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"#c8972a", fontWeight:700 }}>INVOICE</div><div style={{ fontWeight:700, fontSize:8, color:"#0f1923" }}>INV-007</div></div>
        </div>
      </div>
      <div style={{ padding:"3px 4px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
      <div style={{ padding:"3px 4px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
      <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7 }}>Total: R 6,900.00</div>
    </div>
  )},
  { id:"10", label:"Wave",         renderPreview: () => (
    <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
      <div style={{ background:"#0f1923", margin:"-8px -8px 0", padding:"6px 8px 0" }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
          <div style={{ fontWeight:700, fontSize:7, color:"white" }}>Your Business</div>
          <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"#c8972a", fontWeight:700 }}>INVOICE</div><div style={{ fontWeight:700, fontSize:8, color:"white" }}>INV-007</div></div>
        </div>
        <svg viewBox="0 0 100 8" preserveAspectRatio="none" style={{ display:"block", width:"calc(100% + 16px)", marginLeft:-8, height:8 }}><path d="M0,4 C20,8 40,0 60,4 C80,8 90,2 100,4 L100,8 L0,8Z" fill="white"/></svg>
      </div>
      <div style={{ padding:"3px 4px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
      <div style={{ padding:"3px 4px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
      <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7 }}>Total: R 6,900.00</div>
    </div>
  )},
  { id:"11", label:"Triangle",     renderPreview: () => (
    <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
      <div style={{ position:"relative", margin:"-8px -8px 6px", height:38, overflow:"hidden", background:"white", borderBottom:"1px solid #e2e8f0" }}>
        <div style={{ position:"absolute", top:0, right:0, width:0, height:0, borderStyle:"solid", borderWidth:"0 60px 38px 0", borderColor:"transparent #0f1923 transparent transparent" }}></div>
        <div style={{ position:"relative", zIndex:2, padding:"7px 8px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontWeight:700, fontSize:7, color:"#0f1923" }}>Your Business</div>
          <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"white", fontWeight:700 }}>INVOICE</div><div style={{ fontWeight:700, fontSize:8, color:"white" }}>INV-007</div></div>
        </div>
      </div>
      <div style={{ padding:"3px 4px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
      <div style={{ padding:"3px 4px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
      <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7 }}>Total: R 6,900.00</div>
    </div>
  )},
  { id:"12", label:"Split Column", renderPreview: () => (
    <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
      <div style={{ margin:"-8px -8px 6px", display:"flex", height:38 }}>
        <div style={{ background:"#0f1923", width:"40%", padding:"7px 6px", display:"flex", flexDirection:"column", justifyContent:"center" }}><div style={{ fontWeight:700, fontSize:6.5, color:"white" }}>Your Business</div><div style={{ color:"rgba(255,255,255,0.3)", fontSize:5 }}>Cape Town</div></div>
        <div style={{ background:"white", flex:1, padding:"7px 6px", display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"flex-end", borderBottom:"2px solid #c8972a" }}><div style={{ fontSize:5, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.5px" }}>Invoice</div><div style={{ fontWeight:700, fontSize:9, color:"#0f1923" }}>INV-007</div></div>
      </div>
      <div style={{ padding:"3px 4px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
      <div style={{ padding:"3px 4px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
      <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7 }}>Total: R 6,900.00</div>
    </div>
  )},
];

function TemplatesModule({ userId }) {
  const [business, setBusiness] = useState({ pdf_template:"1" });
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  useEffect(() => {
    DB.get("business", userId).then(b => {
      if (b) setBusiness(b);
      setLoading(false);
    });
  }, []);

  const selectTemplate = async (id) => {
    const updated = { ...business, pdf_template: id };
    setBusiness(updated);
    await DB.upsert("business", { ...updated, user_id: userId });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (loading) return <div style={{ padding:40, textAlign:"center", color:"#94a3b8" }}>Loading templates…</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:17, fontWeight:700, color:"#111", marginBottom:4 }}>Document Templates</h2>
          <p style={{ fontSize:13, color:"#64748b" }}>Choose the PDF style applied to all Quotes, Invoices, Receipts &amp; Job Reports.</p>
        </div>
        {saved && (
          <div style={{ display:"flex", alignItems:"center", gap:6, background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"7px 14px", fontSize:12.5, fontWeight:600, color:"#15803d" }}>
            <Icon name="check" size={13} style={{ color:"#15803d" }}/> Template saved
          </div>
        )}
      </div>

      {/* Currently active banner */}
      <div style={{ background:"linear-gradient(135deg,#0f1923,#1a2736)", borderRadius:10, padding:"12px 16px", marginBottom:20, display:"flex", alignItems:"center", gap:12, border:"1px solid rgba(200,151,42,0.25)" }}>
        <div style={{ width:32, height:32, borderRadius:8, background:"rgba(200,151,42,0.15)", border:"1px solid rgba(200,151,42,0.35)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <Icon name="file" size={14} style={{ color:"var(--gold)" }}/>
        </div>
        <div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginBottom:1 }}>Currently active</div>
          <div style={{ fontSize:13.5, fontWeight:700, color:"var(--gold-light)" }}>
            {TEMPLATES_META[business.pdf_template || "1"]?.label || "Classic"}
          </div>
        </div>
      </div>

      {/* Template grid */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(148px, 1fr))", gap:12 }}>
        {TEMPLATE_PREVIEWS.map(t => {
          const active = (business.pdf_template || "1") === t.id;
          return (
            <div
              key={t.id}
              onClick={() => setPreviewTemplate(t.id)}
              style={{
                cursor:"pointer",
                borderRadius:10,
                border: active ? "2.5px solid var(--gold)" : "2px solid var(--border-light)",
                boxShadow: active ? "0 0 0 3px rgba(200,151,42,0.15)" : "0 1px 4px rgba(0,0,0,0.06)",
                overflow:"hidden",
                transition:"all 0.15s",
                background:"white",
              }}
            >
              <div style={{ padding:8, background:"white", overflow:"hidden", height:140 }}>
                {t.renderPreview()}
              </div>
              <div style={{
                padding:"6px 10px",
                background: active ? "var(--ink)" : "#f8fafc",
                borderTop: active ? "none" : "1px solid var(--border-light)",
                display:"flex", alignItems:"center", justifyContent:"space-between"
              }}>
                <span style={{ fontSize:11, fontWeight:700, color: active ? "var(--gold)" : "var(--ink)" }}>{t.label}</span>
                {active && <span style={{ fontSize:10, color:"var(--gold)" }}>✓ Active</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview Modal */}
      {previewTemplate && (() => {
        const t = TEMPLATES_META[previewTemplate] || {};
        const isActive = (business.pdf_template || "1") === previewTemplate;
        return (
          <div onClick={() => setPreviewTemplate(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
            <div onClick={e => e.stopPropagation()} style={{ background:"white", borderRadius:16, overflow:"hidden", width:"100%", maxWidth:560, boxShadow:"0 24px 80px rgba(0,0,0,0.4)" }}>
              <div style={{ background:"var(--ink)", padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:15, color:"white" }}>{t.label}</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginTop:2 }}>{t.desc}</div>
                </div>
                <button onClick={() => setPreviewTemplate(null)} style={{ background:"rgba(255,255,255,0.08)", border:"none", color:"white", width:32, height:32, borderRadius:8, cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
              </div>
              <div style={{ padding:"24px 28px", maxHeight:"60vh", overflowY:"auto" }}>
                <TemplateLargePreview id={previewTemplate} />
              </div>
              <div style={{ padding:"14px 20px", borderTop:"1px solid var(--border)", display:"flex", gap:10, justifyContent:"flex-end" }}>
                <button onClick={() => setPreviewTemplate(null)} style={{ padding:"9px 18px", borderRadius:8, border:"1px solid var(--border-light)", background:"white", cursor:"pointer", fontSize:13, fontWeight:600, color:"var(--ink)" }}>Cancel</button>
                <button
                  onClick={() => { selectTemplate(previewTemplate); setPreviewTemplate(null); }}
                  style={{ padding:"9px 18px", borderRadius:8, border:"none", background: isActive ? "var(--status-grey-bg)" : "var(--ink)", color: isActive ? "var(--status-grey-fg)" : "white", cursor: isActive ? "default" : "pointer", fontSize:13, fontWeight:600 }}
                  disabled={isActive}
                >
                  {isActive ? "✓ Currently Active" : "Select This Template"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function SettingsModule({ userId, onBusinessUpdate }) {
  const [business, setBusiness] = useState({ name:"", vat_number:"", reg_number:"", phone:"", email:"", address:"", bank_name:"", bank_account_holder:"", bank_account_number:"", bank_branch_code:"", payfast_link:"", ozow_link:"", other_payment_link:"", other_payment_label:"", logo:null, pdf_template:"1", currency:"ZAR", tax_rate:15, tax_label:"VAT", unit_system:"metric" });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem("fj_user") || "{}");
  const userIsOwner = isOwner(user.email);
  const [tab, setTab] = useState("business");
  const [pwForm, setPwForm] = useState({ current:"", newPw:"", confirm:"" });
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null); // id of template being previewed in modal

  useEffect(() => {
    DB.get("business", userId).then(b => {
      if (b) setBusiness(b);
      setLoading(false);
    });
  }, []);

  const [bizErrors, setBizErrors] = useState({});

  const save = async () => {
    const errs = {
      name:  V.required(business.name, "Business name"),
      email: V.email(business.email),
      phone: V.phone(business.phone),
    };
    const first = V.run(errs);
    if (first) { setBizErrors(errs); return; }
    setBizErrors({});
    await DB.upsert("business", { ...business, user_id: userId });
    if (onBusinessUpdate) onBusinessUpdate(business);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const changePassword = async () => {
    setPwError(""); setPwSuccess(false); setPwLoading(true);
    if (!pwForm.current) { setPwError("Please enter your current password."); setPwLoading(false); return; }
    if (pwForm.newPw.length < 6) { setPwError("New password must be at least 6 characters."); setPwLoading(false); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwError("New passwords do not match."); setPwLoading(false); return; }
    // Verify current password via Supabase Auth
    const { error: verifyErr } = await supabase.auth.signInWithPassword({ email: user.email, password: pwForm.current });
    if (verifyErr) { setPwError("Current password is incorrect."); setPwLoading(false); return; }
    // Update password via Supabase Auth
    const token = localStorage.getItem("sb_token");
    const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY, "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ password: pwForm.newPw }),
    });
    if (!res.ok) { setPwError("Failed to update password. Please try again."); setPwLoading(false); return; }
    setPwSuccess(true);
    setPwForm({ current:"", newPw:"", confirm:"" });
    setPwLoading(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setBusiness({...business, logo: ev.target.result});
      reader.readAsDataURL(file);
    }
  };

  const tabs = ["business","preferences","banking","account","subscription","support", ...(userIsOwner ? ["admin"] : [])];
  const tabMeta = {
    business:     { icon:"🏢", label:"Business" },
    preferences:  { icon:"⚙️", label:"Preferences" },
    banking:      { icon:"🏦", label:"Banking"  },
    account:      { icon:"👤", label:"Account"  },
    subscription: { icon:"⭐", label:"Plan"     },
    support:      { icon:"💬", label:"Support"  },
    admin:        { icon:"🔐", label:"Admin"    },
  };

  return (
    <div>
      {/* ── TEMPLATE PREVIEW MODAL ── */}
      {previewTemplate && (() => {
        const TEMPLATES_META = {
          "1":  { label:"Classic",      desc:"Navy table header, gold banking bar" },
          "2":  { label:"Dark Header",  desc:"Full-width navy banner, gold total" },
          "3":  { label:"Gold Accent",  desc:"Gradient gold top bar, warm tones" },
          "4":  { label:"Minimal",      desc:"Type-driven, no fills, ultra clean" },
          "5":  { label:"Bold Dark",    desc:"Full dark background, gold accents" },
          "6":  { label:"Blush Pink",   desc:"Soft pink gradient header, rose accents" },
          "5":  { label:"Bold Dark",    desc:"Full dark background, gold accents" },
          "6":  { label:"Blush Pink",   desc:"Soft pink gradient header, rose accents" },
          "7":  { label:"Sky Blue",     desc:"Cool blue gradient header, navy accents" },
          "8":  { label:"Bubble Orbs",  desc:"Gold orb shapes on navy, organic feel" },
          "9":  { label:"Diagonal",     desc:"Angled slash dividing dark and white" },
          "10": { label:"Wave",         desc:"SVG wave breaking from navy header" },
          "11": { label:"Triangle",     desc:"Dark triangle corner, doc type inside" },
          "12": { label:"Split Column", desc:"Vertical dark/light split header" },
        };
        const t = TEMPLATES_META[previewTemplate] || {};
        const isActive = (business.pdf_template || "1") === previewTemplate;
        return (
          <div onClick={() => setPreviewTemplate(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
            <div onClick={e => e.stopPropagation()} style={{ background:"white", borderRadius:16, overflow:"hidden", width:"100%", maxWidth:560, boxShadow:"0 24px 80px rgba(0,0,0,0.4)" }}>
              {/* Modal header */}
              <div style={{ background:"var(--ink)", padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:15, color:"white" }}>{t.label}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginTop:2 }}>{t.desc}</div>
                </div>
                <button onClick={() => setPreviewTemplate(null)} style={{ background:"rgba(255,255,255,0.08)", border:"none", color:"white", width:32, height:32, borderRadius:8, cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
              </div>
              {/* Large preview area — real document structure */}
              <div style={{ padding:28, background:"#f4f5f7", maxHeight:"60vh", overflowY:"auto" }}>
                <div style={{ background:"white", borderRadius:10, padding:"24px 28px", boxShadow:"0 2px 16px rgba(0,0,0,0.08)", fontFamily:"sans-serif", fontSize:11 }}>
                  <TemplateLargePreview id={previewTemplate} />
                </div>
              </div>
              {/* Footer actions */}
              <div style={{ padding:"14px 20px", borderTop:"1px solid var(--border-light)", display:"flex", gap:10, justifyContent:"flex-end" }}>
                <button onClick={() => setPreviewTemplate(null)} style={{ padding:"9px 18px", borderRadius:8, border:"1px solid var(--border-light)", background:"white", cursor:"pointer", fontSize:13, fontWeight:600, color:"var(--ink)" }}>Cancel</button>
                <button onClick={() => { const updated = {...business, pdf_template: previewTemplate}; setBusiness(updated); DB.upsert("business", {...updated, user_id: userId}); setPreviewTemplate(null); }} style={{ padding:"9px 18px", borderRadius:8, border:"none", background: isActive ? "#e2e8f0" : "var(--ink)", cursor:"pointer", fontSize:13, fontWeight:700, color: isActive ? "var(--status-grey-fg)" : "var(--gold)" }}>
                  {isActive ? "✓ Currently Selected" : "Select This Template"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── DESKTOP TABS ── */}
      <div className="tabs settings-desktop-tabs">
        {tabs.map(t => (
          <div key={t} className={tab===t ? "tab active" : "tab"} onClick={() => setTab(t)}>
            {t === "business" ? "Business Settings"
              : t === "preferences" ? "Preferences"
              : t === "banking" ? "Banking Details"
              : t === "subscription" ? "Subscription Plan"
              : t === "account" ? "My Account"
              : t === "support" ? "Support"
              : "🔐 Admin Panel"}
          </div>
        ))}
      </div>

      {/* ── MOBILE TOPBAR ── */}
      <div className="settings-mobile-topbar">
        <div style={{ fontSize:15, fontWeight:700, color:"white", letterSpacing:"0.2px" }}>⚙️ Settings</div>
        <div className="settings-mobile-topbar-badge">v3.1</div>
      </div>

      {/* ── MOBILE NAV GRID ── */}
      <div className="settings-mobile-nav">
        <div className="settings-nav-grid">
          {tabs.map(t => (
            <div key={t} className={tab===t ? "settings-nav-tile active" : "settings-nav-tile"} onClick={() => setTab(t)}>
              <div className="settings-nav-tile-icon">{tabMeta[t]?.icon}</div>
              <div className="settings-nav-tile-label">{tabMeta[t]?.label}</div>
            </div>
          ))}
        </div>
      </div>

      {saved && <div className="alert alert-success" style={{ margin:"0 0 16px" }}><Icon name="check" size={14} />Settings saved successfully.</div>}

      {tab === "business" && (
        <div className="card" style={{ maxWidth:"100%" }}>
          {/* Section header — mobile only */}
          <div className="settings-sec-header">
            <div className="settings-sec-icon">🏢</div>
            <div>
              <div style={{ fontWeight:700, fontSize:14, color:"var(--ink)" }}>Business Settings</div>
              <div style={{ fontSize:11, color:"#94a3b8" }}>Your profile on documents</div>
            </div>
          </div>
          {/* Mobile logo card */}
          <div className="settings-logo-card">
            <div className="settings-logo-preview">
              {business.logo
                ? <img src={business.logo} alt="logo" style={{ width:"100%", height:"100%", objectFit:"contain" }} loading="lazy" />
                : <Icon name="photo" size={24} style={{ color:"rgba(200,151,42,0.5)" }} />}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:13, color:"white", marginBottom:3 }}>Business Logo</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", lineHeight:1.5 }}>Appears on invoices,<br/>quotes &amp; receipts</div>
            </div>
            <input type="file" accept="image/*" id="logo-upload-mobile" style={{ display:"none" }} onChange={handleLogoUpload} />
            <label htmlFor="logo-upload-mobile" className="settings-logo-btn">Upload</label>
          </div>

          <div className="card-title" style={{ marginBottom:20 }}>Business Information</div>
          {/* Desktop logo upload */}
          <div style={{ marginBottom:20 }}>
            <label className="form-label">Business Logo</label>
            <div style={{ display:"flex", alignItems:"center", gap:16 }}>
              {business.logo ? <img src={business.logo} alt="logo" style={{ width:80, height:80, objectFit:"contain", borderRadius:8, border:"1px solid var(--border)" }} loading="lazy" /> : <div style={{ width:80, height:80, background:"var(--surface)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", color:"#94a3b8", border:"2px dashed var(--border)" }}><Icon name="photo" size={28} /></div>}
              <div>
                <input type="file" accept="image/*" id="logo-upload" style={{ display:"none" }} onChange={handleLogoUpload} />
                <label htmlFor="logo-upload" className="btn btn-outline" style={{ cursor:"pointer" }}>Upload Logo</label>
                <div className="text-sm text-muted mt-1">PNG, JPG — appears on invoices & quotes</div>
              </div>
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Business Name *</label><input className="form-input" style={bizErrors.name?{borderColor:"#ef4444"}:{}} value={business.name} onChange={e => setBusiness({...business, name:e.target.value})} /><FormError error={bizErrors.name} /></div>
            <div className="form-group"><label className="form-label">Business Tax Number</label><input className="form-input" value={business.vat_number} onChange={e => setBusiness({...business, vat_number:e.target.value})} placeholder="e.g. 4120000000" /></div>
            <div className="form-group"><label className="form-label">Registration Number</label><input className="form-input" value={business.reg_number} onChange={e => setBusiness({...business, reg_number:e.target.value})} placeholder="2020/123456/07" /></div>
            <div className="form-group"><label className="form-label">Business Phone</label><input className="form-input" style={bizErrors.phone?{borderColor:"#ef4444"}:{}} value={business.phone} onChange={e => setBusiness({...business, phone:e.target.value})} placeholder="011 000 0000" /><FormError error={bizErrors.phone} /></div>
            <div className="form-group"><label className="form-label">Business Email</label><input className="form-input" style={bizErrors.email?{borderColor:"#ef4444"}:{}} type="email" value={business.email} onChange={e => setBusiness({...business, email:e.target.value})} /><FormError error={bizErrors.email} /></div>
          </div>
          <div className="form-group"><label className="form-label">Physical Address</label><textarea className="form-textarea" value={business.address} onChange={e => setBusiness({...business, address:e.target.value})} rows={2} /></div>

          {/* PDF Template Selector */}
          <div style={{ marginTop:8, marginBottom:4 }}>
            <label className="form-label" style={{ marginBottom:12, display:"block" }}>PDF Template Style</label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(148px, 1fr))", gap:12 }}>
              {[
                { id:"1",  label:"Classic",      renderPreview: () => (
                  <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", paddingBottom:5, marginBottom:5, borderBottom:"1.5px solid #e2e8f0" }}>
                      <div><div style={{ fontWeight:700, fontSize:7.5, color:"#0f1923" }}>Your Business</div><div style={{ color:"#94a3b8", marginTop:2 }}>Cape Town · 082 000 0000</div></div>
                      <div style={{ textAlign:"right" }}><div style={{ fontWeight:700, fontSize:10, color:"#0f1923" }}>INVOICE</div><div style={{ color:"#c8972a", fontWeight:600 }}>INV-007</div></div>
                    </div>
                    <div style={{ background:"#f8fafc", borderRadius:3, padding:"3px 5px", marginBottom:5 }}><div style={{ fontSize:5, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.5px" }}>BILL TO</div><div style={{ fontWeight:700, color:"#0f1923" }}>Arman Construction</div></div>
                    <div style={{ width:"100%", borderCollapse:"collapse" }}><div style={{ background:"#0f1923", color:"white", padding:"3px 4px", display:"flex", justifyContent:"space-between" }}><span>Description</span><span>Total</span></div><div style={{ padding:"3px 4px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div><div style={{ padding:"3px 4px", background:"#fafafa", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div></div>
                    <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7 }}>Total: R 6,900.00</div>
                    <div style={{ marginTop:4, padding:"3px 5px", background:"#f8fafc", borderLeft:"2px solid #c8972a", fontSize:5.5, color:"#475569" }}>FNB · 62012345678 · Ref: INV-007</div>
                  </div>
                )},
                { id:"2",  label:"Dark Header",  renderPreview: () => (
                  <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
                    <div style={{ background:"#0f1923", margin:"-8px -8px 6px", padding:"7px 8px", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                      <div><div style={{ fontWeight:700, fontSize:7.5, color:"white" }}>Your Business</div><div style={{ color:"rgba(255,255,255,0.4)", fontSize:5.5, marginTop:1 }}>Cape Town · 082 000 0000</div></div>
                      <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"#c8972a", fontWeight:700, letterSpacing:"0.5px", textTransform:"uppercase" }}>INVOICE</div><div style={{ color:"white", fontWeight:700, fontSize:9 }}>INV-007</div></div>
                    </div>
                    <div style={{ display:"flex", gap:4, marginBottom:5 }}><div style={{ flex:1, background:"#f8fafc", borderRadius:3, padding:"3px 4px" }}><div style={{ fontSize:5, color:"#94a3b8" }}>BILL TO</div><div style={{ fontWeight:700 }}>Arman Construction</div></div><div style={{ flex:1, background:"#f8fafc", borderRadius:3, padding:"3px 4px" }}><div style={{ fontSize:5, color:"#94a3b8" }}>DATE</div><div style={{ fontWeight:700 }}>08 Mar 2026</div></div></div>
                    <div style={{ padding:"3px 4px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
                    <div style={{ padding:"3px 4px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
                    <div style={{ background:"#0f1923", borderRadius:3, padding:"5px 6px", display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:5 }}><span style={{ color:"rgba(255,255,255,0.45)", fontSize:5.5 }}>Total Due</span><span style={{ color:"#c8972a", fontWeight:700, fontSize:8 }}>R 6,900.00</span></div>
                  </div>
                )},
                { id:"3",  label:"Gold Accent",  renderPreview: () => (
                  <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
                    <div style={{ height:3, background:"linear-gradient(90deg,#c8972a,#f0d078,#c8972a)", margin:"-8px -8px 6px" }}></div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <div><div style={{ fontWeight:700, fontSize:7.5 }}>Your Business</div><div style={{ color:"#94a3b8", fontSize:5.5 }}>Cape Town · 082 000 0000</div></div>
                      <div style={{ textAlign:"right" }}><div style={{ display:"inline-block", background:"linear-gradient(135deg,#c8972a,#f0d078)", color:"#0f1923", fontSize:5, fontWeight:700, padding:"1px 5px", borderRadius:8 }}>INVOICE</div><div style={{ fontWeight:700, fontSize:8, marginTop:2 }}>INV-007</div></div>
                    </div>
                    <div style={{ height:0.5, background:"linear-gradient(90deg,rgba(200,151,42,0.2),#c8972a,rgba(200,151,42,0.2))", marginBottom:4 }}></div>
                    <div style={{ marginBottom:4 }}><div style={{ fontSize:5, color:"#c8972a", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px" }}>BILL TO</div><div style={{ fontWeight:700 }}>Arman Construction</div></div>
                    <div style={{ borderBottom:"1.5px solid #c8972a", padding:"2px 3px", display:"flex", justifyContent:"space-between", color:"#c8972a", fontSize:5.5, fontWeight:700 }}><span>Description</span><span>Total</span></div>
                    <div style={{ padding:"3px 3px", borderBottom:"1px solid #fef3c7", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
                    <div style={{ padding:"3px 3px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
                    <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7, color:"#c8972a" }}>Total: R 6,900.00</div>
                  </div>
                )},
                { id:"4",  label:"Minimal",      renderPreview: () => (
                  <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
                    <div style={{ fontWeight:700, fontSize:10, letterSpacing:"-0.3px" }}>Your Business</div>
                    <div style={{ color:"#9ca3af", fontSize:5.5, marginBottom:5 }}>Cape Town · info@biz.co.za</div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", paddingBottom:4, marginBottom:5, borderBottom:"1px solid #f3f4f6" }}>
                      <div><div style={{ fontSize:5, color:"#d1d5db", textTransform:"uppercase", letterSpacing:"0.5px" }}>BILL TO</div><div style={{ fontWeight:600, fontSize:7 }}>Arman Construction</div></div>
                      <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.5px" }}>Invoice</div><div style={{ fontWeight:700, fontSize:9 }}>INV-007</div></div>
                    </div>
                    <div style={{ borderBottom:"1px solid #111827", padding:"2px 3px", display:"flex", justifyContent:"space-between", fontWeight:700, fontSize:5.5 }}><span>Description</span><span>Total</span></div>
                    <div style={{ padding:"3px 3px", borderBottom:"1px solid #f3f4f6", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
                    <div style={{ padding:"3px 3px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
                    <div style={{ borderTop:"1px solid #111827", textAlign:"right", marginTop:4, fontWeight:700, paddingTop:3, fontSize:7 }}>Total: R 6,900.00</div>
                  </div>
                )},
                { id:"5",  label:"Bold Dark",    renderPreview: () => (
                  <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4, background:"#0f1923", margin:"-8px", padding:8, borderRadius:4 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                      <div><div style={{ fontWeight:700, fontSize:7.5, color:"white" }}>Your Business</div><div style={{ color:"rgba(255,255,255,0.3)", fontSize:5.5 }}>Cape Town · 082 000 0000</div></div>
                      <div style={{ textAlign:"right" }}><div style={{ display:"inline-block", background:"#c8972a", color:"#0f1923", fontSize:5, fontWeight:700, padding:"1px 5px", borderRadius:2 }}>INVOICE</div><div style={{ color:"white", fontWeight:700, fontSize:9, marginTop:2 }}>INV-007</div></div>
                    </div>
                    <div style={{ background:"rgba(255,255,255,0.06)", borderRadius:3, padding:"3px 5px", marginBottom:5 }}><div style={{ fontSize:5, color:"rgba(255,255,255,0.3)" }}>BILL TO</div><div style={{ fontWeight:700, color:"white" }}>Arman Construction</div></div>
                    <div style={{ borderBottom:"1px solid rgba(200,151,42,0.4)", padding:"2px 3px", display:"flex", justifyContent:"space-between", color:"#c8972a", fontSize:5.5 }}><span>Description</span><span>Total</span></div>
                    <div style={{ padding:"3px 3px", borderBottom:"1px solid rgba(255,255,255,0.06)", display:"flex", justifyContent:"space-between", color:"rgba(255,255,255,0.75)" }}><span>Geyser Install</span><span>R 4,800</span></div>
                    <div style={{ padding:"3px 3px", display:"flex", justifyContent:"space-between", color:"rgba(255,255,255,0.75)" }}><span>Labour</span><span>R 1,200</span></div>
                    <div style={{ background:"rgba(200,151,42,0.12)", border:"1px solid rgba(200,151,42,0.25)", borderRadius:3, padding:"4px 6px", display:"flex", justifyContent:"space-between", marginTop:5 }}><span style={{ color:"rgba(255,255,255,0.4)", fontSize:5.5 }}>Total Due</span><span style={{ color:"#c8972a", fontWeight:700, fontSize:8 }}>R 6,900.00</span></div>
                  </div>
                )},
                { id:"6",  label:"Blush Pink",   renderPreview: () => (
                  <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
                    <div style={{ background:"linear-gradient(135deg,#fce7f3,#fdf2f8)", margin:"-8px -8px 6px", padding:"7px 8px", borderBottom:"1px solid #fbcfe8", display:"flex", justifyContent:"space-between" }}>
                      <div><div style={{ fontWeight:700, fontSize:7.5, color:"#831843" }}>Your Business</div><div style={{ color:"#be185d", opacity:0.6, fontSize:5.5 }}>Cape Town · 082 000 0000</div></div>
                      <div style={{ textAlign:"right" }}><div style={{ display:"inline-block", background:"#ec4899", color:"white", fontSize:5, fontWeight:700, padding:"1px 5px", borderRadius:8 }}>INVOICE</div><div style={{ color:"#831843", fontWeight:700, fontSize:8, marginTop:2 }}>INV-007</div></div>
                    </div>
                    <div style={{ background:"#fdf2f8", borderLeft:"2px solid #ec4899", borderRadius:"0 3px 3px 0", padding:"3px 5px", marginBottom:5 }}><div style={{ fontSize:5, color:"#be185d", fontWeight:700, textTransform:"uppercase" }}>BILL TO</div><div style={{ fontWeight:700, color:"#831843" }}>Arman Construction</div></div>
                    <div style={{ background:"#831843", color:"white", padding:"2px 4px", display:"flex", justifyContent:"space-between", fontSize:5.5 }}><span>Description</span><span>Total</span></div>
                    <div style={{ padding:"3px 3px", borderBottom:"1px solid #fce7f3", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
                    <div style={{ padding:"3px 3px", background:"#fdf2f8", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
                    <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7, color:"#831843" }}>Total: R 6,900.00</div>
                  </div>
                )},
                { id:"7",  label:"Sky Blue",     renderPreview: () => (
                  <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
                    <div style={{ background:"linear-gradient(135deg,#eff6ff,#dbeafe)", margin:"-8px -8px 6px", padding:"7px 8px", borderBottom:"1px solid #bfdbfe", display:"flex", justifyContent:"space-between" }}>
                      <div><div style={{ fontWeight:700, fontSize:7.5, color:"#1e3a8a" }}>Your Business</div><div style={{ color:"#3b82f6", opacity:0.7, fontSize:5.5 }}>Cape Town · 082 000 0000</div></div>
                      <div style={{ textAlign:"right" }}><div style={{ display:"inline-block", background:"#2563eb", color:"white", fontSize:5, fontWeight:700, padding:"1px 5px", borderRadius:8 }}>INVOICE</div><div style={{ color:"#1e3a8a", fontWeight:700, fontSize:8, marginTop:2 }}>INV-007</div></div>
                    </div>
                    <div style={{ background:"#eff6ff", borderLeft:"2px solid #2563eb", borderRadius:"0 3px 3px 0", padding:"3px 5px", marginBottom:5 }}><div style={{ fontSize:5, color:"#2563eb", fontWeight:700, textTransform:"uppercase" }}>BILL TO</div><div style={{ fontWeight:700, color:"#1e3a8a" }}>Arman Construction</div></div>
                    <div style={{ background:"#1e3a8a", color:"white", padding:"2px 4px", display:"flex", justifyContent:"space-between", fontSize:5.5 }}><span>Description</span><span>Total</span></div>
                    <div style={{ padding:"3px 3px", borderBottom:"1px solid #dbeafe", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
                    <div style={{ padding:"3px 3px", background:"#eff6ff", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
                    <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7, color:"#1e3a8a" }}>Total: R 6,900.00</div>
                  </div>
                )},
                { id:"8",  label:"Bubble Orbs",  renderPreview: () => (
                  <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
                    <div style={{ position:"relative", background:"#0f1923", margin:"-8px -8px 6px", padding:"7px 8px", overflow:"hidden", height:44, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={{ position:"absolute", top:-16, right:-16, width:50, height:50, background:"#c8972a", opacity:0.2, borderRadius:"50%" }}></div>
                      <div style={{ position:"absolute", top:4, right:26, width:28, height:28, background:"#c8972a", opacity:0.12, borderRadius:"50%" }}></div>
                      <div style={{ position:"relative", zIndex:2 }}><div style={{ fontWeight:700, fontSize:7.5, color:"white" }}>Your Business</div><div style={{ color:"rgba(255,255,255,0.4)", fontSize:5.5 }}>Cape Town</div></div>
                      <div style={{ position:"relative", zIndex:2, textAlign:"right" }}><div style={{ fontSize:5, color:"#c8972a", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px" }}>INVOICE</div><div style={{ color:"white", fontWeight:700, fontSize:8 }}>INV-007</div></div>
                    </div>
                    <div style={{ background:"#f8fafc", borderRadius:3, padding:"3px 5px", marginBottom:5 }}><div style={{ fontSize:5, color:"#94a3b8" }}>BILL TO</div><div style={{ fontWeight:700 }}>Arman Construction</div></div>
                    <div style={{ background:"#0f1923", color:"white", padding:"2px 4px", display:"flex", justifyContent:"space-between", fontSize:5.5 }}><span>Description</span><span>Total</span></div>
                    <div style={{ padding:"3px 3px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
                    <div style={{ padding:"3px 3px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
                    <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7 }}>Total: R 6,900.00</div>
                  </div>
                )},
                { id:"9",  label:"Diagonal",     renderPreview: () => (
                  <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
                    <div style={{ position:"relative", margin:"-8px -8px 6px", height:44, overflow:"hidden" }}>
                      <div style={{ position:"absolute", inset:0, background:"#0f1923" }}></div>
                      <div style={{ position:"absolute", bottom:0, right:0, width:"52%", height:"100%", background:"white", transform:"skewX(-10deg) translateX(8px)", transformOrigin:"bottom right" }}></div>
                      <div style={{ position:"absolute", bottom:0, left:0, right:0, height:2, background:"linear-gradient(90deg,#c8972a,#f0d078 40%,transparent)" }}></div>
                      <div style={{ position:"relative", zIndex:2, padding:"8px 8px", display:"flex", justifyContent:"space-between", alignItems:"center", height:"100%" }}>
                        <div><div style={{ fontWeight:700, fontSize:7, color:"white" }}>Your Business</div><div style={{ color:"rgba(255,255,255,0.4)", fontSize:5 }}>Cape Town</div></div>
                        <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"#c8972a", fontWeight:700, textTransform:"uppercase" }}>INVOICE</div><div style={{ fontWeight:700, fontSize:8, color:"#0f1923" }}>INV-007</div></div>
                      </div>
                    </div>
                    <div style={{ background:"#f8fafc", borderRadius:3, padding:"3px 5px", marginBottom:5 }}><div style={{ fontSize:5, color:"#94a3b8" }}>BILL TO</div><div style={{ fontWeight:700 }}>Arman Construction</div></div>
                    <div style={{ background:"#0f1923", color:"white", padding:"2px 4px", display:"flex", justifyContent:"space-between", fontSize:5.5 }}><span>Description</span><span>Total</span></div>
                    <div style={{ padding:"3px 3px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
                    <div style={{ padding:"3px 3px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
                    <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7 }}>Total: R 6,900.00</div>
                  </div>
                )},
                { id:"10", label:"Wave",         renderPreview: () => (
                  <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
                    <div style={{ background:"#0f1923", margin:"-8px -8px 0", padding:"7px 8px 0" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <div><div style={{ fontWeight:700, fontSize:7, color:"white" }}>Your Business</div><div style={{ color:"rgba(255,255,255,0.4)", fontSize:5 }}>Cape Town</div></div>
                        <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"#c8972a", fontWeight:700, textTransform:"uppercase" }}>INVOICE</div><div style={{ fontWeight:700, fontSize:8, color:"white" }}>INV-007</div></div>
                      </div>
                      <svg viewBox="0 0 160 10" preserveAspectRatio="none" style={{ display:"block", width:"calc(100% + 16px)", marginLeft:-8, height:10 }}><path d="M0,5 C30,10 60,0 90,5 C120,10 145,2 160,5 L160,10 L0,10Z" fill="white"/></svg>
                    </div>
                    <div style={{ background:"#f8fafc", borderRadius:3, padding:"3px 5px", marginBottom:5, marginTop:3 }}><div style={{ fontSize:5, color:"#94a3b8" }}>BILL TO</div><div style={{ fontWeight:700 }}>Arman Construction</div></div>
                    <div style={{ background:"#0f1923", color:"white", padding:"2px 4px", display:"flex", justifyContent:"space-between", fontSize:5.5 }}><span>Description</span><span>Total</span></div>
                    <div style={{ padding:"3px 3px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
                    <div style={{ padding:"3px 3px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
                    <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7 }}>Total: R 6,900.00</div>
                  </div>
                )},
                { id:"11", label:"Triangle",     renderPreview: () => (
                  <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
                    <div style={{ position:"relative", margin:"-8px -8px 6px", height:44, overflow:"hidden", background:"white", borderBottom:"1px solid #e2e8f0" }}>
                      <div style={{ position:"absolute", top:0, right:0, width:0, height:0, borderStyle:"solid", borderWidth:"0 70px 44px 0", borderColor:"transparent #0f1923 transparent transparent" }}></div>
                      <div style={{ position:"absolute", top:0, right:0, width:0, height:0, borderStyle:"solid", borderWidth:"0 55px 34px 0", borderColor:"transparent #c8972a transparent transparent", opacity:0.3 }}></div>
                      <div style={{ position:"relative", zIndex:2, padding:"8px 8px", display:"flex", justifyContent:"space-between", alignItems:"center", height:"100%" }}>
                        <div><div style={{ fontWeight:700, fontSize:7, color:"#0f1923" }}>Your Business</div><div style={{ color:"#94a3b8", fontSize:5 }}>Cape Town</div></div>
                        <div style={{ textAlign:"right" }}><div style={{ fontSize:5, color:"white", fontWeight:700, textTransform:"uppercase" }}>INVOICE</div><div style={{ fontWeight:700, fontSize:8, color:"white" }}>INV-007</div></div>
                      </div>
                    </div>
                    <div style={{ background:"#f8fafc", borderRadius:3, padding:"3px 5px", marginBottom:5 }}><div style={{ fontSize:5, color:"#94a3b8" }}>BILL TO</div><div style={{ fontWeight:700 }}>Arman Construction</div></div>
                    <div style={{ background:"#0f1923", color:"white", padding:"2px 4px", display:"flex", justifyContent:"space-between", fontSize:5.5 }}><span>Description</span><span>Total</span></div>
                    <div style={{ padding:"3px 3px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
                    <div style={{ padding:"3px 3px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
                    <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7 }}>Total: R 6,900.00</div>
                  </div>
                )},
                { id:"12", label:"Split Column", renderPreview: () => (
                  <div style={{ fontFamily:"sans-serif", fontSize:6, lineHeight:1.4 }}>
                    <div style={{ margin:"-8px -8px 6px", display:"flex", height:44 }}>
                      <div style={{ background:"#0f1923", width:"40%", padding:"7px 6px", display:"flex", flexDirection:"column", justifyContent:"center" }}><div style={{ fontWeight:700, fontSize:6.5, color:"white" }}>Your Business</div><div style={{ color:"rgba(255,255,255,0.3)", fontSize:5 }}>Cape Town</div></div>
                      <div style={{ background:"white", flex:1, padding:"7px 6px", display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"flex-end", borderBottom:"2px solid #c8972a" }}><div style={{ fontSize:5, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.5px" }}>Invoice</div><div style={{ fontWeight:700, fontSize:9, color:"#0f1923" }}>INV-007</div></div>
                    </div>
                    <div style={{ background:"#f8fafc", borderRadius:3, padding:"3px 5px", marginBottom:5 }}><div style={{ fontSize:5, color:"#94a3b8" }}>BILL TO</div><div style={{ fontWeight:700 }}>Arman Construction</div></div>
                    <div style={{ background:"#0f1923", color:"white", padding:"2px 4px", display:"flex", justifyContent:"space-between", fontSize:5.5 }}><span>Description</span><span>Total</span></div>
                    <div style={{ padding:"3px 3px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between" }}><span>Geyser Install</span><span>R 4,800</span></div>
                    <div style={{ padding:"3px 3px", display:"flex", justifyContent:"space-between" }}><span>Labour</span><span>R 1,200</span></div>
                    <div style={{ textAlign:"right", marginTop:4, fontWeight:700, fontSize:7 }}>Total: R 6,900.00</div>
                  </div>
                )},
              ].map(t => {
                const active = (business.pdf_template || "1") === t.id;
                return (
                  <div
                    key={t.id}
                    onClick={() => setPreviewTemplate(t.id)}
                    style={{
                      cursor:"pointer",
                      borderRadius:10,
                      border: active ? "2.5px solid var(--gold)" : "2px solid var(--border-light)",
                      boxShadow: active ? "0 0 0 3px rgba(200,151,42,0.15)" : "0 1px 4px rgba(0,0,0,0.06)",
                      overflow:"hidden",
                      transition:"all 0.15s",
                      background:"white",
                    }}
                  >
                    {/* Mini preview */}
                    <div style={{ padding:8, background:"white", overflow:"hidden", height:140 }}>
                      {t.renderPreview()}
                    </div>
                    {/* Label bar */}
                    <div style={{
                      padding:"6px 10px",
                      background: active ? "var(--ink)" : "#f8fafc",
                      borderTop: active ? "none" : "1px solid var(--border-light)",
                      display:"flex", alignItems:"center", justifyContent:"space-between"
                    }}>
                      <span style={{ fontSize:11, fontWeight:700, color: active ? "var(--gold)" : "var(--ink)" }}>{t.label}</span>
                      {active && <span style={{ fontSize:10, color:"var(--gold)" }}>✓</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize:11, color:"var(--status-grey-fg)", marginTop:10 }}>Applies to all Quotes, Invoices, Receipts &amp; Job Reports</div>
          </div>

          <button className="btn btn-primary" onClick={save}>Save Business Settings</button>
        </div>
      )}

            {tab === "preferences" && (
        <div style={{ maxWidth:720, display:"flex", flexDirection:"column", gap:20 }}>

          {/* ── Currency & Tax ── */}
          <div className="card">
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
              <div style={{ width:34, height:34, borderRadius:9, background:"rgba(200,151,42,0.1)", border:"1px solid rgba(200,151,42,0.25)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:18 }}>💱</div>
              <div>
                <div className="card-title" style={{ marginBottom:0 }}>Currency & Tax</div>
                <div style={{ fontSize:12, color:"var(--muted)" }}>Applies to all documents — quotes, invoices, estimates, receipts</div>
              </div>
            </div>
            <div className="form-grid" style={{ marginTop:16 }}>
              <div className="form-group">
                <label className="form-label">Currency</label>
                <select className="form-input" value={business.currency||"ZAR"} onChange={e => {
                  const opt = getCurrencyOption(e.target.value);
                  setBusiness({...business, currency:e.target.value, tax_label:opt.taxLabel, tax_rate:opt.taxRate});
                }}>
                  {CURRENCY_OPTIONS.map(c => (
                    <option key={c.code} value={c.code}>{c.flag} {c.code} — {c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Tax Label</label>
                <input className="form-input" value={business.tax_label||""} onChange={e => setBusiness({...business, tax_label:e.target.value})} placeholder="VAT, GST, Sales Tax..." />
              </div>
              <div className="form-group">
                <label className="form-label">Tax Rate (%)</label>
                <input className="form-input" type="number" min="0" max="100" step="0.1" value={business.tax_rate !== undefined ? business.tax_rate : 15} onChange={e => setBusiness({...business, tax_rate:parseFloat(e.target.value)||0})} placeholder="0" />
                <div style={{ fontSize:11, color:"var(--muted)", marginTop:4 }}>Set to 0 to hide tax line on documents</div>
              </div>
            </div>
          </div>

          {/* ── Units & Measurements ── */}
          <div className="card">
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
              <div style={{ width:34, height:34, borderRadius:9, background:"rgba(59,130,246,0.08)", border:"1px solid rgba(59,130,246,0.2)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:18 }}>📐</div>
              <div>
                <div className="card-title" style={{ marginBottom:0 }}>Units & Measurements</div>
                <div style={{ fontSize:12, color:"var(--muted)" }}>Changes measurement labels throughout the app — distances, volumes, weights, areas</div>
              </div>
            </div>
            <div style={{ marginTop:16 }}>
              <label className="form-label" style={{ marginBottom:10, display:"block" }}>Unit System</label>
              <div style={{ display:"flex", gap:10 }}>
                {[
                  { val:"metric",   label:"Metric",   desc:"km · L · kg · m²", flag:"🌍" },
                  { val:"imperial", label:"Imperial", desc:"miles · gal · lbs · ft²", flag:"🇺🇸" },
                ].map(opt => (
                  <button key={opt.val} onClick={() => setBusiness({...business, unit_system:opt.val})}
                    style={{ flex:1, padding:"12px 16px", borderRadius:10, cursor:"pointer", textAlign:"left",
                      border: business.unit_system===opt.val ? "2px solid var(--gold)" : "1.5px solid var(--border)",
                      background: business.unit_system===opt.val ? "var(--gold-dim)" : "var(--off)",
                    }}>
                    <div style={{ fontSize:18, marginBottom:4 }}>{opt.flag}</div>
                    <div style={{ fontWeight:700, fontSize:13 }}>{opt.label}</div>
                    <div style={{ fontSize:11, color:"var(--muted)", marginTop:2 }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button className="btn btn-primary" style={{marginTop:4}} onClick={save}>Save Preferences</button>
        </div>
      )}

      {tab === "banking" && (
        <div style={{ maxWidth:720, display:"flex", flexDirection:"column", gap:20 }}>



                    {/* ── EFT Banking Details ── */}
          <div className="card">
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
              <div style={{ width:34, height:34, borderRadius:9, background:"var(--gold-p)", border:"1px solid var(--gold-b)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
              </div>
              <div>
                <div className="card-title" style={{ marginBottom:0 }}>EFT Banking Details</div>
                <div style={{ fontSize:12, color:"var(--muted)" }}>Appears on all invoices and the client portal payment screen</div>
              </div>
            </div>
            <div className="form-grid" style={{ marginTop:16 }}>
              <div className="form-group"><label className="form-label">Bank Name</label><input className="form-input" value={business.bank_name||""} onChange={e => setBusiness({...business, bank_name:e.target.value})} placeholder="e.g. First National Bank" /></div>
              <div className="form-group"><label className="form-label">Account Holder</label><input className="form-input" value={business.bank_account_holder||""} onChange={e => setBusiness({...business, bank_account_holder:e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Account Number</label><input className="form-input" value={business.bank_account_number||""} onChange={e => setBusiness({...business, bank_account_number:e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Branch Code</label><input className="form-input" value={business.bank_branch_code||""} onChange={e => setBusiness({...business, bank_branch_code:e.target.value})} placeholder="e.g. 250655" /></div>
            </div>
          </div>

          {/* ── Online Payment Links ── */}
          <div className="card">
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
              <div style={{ width:34, height:34, borderRadius:9, background:"rgba(16,185,129,0.08)", border:"1px solid rgba(16,185,129,0.2)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
              </div>
              <div>
                <div className="card-title" style={{ marginBottom:0 }}>Online Payment Links</div>
                <div style={{ fontSize:12, color:"var(--muted)" }}>Clients will see a "Pay Online" button in their portal for each link you add. No integration — just paste your link.</div>
              </div>
            </div>
            <div className="alert alert-info" style={{ marginTop:14, marginBottom:16, fontSize:12 }}>
              Leave blank any payment method you don't use. Only filled-in links will show to clients.
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div className="form-group">
                <label className="form-label" style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ background:"#007bff", color:"white", borderRadius:5, padding:"1px 7px", fontSize:10, fontWeight:700 }}>PAYFAST</span>
                  Payfast Payment Link
                </label>
                <input className="form-input" value={business.payfast_link||""} onChange={e => setBusiness({...business, payfast_link:e.target.value})} placeholder="https://www.payfast.co.za/eng/process?merchant_id=..." />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ background:"#6c2bd9", color:"white", borderRadius:5, padding:"1px 7px", fontSize:10, fontWeight:700 }}>OZOW</span>
                  Ozow Payment Link
                </label>
                <input className="form-input" value={business.ozow_link||""} onChange={e => setBusiness({...business, ozow_link:e.target.value})} placeholder="https://pay.ozow.com/..." />
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:10 }}>
                <div className="form-group">
                  <label className="form-label">Other Payment Link</label>
                  <input className="form-input" value={business.other_payment_link||""} onChange={e => setBusiness({...business, other_payment_link:e.target.value})} placeholder="SnapScan, Peach Payments, custom URL…" />
                </div>
                <div className="form-group">
                  <label className="form-label">Button Label</label>
                  <input className="form-input" value={business.other_payment_label||""} onChange={e => setBusiness({...business, other_payment_label:e.target.value})} placeholder="SnapScan" />
                </div>
              </div>
            </div>
          </div>

          <button className="btn btn-primary" onClick={save}>Save Banking & Payment Details</button>
        </div>
      )}

      {tab === "account" && (
        <div style={{ maxWidth:520, display:"flex", flexDirection:"column", gap:16 }}>
          {/* Account Info */}
          <div className="card">
            <div className="card-header"><span className="card-title">Account Information</span></div>
            <div style={{ padding:"16px 18px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid var(--border-light)" }}>
                <span style={{ fontSize:13, color:"var(--status-grey-fg)" }}>Email address</span>
                <span style={{ fontSize:13, fontWeight:600, color:"var(--ink)" }}>{user.email}</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0" }}>
                <span style={{ fontSize:13, color:"var(--status-grey-fg)" }}>Account type</span>
                <span className="badge badge-green">{userIsOwner ? "Owner" : "Business Account"}</span>
              </div>
            </div>
          </div>

          {/* Change Password */}
          <div className="card">
            <div className="card-header"><span className="card-title">Change Password</span></div>
            <div style={{ padding:"16px 18px" }}>
              {pwError && <div className="alert alert-error" style={{ marginBottom:14 }}>{pwError}</div>}
              {pwSuccess && <div className="alert alert-success" style={{ marginBottom:14 }}>✓ Password updated successfully.</div>}
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <PasswordInput value={pwForm.current} onChange={e => setPwForm({...pwForm, current:e.target.value})} placeholder="Your current password" />
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <PasswordInput value={pwForm.newPw} onChange={e => setPwForm({...pwForm, newPw:e.target.value})} placeholder="At least 6 characters" />
              </div>
              <div className="form-group" style={{ marginBottom:16 }}>
                <label className="form-label">Confirm New Password</label>
                <PasswordInput value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm:e.target.value})} placeholder="Repeat new password" />
              </div>
              <button className="btn btn-primary" onClick={changePassword} disabled={pwLoading}>
                {pwLoading ? "Updating..." : "Update Password"}
              </button>
            </div>
          </div>

          {/* Sign Out */}
          <div className="card">
            <div className="card-header"><span className="card-title">Session</span></div>
            <div style={{ padding:"16px 18px" }}>
              <div style={{ fontSize:13, color:"var(--status-grey-fg)", marginBottom:12 }}>
                Signed in as <strong style={{ color:"var(--ink)" }}>{user.email}</strong>
              </div>
              <button className="btn btn-outline btn-sm" onClick={() => { localStorage.removeItem("fj_user"); localStorage.removeItem("fj_authed"); window.__fjLogout && window.__fjLogout(); }}>
                <Icon name="logout" size={13} />Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "subscription" && (
        <div style={{ maxWidth:520 }}>
          {/* Dark plan card */}
          <div className="settings-plan-dark" style={{ marginBottom:12 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
              <div style={{ fontWeight:700, fontSize:15, color:"white" }}>{user.plan === "paid" ? "FieldJob Pro" : "Demo Trial"}</div>
              <span className={user.plan==="paid" ? "badge badge-green" : "badge badge-amber"}>{user.plan==="paid"?"Active":"Trial"}</span>
            </div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", marginBottom:16, lineHeight:1.6 }}>
              {user.plan === "paid" ? "Full access · Active subscription" : `Trial expires: ${formatDate(user.demo_expires)}`}
            </div>
            {user.plan !== "paid" && (
              <>
                <button className="btn btn-gold btn-lg w-full" onClick={() => setTab("payment-unavailable")} style={{ background:"linear-gradient(135deg,#c8972a,#f0d078)", color:"#0f1923", fontWeight:700 }}>
                  ⚡ Upgrade to Pro — R314/mo
                </button>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.2)", marginTop:8, textAlign:"center" }}>Secure payment via PayFast / Peach Payments</div>
              </>
            )}
            {user.plan === "paid" && (
              <a
                href={`mailto:support@getdiswayne.com?subject=${encodeURIComponent("Cancel Subscription — " + (user.email||""))}&body=${encodeURIComponent("Hi FieldJob Support,\n\nI would like to cancel my FieldJob Pro subscription.\n\nAccount email: " + (user.email||"") + "\n\nPlease confirm cancellation and the effective date.\n\nThank you.")}`}
                style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, width:"100%", padding:"10px", background:"transparent", color:"rgba(239,68,68,0.7)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:10, fontSize:12, fontWeight:600, textDecoration:"none", cursor:"pointer" }}>
                Cancel Subscription
              </a>
            )}
          </div>

          {/* Export Data */}
          <div className="card">
            <div className="card-title" style={{ marginBottom:6 }}>Export My Data</div>
            <div className="text-muted text-sm" style={{ marginBottom:16 }}>Download all your jobs, quotes, invoices, clients, workers and expenses as CSV files.</div>
            <button className="btn btn-outline w-full" onClick={() => exportUserData(userId, business.name)} style={{ display:"flex", alignItems:"center", gap:8, justifyContent:"center" }}>
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download CSV Export
            </button>
          </div>

        </div>
      )}

      {tab === "support" && (
        <div style={{ maxWidth:520 }}>
          {/* Section header */}
          <div className="settings-sec-header" style={{ marginBottom:16 }}>
            <div className="settings-sec-icon">💬</div>
            <div>
              <div style={{ fontWeight:700, fontSize:14, color:"var(--ink)" }}>Support</div>
              <div style={{ fontSize:11, color:"#94a3b8" }}>We're here to help</div>
            </div>
          </div>

          {/* Support rows */}
          <div className="settings-support-card">
            <a className="settings-support-row"
              href={`mailto:support@getdiswayne.com?subject=${encodeURIComponent("FieldJob Support Request")}&body=${encodeURIComponent("Hi FieldJob Support,\n\nI need help with:\n\n[Describe your issue here]\n\nAccount: " + (user?.email||""))}`}>
              <div className="settings-support-icon" style={{ background:"#eff6ff" }}>📧</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:"var(--ink)" }}>Email Support</div>
                <div style={{ fontSize:11, color:"#94a3b8", marginTop:1 }}>support@getdiswayne.com</div>
              </div>
              <div style={{ color:"#d1d5db", fontSize:18 }}>›</div>
            </a>
            <a className="settings-support-row"
              href={`mailto:support@getdiswayne.com?subject=${encodeURIComponent("Bug Report — FieldJob v3.1")}&body=${encodeURIComponent("Hi FieldJob Team,\n\nI'd like to report a bug:\n\nWhat happened:\n[Describe the bug]\n\nSteps to reproduce:\n1. \n2. \n\nAccount: " + (user?.email||""))}`}>
              <div className="settings-support-icon" style={{ background:"#fef2f2" }}>🐛</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:"var(--ink)" }}>Report a Bug</div>
                <div style={{ fontSize:11, color:"#94a3b8", marginTop:1 }}>Something not working right?</div>
              </div>
              <div style={{ color:"#d1d5db", fontSize:18 }}>›</div>
            </a>
            <a className="settings-support-row"
              href={`mailto:support@getdiswayne.com?subject=${encodeURIComponent("Feature Request — FieldJob")}&body=${encodeURIComponent("Hi FieldJob Team,\n\nI have a feature suggestion:\n\n[Describe your idea]\n\nWhy it would help:\n[Explain the use case]\n\nAccount: " + (user?.email||""))}`}>
              <div className="settings-support-icon" style={{ background:"#fffbeb" }}>💡</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:"var(--ink)" }}>Suggest a Feature</div>
                <div style={{ fontSize:11, color:"#94a3b8", marginTop:1 }}>Share your ideas with us</div>
              </div>
              <div style={{ color:"#d1d5db", fontSize:18 }}>›</div>
            </a>
            <a className="settings-support-row"
              href={`mailto:support@getdiswayne.com?subject=${encodeURIComponent("App Feedback — FieldJob")}&body=${encodeURIComponent("Hi FieldJob Team,\n\nHere's my feedback on the app:\n\n[Your feedback]\n\nAccount: " + (user?.email||""))}`}>
              <div className="settings-support-icon" style={{ background:"#f0fdf4" }}>📋</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:"var(--ink)" }}>App Feedback</div>
                <div style={{ fontSize:11, color:"#94a3b8", marginTop:1 }}>Rate your experience</div>
              </div>
              <div style={{ color:"#d1d5db", fontSize:18 }}>›</div>
            </a>
          </div>

          {/* Version card — full dark card on mobile, compact strip on desktop */}
          <div className="settings-version-card">
            <div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", fontWeight:600, letterSpacing:"0.8px", textTransform:"uppercase", marginBottom:4 }}>App Version</div>
              <div style={{ fontSize:16, fontWeight:700, color:"var(--gold)" }}>FieldJob v3.1</div>
              <div style={{ fontSize:10.5, color:"rgba(255,255,255,0.2)", marginTop:2 }}>© 2026 DisWayne Technologies</div>
            </div>
            <div style={{ background:"rgba(200,151,42,0.1)", border:"1px solid rgba(200,151,42,0.2)", borderRadius:20, padding:"4px 12px", fontSize:10, fontWeight:700, color:"rgba(200,151,42,0.7)" }}>Stable</div>
          </div>
          {/* Desktop-only version strip */}
          <div className="settings-version-desktop">
            <span style={{ fontSize:12, color:"var(--status-grey-fg)" }}>FieldJob</span>
            <span style={{ fontSize:12, fontWeight:700, color:"var(--ink)", marginLeft:5 }}>v3.1</span>
            <span style={{ marginLeft:8, background:"var(--ink)", color:"var(--gold)", fontSize:10, fontWeight:700, padding:"2px 10px", borderRadius:20 }}>Stable</span>
            <span style={{ marginLeft:10, fontSize:11, color:"var(--status-grey-fg)" }}>© 2026 DisWayne Technologies</span>
          </div>
        </div>
      )}

      {tab === "admin" && userIsOwner && <AdminPanel />}
      {tab === "payment-unavailable" && (
        <div style={{ maxWidth: 480, margin: "0 auto", textAlign: "center", padding: "48px 24px" }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🔧</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>Service Not Available Yet</h2>
          <p style={{ color: "#64748b", fontSize: 15, lineHeight: 1.7, marginBottom: 24 }}>
            Online subscription payments are currently being set up. Please check back soon or contact us directly to arrange your subscription.
          </p>
          <div className="card" style={{ textAlign: "left", marginBottom: 20 }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>What you get with FieldJob Pro</div>
            {["Full access to all modules","Unlimited jobs, quotes & invoices","Stock & inventory management","Compliance document storage","Expense tracking & profit reports","Priority support"].map(f => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 13.5, color: "#475569" }}>
                <span style={{ color: "var(--status-green-fg)", fontWeight: 700 }}>✓</span> {f}
              </div>
            ))}
            <div style={{ marginTop: 16, padding: "12px", background: "rgba(200,151,42,0.08)", borderRadius: 8, textAlign: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 22, color: "var(--gold)" }}>R314</span>
              <span style={{ color: "#64748b", fontSize: 13 }}> / month</span>
            </div>
          </div>
          <a href="mailto:support@getdiswayne.com?subject=FieldJob Pro Subscription" className="btn btn-gold btn-lg w-full" style={{ display: "block", textDecoration: "none", marginBottom: 12 }}>
            📧 Contact Us to Subscribe
          </a>
          <button className="btn btn-outline w-full" onClick={() => setTab("subscription")}>← Back</button>
        </div>
      )}
    </div>
  );
}



// ─── STORAGE UPLOAD ────────────────────────────────────────────────────────────
const uploadPhoto = async (file, userId) => {
  try {
    // Compress image before upload — resize to max 1200px, quality 0.75
    const compressed = await new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const maxW = 1200;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => resolve(blob), "image/jpeg", 0.75);
        URL.revokeObjectURL(url);
      };
      img.src = url;
    });

    const ext = "jpg";
    const filename = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const res = await fetch(`${SUPABASE_URL}/storage/v1/object/job-photos/${filename}`, {
      method: "POST",
      headers: {
        "apikey": SUPABASE_ANON_KEY,
        "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
        "Content-Type": "image/jpeg",
        "x-upsert": "true",
      },
      body: compressed,
    });
    if (!res.ok) { console.error("Upload failed", await res.text()); return null; }
    return `${SUPABASE_URL}/storage/v1/object/public/job-photos/${filename}`;
  } catch (e) { console.error("Upload error", e); return null; }
};

// ─── CSV EXPORT ────────────────────────────────────────────────────────────────
const exportCSV = (filename, rows) => {
  if (!rows || rows.length === 0) { alert("No data to export."); return; }
  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(","),
    ...rows.map(row => headers.map(h => {
      const val = row[h] === null || row[h] === undefined ? "" : String(row[h]);
      return `"${val.replace(/"/g, '""')}"`;
    }).join(","))
  ].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const exportUserData = async (userId, businessName) => {
  const [jobs, quotes, invoices, clients, workers, expenses] = await Promise.all([
    DB.getList("jobs", userId), DB.getList("quotes", userId),
    DB.getList("invoices", userId), DB.getList("clients", userId),
    DB.getList("workers", userId), DB.getList("expenses", userId),
  ]);
  const safe = (arr) => (arr||[]).map(r => { const {user_id,...rest}=r; return rest; });
  const timestamp = new Date().toISOString().slice(0,10);
  const name = (businessName||"fieldjob").replace(/\s+/g,"-").toLowerCase();
  if (jobs?.length) exportCSV(`${name}-jobs-${timestamp}.csv`, safe(jobs));
  if (quotes?.length) exportCSV(`${name}-quotes-${timestamp}.csv`, safe(quotes));
  if (invoices?.length) exportCSV(`${name}-invoices-${timestamp}.csv`, safe(invoices));
  if (clients?.length) exportCSV(`${name}-clients-${timestamp}.csv`, safe(clients));
  if (workers?.length) exportCSV(`${name}-workers-${timestamp}.csv`, safe(workers));
  if (expenses?.length) exportCSV(`${name}-expenses-${timestamp}.csv`, safe(expenses));
  if (!jobs?.length && !quotes?.length && !invoices?.length && !clients?.length) alert("No data found to export.");
};

// ─── ADMIN PANEL (Owner Only — armaarman55@gmail.com) ─────────────────────────
function AdminPanel() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("Pending");
  const [stats, setStats] = useState({ total:0, pending:0, approved:0, rejected:0, discontinued:0 });
  const [loading, setLoading] = useState(true);
  const [approvalPlans, setApprovalPlans] = useState({});  // reqId -> "demo" | "paid"

  const refresh = async () => {
    const all = await DB.getDemoRequests();
    setRequests(all || []);
    setStats({
      total: all.length,
      pending: all.filter(r=>r.status==="Pending").length,
      approved: all.filter(r=>r.status==="Approved").length,
      rejected: all.filter(r=>r.status==="Rejected").length,
      discontinued: all.filter(r=>r.status==="Discontinued").length,
    });
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const approve = async (req) => {
    const plan = approvalPlans[req.id] || "demo";
    const tempPassword = "FJ" + Math.random().toString(36).slice(2,6).toUpperCase() + Math.floor(1000 + Math.random()*9000);
    const demoExpiry = plan === "demo" ? addDays(today(), 7) : null;

    // 1. Create user in Supabase Auth via Edge Function (uses service role key)
    let supabaseAuthId = null;
    try {
      const { data: authData, error: authErr } = await supabase.auth.adminCreateUser(req.email, tempPassword);
      if (!authErr && authData?.id) {
        supabaseAuthId = authData.id; // real Supabase UUID
      }
    } catch(e) { console.warn("Supabase Auth create user failed:", e); }

    // 2. Use real UUID if created, otherwise fall back to generated id
    const userId = supabaseAuthId || `user_${btoa(req.email).slice(0,12)}`;

    // 3. Create or update fj_users record
    const existing = await DB.getUserByEmail(req.email);
    if (!existing) {
      await DB.insert("users", {
        auth_id: userId, email: req.email,
        business_name: req.business_name, owner_name: req.owner_name,
        phone: req.phone||"", industry: req.industry||"", workers: req.workers||"",
        status: "Approved", plan, demo_expires: demoExpiry,
        role: "user", approved_date: today(), password: tempPassword,
      });
    } else {
      await DB.update("users", existing.id, {
        status: "Approved", plan, demo_expires: demoExpiry,
        approved_date: today(), password: tempPassword,
        auth_id: supabaseAuthId || existing.auth_id,
      });
    }

    // 4. Create business profile if not exists
    const existingBiz = await DB.get("business", userId);
    if (!existingBiz) {
      await DB.upsert("business", {
        user_id: userId, name: req.business_name || req.owner_name || "My Business",
        phone: req.phone||"", email: req.email, vat_number:"", reg_number:"",
        address:"", bank_name:"", bank_account_holder:"",
        bank_account_number:"", bank_branch_code:"", logo:null,
      });
    }

    // 5. Update demo request status
    await DB.update("demo_requests", req.id, { status:"Approved", approved_date: today() });

    // 6. Send approval email with temp password via existing Edge Function
    try {
      await fetch(`${SUPABASE_URL}/functions/v1/send-enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          type: "approval",
          email: req.email,
          owner_name: req.owner_name || req.business_name,
          business_name: req.business_name,
          temp_password: tempPassword,
          login_url: "https://app.fieldjob.getdiswayne.com",
        }),
      });
    } catch(e) {
      console.error("Approval email failed:", e);
      alert(`Account approved! Temporary password for ${req.email}:\n\n${tempPassword}\n\nShare this with the user if email fails.`);
    }

    await refresh();
  };

  const reject = async (req) => {
    await DB.update("demo_requests", req.id, { status:"Rejected" });
    await refresh();
  };

  const discontinue = async (req) => {
    if (!window.confirm(`Discontinue access for ${req.email}? They will not be able to log in until reinstated.`)) return;
    const existing = await DB.getUserByEmail(req.email);
    if (existing) await DB.update("users", existing.id, { status: "Discontinued" });
    await DB.update("demo_requests", req.id, { status: "Discontinued" });
    await refresh();
  };

  const reinstate = async (req) => {
    const existing = await DB.getUserByEmail(req.email);
    if (existing) await DB.update("users", existing.id, { status: "Approved" });
    await DB.update("demo_requests", req.id, { status: "Approved" });
    await refresh();
  };

  const upgradeToPaid = async (req) => {
    const existing = await DB.getUserByEmail(req.email);
    if (existing) {
      await DB.update("users", existing.id, { plan: "paid", demo_expires: null });
    }
    await DB.update("demo_requests", req.id, { plan: "paid" });
    await refresh();
  };

  const deleteReq = async (id) => {
    await DB.remove("demo_requests", id);
    await refresh();
  };

  // Platform-wide user count
  const allApproved = requests.filter(r=>r.status==="Approved");
  const filtered = requests.filter(r => filter === "All" ? true : r.status === filter);

  return (
    <div>
      <div style={{ background:"linear-gradient(135deg,#0f1923,#1e3a5f)", borderRadius:16, padding:"24px 28px", marginBottom:24, color:"white" }}>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", marginBottom:4, letterSpacing:1, textTransform:"uppercase" }}>Owner Admin</div>
        <div style={{ fontSize:22, fontWeight:700, marginBottom:4 }}>FieldJob Platform Dashboard</div>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.6)" }}>Signed in as: {OWNER_EMAIL}</div>
      </div>

      {/* Platform Stats */}
      <div className="stats-grid" style={{ marginBottom:24 }}>
        <div className="stat-card">
          <div className="stat-label">Total Demo Requests</div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-sub">All time submissions</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pending Approval</div>
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-sub">Awaiting your action</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Approved Users</div>
          <div className="stat-value">{stats.approved}</div>
          <div className="stat-sub">Active accounts</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Rejected</div>
          <div className="stat-value">{stats.rejected}</div>
          <div className="stat-sub">Declined requests</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Discontinued</div>
          <div className="stat-value" style={{ color:"var(--status-red-fg)" }}>{stats.discontinued}</div>
          <div className="stat-sub">Access revoked</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        {["Pending","Approved","Rejected","Discontinued","All"].map(f => (
          <button key={f} className={filter===f ? "btn btn-sm btn-primary" : "btn btn-sm btn-outline"} onClick={() => setFilter(f)}>
            {f} {f!=="All" && <span style={{ background:"rgba(255,255,255,0.2)", borderRadius:10, padding:"1px 7px", marginLeft:4, fontSize:11 }}>{requests.filter(r=>r.status===f).length}</span>}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <Icon name="users" size={40}/>
          <h3>{filter === "Pending" ? "No pending requests" : `No ${filter.toLowerCase()} requests`}</h3>
          <p>{filter === "Pending" ? "New demo requests from the landing page will appear here." : ""}</p>
        </div>
      ) : (
        <div className="card" style={{ padding:0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Business Name</th>
                  <th>Owner</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Industry</th>
                  <th>Workers</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.sort((a,b)=>new Date(b.submitted_date)-new Date(a.submitted_date)).map(req => (
                  <tr key={req.id}>
                    <td style={{ fontWeight:600 }}>{req.business_name||"—"}</td>
                    <td>{req.owner_name||"—"}</td>
                    <td>
                      <a href={`mailto:${req.email}`} style={{ color:"var(--status-blue-fg)", textDecoration:"none" }}>{req.email}</a>
                    </td>
                    <td className="text-muted">{req.phone||"—"}</td>
                    <td className="text-muted">{req.industry||"—"}</td>
                    <td className="text-muted" style={{ textAlign:"center" }}>{req.workers||"—"}</td>
                    <td className="text-muted">{formatDate(req.submitted_date)}</td>
                    <td>
                      <span className={req.status==="Approved" ? "badge badge-green" : req.status==="Rejected" ? "badge badge-red" : req.status==="Discontinued" ? "badge badge-red" : "badge badge-amber"}>{req.status}</span>
                      {req.status==="Approved" && req.approved_date && <div className="text-sm text-muted" style={{ marginTop:2 }}>on {formatDate(req.approved_date)}</div>}
                    </td>
                    <td>
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                        {req.status === "Pending" && (
                          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                            {/* Trial / Full Access toggle */}
                            <div style={{ display:"flex", background:"#f3f4f6", borderRadius:6, padding:2, gap:2 }}>
                              <button
                                onClick={() => setApprovalPlans(p => ({ ...p, [req.id]: "demo" }))}
                                style={{ flex:1, padding:"3px 8px", fontSize:11, fontWeight:600, borderRadius:4, border:"none", cursor:"pointer", background: (approvalPlans[req.id]||"demo") === "demo" ? "var(--gold)" : "transparent", color: (approvalPlans[req.id]||"demo") === "demo" ? "#1a1a1a" : "#6b7280", transition:"all 0.15s" }}>
                                🕐 Trial
                              </button>
                              <button
                                onClick={() => setApprovalPlans(p => ({ ...p, [req.id]: "paid" }))}
                                style={{ flex:1, padding:"3px 8px", fontSize:11, fontWeight:600, borderRadius:4, border:"none", cursor:"pointer", background: approvalPlans[req.id] === "paid" ? "#16a34a" : "transparent", color: approvalPlans[req.id] === "paid" ? "white" : "#6b7280", transition:"all 0.15s" }}>
                                ✓ Full Access
                              </button>
                            </div>
                            <div style={{ display:"flex", gap:4 }}>
                              <button className="btn btn-sm btn-success" onClick={() => approve(req)}>✓ Approve</button>
                              <button className="btn btn-sm btn-outline" style={{ color:"var(--status-red-fg)" }} onClick={() => reject(req)}>✗ Reject</button>
                            </div>
                          </div>
                        )}
                        {req.status === "Approved" && (
                          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                            <div style={{ fontSize:12, color:"var(--status-green-fg)" }}>
                              <div>Account created</div>
                              <div className="text-muted" style={{display:"flex",alignItems:"center",gap:5}}>
                                {req.plan === "paid"
                                  ? <><Icon name="check" size={11} style={{color:"var(--emerald)"}}/> Full access</>
                                  : <><Icon name="clock" size={11} style={{color:"var(--gold)"}}/> Trial active</>}
                              </div>
                            </div>
                            {req.plan !== "paid" && (
                              <button
                                className="btn btn-sm btn-outline"
                                style={{ fontSize:11, color:"var(--gold)", borderColor:"rgba(200,151,42,0.5)", fontWeight:700, display:"flex", alignItems:"center", gap:4 }}
                                onClick={() => upgradeToPaid(req)}
                                title="Upgrade this user to full paid access">
                                <Icon name="upgrade" size={12}/> Upgrade to Paid
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-outline"
                              style={{ fontSize:11 }}
                              onClick={() => exportUserData(`user_${btoa(req.email).slice(0,12)}`, req.business_name)}
                              title="Export this user's data as CSV">
                              <Icon name="download" size={11}/> Export CSV
                            </button>
                            <button
                              className="btn btn-sm btn-outline"
                              style={{ color:"var(--status-red-fg)", borderColor:"var(--status-red-fg)", fontSize:11 }}
                              onClick={() => discontinue(req)}>
                              <Icon name="x" size={11}/> Discontinue
                            </button>
                          </div>
                        )}
                        {req.status === "Discontinued" && (
                          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                            <div style={{ fontSize:12, color:"var(--status-red-fg)", fontWeight:600 }}>Access revoked</div>
                            <button
                              className="btn btn-sm btn-success"
                              style={{ fontSize:11 }}
                              onClick={() => reinstate(req)}>
                              ↩ Reinstate
                            </button>
                          </div>
                        )}
                        {req.status === "Rejected" && (
                          <button className="btn btn-sm btn-outline" onClick={() => approve(req)}>Re-approve</button>
                        )}
                        <button className="btn btn-icon btn-outline btn-sm" style={{ color:"var(--status-red-fg)" }} onClick={() => deleteReq(req.id)}><Icon name="trash" size={12}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* How it works note */}
      <div className="alert alert-info" style={{ marginTop:20 }}>
        <strong>How approval works:</strong> When you click Approve, the user's account is saved to Supabase and they can log in immediately. Share the app URL with them: <strong>app.fieldjob.getdiswayne.com</strong>. Their data is stored in the cloud — accessible from any device.
      </div>

    </div>
  );
}

// ─── EXPENSES MODULE ──────────────────────────────────────────────────────────
function ExpensesModule({ userId }) {
  const [expenses, setExpenses] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [business, setBusiness] = useState({ currency:"ZAR", tax_rate:15, tax_label:"VAT", unit_system:"metric", _userId:userId });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [tab, setTab] = useState("all");
  const emptyForm = { category:"Materials & Supplies", description:"", amount:"", fuel_type:"Petrol", litres:"", fuel_price_per_litre:"", km:"", odo_start:"", odo_end:"", rate_per_km:"4.64", job_id:"", date: today(), supplier:"", notes:"", worker_name:"", period:"", deductible_pct:100, vat_claimable:false, receipt_ref:"", repair_type:"", vehicle_ref:"", invoice_ref:"", insurer:"", policy_number:"", cover_type:"Comprehensive", addons:[], billing_period:"Monthly", premium:"", govt_or_business:"Government", toll_purpose:"", subcontractors:[{name:"",amount:""}] };
  const [form, setForm] = useState(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const refresh = async () => {
        const [e, j, b] = await Promise.all([DB.getList("expenses", userId), DB.getList("jobs", userId), DB.get("business", userId)]);
        setExpenses(e||[]); setJobs(j||[]); setBusiness({ ...(b||{}), currency:b?.currency||"ZAR", tax_rate:b?.tax_rate!==undefined?b.tax_rate:15, tax_label:b?.tax_label||"VAT", unit_system:b?.unit_system||"metric", _userId:userId }); setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  // Unit label helpers
  const distLabel      = (biz) => unitLabel("km",  biz?.unit_system);
  const volumeLabel    = (biz) => unitLabel("L",   biz?.unit_system);
  const fuelVolumeLabel= (biz) => unitLabel("L",   biz?.unit_system);
  const weightLabel    = (biz) => unitLabel("kg",  biz?.unit_system);
  const rateKmLabel    = (biz) => unitLabel("rateKm", biz?.unit_system);

  const CATEGORIES = [
    // Travel & Vehicle
    "Fuel & Oil","Vehicle Maintenance & Repairs","Vehicle Insurance",
    "Toll Fees & Parking","Vehicle Licence & Registration",
    // Materials & Direct Costs
    "Materials & Supplies","Subcontractors","Plant & Equipment Hire",
    "Protective Clothing & PPE","Waste Disposal",
    // Staff & Labour
    "Salaries & Wages","Casual Labour","UIF & SDL Contributions",
    "Workmen's Compensation (COIDA)",
    // Premises & Utilities
    "Rent & Lease","Electricity & Water","Home Office","Storage & Warehousing",
    // Communication & Tech
    "Telephone & Airtime","Internet & Connectivity","Software & Subscriptions",
    // Professional Services
    "Accounting & Bookkeeping","Legal Fees","Bank Charges & Fees","Consulting Fees",
    // Insurance
    "Public Liability Insurance","Business Equipment Insurance","Professional Indemnity",
    // Marketing
    "Advertising & Marketing","Business Entertainment","Uniforms & Branding",
    // Capital & Finance
    "Tools & Equipment Purchase","Equipment Repairs & Maintenance",
    "Interest on Business Loans","Lease Payments",
    // Tax & Compliance
    "Tax Payments","Licences & Permits","Training & Development",
    // Other
    "Accommodation & Meals (Work Travel)","Stationery & Office Supplies",
    "Donations (Section 18A)","Miscellaneous"
  ];
  const SARS_RULES = {
    "Fuel & Oil": { pct:100, vat:true },
    "Vehicle Maintenance & Repairs": { pct:100, vat:true },
    "Vehicle Insurance": { pct:100, vat:false },
    "Toll Fees & Parking": { pct:100, vat:false },
    "Vehicle Licence & Registration": { pct:100, vat:false },
    "Materials & Supplies": { pct:100, vat:true },
    "Subcontractors": { pct:100, vat:true },
    "Plant & Equipment Hire": { pct:100, vat:true },
    "Protective Clothing & PPE": { pct:100, vat:true },
    "Waste Disposal": { pct:100, vat:true },
    "Salaries & Wages": { pct:100, vat:false },
    "Casual Labour": { pct:100, vat:false },
    "UIF & SDL Contributions": { pct:100, vat:false },
    "Workmen's Compensation (COIDA)": { pct:100, vat:false },
    "Rent & Lease": { pct:100, vat:true },
    "Electricity & Water": { pct:100, vat:true },
    "Home Office": { pct:100, vat:false },
    "Storage & Warehousing": { pct:100, vat:true },
    "Telephone & Airtime": { pct:100, vat:true },
    "Internet & Connectivity": { pct:100, vat:true },
    "Software & Subscriptions": { pct:100, vat:true },
    "Accounting & Bookkeeping": { pct:100, vat:true },
    "Legal Fees": { pct:100, vat:true },
    "Bank Charges & Fees": { pct:100, vat:true },
    "Consulting Fees": { pct:100, vat:true },
    "Public Liability Insurance": { pct:100, vat:false },
    "Business Equipment Insurance": { pct:100, vat:false },
    "Professional Indemnity": { pct:100, vat:false },
    "Advertising & Marketing": { pct:100, vat:true },
    "Business Entertainment": { pct:80, vat:false },
    "Uniforms & Branding": { pct:100, vat:true },
    "Tools & Equipment Purchase": { pct:100, vat:true },
    "Equipment Repairs & Maintenance": { pct:100, vat:true },
    "Interest on Business Loans": { pct:100, vat:false },
    "Lease Payments": { pct:100, vat:true },
    "Tax Payments": { pct:0, vat:false },
    "Licences & Permits": { pct:100, vat:false },
    "Training & Development": { pct:100, vat:true },
    "Accommodation & Meals (Work Travel)": { pct:100, vat:true },
    "Stationery & Office Supplies": { pct:100, vat:true },
    "Donations (Section 18A)": { pct:100, vat:false },
    "Miscellaneous": { pct:100, vat:false }
  };
  const isFuel = form.category === "Fuel & Oil";
  const isKm = false; // Vehicle Maintenance now has its own panel with amount field
  const hasOwnAmountPanel = ["Vehicle Maintenance & Repairs","Vehicle Insurance","Toll Fees & Parking","Subcontractors"].includes(form.category);

  const calcAmount = () => {
    if (isFuel && form.litres && form.fuel_price_per_litre) return (parseFloat(form.litres||0) * parseFloat(form.fuel_price_per_litre||0)).toFixed(2);
    if (isKm && form.km && form.rate_per_km) return (parseFloat(form.km||0) * parseFloat(form.rate_per_km||0)).toFixed(2);
    return form.amount;
  };

  const [errors, setErrors] = useState({});

  const save = async () => {
    const kmFinal = form.odo_start && form.odo_end
      ? Math.max(0, parseFloat(form.odo_end||0) - parseFloat(form.odo_start||0))
      : parseFloat(form.km||0);
    const amt = isFuel
      ? parseFloat(form.litres||0) * parseFloat(form.fuel_price_per_litre||0)
      : parseFloat(form.amount||0);

    // Validate based on category
    const errs = { date: V.date(form.date, "Date") };
    if (isFuel) {
      errs.litres = V.amountRequired(form.litres, "Litres");
      errs.fuel_price_per_litre = V.amountRequired(form.fuel_price_per_litre, "Price per litre");
    } else if (form.category !== "Subcontractors") {
      errs.amount = V.amountRequired(form.amount, "Amount");
    }
    const first = V.run(errs);
    if (first) { setErrors(errs); return; }
    setErrors({});

    const buildPayload = (overrides={}) => ({
      user_id:userId, category:form.category, description:form.description||"",
      amount:parseFloat((overrides.amount||amt||0)), date:form.date,
      fuel_type:form.fuel_type||"", litres:parseFloat(form.litres||0)||null,
      fuel_price_per_litre:parseFloat(form.fuel_price_per_litre||0)||null,
      km:kmFinal||null, odo_start:parseFloat(form.odo_start||0)||null,
      odo_end:parseFloat(form.odo_end||0)||null,
      rate_per_km:parseFloat(form.rate_per_km||0)||null,
      job_id:form.job_id||null, notes:form.notes||"",
      worker_name:form.worker_name||"", period:form.period||"",
      sars_category:form.category,
      deductible_pct:parseFloat(form.deductible_pct||100),
      vat_claimable:!!form.vat_claimable, tax_rate_used: business?.tax_rate||15, receipt_ref:form.receipt_ref||form.invoice_ref||"",
      // Vehicle Maintenance
      repair_type:form.repair_type||"", vehicle_ref:form.vehicle_ref||"",
      supplier:form.supplier||overrides.supplier||"",
      // Vehicle Insurance
      insurer:form.insurer||"", policy_number:form.policy_number||"",
      cover_type:form.cover_type||"", billing_period:form.billing_period||"",
      addons:form.addons?.length ? JSON.stringify(form.addons) : null,
      // Toll
      govt_or_business:form.govt_or_business||"", toll_purpose:form.toll_purpose||"",
      ...overrides,
    });

    if (form.category === "Subcontractors" && !form.id) {
      // Save each subcontractor as a separate record
      const subs = (form.subcontractors||[]).filter(s => s.name || s.amount);
      if (subs.length === 0) { alert("Please add at least one subcontractor company."); return; }
      for (const sc of subs) {
        await DB.insert("expenses", buildPayload({ supplier:sc.name, amount:parseFloat(sc.amount||0) }));
      }
    } else {
      if (form.id) await DB.update("expenses", form.id, buildPayload());
      else await DB.insert("expenses", buildPayload());
    }
    await refresh(); setModal(false); setForm(emptyForm);
  };

    const filtered = tab === "all" ? expenses : expenses.filter(e => e.category === tab || (tab === "Fuel & Oil" && (e.category === "Diesel" || e.category === "Petrol" || e.category === "Fuel & Oil")));

  const totalByCategory = CATEGORIES.reduce((acc, c) => {
    acc[c] = expenses.filter(e => e.category === c).reduce((s, e) => s + (parseFloat(e.amount)||0), 0);
    return acc;
  }, {});
  const grandTotal = expenses.reduce((s, e) => s + (parseFloat(e.amount)||0), 0);

  // Job profit calc
  const jobProfits = jobs.map(j => {
    const jobExpenses = expenses.filter(e => e.job_id === j.id).reduce((s, e) => s + (parseFloat(e.amount)||0), 0);
    const revenue = j.estimated_cost || 0;
    const profit = revenue - jobExpenses;
    return { ...j, jobExpenses, profit, margin: revenue > 0 ? ((profit/revenue)*100).toFixed(1) : 0 };
  }).filter(j => j.estimated_cost > 0);

  const CATCOLORS = {
    "Fuel & Oil":"#ea580c","Vehicle Maintenance & Repairs":"#c2410c",
    "Vehicle Insurance":"#9a3412","Toll Fees & Parking":"#b45309","Vehicle Licence & Registration":"#92400e",
    "Materials & Supplies":"var(--status-blue-fg)","Subcontractors":"var(--gold)",
    "Plant & Equipment Hire":"#1d4ed8","Protective Clothing & PPE":"#1e40af","Waste Disposal":"#64748b",
    "Salaries & Wages":"#7c3aed","Casual Labour":"#6d28d9","UIF & SDL Contributions":"#5b21b6",
    "Workmen's Compensation (COIDA)":"#4c1d95",
    "Rent & Lease":"#065f46","Electricity & Water":"#047857","Home Office":"#059669","Storage & Warehousing":"#10b981",
    "Telephone & Airtime":"#0e7490","Internet & Connectivity":"#0891b2","Software & Subscriptions":"#06b6d4",
    "Accounting & Bookkeeping":"#0284c7","Legal Fees":"#0369a1","Bank Charges & Fees":"#075985","Consulting Fees":"#0c4a6e",
    "Public Liability Insurance":"#dc2626","Business Equipment Insurance":"#b91c1c","Professional Indemnity":"#991b1b",
    "Advertising & Marketing":"#db2777","Business Entertainment":"#be185d","Uniforms & Branding":"#9d174d",
    "Tools & Equipment Purchase":"#1a6a6a","Equipment Repairs & Maintenance":"#155e75",
    "Interest on Business Loans":"#1e3a5f","Lease Payments":"#1e3a8a",
    "Tax Payments":"#374151","Licences & Permits":"#4b5563","Training & Development":"#6b7280",
    "Accommodation & Meals (Work Travel)":"#5a4080","Stationery & Office Supplies":"#7c6a9a",
    "Donations (Section 18A)":"#be123c","Miscellaneous":"#9299a4"
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className="stats-grid" style={{ marginBottom:16 }}>
        <div className="stat-card kpi-red">
          <div className="stat-top"><div className="stat-label">Total Expenses</div><div className="stat-icon"><Icon name="expenses" size={14} style={{ color:"var(--status-grey-fg)"}}/></div></div>
          <div className="stat-value">{formatCurrency(grandTotal, business?.currency)}</div>
          <div className="stat-sub">All categories</div>
        </div>
        <div className="stat-card">
          <div className="stat-top"><div className="stat-label">Fuel</div><div className="stat-icon"><Icon name="fuel" size={14} style={{ color:"var(--status-grey-fg)"}}/></div></div>
          <div className="stat-value">{formatCurrency((totalByCategory["Diesel"]||0)+(totalByCategory["Petrol"]||0), business?.currency)}</div>
          <div className="stat-sub">Diesel + Petrol</div>
        </div>
        <div className="stat-card kpi-green">
          <div className="stat-top"><div className="stat-label">Materials</div><div className="stat-icon"><Icon name="jobs" size={14} style={{ color:"var(--status-grey-fg)"}}/></div></div>
          <div className="stat-value">{formatCurrency(totalByCategory["Materials"]||0, business?.currency)}</div>
          <div className="stat-sub">Stock &amp; consumables</div>
        </div>
        <div className="stat-card">
          <div className="stat-top"><div className="stat-label">Labour &amp; Subs</div><div className="stat-icon"><Icon name="workers" size={14} style={{ color:"var(--status-grey-fg)"}}/></div></div>
          <div className="stat-value">{formatCurrency((totalByCategory["Labour"]||0, business?.currency)+(totalByCategory["Subcontractor"]||0))}</div>
          <div className="stat-sub">People costs</div>
        </div>
      </div>

      {/* ── MOBILE ONLY: Category + Profitability shown above list ── */}
      <div className="expense-mobile-summary">
        {CATEGORIES.filter(c => totalByCategory[c] > 0).length > 0 && (
          <div className="card" style={{ marginBottom:12 }}>
            <div className="card-title" style={{ marginBottom:10, fontSize:13 }}>Spend by Category</div>
            {CATEGORIES.filter(c => totalByCategory[c] > 0).map(c => (
              <div key={c} style={{ marginBottom:8 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3, fontSize:12 }}>
                  <span style={{ color:CATCOLORS[c], fontWeight:600 }}>{c}</span>
                  <span style={{ fontWeight:600 }}>{formatCurrency(totalByCategory[c], business?.currency)}</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width:(grandTotal?((totalByCategory[c]/grandTotal)*100):0)+"%", background:CATCOLORS[c] }}></div>
                </div>
              </div>
            ))}
          </div>
        )}
        {jobProfits.length > 0 && (
          <div className="card" style={{ marginBottom:12 }}>
            <div style={{ fontWeight:700, fontSize:13, padding:"14px 16px 10px", borderBottom:"1px solid var(--border)" }}>Job Profitability</div>
            {jobProfits.slice(0,5).map(j => (
              <div key={j.id} style={{ padding:"8px 14px", borderBottom:"1px solid var(--border-light)" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3 }}>
                  <span style={{ fontWeight:600, fontSize:12, color:"var(--ink)" }}>{j.title}</span>
                  <span className={j.profit>=0?"badge badge-green":"badge badge-red"}>{j.margin}%</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"#94a3b8" }}>
                  <span>Rev: {formatCurrency(j.estimated_cost, business?.currency)}</span>
                  <span style={{ fontWeight:600, color:j.profit>=0?"var(--status-green-fg)":"var(--status-red-fg)" }}>{j.profit>=0?"Profit":"Loss"}: {formatCurrency(Math.abs(j.profit), business?.currency)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Filter tabs + Add button ── */}
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
        <div style={{ flex:1, overflowX:"auto", display:"flex", gap:6, paddingBottom:2 }}>
          {["all","Fuel & Oil","Materials & Supplies","Salaries & Wages","Subcontractors","Other"].map(t => (
            <button key={t} className={tab===t?"btn btn-sm btn-primary":"btn btn-sm btn-outline"}
              onClick={() => setTab(t)}
              style={{ fontSize:11.5, padding:"5px 12px", whiteSpace:"nowrap", flexShrink:0 }}>
              {t==="all"?"All":t==="Fuel & Oil"?"⛽ Fuel":t==="Materials & Supplies"?"Materials":t==="Salaries & Wages"?"Staff":t==="Subcontractors"?"Subcon":t}
            </button>
          ))}
        </div>
        <button className="btn btn-outline btn-sm" style={{ flexShrink:0, color:"#10b981", borderColor:"rgba(16,185,129,0.4)" }}
          onClick={() => exportExpensesXLSX(filtered, tab)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><polyline points="8,13 12,17 16,13"/><line x1="12" y1="17" x2="12" y2="9"/></svg>
          Excel
        </button>
        <button className="btn btn-primary btn-sm" style={{ flexShrink:0 }}
          onClick={() => { setForm(emptyForm); setModal(true); }}>
          <Icon name="plus" size={13}/>Add
        </button>
      </div>

      {/* ── Main grid: list left, sidebar right (desktop only sidebar) ── */}
      <div className="dashboard-main-grid" style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16 }}>

        {/* Expense list */}
        <div className="card" style={{ padding:0 }}>
          {filtered.length === 0 ? (
            <div className="empty-state"><Icon name="expenses" size={36}/><h3>No expenses recorded</h3><p>Start tracking to see job profitability.</p></div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="table-wrap expense-desktop-table">
                <table>
                  <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Job</th><th>Details</th><th>Amount</th><th></th></tr></thead>
                  <tbody>
                    {filtered.sort((a,b)=>new Date(b.date)-new Date(a.date)).map(e => {
                      const job = jobs.find(j=>j.id===e.job_id);
                      return (
                        <tr key={e.id}>
                          <td className="text-muted">{formatDate(e.date)}</td>
                          <td><span className="badge" style={{ background:`${CATCOLORS[e.category]}22`, color:CATCOLORS[e.category], border:`1px solid ${CATCOLORS[e.category]}44` }}>{e.category}</span></td>
                          <td style={{ fontWeight:500 }}>{e.description}</td>
                          <td className="text-muted text-sm">{job?.title||"—"}</td>
                          <td className="text-sm text-muted">
                            {e.category==="Fuel & Oil"&&e.litres?`${e.litres}${fuelVolumeLabel(business)} · ${e.km||""}${distLabel(business)}`:e.worker_name||""}
                            {e.category==="Vehicle"&&e.km?`${e.km} ${distLabel(business)} @ ${getCurrencyOption(business?.currency||"ZAR").symbol}${e.rate_per_km}/${distLabel(business)}`:""}
                            {e.supplier||""}
                          </td>
                          <td style={{ fontWeight:700, color:"var(--status-red-fg)" }}>{formatCurrency(e.amount, business?.currency)}</td>
                          <td><div style={{ display:"flex", gap:4 }}>
                            <button className="btn btn-icon btn-outline btn-sm" onClick={() => { setForm({...e}); setModal(true); }}><Icon name="edit" size={12}/></button>
                            <button className="btn btn-icon btn-outline btn-sm" style={{ color:"var(--status-red-fg)" }} onClick={() => setDeleteConfirm({ id:e.id, label:e.description||e.category||"this expense" })}><Icon name="trash" size={12}/></button>
                          </div></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Mobile cards */}
              <div className="expense-mobile-cards">
                {filtered.sort((a,b)=>new Date(b.date)-new Date(a.date)).map(e => {
                  const job = jobs.find(j=>j.id===e.job_id);
                  const detail = (e.category==="Diesel"||e.category==="Petrol")&&e.litres
                    ? `${e.litres}${fuelVolumeLabel(business)} @ ${getCurrencyOption(business?.currency||"ZAR").symbol}${e.fuel_price_per_litre}/${fuelVolumeLabel(business)}`
                    : e.category==="Vehicle"&&e.km
                    ? `${e.km} ${distLabel(business)} @ ${getCurrencyOption(business?.currency||"ZAR").symbol}${e.rate_per_km}/${distLabel(business)}`
                    : e.supplier||"";
                  return (
                    <div key={e.id} style={{ padding:"13px 16px", borderBottom:"1px solid var(--border-light)", display:"flex", alignItems:"flex-start", gap:12 }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                          <span className="badge" style={{ background:`${CATCOLORS[e.category]}22`, color:CATCOLORS[e.category], border:`1px solid ${CATCOLORS[e.category]}44`, fontSize:10 }}>{e.category}</span>
                          <span style={{ fontWeight:700, color:"var(--status-red-fg)", fontSize:14 }}>{formatCurrency(e.amount, business?.currency)}</span>
                        </div>
                        <div style={{ fontWeight:600, fontSize:13, color:"var(--ink)", marginBottom:2 }}>{e.description||"—"}</div>
                        <div style={{ fontSize:11, color:"#94a3b8", display:"flex", gap:8, flexWrap:"wrap" }}>
                          <span>{formatDate(e.date)}</span>
                          {job&&<span>📋 {job.title}</span>}
                          {detail&&<span>{detail}</span>}
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                        <button className="btn btn-icon btn-outline btn-sm" onClick={() => { setForm({...e}); setModal(true); }}><Icon name="edit" size={12}/></button>
                        <button className="btn btn-icon btn-outline btn-sm" style={{ color:"var(--status-red-fg)" }} onClick={() => setDeleteConfirm({ id:e.id, label:e.description||e.category||"this expense" })}><Icon name="trash" size={12}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Desktop sidebar */}
        <div className="expense-desktop-sidebar">
          <div className="card">
            <div style={{ fontWeight:700, fontSize:13, padding:"14px 16px 10px", borderBottom:"1px solid var(--border)", marginBottom:0 }}>Job Profitability</div>
            {jobProfits.length === 0 ? (
              <div style={{ textAlign:"center", padding:"20px 0", color:"#94a3b8", fontSize:13 }}>Link expenses to jobs to see profit margins.</div>
            ) : (
              jobProfits.slice(0,8).map(j => (
                <div key={j.id} style={{ padding:"10px 16px", borderBottom:"1px solid var(--border)" }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>{j.title}</div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:12 }}>
                    <span className="text-muted">Revenue: {formatCurrency(j.estimated_cost, business?.currency)}</span>
                    <span className="text-muted">Exp: {formatCurrency(j.jobExpenses, business?.currency)}</span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:4 }}>
                    <span style={{ fontWeight:700, color:j.profit>=0?"var(--status-green-fg)":"var(--status-red-fg)", fontSize:13 }}>
                      {j.profit>=0?"Profit: ":"Loss: "}{formatCurrency(Math.abs(j.profit), business?.currency)}
                    </span>
                    <span className={j.profit>=0?"badge badge-green":"badge badge-red"}>{j.margin}%</span>
                  </div>
                  <div className="progress-bar" style={{ marginTop:6 }}>
                    <div className="progress-fill" style={{ width:`${Math.min(100,Math.max(0,j.margin))}%`, background:j.profit>=0?"var(--status-green-fg)":"var(--status-red-fg)" }}></div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="card" style={{ marginTop:14 }}>
            <div className="card-title" style={{ marginBottom:12 }}>Spend by Category</div>
            {CATEGORIES.filter(c => totalByCategory[c] > 0).map(c => (
              <div key={c} style={{ marginBottom:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3, fontSize:12.5 }}>
                  <span style={{ color:CATCOLORS[c], fontWeight:600 }}>{c}</span>
                  <span style={{ fontWeight:600 }}>{formatCurrency(totalByCategory[c], business?.currency)}</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width:(grandTotal?((totalByCategory[c]/grandTotal)*100):0)+"%", background:CATCOLORS[c] }}></div>
                </div>
              </div>
            ))}
            {Object.values(totalByCategory).every(v=>v===0) && <div className="text-muted text-sm" style={{ textAlign:"center", padding:"8px 0" }}>No expenses yet</div>}
          </div>
        </div>

      </div>

      {/* Add/Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{form.id ? "Edit Expense" : "Add Expense"}</h3>
              <button className="btn btn-icon btn-outline btn-sm" onClick={() => setModal(false)}><Icon name="x"/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select className="form-select" value={form.category} onChange={e => {
                    const cat = e.target.value;
                    const rule = SARS_RULES[cat] || { pct:100, vat:false };
                    setForm({...form, category:cat, deductible_pct:rule.pct, vat_claimable:rule.vat});
                  }}>
                    <optgroup label="── Travel & Vehicle ──">
                      {["Fuel & Oil","Vehicle Maintenance & Repairs","Vehicle Insurance","Toll Fees & Parking","Vehicle Licence & Registration"].map(c=><option key={c}>{c}</option>)}
                    </optgroup>
                    <optgroup label="── Materials & Direct Costs ──">
                      {["Materials & Supplies","Subcontractors","Plant & Equipment Hire","Protective Clothing & PPE","Waste Disposal"].map(c=><option key={c}>{c}</option>)}
                    </optgroup>
                    <optgroup label="── Staff & Labour ──">
                      {["Salaries & Wages","Casual Labour","UIF & SDL Contributions","Workmen's Compensation (COIDA)"].map(c=><option key={c}>{c}</option>)}
                    </optgroup>
                    <optgroup label="── Premises & Utilities ──">
                      {["Rent & Lease","Electricity & Water","Home Office","Storage & Warehousing"].map(c=><option key={c}>{c}</option>)}
                    </optgroup>
                    <optgroup label="── Communication & Tech ──">
                      {["Telephone & Airtime","Internet & Connectivity","Software & Subscriptions"].map(c=><option key={c}>{c}</option>)}
                    </optgroup>
                    <optgroup label="── Professional Services ──">
                      {["Accounting & Bookkeeping","Legal Fees","Bank Charges & Fees","Consulting Fees"].map(c=><option key={c}>{c}</option>)}
                    </optgroup>
                    <optgroup label="── Insurance ──">
                      {["Public Liability Insurance","Business Equipment Insurance","Professional Indemnity"].map(c=><option key={c}>{c}</option>)}
                    </optgroup>
                    <optgroup label="── Marketing ──">
                      {["Advertising & Marketing","Business Entertainment","Uniforms & Branding"].map(c=><option key={c}>{c}</option>)}
                    </optgroup>
                    <optgroup label="── Capital & Finance ──">
                      {["Tools & Equipment Purchase","Equipment Repairs & Maintenance","Interest on Business Loans","Lease Payments"].map(c=><option key={c}>{c}</option>)}
                    </optgroup>
                    <optgroup label="── Tax & Compliance ──">
                      {["Tax Payments","Licences & Permits","Training & Development"].map(c=><option key={c}>{c}</option>)}
                    </optgroup>
                    <optgroup label="── Other ──">
                      {["Accommodation & Meals (Work Travel)","Stationery & Office Supplies","Donations (Section 18A)","Miscellaneous"].map(c=><option key={c}>{c}</option>)}
                    </optgroup>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input className="form-input" type="date" value={form.date} onChange={e => setForm({...form, date:e.target.value})}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input className="form-input" value={form.description} onChange={e => setForm({...form, description:e.target.value})} placeholder="e.g. 50mm copper pipe"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Link to Job (optional)</label>
                  <select className="form-select" value={form.job_id} onChange={e => setForm({...form, job_id:e.target.value})}>
                    <option value="">No job linked</option>
                    {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
                  </select>
                </div>
              </div>

              {/* Fuel specific fields */}
              {form.category === "Fuel & Oil" && (
                <div style={{ background:"#fff7ed", padding:14, borderRadius:10, marginBottom:14, border:"1px solid #fed7aa" }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:10, color:"#ea580c" }}>⛽ Fuel &amp; Oil Details</div>
                  <div className="form-grid" style={{ marginBottom:10 }}>
                    <div className="form-group">
                      <label className="form-label">Fuel Type</label>
                      <select className="form-select" value={form.fuel_type} onChange={e=>setForm({...form,fuel_type:e.target.value})}>
                        <option>Petrol</option><option>Diesel</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">{fuelVolumeLabel(business)} Filled</label>
                      <input className="form-input" type="number" value={form.litres} onChange={e => setForm({...form, litres:e.target.value})} placeholder="0.00"/>
                    </div>
                  </div>
                  <div className="form-grid" style={{ marginBottom:10 }}>
                    <div className="form-group">
                      <label className="form-label">Price per {fuelVolumeLabel(business)} ({getCurrencyOption(business?.currency||"ZAR").symbol})</label>
                      <input className="form-input" type="number" value={form.fuel_price_per_litre} onChange={e => setForm({...form, fuel_price_per_litre:e.target.value})} placeholder="0.00"/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">{distLabel(business)} Travelled</label>
                      <input className="form-input" type="number" value={form.km} onChange={e => setForm({...form, km:e.target.value})} placeholder="0"/>
                    </div>
                  </div>
                  <div className="form-grid" style={{ marginBottom:10 }}>
                    <div className="form-group">
                      <label className="form-label">Odometer Start ({distLabel(business)}, optional)</label>
                      <input className="form-input" type="number" value={form.odo_start} onChange={e => setForm({...form, odo_start:e.target.value})} placeholder={`${distLabel(business)} reading`}/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Odometer End ({distLabel(business)}, optional)</label>
                      <input className="form-input" type="number" value={form.odo_end} onChange={e => setForm({...form, odo_end:e.target.value, km: form.odo_start ? String(Math.max(0,(parseFloat(e.target.value)||0)-(parseFloat(form.odo_start)||0))) : form.km})} placeholder={`${distLabel(business)} reading`}/>
                    </div>
                  </div>
                  {form.litres && form.fuel_price_per_litre && (
                    <div style={{ background:"white", padding:"8px 12px", borderRadius:8, fontSize:13, marginBottom:8 }}>
                      <strong>Total: {formatCurrency(parseFloat(form.litres||0)*parseFloat(form.fuel_price_per_litre||0), business?.currency)}</strong>
                      <span className="text-muted" style={{ marginLeft:12 }}>{form.litres}{fuelVolumeLabel(business)} × {getCurrencyOption(business?.currency||"ZAR").symbol}{form.fuel_price_per_litre}/{fuelVolumeLabel(business)}</span>
                      {form.km && <span className="text-muted" style={{ marginLeft:12 }}>· {form.km} {distLabel(business)}</span>}
                    </div>
                  )}
                  <div style={{ fontSize:10.5, color:"#92400e", background:"#fef3c7", padding:"6px 10px", borderRadius:6 }}>
                    📓 Logbook entry auto-created if linked to a job below
                  </div>
                </div>
              )}

              {/* Staff & Labour fields */}
              {(form.category === "Salaries & Wages" || form.category === "Casual Labour" || form.category === "UIF & SDL Contributions" || form.category === "Workmen's Compensation (COIDA)") && (
                <div style={{ background:"rgba(124,58,237,0.06)", padding:14, borderRadius:10, marginBottom:14, border:"1px solid rgba(124,58,237,0.2)" }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:10, color:"#7c3aed" }}>👷 Staff / Labour Details</div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Worker / Employee Name</label>
                      <input className="form-input" value={form.worker_name} onChange={e=>setForm({...form,worker_name:e.target.value})} placeholder="Name or description"/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">{form.category === "UIF & SDL Contributions" || form.category === "Workmen's Compensation (COIDA)" ? "Period / Reference" : "Pay Period"}</label>
                      {form.category === "UIF & SDL Contributions" || form.category === "Workmen's Compensation (COIDA)" ? (
                        <input className="form-input" value={form.period} onChange={e=>setForm({...form,period:e.target.value})} placeholder="e.g. March 2026"/>
                      ) : (
                        <select className="form-select" value={form.period} onChange={e=>setForm({...form,period:e.target.value})}>
                          <option value="">Select period...</option>
                          <option>Daily</option><option>Weekly</option><option>Fortnightly</option><option>Monthly</option>
                        </select>
                      )}
                    </div>
                  </div>
                  {form.category === "Casual Labour" && (
                    <div style={{ fontSize:10.5, color:"#92400e", background:"#fef3c7", padding:"6px 10px", borderRadius:6, marginTop:4 }}>
                      ⚠️ Keep detailed records for casual labour — name, amount, date, and work description are required for tax compliance.
                    </div>
                  )}
                </div>
              )}

              {/* Vehicle km fields */}
              {form.category === "Vehicle Maintenance & Repairs" && (
                <div style={{ background:"#f0fdf4", padding:14, borderRadius:10, marginBottom:14, border:"1px solid #bbf7d0" }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:10, color:"#16a34a" }}>🔧 Vehicle Maintenance / Repair Details</div>
                  <div className="form-grid" style={{ marginBottom:10 }}>
                    <div className="form-group">
                      <label className="form-label">Type of Service / Repair</label>
                      <select className="form-select" value={form.repair_type} onChange={e=>setForm({...form,repair_type:e.target.value})}>
                        <option value="">Select type...</option>
                        <option>Full Service</option>
                        <option>Minor Service</option>
                        <option>Tyres</option>
                        <option>Brakes</option>
                        <option>Suspension</option>
                        <option>Electrical</option>
                        <option>Body Repair / Panel Beating</option>
                        <option>Windscreen</option>
                        <option>Air Conditioning</option>
                        <option>Exhaust</option>
                        <option>Battery</option>
                        <option>Other Repair</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Vehicle (Reg / Description)</label>
                      <input className="form-input" value={form.vehicle_ref} onChange={e=>setForm({...form,vehicle_ref:e.target.value})} placeholder="e.g. Hilux — CA 123-456"/>
                    </div>
                  </div>
                  <div className="form-grid" style={{ marginBottom:10 }}>
                    <div className="form-group">
                      <label className="form-label">Repair Shop / Workshop Name</label>
                      <input className="form-input" value={form.supplier} onChange={e=>setForm({...form,supplier:e.target.value})} placeholder="e.g. AutoZone, Fix-A-Car"/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Invoice / Job Card Ref</label>
                      <input className="form-input" value={form.invoice_ref} onChange={e=>setForm({...form,invoice_ref:e.target.value})} placeholder="e.g. WS-0042"/>
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Amount ({getCurrencyOption(business?.currency||"ZAR").symbol})</label>
                    <input className="form-input" type="number" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder="0.00"/>
                  </div>
                </div>
              )}

              {/* Vehicle Insurance */}
              {form.category === "Vehicle Insurance" && (
                <div style={{ background:"rgba(239,68,68,0.05)", padding:14, borderRadius:10, marginBottom:14, border:"1px solid rgba(239,68,68,0.2)" }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:10, color:"#dc2626" }}>🛡️ Vehicle Insurance Details</div>
                  <div className="form-grid" style={{ marginBottom:10 }}>
                    <div className="form-group">
                      <label className="form-label">Insurance Company</label>
                      <input className="form-input" value={form.insurer} onChange={e=>setForm({...form,insurer:e.target.value})} placeholder="e.g. OUTsurance, King Price, Santam"/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Policy Number (optional)</label>
                      <input className="form-input" value={form.policy_number} onChange={e=>setForm({...form,policy_number:e.target.value})} placeholder="Policy number"/>
                    </div>
                  </div>
                  <div className="form-grid" style={{ marginBottom:10 }}>
                    <div className="form-group">
                      <label className="form-label">Cover Type</label>
                      <select className="form-select" value={form.cover_type} onChange={e=>setForm({...form,cover_type:e.target.value})}>
                        <option>Comprehensive</option>
                        <option>Third Party, Fire & Theft</option>
                        <option>Third Party Only</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Billing Period</label>
                      <select className="form-select" value={form.billing_period} onChange={e=>setForm({...form,billing_period:e.target.value})}>
                        <option>Monthly</option>
                        <option>Annual</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom:10 }}>
                    <label className="form-label">Premium Amount ({getCurrencyOption(business?.currency||"ZAR").symbol})</label>
                    <input className="form-input" type="number" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder="0.00"/>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label" style={{ marginBottom:6 }}>Add-ons / Extras Covered</label>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                      {["Rim Insurance","Car Sound System","Scratch & Dents","Vehicle Maintenance Plan","Roadside Assistance","Windscreen Cover"].map(addon=>(
                        <label key={addon} style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, cursor:"pointer", padding:"4px 10px", borderRadius:20, border:`1px solid ${(form.addons||[]).includes(addon)?"#dc2626":"var(--border)"}`, background:(form.addons||[]).includes(addon)?"rgba(239,68,68,0.08)":"white" }}>
                          <input type="checkbox" checked={(form.addons||[]).includes(addon)} onChange={e=>{
                            const cur=form.addons||[];
                            setForm({...form,addons:e.target.checked?[...cur,addon]:cur.filter(a=>a!==addon)});
                          }} style={{ display:"none" }}/>
                          {(form.addons||[]).includes(addon)?"✓ ":""}{addon}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Toll Fees & Parking */}
              {form.category === "Toll Fees & Parking" && (
                <div style={{ background:"rgba(245,158,11,0.05)", padding:14, borderRadius:10, marginBottom:14, border:"1px solid rgba(245,158,11,0.25)" }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:10, color:"#b45309" }}>🛣️ Toll / Parking Details</div>
                  <div className="form-grid" style={{ marginBottom:10 }}>
                    <div className="form-group">
                      <label className="form-label">Paid to</label>
                      <select className="form-select" value={form.govt_or_business} onChange={e=>setForm({...form,govt_or_business:e.target.value})}>
                        <option>Government</option>
                        <option>Business</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Amount ({getCurrencyOption(business?.currency||"ZAR").symbol})</label>
                      <input className="form-input" type="number" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder="0.00"/>
                    </div>
                  </div>
                  {form.govt_or_business === "Government" && (
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label className="form-label">Route / Plaza (optional)</label>
                      <input className="form-input" value={form.toll_purpose} onChange={e=>setForm({...form,toll_purpose:e.target.value})} placeholder="e.g. N1 Huguenot Toll, e-toll ref"/>
                    </div>
                  )}
                  {form.govt_or_business === "Business" && (
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label className="form-label">Where / Purpose</label>
                      <input className="form-input" value={form.toll_purpose} onChange={e=>setForm({...form,toll_purpose:e.target.value})} placeholder="e.g. Sandton City parking — client visit"/>
                    </div>
                  )}
                </div>
              )}

              {/* Subcontractors */}
              {form.category === "Subcontractors" && (
                <div style={{ background:"rgba(200,151,42,0.05)", padding:14, borderRadius:10, marginBottom:14, border:"1px solid rgba(200,151,42,0.2)" }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:10, color:"var(--gold)" }}>🏗️ Subcontractor Companies</div>
                  <div style={{ fontSize:11, color:"var(--muted)", marginBottom:10 }}>Each company is saved as a separate expense record linked to the same job.</div>
                  {(form.subcontractors||[{name:"",amount:""}]).map((sc,i)=>(
                    <div key={i} style={{ display:"flex", gap:8, alignItems:"flex-end", marginBottom:8 }}>
                      <div style={{ flex:2 }}>
                        {i===0&&<label className="form-label">Company Name</label>}
                        <input className="form-input" value={sc.name} onChange={e=>{const s=[...(form.subcontractors||[])];s[i]={...s[i],name:e.target.value};setForm({...form,subcontractors:s});}} placeholder="e.g. Spark Electricals (Pty) Ltd"/>
                      </div>
                      <div style={{ flex:1 }}>
                        {i===0&&<label className="form-label">Amount Paid ({getCurrencyOption(business?.currency||"ZAR").symbol})</label>}
                        <input className="form-input" type="number" value={sc.amount} onChange={e=>{const s=[...(form.subcontractors||[])];s[i]={...s[i],amount:e.target.value};setForm({...form,subcontractors:s});}} placeholder="0.00"/>
                      </div>
                      {(form.subcontractors||[]).length>1&&(
                        <button onClick={()=>{const s=(form.subcontractors||[]).filter((_,j)=>j!==i);setForm({...form,subcontractors:s});}} style={{ padding:"8px 10px", border:"1px solid #fca5a5", borderRadius:7, background:"#fee2e2", color:"#dc2626", cursor:"pointer", marginBottom:1, flexShrink:0 }}>✕</button>
                      )}
                    </div>
                  ))}
                  <button onClick={()=>setForm({...form,subcontractors:[...(form.subcontractors||[]),{name:"",amount:""}]})} style={{ fontSize:12, color:"var(--gold)", background:"var(--gold-p)", border:"1px solid var(--gold-b)", borderRadius:7, padding:"6px 14px", cursor:"pointer", marginTop:2 }}>+ Add Another Company</button>
                  {(form.subcontractors||[]).length>0&&(form.subcontractors||[]).some(s=>s.amount)&&(
                    <div style={{ marginTop:10, padding:"8px 12px", background:"white", borderRadius:7, fontSize:12, fontWeight:600, color:"var(--ink)", border:"1px solid var(--border)" }}>
                      Total: {formatCurrency((form.subcontractors||[]).reduce((t,s)=>t+(parseFloat(s.amount)||0),0), business?.currency)}
                      <span style={{ fontWeight:400, color:"var(--muted)", marginLeft:8 }}>across {(form.subcontractors||[]).filter(s=>s.name).length} {(form.subcontractors||[]).filter(s=>s.name).length===1?"company":"companies"}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Standard amount — hidden for categories with their own dedicated panel */}
              {!isFuel && !hasOwnAmountPanel && (
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Amount ({getCurrencyOption(business?.currency||"ZAR").symbol}) *</label>
                    <input className="form-input" type="number" value={form.amount} onChange={e => setForm({...form, amount:e.target.value})} placeholder="0.00"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Supplier / Vendor</label>
                    <input className="form-input" value={form.supplier} onChange={e => setForm({...form, supplier:e.target.value})} placeholder={form.category==="Salaries & Wages"||form.category==="Casual Labour"?"Not applicable":form.category==="Rent & Lease"?"Landlord / Property owner":"e.g. Builders Warehouse"}/>
                  </div>
                </div>
              )}
              {/* SARS fields */}
              <div style={{ background:"rgba(200,151,42,0.06)", padding:12, borderRadius:8, marginBottom:12, border:"1px solid rgba(200,151,42,0.2)" }}>
                <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.7px", color:"var(--gold)", marginBottom:10 }}>Tax & Compliance</div>
                <div className="form-grid" style={{ marginBottom:8 }}>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Deductible %</label>
                    <input className="form-input" type="number" min="0" max="100"
                      value={form.deductible_pct}
                      onChange={e=>setForm({...form,deductible_pct:e.target.value})}/>
                    {parseFloat(form.deductible_pct||100)<100 && <div style={{fontSize:10,color:"#d97706",marginTop:3}}>⚠️ Partial deduction — Business Entertainment max 80%</div>}
                    {parseFloat(form.deductible_pct||100)===0 && <div style={{fontSize:10,color:"#dc2626",marginTop:3}}>✗ Not tax deductible (Tax Payments)</div>}
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Receipt / Reference No.</label>
                    <input className="form-input" value={form.receipt_ref||""} onChange={e=>setForm({...form,receipt_ref:e.target.value})} placeholder="Invoice or receipt number"/>
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"6px 0" }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:500 }}>{business?.tax_label||"VAT"} Claimable (Input Tax)</div>
                    <div style={{ fontSize:10.5, color:"var(--muted)" }}>{business?.tax_label||"VAT"}-registered businesses only</div>
                  </div>
                  <button onClick={()=>setForm({...form,vat_claimable:!form.vat_claimable})}
                    style={{ width:34, height:19, borderRadius:10, border:"none", cursor:"pointer", background:form.vat_claimable?"var(--emerald)":"#cbd5e1", position:"relative", flexShrink:0 }}>
                    <span style={{ position:"absolute", width:13, height:13, borderRadius:"50%", background:"white", top:3, left:form.vat_claimable?18:3, transition:"left 0.15s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }}/>
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => setForm({...form, notes:e.target.value})}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>Save Expense</button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal item={deleteConfirm?.label} onConfirm={async () => { if(deleteConfirm){ await DB.remove("expenses", deleteConfirm.id); await refresh(); setDeleteConfirm(null); } }} onCancel={() => setDeleteConfirm(null)} />
    </div>
  );
}
function InventoryModule({ userId }) {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [business, setBusiness] = useState({ currency:"ZAR", _userId:userId });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("stock");
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState("");
  const emptyItem = { name:"", sku:"", category:"", unit:"Each", qty_in_stock:0, min_stock_level:5, unit_cost:0, supplier_id:"", notes:"" };
  const emptySupplier = { name:"", contact:"", phone:"", email:"", address:"" };
  const emptyPurchase = { item_id:"", qty:0, unit_cost:0, supplier_id:"", date:today(), invoice_ref:"", notes:"" };
  const [form, setForm] = useState(emptyItem);
  const [supForm, setSupForm] = useState(emptySupplier);
  const [purForm, setPurForm] = useState(emptyPurchase);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const refresh = async () => {
    const [inv, sup, pur, j] = await Promise.all([
      DB.getList("inventory", userId), DB.getList("suppliers", userId),
      DB.getList("purchases", userId), DB.getList("jobs", userId),
    ]);
    const b = await DB.get("business", userId);
    setItems(inv||[]); setSuppliers(sup||[]); setPurchases(pur||[]); setJobs(j||[]); setBusiness({ ...(b||{}), currency:b?.currency||"ZAR", unit_system:b?.unit_system||"metric", _userId:userId }); setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  // Unit label helpers
  const distLabel       = (biz) => unitLabel("km",  biz?.unit_system);
  const lengthLabel     = (biz) => unitLabel("m",   biz?.unit_system);
  const fuelVolumeLabel = (biz) => unitLabel("L",   biz?.unit_system);
  const weightLabel     = (biz) => unitLabel("kg",  biz?.unit_system);
  const areaLabel       = (biz) => unitLabel("m2",  biz?.unit_system);

  const UNITS = ["Each", `${lengthLabel(business)} (length)`, `${weightLabel(business)}`, `${fuelVolumeLabel(business)} (volume)`, "Box","Roll","Bag","Set","Pair","Length"];
  const CATEGORIES = ["Electrical","Plumbing","Structural","Finishing","Tools","Safety","Consumables","Other"];

  const [itemErrors, setItemErrors] = useState({});
  const [supErrors, setSupErrors] = useState({});
  const [purErrors, setPurErrors] = useState({});

  const saveItem = async () => {
    const errs = {
      name:      V.required(form.name, "Item name"),
      qty:       V.amount(form.qty_in_stock, "Quantity"),
      unit_cost: V.amount(form.unit_cost, "Unit cost"),
    };
    const first = V.run(errs);
    if (first) { setItemErrors(errs); return; }
    setItemErrors({});
    const data = { ...form, user_id:userId, qty_in_stock:parseFloat(form.qty_in_stock)||0, unit_cost:parseFloat(form.unit_cost)||0, min_stock_level:parseFloat(form.min_stock_level)||0 };
    if (form.id) await DB.update("inventory", form.id, data); else await DB.insert("inventory", data);
    await refresh(); setModal(null); setForm(emptyItem);
  };

  const saveSupplier = async () => {
    const errs = {
      name:  V.required(supForm.name, "Supplier name"),
      email: V.email(supForm.email),
      phone: V.phone(supForm.phone),
    };
    const first = V.run(errs);
    if (first) { setSupErrors(errs); return; }
    setSupErrors({});
    if (supForm.id) await DB.update("suppliers", supForm.id, { ...supForm, user_id:userId }); else await DB.insert("suppliers", { ...supForm, user_id:userId });
    await refresh(); setModal(null); setSupForm(emptySupplier);
  };

  const savePurchase = async () => {
    const errs = {
      item_id:   V.required(purForm.item_id, "Item"),
      qty:       V.amountRequired(purForm.qty, "Quantity"),
      unit_cost: V.amountRequired(purForm.unit_cost, "Unit cost"),
      date:      V.date(purForm.date, "Date"),
    };
    const first = V.run(errs);
    if (first) { setPurErrors(errs); return; }
    setPurErrors({});
    const item = items.find(i=>i.id===purForm.item_id);
    if (item) {
      const newQty = (parseFloat(item.qty_in_stock)||0) + (parseFloat(purForm.qty)||0);
      await DB.update("inventory", item.id, { qty_in_stock: newQty });
    }
    await DB.insert("purchases", { ...purForm, user_id:userId, total: parseFloat(purForm.qty)*parseFloat(purForm.unit_cost) });
    await refresh(); setModal(null); setPurForm(emptyPurchase);
  };

  const useStock = async (itemId, qty) => {
    const item = items.find(i=>i.id===itemId);
    if (item) { await DB.update("inventory", itemId, { qty_in_stock: Math.max(0,(parseFloat(item.qty_in_stock)||0)-qty) }); await refresh(); }
  };

  const lowStock = items.filter(i => (parseFloat(i.qty_in_stock)||0) <= (parseFloat(i.min_stock_level)||0));
  const totalValue = items.reduce((s,i) => s + (parseFloat(i.qty_in_stock)||0)*(parseFloat(i.unit_cost)||0), 0);
  const filtered = items.filter(i => i.name?.toLowerCase().includes(search.toLowerCase()) || i.sku?.toLowerCase().includes(search.toLowerCase()) || i.category?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      {/* Alerts */}
      {lowStock.length > 0 && (
        <div className="alert alert-warning" style={{ display:"flex", alignItems:"center", gap:10 }}>
          <Icon name="warning" size={16}/>
          <strong>{lowStock.length} item{lowStock.length>1?"s":""} low on stock:</strong> {lowStock.map(i=>i.name).join(", ")}
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom:20 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background:"rgba(14,165,233,0.1)" }}><Icon name="inventory" size={20} style={{ color:"var(--status-blue-fg)" }}/></div>
          <div className="stat-label">Total Stock Items</div>
          <div className="stat-value">{items.length}</div>
          <div className="stat-sub">{lowStock.length} low stock alerts</div>
        </div>
        <div className="stat-card kpi-green">
          <div className="stat-top">
            <div className="stat-label">Stock Value</div>
            <div className="stat-icon"><Icon name="currency" size={14} style={{ color:"var(--status-grey-fg)" }}/></div>
          </div>
          <div className="stat-value">{formatCurrency(totalValue, business?.currency)}</div>
          <div className="stat-sub">At cost price</div>
        </div>
        <div className="stat-card kpi-red">
          <div className="stat-top">
            <div className="stat-label">Low Stock Alerts</div>
            <div className="stat-icon"><Icon name="warning" size={14} style={{ color:"var(--status-grey-fg)" }}/></div>
          </div>
          <div className="stat-value">{lowStock.length}</div>
          <div className="stat-sub">Below minimum level</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background:"rgba(139,92,246,0.1)" }}><Icon name="clients" size={20} style={{ color:"#6d28d9" }}/></div>
          <div className="stat-label">Suppliers</div>
          <div className="stat-value">{suppliers.length}</div>
          <div className="stat-sub">Registered suppliers</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {[["stock","📦 Stock Items"],["purchases","🛒 Purchase Records"],["suppliers","🏭 Suppliers"]].map(([id,label]) => (
          <div key={id} className={tab===id ? "tab active" : "tab"} onClick={() => setTab(id)}>{label}</div>
        ))}
      </div>

      {/* STOCK ITEMS TAB */}
      {tab === "stock" && (
        <div>
          <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
            <div className="search-bar" style={{ flex:1, minWidth:180 }}>
              <Icon name="search" size={14} style={{ color:"#94a3b8" }}/>
              <input placeholder="Search stock..." value={search} onChange={e => setSearch(e.target.value)}/>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => { setForm(emptyItem); setModal("item"); }}><Icon name="plus" size={13}/>Add Item</button>
          </div>
          {filtered.length === 0 ? (
            <div className="empty-state"><Icon name="inventory" size={40}/><h3>No stock items yet</h3><p>Add materials, tools and consumables your team uses on jobs.</p></div>
          ) : (
            <div className="card" style={{ padding:0 }}>
              {/* Desktop table */}
              <div className="table-wrap inventory-desktop-table">
                <table>
                  <thead><tr><th>Item</th><th>SKU</th><th>Category</th><th>In Stock</th><th>Min Level</th><th>Unit Cost</th><th>Stock Value</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {filtered.map(item => {
                      const qty = parseFloat(item.qty_in_stock)||0;
                      const min = parseFloat(item.min_stock_level)||0;
                      const isLow = qty <= min;
                      const isOut = qty === 0;
                      return (
                        <tr key={item.id}>
                          <td><div style={{ fontWeight:600 }}>{item.name}</div><div className="text-sm text-muted">{item.notes}</div></td>
                          <td className="text-muted text-sm">{item.sku||"—"}</td>
                          <td><span className="badge badge-gray">{item.category||"—"}</span></td>
                          <td style={{ fontWeight:700, color:isOut?"var(--status-red-fg)":isLow?"var(--status-amber-fg)":"var(--status-green-fg)" }}>{qty} {item.unit}</td>
                          <td className="text-muted">{min} {item.unit}</td>
                          <td>{formatCurrency(item.unit_cost, business?.currency)}</td>
                          <td style={{ fontWeight:600 }}>{formatCurrency(qty*(parseFloat(item.unit_cost)||0), business?.currency)}</td>
                          <td>{isOut ? <span className="badge badge-red">Out of Stock</span> : isLow ? <span className="badge badge-amber">Low Stock</span> : <span className="badge badge-green">In Stock</span>}</td>
                          <td><div style={{ display:"flex", gap:4 }}>
                            <button className="btn btn-sm btn-outline" onClick={() => { setForm({...item}); setModal("item"); }}><Icon name="edit" size={12}/></button>
                            <button className="btn btn-sm btn-outline" style={{ color:"var(--status-red-fg)" }} onClick={() => setDeleteConfirm({ id: item.id, label: item.name || 'this item', table: 'inventory' })}><Icon name="trash" size={12}/></button>
                          </div></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Mobile cards */}
              <div className="inventory-mobile-cards">
                {filtered.map(item => {
                  const qty = parseFloat(item.qty_in_stock)||0;
                  const min = parseFloat(item.min_stock_level)||0;
                  const isLow = qty <= min; const isOut = qty === 0;
                  const stockVal = qty*(parseFloat(item.unit_cost)||0);
                  return (
                    <div key={item.id} style={{ padding:"13px 16px", borderBottom:"1px solid var(--border-light)" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                        <div style={{ minWidth:0, flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:13, color:"var(--ink)" }}>{item.name}</div>
                          {item.sku && <div style={{ fontSize:10.5, color:"#94a3b8", marginTop:1 }}>SKU: {item.sku}</div>}
                        </div>
                        <div style={{ display:"flex", gap:6, flexShrink:0, marginLeft:10 }}>
                          {isOut ? <span className="badge badge-red">Out</span> : isLow ? <span className="badge badge-amber">Low</span> : <span className="badge badge-green">In Stock</span>}
                        </div>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:8 }}>
                        <div><div style={{ fontSize:9.5, color:"#94a3b8", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>In Stock</div><div style={{ fontSize:14, fontWeight:700, color:isOut?"var(--status-red-fg)":isLow?"var(--status-amber-fg)":"var(--status-green-fg)" }}>{qty} <span style={{ fontSize:10, fontWeight:400 }}>{item.unit}</span></div></div>
                        <div><div style={{ fontSize:9.5, color:"#94a3b8", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Unit Cost</div><div style={{ fontSize:13, fontWeight:600, color:"var(--ink)" }}>{formatCurrency(item.unit_cost, business?.currency)}</div></div>
                        <div><div style={{ fontSize:9.5, color:"#94a3b8", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Value</div><div style={{ fontSize:13, fontWeight:700, color:"var(--gold)" }}>{formatCurrency(stockVal, business?.currency)}</div></div>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <span className="badge badge-gray" style={{ fontSize:10 }}>{item.category||"—"}</span>
                        <div style={{ display:"flex", gap:6 }}>
                          <button className="btn btn-sm btn-outline" onClick={() => { setForm({...item}); setModal("item"); }}><Icon name="edit" size={12}/> Edit</button>
                          <button className="btn btn-sm btn-outline" style={{ color:"var(--status-red-fg)" }} onClick={() => setDeleteConfirm({ id:item.id, label:item.name||"this item", table:"inventory" })}><Icon name="trash" size={12}/></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* PURCHASES TAB */}
      {tab === "purchases" && (
        <div>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:16 }}>
            <button className="btn btn-primary btn-sm" onClick={() => { setPurForm(emptyPurchase); setModal("purchase"); }}><Icon name="plus" size={13}/>Record Purchase</button>
          </div>
          {purchases.length === 0 ? (
            <div className="empty-state"><Icon name="receipt" size={40}/><h3>No purchase records</h3><p>Record stock purchases to track spending and update stock levels automatically.</p></div>
          ) : (
            <div className="card" style={{ padding:0 }}>
              {/* Desktop table */}
              <div className="table-wrap inventory-desktop-table">
                <table>
                  <thead><tr><th>Date</th><th>Item</th><th>Qty</th><th>Unit Cost</th><th>Total</th><th>Supplier</th><th>Inv Ref</th></tr></thead>
                  <tbody>
                    {purchases.sort((a,b)=>new Date(b.date)-new Date(a.date)).map(p => {
                      const item = items.find(i=>i.id===p.item_id);
                      const supplier = suppliers.find(s=>s.id===p.supplier_id);
                      return (
                        <tr key={p.id}>
                          <td className="text-muted">{formatDate(p.date)}</td>
                          <td style={{ fontWeight:500 }}>{item?.name||p.item_id||"—"}</td>
                          <td>{p.qty} {item?.unit||""}</td>
                          <td>{formatCurrency(p.unit_cost, business?.currency)}</td>
                          <td style={{ fontWeight:700 }}>{formatCurrency(p.total||parseFloat(p.qty)*parseFloat(p.unit_cost), business?.currency)}</td>
                          <td className="text-muted">{supplier?.name||"—"}</td>
                          <td className="text-muted text-sm">{p.invoice_ref||"—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Mobile purchase cards */}
              <div className="inventory-mobile-cards">
                {purchases.sort((a,b)=>new Date(b.date)-new Date(a.date)).map(p => {
                  const item = items.find(i=>i.id===p.item_id);
                  const supplier = suppliers.find(s=>s.id===p.supplier_id);
                  const total = p.total||(parseFloat(p.qty)||0)*(parseFloat(p.unit_cost)||0);
                  return (
                    <div key={p.id} style={{ padding:"13px 16px", borderBottom:"1px solid var(--border-light)" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                        <div style={{ fontWeight:700, fontSize:13, color:"var(--ink)" }}>{item?.name||"—"}</div>
                        <div style={{ fontFamily:"var(--mono)", fontWeight:700, fontSize:14, color:"var(--gold)" }}>{formatCurrency(total, business?.currency)}</div>
                      </div>
                      <div style={{ display:"flex", gap:12, marginBottom:4, fontSize:11.5, color:"#64748b", flexWrap:"wrap" }}>
                        <span>📦 {p.qty} {item?.unit||""} @ {formatCurrency(p.unit_cost, business?.currency)}</span>
                        <span>🏭 {supplier?.name||"—"}</span>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <span style={{ fontSize:11, color:"#94a3b8" }}>{formatDate(p.date)}</span>
                        {p.invoice_ref && <span style={{ fontSize:10.5, color:"#94a3b8", background:"#f1f5f9", padding:"2px 7px", borderRadius:4 }}>Ref: {p.invoice_ref}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* SUPPLIERS TAB */}
      {tab === "suppliers" && (
        <div>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:16 }}>
            <button className="btn btn-primary btn-sm" onClick={() => { setSupForm(emptySupplier); setModal("supplier"); }}><Icon name="plus" size={13}/>Add Supplier</button>
          </div>
          {suppliers.length === 0 ? (
            <div className="empty-state"><Icon name="building" size={40}/><h3>No suppliers added</h3><p>Add your regular material suppliers for quick reference.</p></div>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:14 }}>
              {suppliers.map(s => (
                <div key={s.id} className="card">
                  <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>{s.name}</div>
                  {s.contact && <div className="text-muted text-sm">👤 {s.contact}</div>}
                  {s.phone && <div className="text-muted text-sm">📱 {s.phone}</div>}
                  {s.email && <div className="text-muted text-sm">✉️ {s.email}</div>}
                  {s.address && <div className="text-muted text-sm">📍 {s.address}</div>}
                  <div style={{ display:"flex", gap:8, marginTop:12 }}>
                    <button className="btn btn-outline btn-sm" style={{ flex:1 }} onClick={() => { setSupForm({...s}); setModal("supplier"); }}><Icon name="edit" size={12}/>Edit</button>
                    <button className="btn btn-outline btn-sm" style={{ color:"var(--status-red-fg)" }} onClick={() => setDeleteConfirm({ id: s.id, label: s.name || 'this supplier', table: 'suppliers' })}><Icon name="trash" size={12}/></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ITEM MODAL */}
      {modal === "item" && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>{form.id?"Edit Stock Item":"Add Stock Item"}</h3><button className="btn btn-icon btn-outline btn-sm" onClick={() => setModal(null)}><Icon name="x"/></button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Item Name *</label><input className="form-input" style={itemErrors.name?{borderColor:"#ef4444"}:{}} value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="e.g. 32A Circuit Breaker"/><FormError error={itemErrors.name} /></div>
                <div className="form-group"><label className="form-label">SKU / Code</label><input className="form-input" value={form.sku} onChange={e => setForm({...form, sku:e.target.value})} placeholder="e.g. CB-32A-DBN"/></div>
                <div className="form-group"><label className="form-label">Category</label><select className="form-select" value={form.category} onChange={e => setForm({...form, category:e.target.value})}><option value="">Select</option>{CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Unit of Measure</label><select className="form-select" value={form.unit} onChange={e => setForm({...form, unit:e.target.value})}>{UNITS.map(u=><option key={u} value={u}>{u}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Current Stock (qty)</label><input className="form-input" style={itemErrors.qty?{borderColor:"#ef4444"}:{}} type="number" min="0" value={form.qty_in_stock} onChange={e => setForm({...form, qty_in_stock:e.target.value})}/><FormError error={itemErrors.qty} /></div>
                <div className="form-group"><label className="form-label">Minimum Stock Level</label><input className="form-input" type="number" min="0" value={form.min_stock_level} onChange={e => setForm({...form, min_stock_level:e.target.value})} placeholder="Alert when below this"/></div>
                <div className="form-group"><label className="form-label">Unit Cost ({getCurrencyOption(business?.currency||"ZAR").symbol})</label><input className="form-input" style={itemErrors.unit_cost?{borderColor:"#ef4444"}:{}} type="number" min="0" value={form.unit_cost} onChange={e => setForm({...form, unit_cost:e.target.value})} placeholder="0.00"/><FormError error={itemErrors.unit_cost} /></div>
                <div className="form-group"><label className="form-label">Preferred Supplier</label><select className="form-select" value={form.supplier_id} onChange={e => setForm({...form, supplier_id:e.target.value})}><option value="">None</option>{suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
              </div>
              <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={form.notes} onChange={e => setForm({...form, notes:e.target.value})}/></div>
            </div>
            <div className="modal-footer"><button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button><button className="btn btn-primary" onClick={saveItem}>Save Item</button></div>
          </div>
        </div>
      )}

      {/* PURCHASE MODAL */}
      {modal === "purchase" && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Record Purchase</h3><button className="btn btn-icon btn-outline btn-sm" onClick={() => setModal(null)}><Icon name="x"/></button></div>
            <div className="modal-body">
              <div className="alert alert-info">Recording a purchase will automatically increase the stock quantity for the selected item.</div>
              <div className="form-group"><label className="form-label">Stock Item *</label><select className="form-select" style={purErrors.item_id?{borderColor:"#ef4444"}:{}} value={purForm.item_id} onChange={e => { const it=items.find(i=>i.id===e.target.value); setPurForm({...purForm, item_id:e.target.value, unit_cost:it?.unit_cost||0, supplier_id:it?.supplier_id||""}); }}><option value="">Select item</option>{items.map(i=><option key={i.id} value={i.id}>{i.name} (current: {i.qty_in_stock} {i.unit})</option>)}</select><FormError error={purErrors.item_id} /></div>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Quantity purchased *</label><input className="form-input" style={purErrors.qty?{borderColor:"#ef4444"}:{}} type="number" min="0" value={purForm.qty} onChange={e => setPurForm({...purForm, qty:e.target.value})}/><FormError error={purErrors.qty} /></div>
                <div className="form-group"><label className="form-label">Unit Cost ({getCurrencyOption(business?.currency||"ZAR").symbol}) *</label><input className="form-input" style={purErrors.unit_cost?{borderColor:"#ef4444"}:{}} type="number" min="0" value={purForm.unit_cost} onChange={e => setPurForm({...purForm, unit_cost:e.target.value})}/><FormError error={purErrors.unit_cost} /></div>
                <div className="form-group"><label className="form-label">Date *</label><input className="form-input" style={purErrors.date?{borderColor:"#ef4444"}:{}} type="date" value={purForm.date} onChange={e => setPurForm({...purForm, date:e.target.value})}/><FormError error={purErrors.date} /></div>
                <div className="form-group"><label className="form-label">Supplier</label><select className="form-select" value={purForm.supplier_id} onChange={e => setPurForm({...purForm, supplier_id:e.target.value})}><option value="">None</option>{suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
              </div>
              <div className="form-group"><label className="form-label">Invoice Reference</label><input className="form-input" value={purForm.invoice_ref} onChange={e => setPurForm({...purForm, invoice_ref:e.target.value})} placeholder="e.g. INV-BW-001"/></div>
              {purForm.qty && purForm.unit_cost && (
                <div style={{ background:"var(--surface)", padding:"10px 14px", borderRadius:8, marginTop:8, fontWeight:600 }}>
                  Total: {formatCurrency(parseFloat(purForm.qty)*parseFloat(purForm.unit_cost), business?.currency)}
                </div>
              )}
            </div>
            <div className="modal-footer"><button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button><button className="btn btn-primary" onClick={savePurchase}>Save Purchase & Update Stock</button></div>
          </div>
        </div>
      )}

      {/* SUPPLIER MODAL */}
      {modal === "supplier" && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>{supForm.id?"Edit Supplier":"Add Supplier"}</h3><button className="btn btn-icon btn-outline btn-sm" onClick={() => setModal(null)}><Icon name="x"/></button></div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">Supplier Name *</label><input className="form-input" style={supErrors.name?{borderColor:"#ef4444"}:{}} value={supForm.name} onChange={e => setSupForm({...supForm, name:e.target.value})} placeholder="e.g. Builders Warehouse Johannesburg"/><FormError error={supErrors.name} /></div>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Contact Person</label><input className="form-input" value={supForm.contact} onChange={e => setSupForm({...supForm, contact:e.target.value})}/></div>
                <div className="form-group"><label className="form-label">Phone</label><input className="form-input" style={supErrors.phone?{borderColor:"#ef4444"}:{}} value={supForm.phone} onChange={e => setSupForm({...supForm, phone:e.target.value})} placeholder="011 000 0000"/><FormError error={supErrors.phone} /></div>
                <div className="form-group"><label className="form-label">Email</label><input className="form-input" style={supErrors.email?{borderColor:"#ef4444"}:{}} type="email" value={supForm.email} onChange={e => setSupForm({...supForm, email:e.target.value})}/><FormError error={supErrors.email} /></div>
              </div>
              <div className="form-group"><label className="form-label">Address</label><textarea className="form-textarea" rows={2} value={supForm.address} onChange={e => setSupForm({...supForm, address:e.target.value})}/></div>
            </div>
            <div className="modal-footer"><button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button><button className="btn btn-primary" onClick={saveSupplier}>Save Supplier</button></div>
          </div>
        </div>
      )}
      <DeleteConfirmModal
        item={deleteConfirm?.label}
        onConfirm={async () => {
          if (deleteConfirm) {
            if (deleteConfirm.table === 'suppliers') {
              await DB.remove("suppliers", deleteConfirm.id);
            } else {
              await DB.remove("inventory", deleteConfirm.id);
            }
            await refresh();
            setDeleteConfirm(null);
          }
        }}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}

// ─── COMPLIANCE MODULE ─────────────────────────────────────────────────────────
function ComplianceModule({ userId }) {
  const [docs, setDocs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [business, setBusiness] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);

  const DOC_TYPES = [
    "Business License","Business Registration","Tax Clearance Certificate","Tax Registration",
    "COID / Workmen's Compensation","Public Liability Insurance","Professional Indemnity",
    "Vehicle Insurance","NHBRC Registration","CCC — Certificate of Compliance",
    "Safety File","Worker Certificate","Driver's License (PDP)","First Aid Certificate",
    "Fall Arrest Certificate","Electrical CoC","Gas CoC","Plumbing CoC","Other"
  ];
  const WORKER_TYPES = ["Worker Certificate","Driver's License (PDP)","First Aid Certificate","Fall Arrest Certificate","Other"];

  const emptyForm = { doc_type:"Business License", title:"", issued_by:"", issue_date:"", expiry_date:"", worker_id:"", file_name:"", file_data:"", status:"Valid", notes:"" };
  const [form, setForm] = useState(emptyForm);

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const refresh = async () => {
    const [d, w, b] = await Promise.all([
      DB.getList("compliance", userId), DB.getList("workers", userId), DB.get("business", userId),
    ]);
    setDocs(d||[]); setWorkers(w||[]); setBusiness(b||{}); setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, file_name: file.name, file_data: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const [errors, setErrors] = useState({});

  const save = async () => {
    const errs = {
      title:      V.required(form.title, "Document title"),
      issue_date: form.issue_date ? V.dueDate(form.expiry_date, form.issue_date) : null,
    };
    // Expiry date must be after issue date if both are set
    if (form.expiry_date && form.issue_date && new Date(form.expiry_date) < new Date(form.issue_date)) {
      errs.expiry_date = "Expiry date cannot be before issue date";
    }
    const first = V.run(errs);
    if (first) { setErrors(errs); return; }
    setErrors({});
    const data = { ...form, user_id: userId };
    if (form.id) await DB.update("compliance", form.id, data); else await DB.insert("compliance", data);
    await refresh(); setModal(false); setForm(emptyForm);
  };

  const getStatus = (doc) => {
    if (!doc.expiry_date) return "no-expiry";
    const diff = daysLeft(doc.expiry_date);
    if (diff < 0) return "expired";
    if (diff <= 30) return "expiring-soon";
    return "valid";
  };

  const statusBadge = { "valid":"badge-green", "expiring-soon":"badge-amber", "expired":"badge-red", "no-expiry":"badge-gray" };
  const statusLabel = { "valid":"Valid", "expiring-soon":"Expiring Soon", "expired":"EXPIRED", "no-expiry":"No Expiry" };

  const expiredDocs = docs.filter(d => getStatus(d) === "expired");
  const expiringSoon = docs.filter(d => getStatus(d) === "expiring-soon");

  const businessDocs = docs.filter(d => !WORKER_TYPES.includes(d.doc_type) || !d.worker_id);
  const workerDocs = docs.filter(d => d.worker_id && WORKER_TYPES.includes(d.doc_type));

  return (
    <div>
      {/* Alerts */}
      {expiredDocs.length > 0 && (
        <div className="alert alert-error" style={{ display:"flex", alignItems:"center", gap:10 }}>
          <Icon name="warning" size={16}/>
          <div><strong>{expiredDocs.length} document{expiredDocs.length>1?"s":""} EXPIRED:</strong> {expiredDocs.map(d=>d.title||d.doc_type).join(", ")}. Renew immediately.</div>
        </div>
      )}
      {expiringSoon.length > 0 && (
        <div className="alert alert-warning" style={{ display:"flex", alignItems:"center", gap:10 }}>
          <Icon name="warning" size={16}/>
          <strong>{expiringSoon.length} document{expiringSoon.length>1?"s":""} expiring within 30 days.</strong>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom:20 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background:"rgba(16,185,129,0.1)" }}><Icon name="compliance" size={20} style={{ color:"var(--status-green-fg)" }}/></div>
          <div className="stat-label">Total Documents</div>
          <div className="stat-value">{docs.length}</div>
          <div className="stat-sub">Stored & tracked</div>
        </div>
        <div className="stat-card kpi-green">
          <div className="stat-top">
            <div className="stat-label">Valid Documents</div>
            <div className="stat-icon"><Icon name="check" size={14} style={{ color:"var(--status-grey-fg)" }}/></div>
          </div>
          <div className="stat-value">{docs.filter(d=>getStatus(d)==="valid"||getStatus(d)==="no-expiry").length}</div>
          <div className="stat-sub">Up to date</div>
        </div>
        <div className="stat-card kpi-warn">
          <div className="stat-top">
            <div className="stat-label">Expiring Soon</div>
            <div className="stat-icon"><Icon name="alert" size={14} style={{ color:"var(--status-grey-fg)" }}/></div>
          </div>
          <div className="stat-value">{expiringSoon.length}</div>
          <div className="stat-sub">Within 30 days</div>
        </div>
        <div className="stat-card kpi-red">
          <div className="stat-top">
            <div className="stat-label">Expired</div>
            <div className="stat-icon"><Icon name="warning" size={14} style={{ color:"var(--status-grey-fg)" }}/></div>
          </div>
          <div className="stat-value">{expiredDocs.length}</div>
          <div className="stat-sub">Action required</div>
        </div>
      </div>

      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:16 }}>
        <button className="btn btn-primary" onClick={() => { setForm(emptyForm); setModal(true); }}><Icon name="plus" size={13}/>Add Document</button>
      </div>

      {/* Business Documents */}
      {businessDocs.length > 0 && (
        <div className="card" style={{ marginBottom:16, padding:0 }}>
          <div style={{ padding:"14px 20px", borderBottom:"1px solid var(--border)", fontWeight:600, fontSize:14 }}>🏢 Business Documents</div>
          {/* Desktop table */}
          <div className="table-wrap compliance-desktop-table">
            <table>
              <thead><tr><th>Document Type</th><th>Title / Reference</th><th>Issued By</th><th>Issue Date</th><th>Expiry</th><th>Days Left</th><th>Status</th><th>File</th><th></th></tr></thead>
              <tbody>
                {businessDocs.map(doc => {
                  const st = getStatus(doc);
                  const dl = doc.expiry_date ? daysLeft(doc.expiry_date) : null;
                  return (
                    <tr key={doc.id}>
                      <td style={{ fontWeight:500 }}>{doc.doc_type}</td>
                      <td>{doc.title||"—"}</td>
                      <td className="text-muted">{doc.issued_by||"—"}</td>
                      <td className="text-muted">{formatDate(doc.issue_date)}</td>
                      <td className="text-muted" style={{ color:st==="expired"?"var(--status-red-fg)":st==="expiring-soon"?"var(--status-amber-fg)":"inherit" }}>{formatDate(doc.expiry_date)||"—"}</td>
                      <td style={{ fontWeight:600, color:dl<0?"var(--status-red-fg)":dl<=30?"var(--status-amber-fg)":"var(--status-green-fg)" }}>{dl !== null ? (dl < 0 ? `${Math.abs(dl)}d overdue` : `${dl}d`) : "—"}</td>
                      <td><span className={`badge ${statusBadge[st]}`}>{statusLabel[st]}</span></td>
                      <td>{doc.file_data ? <a href={doc.file_data} download={doc.file_name} className="btn btn-sm btn-outline"><Icon name="download" size={12}/>Download</a> : <span className="text-muted text-sm">No file</span>}</td>
                      <td><div style={{ display:"flex", gap:4 }}><button className="btn btn-icon btn-outline btn-sm" onClick={() => { setForm({...doc}); setModal(true); }}><Icon name="edit" size={12}/></button><button className="btn btn-icon btn-outline btn-sm" style={{ color:"var(--status-red-fg)" }} onClick={() => setDeleteConfirm({ id: doc.id, label: doc.title || doc.doc_type || "this document" })}><Icon name="trash" size={12}/></button></div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="compliance-mobile-cards">
            {businessDocs.map(doc => {
              const st = getStatus(doc);
              const dl = doc.expiry_date ? daysLeft(doc.expiry_date) : null;
              return (
                <div key={doc.id} style={{ padding:"14px 16px", borderBottom:"1px solid var(--border-light)" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:"var(--ink)" }}>{doc.doc_type}</div>
                      {doc.title && <div style={{ fontSize:12, color:"var(--status-grey-fg)", marginTop:1 }}>{doc.title}</div>}
                    </div>
                    <span className={`badge ${statusBadge[st]}`} style={{ marginLeft:8, flexShrink:0 }}>{statusLabel[st]}</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px 12px", fontSize:11.5, color:"var(--status-grey-fg)", marginBottom:8 }}>
                    {doc.issued_by && <div><span style={{ color:"#94a3b8" }}>Issued by: </span>{doc.issued_by}</div>}
                    {doc.issue_date && <div><span style={{ color:"#94a3b8" }}>Issue date: </span>{formatDate(doc.issue_date)}</div>}
                    {doc.expiry_date && <div style={{ color:st==="expired"?"var(--status-red-fg)":st==="expiring-soon"?"var(--status-amber-fg)":"inherit" }}><span style={{ color:"#94a3b8" }}>Expires: </span>{formatDate(doc.expiry_date)}</div>}
                    {dl !== null && <div style={{ fontWeight:600, color:dl<0?"var(--status-red-fg)":dl<=30?"var(--status-amber-fg)":"var(--status-green-fg)" }}>{dl < 0 ? `${Math.abs(dl)}d overdue` : `${dl} days left`}</div>}
                  </div>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    {doc.file_data && <a href={doc.file_data} download={doc.file_name} className="btn btn-sm btn-outline" style={{ fontSize:11 }}><Icon name="download" size={11}/>Download</a>}
                    <button className="btn btn-sm btn-outline" style={{ fontSize:11 }} onClick={() => { setForm({...doc}); setModal(true); }}><Icon name="edit" size={11}/>Edit</button>
                    <button className="btn btn-sm btn-outline" style={{ fontSize:11, color:"var(--status-red-fg)" }} onClick={() => setDeleteConfirm({ id: doc.id, label: doc.title || doc.doc_type || "this document" })}><Icon name="trash" size={11}/>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Worker Documents */}
      {workerDocs.length > 0 && (
        <div className="card" style={{ marginBottom:16, padding:0 }}>
          <div style={{ padding:"14px 20px", borderBottom:"1px solid var(--border)", fontWeight:600, fontSize:14 }}>👷 Worker Certificates &amp; Licences</div>
          {/* Desktop table */}
          <div className="table-wrap compliance-desktop-table">
            <table>
              <thead><tr><th>Worker</th><th>Document Type</th><th>Issued By</th><th>Expiry</th><th>Days Left</th><th>Status</th><th>File</th><th></th></tr></thead>
              <tbody>
                {workerDocs.map(doc => {
                  const worker = workers.find(w=>w.id===doc.worker_id);
                  const st = getStatus(doc);
                  const dl = doc.expiry_date ? daysLeft(doc.expiry_date) : null;
                  return (
                    <tr key={doc.id}>
                      <td style={{ fontWeight:600 }}>{worker?.name||"Unknown"}</td>
                      <td>{doc.doc_type}</td>
                      <td className="text-muted">{doc.issued_by||"—"}</td>
                      <td className="text-muted" style={{ color:st==="expired"?"var(--status-red-fg)":st==="expiring-soon"?"var(--status-amber-fg)":"inherit" }}>{formatDate(doc.expiry_date)||"—"}</td>
                      <td style={{ fontWeight:600, color:dl<0?"var(--status-red-fg)":dl<=30?"var(--status-amber-fg)":"var(--status-green-fg)" }}>{dl !== null ? (dl < 0 ? `${Math.abs(dl)}d overdue` : `${dl}d`) : "—"}</td>
                      <td><span className={`badge ${statusBadge[st]}`}>{statusLabel[st]}</span></td>
                      <td>{doc.file_data ? <a href={doc.file_data} download={doc.file_name} className="btn btn-sm btn-outline"><Icon name="download" size={12}/>Download</a> : <span className="text-muted text-sm">No file</span>}</td>
                      <td><div style={{ display:"flex", gap:4 }}><button className="btn btn-icon btn-outline btn-sm" onClick={() => { setForm({...doc}); setModal(true); }}><Icon name="edit" size={12}/></button><button className="btn btn-icon btn-outline btn-sm" style={{ color:"var(--status-red-fg)" }} onClick={() => setDeleteConfirm({ id: doc.id, label: doc.title || doc.doc_type || "this document" })}><Icon name="trash" size={12}/></button></div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="compliance-mobile-cards">
            {workerDocs.map(doc => {
              const worker = workers.find(w=>w.id===doc.worker_id);
              const st = getStatus(doc);
              const dl = doc.expiry_date ? daysLeft(doc.expiry_date) : null;
              return (
                <div key={doc.id} style={{ padding:"14px 16px", borderBottom:"1px solid var(--border-light)" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:"var(--ink)" }}>{worker?.name||"Unknown Worker"}</div>
                      <div style={{ fontSize:12, color:"var(--status-grey-fg)", marginTop:1 }}>{doc.doc_type}</div>
                    </div>
                    <span className={`badge ${statusBadge[st]}`} style={{ marginLeft:8, flexShrink:0 }}>{statusLabel[st]}</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px 12px", fontSize:11.5, color:"var(--status-grey-fg)", marginBottom:8 }}>
                    {doc.issued_by && <div><span style={{ color:"#94a3b8" }}>Issued by: </span>{doc.issued_by}</div>}
                    {doc.expiry_date && <div style={{ color:st==="expired"?"var(--status-red-fg)":st==="expiring-soon"?"var(--status-amber-fg)":"inherit" }}><span style={{ color:"#94a3b8" }}>Expires: </span>{formatDate(doc.expiry_date)}</div>}
                    {dl !== null && <div style={{ fontWeight:600, color:dl<0?"var(--status-red-fg)":dl<=30?"var(--status-amber-fg)":"var(--status-green-fg)" }}>{dl < 0 ? `${Math.abs(dl)}d overdue` : `${dl} days left`}</div>}
                  </div>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    {doc.file_data && <a href={doc.file_data} download={doc.file_name} className="btn btn-sm btn-outline" style={{ fontSize:11 }}><Icon name="download" size={11}/>Download</a>}
                    <button className="btn btn-sm btn-outline" style={{ fontSize:11 }} onClick={() => { setForm({...doc}); setModal(true); }}><Icon name="edit" size={11}/>Edit</button>
                    <button className="btn btn-sm btn-outline" style={{ fontSize:11, color:"var(--status-red-fg)" }} onClick={() => setDeleteConfirm({ id: doc.id, label: doc.title || doc.doc_type || "this document" })}><Icon name="trash" size={11}/>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {docs.length === 0 && (
        <div className="empty-state"><Icon name="compliance" size={48}/><h3>No compliance documents stored</h3><p>Upload your business licenses, safety docs, insurance papers and worker certificates. You'll get automatic expiry alerts.</p></div>
      )}

      {/* ADD/EDIT MODAL */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>{form.id?"Edit Document":"Add Compliance Document"}</h3><button className="btn btn-icon btn-outline btn-sm" onClick={() => setModal(false)}><Icon name="x"/></button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Document Type *</label>
                  <select className="form-select" value={form.doc_type} onChange={e => setForm({...form, doc_type:e.target.value})}>
                    {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Title / Reference Number *</label>
                  <input className="form-input" style={errors.title?{borderColor:"#ef4444"}:{}} value={form.title} onChange={e => setForm({...form, title:e.target.value})} placeholder="e.g. COID Cert No. 1234567"/>
                  <FormError error={errors.title} />
                </div>
                <div className="form-group">
                  <label className="form-label">Issued By</label>
                  <input className="form-input" value={form.issued_by} onChange={e => setForm({...form, issued_by:e.target.value})} placeholder="e.g. City of Johannesburg"/>
                </div>
                {workers.length > 0 && WORKER_TYPES.includes(form.doc_type) && (
                  <div className="form-group">
                    <label className="form-label">Assign to Worker (optional)</label>
                    <select className="form-select" value={form.worker_id} onChange={e => setForm({...form, worker_id:e.target.value})}>
                      <option value="">Business document</option>
                      {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Issue Date</label>
                  <input className="form-input" type="date" value={form.issue_date} onChange={e => setForm({...form, issue_date:e.target.value})}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Expiry Date</label>
                  <input className="form-input" style={errors.expiry_date?{borderColor:"#ef4444"}:{}} type="date" value={form.expiry_date} onChange={e => setForm({...form, expiry_date:e.target.value})}/>
                  <FormError error={errors.expiry_date} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Upload Document (PDF, image)</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" id="comp-file" style={{ display:"none" }} onChange={handleFile}/>
                <label htmlFor="comp-file" className="btn btn-outline" style={{ cursor:"pointer", display:"inline-flex", alignItems:"center", gap:8 }}>
                  <Icon name="upload" size={14}/>{form.file_name || "Choose File"}
                </label>
                {form.file_name && <div className="text-sm text-muted mt-1">📎 {form.file_name} — saved locally</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => setForm({...form, notes:e.target.value})}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>Save Document</button>
            </div>
          </div>
        </div>
      )}
      <DeleteConfirmModal item={deleteConfirm?.label} onConfirm={async () => { if(deleteConfirm){ await DB.remove("compliance", deleteConfirm.id); await refresh(); setDeleteConfirm(null); } }} onCancel={() => setDeleteConfirm(null)} />
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
// ─── PWA INSTALL PROMPT ───────────────────────────────────────────────────────
// ─── PWA UPDATE PROMPT ───────────────────────────────────────────────────────

// ── EXPORTS ──────────────────────────────────────────────────────────────────
export {
  ShareModal, LandingPage, AuthPage, ForgotPage, SetPasswordPage, ExpiredScreen, Sidebar,
  MobileKpiStrip, Dashboard, CalendarView,
  JobsModule, QuotesModule, EstimatesModule, InvoicesModule, ReceiptsModule,
  TemplatesModule, ClientsModule, WorkersModule, PortalsModule,
  ExpensesModule, InventoryModule, CashflowModule, ComplianceModule,
  ReportsModule, SettingsModule,
};
